import {
  type MouseEvent as ReactMouseEvent,
  type MutableRefObject,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  CandlestickSeries,
  ColorType,
  CrosshairMode,
  HistogramSeries,
  IChartApi,
  ISeriesApi,
  ISeriesMarkersPluginApi,
  CandlestickData,
  LineData,
  LineSeries,
  LineStyle,
  MouseEventParams,
  PriceScaleMode,
  SeriesMarker,
  Time,
  UTCTimestamp,
  WhitespaceData,
  createChart,
  createSeriesMarkers,
} from 'lightweight-charts';
import type { StrategyOverlayResult } from '../overlays/traderPro17Strategy';
import { useKeyboardSafeModal } from '../hooks/useKeyboardSafeModal';
import {
  createAlertsStorageKey,
  createRuntimeStorageKey,
  parseAlertsStorageSnapshot,
  parseRuntimeStorageSnapshot,
  normalizeChartDrawingItems,
} from '../chartStorage';
import { ChartDrawingToolbar } from './ChartDrawingToolbar';
import { DataSourceSettingsPanel } from './DataSourceSettingsPanel';
import type {
  AlertSoundType,
  BasicStudyOverlaySettings,
  BasicIndicatorKey,
  Candle,
  CandleInterval,
  ChartAlertItem,
  ChartDrawingAnchor,
  ChartDrawingItem,
  ChartOverlaySettings,
  ChartDrawingKind,
  ChartDrawingTool,
  ChartTemplateItem,
  ChartScaleModeOption,
  ChartSymbolPrecisionOption,
  DataSourceSettings,
  IndicatorInstance,
  IndicatorInstanceId,
  IndicatorKey,
  OverlayLegendItem,
  OverlayLineStyle,
  OverlaySettings,
  OverlaySelection,
  MobileDrawingToolbarPosition,
  PaperTradingAccountSummary,
  PaperTradingOrderItem,
  PivotPointsLabelPosition,
  PivotPointsLevel,
  RuntimeStatusOptions,
} from '../types';
import {
  BASIC_INDICATOR_DEFINITIONS,
  PANE_INDICATOR_KEYS as PANE_INDICATOR_KEYS_REGISTRY,
  isBasicIndicatorKey,
} from '../indicatorRegistry';
import { computeTraderPro17Overlay } from '../overlays/traderPro17';
import { computeTraderPro17StrategyOverlay } from '../overlays/traderPro17Strategy';
import {
  TraderPro17ShapesPrimitive,
  buildTraderPro17ShapeItems,
} from '../overlays/traderPro17Shapes';
import { computeTraderProV3Overlay } from '../overlays/traderProV3';
import { computeColorProTradingSuiteV3Overlay } from '../overlays/colorProTradingSuiteV3';
import { computeColorProTradingSuiteV31Overlay } from '../overlays/colorProTradingSuiteV31';
import {
  type ColorProTradingSuiteV31StrategyOverlayResult,
  computeColorProTradingSuiteV31StrategyOverlay,
} from '../overlays/colorProTradingSuiteV31Strategy';
import {
  type OpenCode5mStrategyOverlayResult,
  computeOpenCode5mStrategyOverlay,
} from '../overlays/openCode5mStrategy';
import { CandleHelperSignal, computeCandleHelperSignals } from '../overlays/candleHelper';
import { computeCmVixFixUcsSqueezeOverlay } from '../overlays/cmVixFixUcsSqueeze';
import { computeBollingerBandsOverlay } from '../overlays/bollingerBands';
import { BollingerBandsFillPrimitive } from '../overlays/bollingerBandsFill';
import { computeIchimokuCloudOverlay } from '../overlays/ichimokuCloud';
import { computeMurreyMathLinesV6Overlay } from '../overlays/murreyMathLinesV6';
import { buildChartPatternDrawingSegments } from '../overlays/chartPatterns';
import {
  DEFAULT_PIVOT_LEVELS_COLOR,
  DEFAULT_PIVOT_LEVELS_VISIBILITY,
  type BasicStudyComputation,
  type BasicStudyContext,
  computeBasicStudy,
} from '../overlays/basicStudies';
import {
  BasicStudyDrawingPrimitive,
  type BasicStudyDrawingLineInput,
  type BasicStudyDrawingSegmentInput,
} from '../overlays/basicStudyDrawingPrimitive';
import { MAX_PERSISTED_CANDLES } from '../candleConstants';
import type { BotTradeEventPayload } from '../api/botMonitor';
import type { MarketEventItem } from '../api/events';
import type { ProviderName } from '../lib/providerRouting';
import {
  buildFibPriceLevels,
  createDrawingItem,
  getDrawingToolLabel,
  getRequiredAnchorCount,
  updateDrawingAnchors,
} from '../lib/chartDrawings';

export {
  createAlertsStorageKey,
  createRuntimeStorageKey,
  parseAlertsStorageSnapshot,
  parseRuntimeStorageSnapshot,
} from '../chartStorage';

interface Props {
  candles: Candle[];
  murreyMathLinesV6AltCandles?: Candle[];
  traderPro17AltCandles?: Candle[];
  cmVixFixAltCandles?: Candle[];
  breadthByTimestamp?: BasicStudyContext['breadthByTimestamp'];
  botTradeEvents?: BotTradeEventPayload[];
  botTradeStrategyKey?: string;
  newsEvents?: MarketEventItem[];
  ideaEvents?: MarketEventItem[];
  economicEvents?: MarketEventItem[];
  eventsLoading?: boolean;
  eventsErrorMessage?: string;
  strategyMinuteCandles?: Candle[];
  strategyOverlayOverride?: StrategyOverlayResult | null;
  strategyOverlaySourceCandles?: Candle[];
  colorProV31StrategyOverlayOverride?: ColorProTradingSuiteV31StrategyOverlayResult | null;
  colorProV31StrategyOverlaySourceCandles?: Candle[];
  openCode5mStrategyOverlayOverride?: OpenCode5mStrategyOverlayResult | null;
  openCode5mStrategyOverlaySourceCandles?: Candle[];
  dailyCandles: Candle[];
  weeklyCandles: Candle[];
  monthlyCandles: Candle[];
  provider: ProviderName;
  symbol: string;
  interval: CandleInterval;
  providerLabel?: string;
  statusIndicator?: {
    state: 'live' | 'marketClosed' | 'disconnected' | 'reconnecting';
    label: string;
  };
  statusIndicatorDetails?: {
    title: string;
    description: string;
  };
  overlays: OverlaySelection;
  overlaySettings: OverlaySettings;
  indicatorInstances?: IndicatorInstance[];
  instanceSettings?: Record<IndicatorInstanceId, unknown>;
  overlayLegendItems?: OverlayLegendItem[];
  areOverlayLegendItemsVisible?: boolean;
  showMarketStatus?: boolean;
  showIndicatorTitles?: boolean;
  mobileDrawingToolbarCollapsed?: boolean;
  loading?: boolean;
  errorMessage?: string;
  staleMessage?: string;
  staleTone?: 'offline' | 'warning';
  isReplayMode?: boolean;
  replayTotalBars?: number;
  replayIndex?: number | null;
  replayControlsVisible?: boolean;
  replaySelectionEnabled?: boolean;
  onReplayStartSelect?: (index: number) => void;
  onRequestMoreHistory?: () => Promise<void> | void;
  isLoadingMoreHistory?: boolean;
  hasMoreHistory?: boolean;
  onToggleOverlayLegendVisibility?: (instanceId: IndicatorInstanceId) => void;
  onToggleOverlayLegendItemsVisibility?: () => void;
  onShowMarketStatusChange?: (visible: boolean) => void;
  onShowIndicatorTitlesChange?: (visible: boolean) => void;
  onMobileDrawingToolbarCollapsedChange?: (collapsed: boolean) => void;
  onOpenOverlayLegendSettings?: (instanceId: IndicatorInstanceId) => void;
  onRemoveOverlayLegendItem?: (instanceId: IndicatorInstanceId) => void;
  onMoveOverlayLegendItem?: (instanceId: IndicatorInstanceId, direction: 'up' | 'down') => void;
  onSetCandleHelperVisibility?: (visible: boolean) => void;
  onRefreshEvents?: () => Promise<void> | void;
  alertItems?: ChartAlertItem[];
  onAlertItemsChange?: (
    next:
      | ChartAlertItem[]
      | ((current: ChartAlertItem[]) => ChartAlertItem[]),
  ) => void;
  drawingItems?: ChartDrawingItem[];
  onDrawingItemsChange?: (
    next: ChartDrawingItem[] | ((current: ChartDrawingItem[]) => ChartDrawingItem[]),
  ) => void;
  activeDrawingTool?: ChartDrawingTool;
  onActiveDrawingToolChange?: (tool: ChartDrawingTool) => void;
  isMobileOverlayActive?: boolean;
  paperOrders?: PaperTradingOrderItem[];
  paperAccount?: PaperTradingAccountSummary | null;
  onCreatePaperOrder?: (
    payload: Pick<PaperTradingOrderItem, 'provider' | 'symbol' | 'side' | 'orderType' | 'price' | 'quantity'>,
  ) => Promise<void> | void;
  onClearPaperOrders?: (provider: ProviderName, symbol: string) => Promise<void> | void;
  openAlertsRequest?: {
    tab: 'current' | 'all';
    requestId: number;
  } | null;
  dataSources: DataSourceSettings;
  defaultDataSources: DataSourceSettings;
  onDataSourcesChange: (next: DataSourceSettings) => void;
  onHardReloadCandles: () => void;
  onUpdateChartSettings?: (
    updater: (current: ChartOverlaySettings) => ChartOverlaySettings,
  ) => void;
  onVisibleCandleRangeChange?: (
    range: {
      fromIndex: number;
      toIndex: number;
    } | null,
  ) => void;
}

interface ReplayStartAnchor {
  selectedIndex: number;
  clickedLogical: number;
  visibleRange: {
    from: number;
    to: number;
  } | null;
  requiredReplayPadding: number;
}

const INTRADAY_INTERVALS: CandleInterval[] = ['1m', '3m', '5m', '15m', '30m', '45m', '1h', '2h', '3h', '4h'];
const INTERVAL_STEP_SECONDS: Record<CandleInterval, number> = {
  '1m': 60,
  '3m': 180,
  '5m': 300,
  '15m': 900,
  '30m': 1_800,
  '45m': 2_700,
  '1h': 3_600,
  '2h': 7_200,
  '3h': 10_800,
  '4h': 14_400,
  '1d': 86_400,
  '1w': 604_800,
  '1M': 2_592_000,
};
const PANE_INDICATOR_KEYS: IndicatorKey[] = PANE_INDICATOR_KEYS_REGISTRY as IndicatorKey[];
const CM_WVF_RANGE_LINE_COLOR = '#f97316';
const CM_WVF_STD_LINE_COLOR = '#22d3ee';
const MIN_NON_REPLAY_VISIBLE_BARS = 30;
const MAX_FUTURE_WHITESPACE_BARS = 1000;
const INITIAL_FIT_GROWTH_REFIT_MAX_PREVIOUS_BARS = 5;
const DEFAULT_LEGEND_TOP_PX = 56;
const STATUS_LINE_TOP_PX = 12;
const MOBILE_LEGEND_GAP_PX = 8;
const MOBILE_LEGEND_BREAKPOINT_PX = 640;
const STATUS_ACTIONS_HEIGHT_PX = 34;
const LEFT_DRAWING_TOOLBAR_WIDTH_PX = 54;

export function bindChartContainerResize(
  container: HTMLDivElement,
  chart: Pick<IChartApi, 'resize'>,
): () => void {
  let lastWidth = -1;
  let lastHeight = -1;

  const syncChartSize = () => {
    const { clientWidth, clientHeight } = container;

    if (clientWidth === lastWidth && clientHeight === lastHeight) {
      return;
    }

    lastWidth = clientWidth;
    lastHeight = clientHeight;
    chart.resize(clientWidth, clientHeight);
  };

  syncChartSize();

  const resizeObserver =
    typeof ResizeObserver === 'undefined'
      ? null
      : new ResizeObserver(() => {
          syncChartSize();
        });
  resizeObserver?.observe(container);

  window.addEventListener('resize', syncChartSize);
  window.visualViewport?.addEventListener('resize', syncChartSize);

  return () => {
    resizeObserver?.disconnect();
    window.removeEventListener('resize', syncChartSize);
    window.visualViewport?.removeEventListener('resize', syncChartSize);
  };
}

interface ChartSettingsModalFrameProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
}

export function ChartSettingsModalFrame({
  open,
  onClose,
  children,
}: ChartSettingsModalFrameProps) {
  const {
    dialogRef,
    isKeyboardSafeLayout,
    viewportStyle,
  } = useKeyboardSafeModal(open);

  if (!open) {
    return null;
  }

  return (
    <div
      className={`fixed inset-0 z-40 flex bg-slate-950/60 ${
        isKeyboardSafeLayout ? 'items-start justify-stretch p-3 sm:p-4' : 'items-center justify-center p-4'
      }`}
      style={viewportStyle}
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Chart settings"
        tabIndex={-1}
        className={`w-full border border-slate-700 bg-slate-900 shadow-2xl ${
          isKeyboardSafeLayout
            ? 'flex max-h-full min-h-0 flex-1 flex-col overflow-hidden rounded-2xl'
            : 'max-w-5xl rounded-xl'
        }`}
        onClick={(event) => event.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

type VolumePoint = {
  time: UTCTimestamp;
  value: number;
  color: string;
};

function candlesSharePrefix(previous: Candle[], next: Candle[], prefixLength: number): boolean {
  for (let index = 0; index < prefixLength; index += 1) {
    if (previous[index] !== next[index]) {
      return false;
    }
  }
  return true;
}

function isSingleChartStep(
  previousTimestampSeconds: number,
  nextTimestampSeconds: number,
  interval: CandleInterval,
): boolean {
  if (interval === '1M') {
    const previousDate = new Date(previousTimestampSeconds * 1000);
    const nextDate = new Date(nextTimestampSeconds * 1000);
    const monthDelta =
      (nextDate.getUTCFullYear() - previousDate.getUTCFullYear()) * 12 +
      (nextDate.getUTCMonth() - previousDate.getUTCMonth());
    return monthDelta === 1;
  }

  return nextTimestampSeconds - previousTimestampSeconds === INTERVAL_STEP_SECONDS[interval];
}

function addChartTimeSteps(timestampSeconds: number, stepCount: number, interval: CandleInterval): number | null {
  if (!Number.isFinite(timestampSeconds) || !Number.isInteger(stepCount)) {
    return null;
  }

  if (interval === '1M') {
    const date = new Date(timestampSeconds * 1000);
    date.setUTCMonth(date.getUTCMonth() + stepCount);
    const nextTimestampSeconds = Math.floor(date.getTime() / 1000);
    return Number.isFinite(nextTimestampSeconds) ? nextTimestampSeconds : null;
  }

  const nextTimestampSeconds = timestampSeconds + INTERVAL_STEP_SECONDS[interval] * stepCount;
  return Number.isFinite(nextTimestampSeconds) ? nextTimestampSeconds : null;
}

function isPaneIndicator(key: IndicatorKey): boolean {
  return PANE_INDICATOR_KEYS.includes(key);
}

interface ColorProLineSpec {
  key:
    | 'bollingerUpper'
    | 'bollingerBasis'
    | 'bollingerLower'
    | 'dailyResistance'
    | 'dailySupport'
    | 'weeklyResistance'
    | 'weeklySupport'
    | 'pivotPoints';
  group: 'bollinger' | 'daily' | 'weekly' | 'pivot';
  lineWidth: 1 | 2 | 3 | 4;
  lineStyle: LineStyle;
}

const COLORPRO_LINE_SPECS: ColorProLineSpec[] = [
  { key: 'bollingerUpper', group: 'bollinger', lineWidth: 1, lineStyle: LineStyle.Solid },
  { key: 'bollingerBasis', group: 'bollinger', lineWidth: 1, lineStyle: LineStyle.Solid },
  { key: 'bollingerLower', group: 'bollinger', lineWidth: 1, lineStyle: LineStyle.Solid },
  { key: 'dailyResistance', group: 'daily', lineWidth: 1, lineStyle: LineStyle.Dotted },
  { key: 'dailySupport', group: 'daily', lineWidth: 1, lineStyle: LineStyle.Dotted },
  { key: 'weeklyResistance', group: 'weekly', lineWidth: 2, lineStyle: LineStyle.Dotted },
  { key: 'weeklySupport', group: 'weekly', lineWidth: 2, lineStyle: LineStyle.Dotted },
  { key: 'pivotPoints', group: 'pivot', lineWidth: 1, lineStyle: LineStyle.Dashed },
];

interface ColorProMarkerColors {
  bearishPivot: string;
  confirmedReversal: string;
  bullishRegular: string;
  bearishRegular: string;
  bullishHidden: string;
  bearishHidden: string;
}

interface ColorProSignalPoint {
  index: number;
  code: 'B' | 'C' | 'R' | 'H';
  direction: 'bullish' | 'bearish';
  price: number;
}

interface ColorProOverlayResult {
  bollingerUpper: (number | undefined)[];
  bollingerBasis: (number | undefined)[];
  bollingerLower: (number | undefined)[];
  dailyResistance: (number | undefined)[];
  dailySupport: (number | undefined)[];
  weeklyResistance: (number | undefined)[];
  weeklySupport: (number | undefined)[];
  pivotPoints: (number | undefined)[];
  signals: ColorProSignalPoint[];
}

interface CandleHelperMarkerColors {
  bullishEngulfing: string;
  bearishEngulfing: string;
  doji: string;
}

interface BotTradesMarkerColors {
  entry: string;
  exit: string;
  paper: string;
}

interface BotTradesMarkerVisibility {
  showCompletedTrades: boolean;
  showLabels: boolean;
}

interface BasicStudySeriesRefs {
  primary: ISeriesApi<'Line'> | null;
  secondary: ISeriesApi<'Line'> | null;
  tertiary: ISeriesApi<'Line'> | null;
  histogram: ISeriesApi<'Histogram'> | null;
}

type BasicDrawingIndicatorKey =
  | 'chartPatterns'
  | 'autoFibExtension'
  | 'autoFibRetracement'
  | 'autoPitchfork'
  | 'pivotPointsStandard'
  | 'robBookerZivGhostPivots'
  | 'zigZag';

const BASIC_DRAWING_INDICATOR_KEYS: BasicDrawingIndicatorKey[] = [
  'chartPatterns',
  'autoFibExtension',
  'autoFibRetracement',
  'autoPitchfork',
  'pivotPointsStandard',
  'robBookerZivGhostPivots',
  'zigZag',
];

function isBasicDrawingIndicatorKey(key: BasicIndicatorKey): key is BasicDrawingIndicatorKey {
  return BASIC_DRAWING_INDICATOR_KEYS.includes(key as BasicDrawingIndicatorKey);
}

const STATUS_LINE_PRICE_DIGITS = 2;

const DEFAULT_CHART_DISPLAY_SETTINGS: ChartOverlaySettings = {
  autoFit: true,
  scalePriceChartOnly: true,
  invertScale: false,
  scaleMode: 'regular',
  labels: {
    crosshairLabels: true,
    scaleTicks: false,
  },
  lines: {
    horizontalGrid: true,
    verticalGrid: true,
    crosshairLines: true,
  },
  symbol: {
    colorBarsBasedOnPreviousClose: false,
    showBody: true,
    showBorders: true,
    showWick: true,
    upBodyColor: '#22c55e',
    downBodyColor: '#ef4444',
    upBorderColor: '#22c55e',
    downBorderColor: '#ef4444',
    upWickColor: '#22c55e',
    downWickColor: '#ef4444',
    precision: 'default',
    timezone: 'UTC',
  },
  statusLine: {
    showSymbol: true,
    showInterval: true,
    showExchange: true,
    showOHLC: true,
    showChange: true,
    showVolume: false,
    showLastDayChange: false,
    showPaperSummary: false,
    showIndicatorArguments: false,
    backgroundOpacity: 10,
  },
  canvas: {
    backgroundType: 'solid',
    backgroundColor: '#0f172a',
    backgroundTopColor: '#111827',
    backgroundBottomColor: '#0f172a',
    textColor: '#e2e8f0',
    gridColor: '#1e293b',
    crosshairColor: '#94a3b8',
    margins: {
      top: 10,
      bottom: 8,
      right: 5,
    },
  },
  trading: {
    showPositionsAndOrders: true,
    showExecutionMarks: true,
    showExecutionLabels: true,
    showSeriesLabels: true,
  },
  alerts: {
    showAlertLines: true,
    onlyActiveAlerts: true,
    autoHideToasts: true,
    alertSoundType: 'chime',
    alertSoundVolume: 70,
  },
  events: {
    showIdeas: false,
    showSessionBreaks: false,
    showEconomicEvents: false,
    showLatestNews: false,
    showNewsNotifications: false,
  },
};

type ContextMenuKind =
  | 'mainPane'
  | 'indicatorPane'
  | 'priceAxis'
  | 'timeAxis'
  | 'statusLine'
  | 'legendItem';

type ContextMenuSubmenu = 'labels' | 'lines' | 'chartTemplate' | 'timeZone' | null;

interface ChartContextMenuState {
  open: boolean;
  x: number;
  y: number;
  kind: ContextMenuKind;
  submenu: ContextMenuSubmenu;
  price: number | null;
  time: number | null;
  paneIndex: number;
  indicatorInstanceId: IndicatorInstanceId | null;
  indicatorLabel: string | null;
}

interface ChartRuntimeStorageSnapshot {
  version: 2;
  alerts: ChartAlertItem[];
  drawings: ChartDrawingItem[];
  orders: PaperTradingOrderItem[];
  templates: ChartTemplateItem[];
  runtimeStatus: RuntimeStatusOptions;
}

interface ChartAlertsStorageSnapshot {
  version: 1;
  alerts: ChartAlertItem[];
}

interface ToastMessage {
  id: string;
  tone: 'info' | 'success' | 'warning';
  text: string;
}

export type TouchAxisKind = 'priceAxis' | 'timeAxis';

type TouchAxisSeries = ISeriesApi<'Candlestick'> | ISeriesApi<'Line'> | ISeriesApi<'Histogram'>;

interface TouchPaneHit {
  paneIndex: number;
  paneTop: number;
  paneHeight: number;
  paneLocalY: number;
}

interface TouchAxisTarget extends TouchPaneHit {
  axisKind: TouchAxisKind;
  chartX: number;
  chartY: number;
  plotLeft: number;
  plotWidth: number;
  plotX: number;
  targetSeries: TouchAxisSeries | null;
}

interface TouchAxisDragState {
  pointerId: number;
  axisKind: TouchAxisKind;
  paneIndex: number;
  originClientX: number;
  originClientY: number;
  plotLeft: number;
  paneTop: number;
  targetSeries: TouchAxisSeries | null;
  anchorX: number;
  anchorY: number;
  chartWidth: number;
  chartHeight: number;
  initialLogicalRange: { from: number; to: number } | null;
  initialPriceRange: { from: number; to: number } | null;
  anchorLogical: number | null;
  anchorPrice: number | null;
  dragging: boolean;
}

interface MouseInteractionState {
  pointerId: number;
  startedOnAxis: boolean;
}

interface DrawingDraftState {
  kind: ChartDrawingKind;
  anchors: ChartDrawingAnchor[];
  previewAnchor: ChartDrawingAnchor | null;
  previewPoint: ScreenPoint | null;
}

interface DrawingHandleDragState {
  drawingId: string;
  anchorIndex: number;
  pointerId: number;
}

interface DrawingBodyDragState {
  drawingId: string;
  pointerId: number;
  originLogical: number;
  originPrice: number;
  originAnchors: ChartDrawingAnchor[];
}

interface MobileDrawingToolbarDragState {
  pointerId: number;
  originClientX: number;
  originClientY: number;
  originPosition: MobileDrawingToolbarPosition;
}

interface ScreenPoint {
  x: number;
  y: number;
}

interface FloatingPanelRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

const DEFAULT_CONTEXT_MENU_STATE: ChartContextMenuState = {
  open: false,
  x: 0,
  y: 0,
  kind: 'mainPane',
  submenu: null,
  price: null,
  time: null,
  paneIndex: 0,
  indicatorInstanceId: null,
  indicatorLabel: null,
};

const DEFAULT_RUNTIME_STATUS_OPTIONS: RuntimeStatusOptions = {
  showMarketStatus: true,
  showBuySellButtons: true,
  showIndicatorTitles: true,
  priceScaleSide: 'right',
  lockPriceToBarRatio: false,
  verticalCursorLockTime: null,
  mobileDrawingToolbarPosition: null,
  mobileDrawingToolbarCollapsed: false,
};

const MOBILE_DRAWING_TOOLBAR_BREAKPOINT_PX = 680;
const MOBILE_DRAWING_TOOLBAR_MARGIN_PX = 12;
const MOBILE_DRAWING_TOOLBAR_HANDLE_DOUBLE_TAP_MS = 320;
const MOBILE_DRAWING_TOOLBAR_TAP_DRAG_THRESHOLD_PX = 10;

const PRICE_AXIS_HIT_WIDTH_PX = 72;
const TIME_AXIS_HIT_HEIGHT_PX = 30;
const LONG_PRESS_DURATION_MS = 750;
const LONG_PRESS_MOVEMENT_THRESHOLD_PX = 10;
const LOGICAL_RANGE_CHANGE_EPSILON = 0.0001;
const MARKET_EVENTS_PANEL_DEFAULT_WIDTH = 340;
const MARKET_EVENTS_PANEL_DEFAULT_HEIGHT = 360;
const MARKET_EVENTS_PANEL_MIN_WIDTH = 260;
const MARKET_EVENTS_PANEL_MIN_HEIGHT = 160;
const MARKET_EVENTS_PANEL_MARGIN_PX = 12;

function clampUnitInterval(value: number): number {
  return Math.min(1, Math.max(0, value));
}

function clampCoordinate(value: number, size: number): number {
  return Math.min(size, Math.max(0, value));
}

function clampFloatingPanelRect(
  rect: FloatingPanelRect,
  boundsWidth: number,
  boundsHeight: number,
): FloatingPanelRect {
  const maxWidth = Math.max(boundsWidth - MARKET_EVENTS_PANEL_MARGIN_PX * 2, 180);
  const maxHeight = Math.max(boundsHeight - MARKET_EVENTS_PANEL_MARGIN_PX * 2, 120);
  const minWidth = Math.min(MARKET_EVENTS_PANEL_MIN_WIDTH, maxWidth);
  const minHeight = Math.min(MARKET_EVENTS_PANEL_MIN_HEIGHT, maxHeight);
  const width = Math.max(minWidth, Math.min(rect.width, maxWidth));
  const height = Math.max(minHeight, Math.min(rect.height, maxHeight));
  const maxX = Math.max(MARKET_EVENTS_PANEL_MARGIN_PX, boundsWidth - width - MARKET_EVENTS_PANEL_MARGIN_PX);
  const maxY = Math.max(MARKET_EVENTS_PANEL_MARGIN_PX, boundsHeight - height - MARKET_EVENTS_PANEL_MARGIN_PX);

  return {
    x: Math.max(MARKET_EVENTS_PANEL_MARGIN_PX, Math.min(rect.x, maxX)),
    y: Math.max(MARKET_EVENTS_PANEL_MARGIN_PX, Math.min(rect.y, maxY)),
    width,
    height,
  };
}

export function clampMobileDrawingToolbarPosition(
  position: MobileDrawingToolbarPosition,
  panelWidth: number,
  panelHeight: number,
  boundsWidth: number,
  boundsHeight: number,
): MobileDrawingToolbarPosition {
  if (boundsWidth <= 0 || boundsHeight <= 0) {
    return position;
  }

  const maxX = Math.max(
    MOBILE_DRAWING_TOOLBAR_MARGIN_PX,
    boundsWidth - Math.max(0, panelWidth) - MOBILE_DRAWING_TOOLBAR_MARGIN_PX,
  );
  const maxY = Math.max(
    MOBILE_DRAWING_TOOLBAR_MARGIN_PX,
    boundsHeight - Math.max(0, panelHeight) - MOBILE_DRAWING_TOOLBAR_MARGIN_PX,
  );

  return {
    x: Math.max(MOBILE_DRAWING_TOOLBAR_MARGIN_PX, Math.min(position.x, maxX)),
    y: Math.max(MOBILE_DRAWING_TOOLBAR_MARGIN_PX, Math.min(position.y, maxY)),
  };
}

function areMobileDrawingToolbarPositionsEqual(
  left: MobileDrawingToolbarPosition | null,
  right: MobileDrawingToolbarPosition | null,
): boolean {
  if (left === right) {
    return true;
  }

  if (!left || !right) {
    return false;
  }

  return left.x === right.x && left.y === right.y;
}

function areFloatingPanelRectsEqual(left: FloatingPanelRect, right: FloatingPanelRect): boolean {
  return left.x === right.x && left.y === right.y && left.width === right.width && left.height === right.height;
}

function isFiniteRange(range: { from: number; to: number } | null): range is { from: number; to: number } {
  return (
    range !== null &&
    Number.isFinite(range.from) &&
    Number.isFinite(range.to) &&
    range.to > range.from
  );
}

function cloneLogicalRange(range: { from: number; to: number } | null): { from: number; to: number } | null {
  if (!range) {
    return null;
  }

  return { from: range.from, to: range.to };
}

function didLogicalRangeChange(
  previousRange: { from: number; to: number } | null,
  nextRange: { from: number; to: number } | null,
): boolean {
  if (!previousRange && !nextRange) {
    return false;
  }

  if (!previousRange || !nextRange) {
    return true;
  }

  return (
    Math.abs(previousRange.from - nextRange.from) > LOGICAL_RANGE_CHANGE_EPSILON ||
    Math.abs(previousRange.to - nextRange.to) > LOGICAL_RANGE_CHANGE_EPSILON
  );
}

function getAxisDragScaleFactor(delta: number, referenceSize: number): number {
  return Math.pow(2, delta / Math.max(1, referenceSize));
}

function resolveTouchPaneHit(chart: IChartApi, chartRect: DOMRect, clientY: number): TouchPaneHit | null {
  const panes = chart.panes();
  for (let index = 0; index < panes.length; index += 1) {
    const paneElement = panes[index].getHTMLElement();
    if (!paneElement) {
      continue;
    }
    const paneRect = paneElement.getBoundingClientRect();
    if (clientY >= paneRect.top && clientY <= paneRect.bottom) {
      return {
        paneIndex: index,
        paneTop: paneRect.top - chartRect.top,
        paneHeight: paneRect.height,
        paneLocalY: clientY - paneRect.top,
      };
    }
  }

  return null;
}

export function getTimeAxisAnchorLogicalValue(
  initialRange: { from: number; to: number } | null,
  anchorX: number,
  chartWidth: number,
): number | null {
  if (!isFiniteRange(initialRange) || chartWidth <= 0) {
    return null;
  }

  const ratio = clampUnitInterval(anchorX / chartWidth);
  return initialRange.from + ratio * (initialRange.to - initialRange.from);
}

export function getPriceAxisAnchorValue(
  initialRange: { from: number; to: number } | null,
  anchorY: number,
  chartHeight: number,
  invertScale: boolean,
): number | null {
  if (!isFiniteRange(initialRange) || chartHeight <= 0) {
    return null;
  }

  const ratio = clampUnitInterval(anchorY / chartHeight);
  const span = initialRange.to - initialRange.from;
  return invertScale ? initialRange.from + ratio * span : initialRange.from + (1 - ratio) * span;
}

export function hasLongPressMovedBeyondThreshold(
  originClientX: number,
  originClientY: number,
  currentClientX: number,
  currentClientY: number,
): boolean {
  return (
    Math.hypot(currentClientX - originClientX, currentClientY - originClientY) >
    LONG_PRESS_MOVEMENT_THRESHOLD_PX
  );
}

export function resolveTouchAxisKind(
  x: number,
  y: number,
  width: number,
  height: number,
  priceScaleSide: 'left' | 'right',
): TouchAxisKind | null {
  if (width <= 0 || height <= 0) {
    return null;
  }

  const isTimeAxis = y >= height - TIME_AXIS_HIT_HEIGHT_PX;
  const isPriceAxis = priceScaleSide === 'left' ? x <= PRICE_AXIS_HIT_WIDTH_PX : x >= width - PRICE_AXIS_HIT_WIDTH_PX;

  if (isTimeAxis) {
    return 'timeAxis';
  }

  return isPriceAxis ? 'priceAxis' : null;
}

export function isTouchAxisScalingEnabled(axisKind: TouchAxisKind, scalePriceChartOnly: boolean): boolean {
  return axisKind === 'priceAxis' || !scalePriceChartOnly;
}

export function shouldUseCustomAxisTouchDrag(
  pointerType: string,
  axisKind: TouchAxisKind | null,
  scalePriceChartOnly: boolean,
): boolean {
  if (pointerType === 'mouse' || axisKind === null) {
    return false;
  }

  if (pointerType === 'touch') {
    return axisKind === 'priceAxis';
  }

  return isTouchAxisScalingEnabled(axisKind, scalePriceChartOnly);
}

export function shouldStartTouchAxisDrag(
  axisKind: TouchAxisKind,
  originClientX: number,
  originClientY: number,
  currentClientX: number,
  currentClientY: number,
): boolean {
  const deltaX = Math.abs(currentClientX - originClientX);
  const deltaY = Math.abs(currentClientY - originClientY);
  const primaryDelta = axisKind === 'timeAxis' ? deltaX : deltaY;
  const secondaryDelta = axisKind === 'timeAxis' ? deltaY : deltaX;

  return primaryDelta > LONG_PRESS_MOVEMENT_THRESHOLD_PX && primaryDelta >= secondaryDelta;
}

export function getPriceAxisRangeForTouchDrag(
  initialRange: { from: number; to: number } | null,
  anchorPrice: number | null,
  anchorY: number,
  currentY: number,
  chartHeight: number,
  invertScale: boolean,
): { from: number; to: number } | null {
  if (!isFiniteRange(initialRange) || anchorPrice === null || !Number.isFinite(anchorPrice) || chartHeight <= 0) {
    return null;
  }

  const initialSpan = initialRange.to - initialRange.from;
  const nextSpan = Math.max(Number.EPSILON, initialSpan * getAxisDragScaleFactor(currentY - anchorY, chartHeight));
  const anchorRatio = clampUnitInterval(anchorY / chartHeight);

  if (invertScale) {
    return {
      from: anchorPrice - anchorRatio * nextSpan,
      to: anchorPrice + (1 - anchorRatio) * nextSpan,
    };
  }

  return {
    from: anchorPrice - (1 - anchorRatio) * nextSpan,
    to: anchorPrice + anchorRatio * nextSpan,
  };
}

export function getTimeAxisLogicalRangeForTouchDrag(
  initialRange: { from: number; to: number } | null,
  anchorLogical: number | null,
  anchorX: number,
  currentX: number,
  chartWidth: number,
): { from: number; to: number } | null {
  if (!isFiniteRange(initialRange) || anchorLogical === null || !Number.isFinite(anchorLogical) || chartWidth <= 0) {
    return null;
  }

  const initialSpan = initialRange.to - initialRange.from;
  const nextSpan = Math.max(1, initialSpan * getAxisDragScaleFactor(currentX - anchorX, chartWidth));
  const anchorRatio = clampUnitInterval(anchorX / chartWidth);

  return {
    from: anchorLogical - anchorRatio * nextSpan,
    to: anchorLogical + (1 - anchorRatio) * nextSpan,
  };
}

function createRuntimeId(prefix: string): string {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

const ALERT_SOUND_OPTIONS: Array<{ value: AlertSoundType; label: string; description: string }> = [
  { value: 'chime', label: 'Chime', description: 'Soft two-note chime' },
  { value: 'beep', label: 'Beep', description: 'Clear single-tone beep' },
  { value: 'pulse', label: 'Pulse', description: 'Fast triple pulse' },
];

function clampAlertSoundVolume(volume: number): number {
  return Math.max(0, Math.min(100, Math.round(volume)));
}

function isPriceWithinCandle(candle: Candle | null, price: number): boolean {
  if (!candle) {
    return false;
  }

  return candle.low <= price && candle.high >= price;
}

function didPriceAlertTrigger(previousCandle: Candle | null, currentCandle: Candle | null, price: number): boolean {
  if (!currentCandle || !Number.isFinite(price)) {
    return false;
  }

  if (!previousCandle) {
    return isPriceWithinCandle(currentCandle, price);
  }

  const wasWithinPreviousCandle = isPriceWithinCandle(previousCandle, price);
  const isWithinCurrentCandle = isPriceWithinCandle(currentCandle, price);

  if (isWithinCurrentCandle && !wasWithinPreviousCandle) {
    return true;
  }

  return previousCandle.close < price ? currentCandle.high >= price : previousCandle.close > price && currentCandle.low <= price;
}

function buildAlertSoundNotes(soundType: AlertSoundType): Array<{
  delay: number;
  duration: number;
  frequency: number;
  gain: number;
  type: OscillatorType;
}> {
  if (soundType === 'beep') {
    return [{ delay: 0, duration: 0.22, frequency: 880, gain: 0.18, type: 'square' }];
  }

  if (soundType === 'pulse') {
    return [
      { delay: 0, duration: 0.1, frequency: 540, gain: 0.14, type: 'sawtooth' },
      { delay: 0.14, duration: 0.1, frequency: 680, gain: 0.16, type: 'sawtooth' },
      { delay: 0.28, duration: 0.12, frequency: 820, gain: 0.18, type: 'sawtooth' },
    ];
  }

  return [
    { delay: 0, duration: 0.2, frequency: 660, gain: 0.12, type: 'sine' },
    { delay: 0.18, duration: 0.34, frequency: 990, gain: 0.16, type: 'triangle' },
  ];
}

export function buildSessionBreakTimes(candles: Candle[], interval: CandleInterval): number[] {
  if (!INTRADAY_INTERVALS.includes(interval)) {
    return [];
  }

  const breakTimes: number[] = [];
  let previousDayKey: string | null = null;

  for (const candle of candles) {
    const timestamp = toUnixTimestamp(candle.time);
    if (timestamp === null) {
      continue;
    }

    const date = new Date(Number(timestamp) * 1000);
    const dayKey = `${date.getUTCFullYear()}-${date.getUTCMonth()}-${date.getUTCDate()}`;
    if (previousDayKey !== null && previousDayKey !== dayKey) {
      breakTimes.push(Number(timestamp));
    }
    previousDayKey = dayKey;
  }

  return breakTimes;
}

const SYMBOL_PRECISION_OPTIONS: Array<{ value: ChartSymbolPrecisionOption; label: string }> = [
  { value: 'default', label: 'Default' },
  { value: '0', label: 'Integer' },
  { value: '1', label: '1 decimal' },
  { value: '2', label: '2 decimals' },
  { value: '3', label: '3 decimals' },
  { value: '4', label: '4 decimals' },
  { value: '5', label: '5 decimals' },
  { value: '6', label: '6 decimals' },
  { value: '7', label: '7 decimals' },
  { value: '8', label: '8 decimals' },
  { value: '9', label: '9 decimals' },
  { value: '10', label: '10 decimals' },
  { value: '11', label: '11 decimals' },
  { value: '12', label: '12 decimals' },
  { value: '13', label: '13 decimals' },
  { value: '14', label: '14 decimals' },
  { value: '15', label: '15 decimals' },
  { value: '1/2', label: '1/2' },
  { value: '1/4', label: '1/4' },
  { value: '1/8', label: '1/8' },
  { value: '1/16', label: '1/16' },
  { value: '1/32', label: '1/32' },
  { value: '1/64', label: '1/64' },
  { value: '1/128', label: '1/128' },
  { value: '1/320', label: '1/320' },
];

interface SymbolTimezoneOption {
  value: string;
  label: string;
}

const SYMBOL_TIMEZONE_OPTIONS: SymbolTimezoneOption[] = [
  { value: 'UTC', label: 'UTC' },
  { value: 'EXCHANGE', label: 'Exchange' },
  { value: 'Pacific/Honolulu', label: '(UTC-10) Honolulu' },
  { value: 'America/Anchorage', label: '(UTC-9) Anchorage' },
  { value: 'America/Juneau', label: '(UTC-9) Juneau' },
  { value: 'America/Los_Angeles', label: '(UTC-8) Los Angeles' },
  { value: 'America/Vancouver', label: '(UTC-8) Vancouver' },
  { value: 'America/Denver', label: '(UTC-7) Denver' },
  { value: 'America/Phoenix', label: '(UTC-7) Phoenix' },
  { value: 'America/Chicago', label: '(UTC-6) Chicago' },
  { value: 'America/Mexico_City', label: '(UTC-6) Mexico City' },
  { value: 'America/El_Salvador', label: '(UTC-6) San Salvador' },
  { value: 'America/Bogota', label: '(UTC-5) Bogota' },
  { value: 'America/Lima', label: '(UTC-5) Lima' },
  { value: 'America/New_York', label: '(UTC-5) New York' },
  { value: 'America/Toronto', label: '(UTC-5) Toronto' },
  { value: 'America/Caracas', label: '(UTC-4) Caracas' },
  { value: 'America/Halifax', label: '(UTC-4) Halifax' },
  { value: 'America/Argentina/Buenos_Aires', label: '(UTC-3) Buenos Aires' },
  { value: 'America/Santiago', label: '(UTC-3) Santiago' },
  { value: 'America/Sao_Paulo', label: '(UTC-3) Sao Paulo' },
  { value: 'Atlantic/Azores', label: '(UTC-1) Azores' },
  { value: 'Africa/Casablanca', label: '(UTC) Casablanca' },
  { value: 'Europe/Dublin', label: '(UTC) Dublin' },
  { value: 'Europe/Lisbon', label: '(UTC) Lisbon' },
  { value: 'Europe/London', label: '(UTC) London' },
  { value: 'Atlantic/Reykjavik', label: '(UTC) Reykjavik' },
  { value: 'Europe/Amsterdam', label: '(UTC+1) Amsterdam' },
  { value: 'Europe/Belgrade', label: '(UTC+1) Belgrade' },
  { value: 'Europe/Berlin', label: '(UTC+1) Berlin' },
  { value: 'Europe/Bratislava', label: '(UTC+1) Bratislava' },
  { value: 'Europe/Brussels', label: '(UTC+1) Brussels' },
  { value: 'Europe/Budapest', label: '(UTC+1) Budapest' },
  { value: 'Europe/Copenhagen', label: '(UTC+1) Copenhagen' },
  { value: 'Africa/Lagos', label: '(UTC+1) Lagos' },
  { value: 'Europe/Luxembourg', label: '(UTC+1) Luxembourg' },
  { value: 'Europe/Madrid', label: '(UTC+1) Madrid' },
  { value: 'Europe/Malta', label: '(UTC+1) Malta' },
  { value: 'Europe/Oslo', label: '(UTC+1) Oslo' },
  { value: 'Europe/Paris', label: '(UTC+1) Paris' },
  { value: 'Europe/Prague', label: '(UTC+1) Prague' },
  { value: 'Europe/Rome', label: '(UTC+1) Rome' },
  { value: 'Europe/Stockholm', label: '(UTC+1) Stockholm' },
  { value: 'Africa/Tunis', label: '(UTC+1) Tunis' },
  { value: 'Europe/Vienna', label: '(UTC+1) Vienna' },
  { value: 'Europe/Warsaw', label: '(UTC+1) Warsaw' },
  { value: 'Europe/Zurich', label: '(UTC+1) Zurich' },
  { value: 'Europe/Athens', label: '(UTC+2) Athens' },
  { value: 'Europe/Bucharest', label: '(UTC+2) Bucharest' },
  { value: 'Africa/Cairo', label: '(UTC+2) Cairo' },
  { value: 'Europe/Helsinki', label: '(UTC+2) Helsinki' },
  { value: 'Asia/Jerusalem', label: '(UTC+2) Jerusalem' },
  { value: 'Africa/Johannesburg', label: '(UTC+2) Johannesburg' },
  { value: 'Asia/Nicosia', label: '(UTC+2) Nicosia' },
  { value: 'Europe/Riga', label: '(UTC+2) Riga' },
  { value: 'Europe/Tallinn', label: '(UTC+2) Tallinn' },
  { value: 'Europe/Vilnius', label: '(UTC+2) Vilnius' },
  { value: 'Asia/Bahrain', label: '(UTC+3) Bahrain' },
  { value: 'Europe/Istanbul', label: '(UTC+3) Istanbul' },
  { value: 'Asia/Kuwait', label: '(UTC+3) Kuwait' },
  { value: 'Europe/Moscow', label: '(UTC+3) Moscow' },
  { value: 'Africa/Nairobi', label: '(UTC+3) Nairobi' },
  { value: 'Asia/Qatar', label: '(UTC+3) Qatar' },
  { value: 'Asia/Riyadh', label: '(UTC+3) Riyadh' },
  { value: 'Asia/Tehran', label: '(UTC+3:30) Tehran' },
  { value: 'Asia/Dubai', label: '(UTC+4) Dubai' },
  { value: 'Asia/Muscat', label: '(UTC+4) Muscat' },
  { value: 'Asia/Kabul', label: '(UTC+4:30) Kabul' },
  { value: 'Asia/Ashgabat', label: '(UTC+5) Ashgabat' },
  { value: 'Asia/Almaty', label: '(UTC+5) Astana' },
  { value: 'Asia/Karachi', label: '(UTC+5) Karachi' },
  { value: 'Asia/Colombo', label: '(UTC+5:30) Colombo' },
  { value: 'Asia/Kolkata', label: '(UTC+5:30) Kolkata' },
  { value: 'Asia/Kathmandu', label: '(UTC+5:45) Kathmandu' },
  { value: 'Asia/Dhaka', label: '(UTC+6) Dhaka' },
  { value: 'Asia/Yangon', label: '(UTC+6:30) Yangon' },
  { value: 'Asia/Bangkok', label: '(UTC+7) Bangkok' },
  { value: 'Asia/Ho_Chi_Minh', label: '(UTC+7) Ho Chi Minh' },
  { value: 'Asia/Jakarta', label: '(UTC+7) Jakarta' },
  { value: 'Asia/Shanghai', label: '(UTC+8) Chongqing' },
  { value: 'Asia/Hong_Kong', label: '(UTC+8) Hong Kong' },
  { value: 'Asia/Kuala_Lumpur', label: '(UTC+8) Kuala Lumpur' },
  { value: 'Asia/Manila', label: '(UTC+8) Manila' },
  { value: 'Australia/Perth', label: '(UTC+8) Perth' },
  { value: 'Asia/Shanghai', label: '(UTC+8) Shanghai' },
  { value: 'Asia/Singapore', label: '(UTC+8) Singapore' },
  { value: 'Asia/Taipei', label: '(UTC+8) Taipei' },
  { value: 'Asia/Seoul', label: '(UTC+9) Seoul' },
  { value: 'Asia/Tokyo', label: '(UTC+9) Tokyo' },
  { value: 'Australia/Brisbane', label: '(UTC+10) Brisbane' },
  { value: 'Australia/Adelaide', label: '(UTC+10:30) Adelaide' },
  { value: 'Australia/Sydney', label: '(UTC+11) Sydney' },
  { value: 'Pacific/Norfolk', label: '(UTC+12) Norfolk Island' },
  { value: 'Pacific/Auckland', label: '(UTC+13) New Zealand' },
  { value: 'Pacific/Fakaofo', label: '(UTC+13) Tokelau' },
  { value: 'Pacific/Chatham', label: '(UTC+13:45) Chatham Islands' },
];

interface PricePrecisionConfig {
  fractionDigits: number;
  minMove: number;
}

function countFractionDigits(value: number): number {
  const text = value.toString();
  if (!text.includes('.')) {
    return 0;
  }
  return text.length - text.indexOf('.') - 1;
}

function resolvePricePrecisionConfig(
  precision: ChartSymbolPrecisionOption | string,
): PricePrecisionConfig {
  if (precision === 'default') {
    return {
      fractionDigits: STATUS_LINE_PRICE_DIGITS,
      minMove: 1 / 10 ** STATUS_LINE_PRICE_DIGITS,
    };
  }

  if (/^\d+$/.test(precision)) {
    const parsedDigits = Number.parseInt(precision, 10);
    const safeDigits = Number.isFinite(parsedDigits)
      ? Math.max(0, Math.min(15, parsedDigits))
      : STATUS_LINE_PRICE_DIGITS;
    return {
      fractionDigits: safeDigits,
      minMove: 1 / 10 ** safeDigits,
    };
  }

  const fractionMatch = /^1\/(\d+)$/.exec(precision);
  if (fractionMatch) {
    const denominator = Number.parseInt(fractionMatch[1], 10);
    if (Number.isFinite(denominator) && denominator > 0) {
      const minMove = 1 / denominator;
      const fractionDigits = Math.min(15, Math.max(0, countFractionDigits(minMove)));
      return {
        fractionDigits,
        minMove,
      };
    }
  }

  return {
    fractionDigits: STATUS_LINE_PRICE_DIGITS,
    minMove: 1 / 10 ** STATUS_LINE_PRICE_DIGITS,
  };
}

function quantizePrice(value: number, minMove: number): number {
  if (!Number.isFinite(value) || !Number.isFinite(minMove) || minMove <= 0) {
    return value;
  }
  return Math.round(value / minMove) * minMove;
}

function resolveSymbolTimezone(timezoneValue: string): string {
  if (timezoneValue === 'EXCHANGE' || timezoneValue === 'Exchange') {
    return 'UTC';
  }

  try {
    new Intl.DateTimeFormat('en-US', { timeZone: timezoneValue }).format(new Date());
    return timezoneValue;
  } catch {
    return 'UTC';
  }
}

function toPriceScaleMode(mode: ChartScaleModeOption): PriceScaleMode {
  if (mode === 'percent') {
    return PriceScaleMode.Percentage;
  }
  if (mode === 'indexedTo100') {
    return PriceScaleMode.IndexedTo100;
  }
  if (mode === 'logarithmic') {
    return PriceScaleMode.Logarithmic;
  }
  return PriceScaleMode.Normal;
}

function LegendEyeIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-2.5 w-2.5" fill="none" aria-hidden>
      <path
        d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function LegendEyeOffIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-2.5 w-2.5" fill="none" aria-hidden>
      <path
        d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M4.5 4.5l15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function LegendSettingsIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-2.5 w-2.5" fill="none" aria-hidden>
      <path
        d="M12 2.75v2.1M12 19.15v2.1M4.85 4.85l1.5 1.5M17.65 17.65l1.5 1.5M2.75 12h2.1M19.15 12h2.1M4.85 19.15l1.5-1.5M17.65 6.35l1.5-1.5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      <circle cx="12" cy="12" r="3.1" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  );
}

function ChartSettingsIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" aria-hidden>
      <path
        d="M8.02 4.29h7.96l3.73 3.72v7.98l-3.73 3.72H8.02l-3.73-3.72V8.01L8.02 4.3z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="2" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  );
}

function ChartMenuCheckIcon() {
  return (
    <svg viewBox="0 0 12 12" className="h-2.5 w-2.5" fill="none" aria-hidden>
      <path
        d="M2.2 6.4l2.1 2.1 5.5-5.4"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function formatNumber(value: number, fractionDigits: number): string {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);
}

function formatSignedNumber(value: number, fractionDigits: number): string {
  const sign = value >= 0 ? '+' : '-';
  return `${sign}${formatNumber(Math.abs(value), fractionDigits)}`;
}

function formatSignedPercent(value: number): string {
  const sign = value >= 0 ? '+' : '-';
  const percent = formatNumber(Math.abs(value), 2);
  return `${sign}${percent}%`;
}

function formatIndicatorInputNumber(value: number): string {
  if (Number.isInteger(value)) {
    return String(value);
  }

  return value.toFixed(4).replace(/\.?0+$/, '');
}

function formatIndicatorInputValue(value: unknown): string | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? formatIndicatorInputNumber(value) : null;
  }

  if (typeof value === 'boolean') {
    return value ? 'On' : 'Off';
  }

  if (typeof value === 'string') {
    return value;
  }

  return null;
}

export function formatIndicatorInputsText(inputs: Record<string, unknown>): string {
  return Object.values(inputs)
    .map((value) => formatIndicatorInputValue(value))
    .filter((value): value is string => value !== null)
    .join(', ');
}

function toChartLineStyle(style: OverlayLineStyle): LineStyle {
  if (style === 'dashed') {
    return LineStyle.Dashed;
  }
  if (style === 'dotted') {
    return LineStyle.Dotted;
  }
  return LineStyle.Solid;
}

function toSvgDashArray(style: OverlayLineStyle): string | undefined {
  if (style === 'dashed') {
    return '7 5';
  }
  if (style === 'dotted') {
    return '2 5';
  }
  return undefined;
}

function toRgba(hex: string, opacity: number): string {
  const normalized = hex.trim();
  const shortHex = /^#([0-9a-fA-F]{3})$/;
  const longHex = /^#([0-9a-fA-F]{6})$/;

  if (shortHex.test(normalized)) {
    const match = shortHex.exec(normalized);
    const digits = match ? match[1] : '000';
    const r = Number.parseInt(digits[0] + digits[0], 16);
    const g = Number.parseInt(digits[1] + digits[1], 16);
    const b = Number.parseInt(digits[2] + digits[2], 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }

  if (longHex.test(normalized)) {
    const match = longHex.exec(normalized);
    const digits = match ? match[1] : '000000';
    const r = Number.parseInt(digits.slice(0, 2), 16);
    const g = Number.parseInt(digits.slice(2, 4), 16);
    const b = Number.parseInt(digits.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }

  return hex;
}

function toUnixTimestamp(time: string): UTCTimestamp | null {
  const timestampMs = Date.parse(time);
  if (!Number.isFinite(timestampMs)) {
    return null;
  }

  const timestampSeconds = Math.floor(timestampMs / 1000);
  if (!Number.isFinite(timestampSeconds)) {
    return null;
  }

  return timestampSeconds as UTCTimestamp;
}

function parseSessionStartFromRange(sessionRange: string): { hour: number; minute: number } | null {
  const trimmed = sessionRange.trim();
  const match = /^(\d{2})(\d{2})-(\d{2})(\d{2})$/.exec(trimmed);
  if (!match) {
    return null;
  }

  const startHour = Number(match[1]);
  const startMinute = Number(match[2]);
  if (
    !Number.isInteger(startHour) ||
    !Number.isInteger(startMinute) ||
    startHour < 0 ||
    startHour > 23 ||
    startMinute < 0 ||
    startMinute > 59
  ) {
    return null;
  }

  return {
    hour: startHour,
    minute: startMinute,
  };
}

function resolveSessionStartMs(latestCandleTime: string, sessionRange: string): number | null {
  const latestMs = Date.parse(latestCandleTime);
  if (!Number.isFinite(latestMs)) {
    return null;
  }

  const sessionStart = parseSessionStartFromRange(sessionRange);
  if (!sessionStart) {
    return null;
  }

  const latestDate = new Date(latestMs);
  return Date.UTC(
    latestDate.getUTCFullYear(),
    latestDate.getUTCMonth(),
    latestDate.getUTCDate(),
    sessionStart.hour,
    sessionStart.minute,
    0,
    0,
  );
}

function movingAverageSourceValue(
  candle: Candle,
  source: OverlaySettings['movingAverage']['inputs']['source'],
): number {
  if (source === 'open') {
    return candle.open;
  }
  if (source === 'high') {
    return candle.high;
  }
  if (source === 'low') {
    return candle.low;
  }
  return candle.close;
}

function computeSimpleMovingAverageValues(values: number[], period: number): Array<number | undefined> {
  if (period <= 0) {
    return new Array(values.length).fill(undefined);
  }

  const result: Array<number | undefined> = new Array(values.length).fill(undefined);
  let runningTotal = 0;

  for (let index = 0; index < values.length; index += 1) {
    runningTotal += values[index];

    if (index >= period) {
      runningTotal -= values[index - period];
    }

    if (index >= period - 1) {
      result[index] = runningTotal / period;
    }
  }

  return result;
}

function computeMovingAverageSeries(
  candles: Candle[],
  period: number,
  maType: OverlaySettings['movingAverage']['inputs']['maType'],
  source: OverlaySettings['movingAverage']['inputs']['source'],
  offsetPercent: number,
) {
  if (period <= 0) {
    return [];
  }

  const values = candles.map((candle) => movingAverageSourceValue(candle, source));
  const maValues =
    maType === 'ema' ? computeEmaSeries(values, period) : computeSimpleMovingAverageValues(values, period);
  const offsetMultiplier = 1 + offsetPercent / 100;

  const result: { time: UTCTimestamp; value: number }[] = [];
  let previousTime = Number.NEGATIVE_INFINITY;

  for (let index = 0; index < candles.length; index += 1) {
    const value = maValues[index];
    if (value === undefined || !Number.isFinite(value)) {
      continue;
    }

    const time = toUnixTimestamp(candles[index].time);
    if (time === null) {
      continue;
    }
    const unixTime = Number(time);
    if (!Number.isFinite(unixTime) || unixTime <= previousTime) {
      continue;
    }

    result.push({ time, value: value * offsetMultiplier });
    previousTime = unixTime;
  }

  return result;
}

function computeRsiSeries(candles: Candle[], length: number): LineData<UTCTimestamp>[] {
  if (length <= 0 || candles.length <= length) {
    return [];
  }

  const output: LineData<UTCTimestamp>[] = [];
  let gainSum = 0;
  let lossSum = 0;

  for (let index = 1; index <= length; index += 1) {
    const delta = candles[index].close - candles[index - 1].close;
    if (delta >= 0) {
      gainSum += delta;
    } else {
      lossSum += -delta;
    }
  }

  let avgGain = gainSum / length;
  let avgLoss = lossSum / length;
  let previousTime = Number.NEGATIVE_INFINITY;

  const firstRsi = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss);
  const firstTime = toUnixTimestamp(candles[length].time);
  if (firstTime !== null) {
    const unixFirstTime = Number(firstTime);
    if (Number.isFinite(unixFirstTime) && unixFirstTime > previousTime) {
      output.push({ time: firstTime, value: firstRsi });
      previousTime = unixFirstTime;
    }
  }

  for (let index = length + 1; index < candles.length; index += 1) {
    const delta = candles[index].close - candles[index - 1].close;
    const gain = delta > 0 ? delta : 0;
    const loss = delta < 0 ? -delta : 0;

    avgGain = (avgGain * (length - 1) + gain) / length;
    avgLoss = (avgLoss * (length - 1) + loss) / length;
    const rsiValue = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss);

    const time = toUnixTimestamp(candles[index].time);
    if (time === null) {
      continue;
    }
    const unixTime = Number(time);
    if (!Number.isFinite(unixTime) || unixTime <= previousTime) {
      continue;
    }

    output.push({ time, value: rsiValue });
    previousTime = unixTime;
  }

  return output;
}

interface MacdSeriesOutput {
  macdLine: LineData<UTCTimestamp>[];
  signalLine: LineData<UTCTimestamp>[];
  histogram: Array<{ time: UTCTimestamp; value: number }>;
}

function computeEmaSeries(values: number[], length: number): Array<number | undefined> {
  if (length <= 0 || values.length < length) {
    return new Array(values.length).fill(undefined);
  }

  const output: Array<number | undefined> = new Array(values.length).fill(undefined);
  let seedSum = 0;
  for (let index = 0; index < length; index += 1) {
    seedSum += values[index];
  }

  let ema = seedSum / length;
  output[length - 1] = ema;
  const multiplier = 2 / (length + 1);

  for (let index = length; index < values.length; index += 1) {
    ema = (values[index] - ema) * multiplier + ema;
    output[index] = ema;
  }

  return output;
}

function computeMacdSeries(
  candles: Candle[],
  fastLength: number,
  slowLength: number,
  signalLength: number,
): MacdSeriesOutput {
  if (
    candles.length < slowLength + signalLength ||
    fastLength <= 0 ||
    slowLength <= fastLength ||
    signalLength <= 0
  ) {
    return {
      macdLine: [],
      signalLine: [],
      histogram: [],
    };
  }

  const closes = candles.map((candle) => candle.close);
  const fastEma = computeEmaSeries(closes, fastLength);
  const slowEma = computeEmaSeries(closes, slowLength);
  const macdValues: Array<number | undefined> = new Array(candles.length).fill(undefined);

  for (let index = 0; index < candles.length; index += 1) {
    const fast = fastEma[index];
    const slow = slowEma[index];
    if (fast === undefined || slow === undefined) {
      continue;
    }
    macdValues[index] = fast - slow;
  }

  const signalValues: Array<number | undefined> = new Array(candles.length).fill(undefined);
  const signalSeed: number[] = [];
  let signalEma: number | null = null;
  const signalMultiplier = 2 / (signalLength + 1);

  for (let index = 0; index < macdValues.length; index += 1) {
    const macdValue = macdValues[index];
    if (macdValue === undefined) {
      continue;
    }

    if (signalEma === null) {
      signalSeed.push(macdValue);
      if (signalSeed.length < signalLength) {
        continue;
      }
      const seedSum = signalSeed.reduce((total, value) => total + value, 0);
      signalEma = seedSum / signalLength;
      signalValues[index] = signalEma;
      continue;
    }

    signalEma = (macdValue - signalEma) * signalMultiplier + signalEma;
    signalValues[index] = signalEma;
  }

  const output: MacdSeriesOutput = {
    macdLine: [],
    signalLine: [],
    histogram: [],
  };
  let previousTime = Number.NEGATIVE_INFINITY;

  for (let index = 0; index < candles.length; index += 1) {
    const macdValue = macdValues[index];
    const signalValue = signalValues[index];
    if (macdValue === undefined || signalValue === undefined) {
      continue;
    }

    const time = toUnixTimestamp(candles[index].time);
    if (time === null) {
      continue;
    }
    const unixTime = Number(time);
    if (!Number.isFinite(unixTime) || unixTime <= previousTime) {
      continue;
    }

    output.macdLine.push({ time, value: macdValue });
    output.signalLine.push({ time, value: signalValue });
    output.histogram.push({ time, value: macdValue - signalValue });
    previousTime = unixTime;
  }

  return output;
}

function computeProjectedDailyMovingAverage(
  candles: Candle[],
  dailyCandles: Candle[],
  period: number,
): LineData<UTCTimestamp>[] {
  if (!candles.length || !dailyCandles.length || period <= 0 || dailyCandles.length < period) {
    return [];
  }

  const dailyByTime = new Map<number, number>();
  for (const candle of dailyCandles) {
    const time = toUnixTimestamp(candle.time);
    if (time === null || !Number.isFinite(candle.close)) {
      continue;
    }
    dailyByTime.set(Number(time), candle.close);
  }

  const dailyPoints = [...dailyByTime.entries()]
    .sort((left, right) => left[0] - right[0])
    .map(([time, close]) => ({ time, close }));

  if (dailyPoints.length < period) {
    return [];
  }

  const dailySma: Array<number | undefined> = new Array(dailyPoints.length).fill(undefined);
  let runningTotal = 0;
  for (let index = 0; index < dailyPoints.length; index += 1) {
    runningTotal += dailyPoints[index].close;

    if (index >= period) {
      runningTotal -= dailyPoints[index - period].close;
    }

    if (index >= period - 1) {
      dailySma[index] = runningTotal / period;
    }
  }

  const candleTimeSet = new Set<number>();
  for (const candle of candles) {
    const time = toUnixTimestamp(candle.time);
    if (time !== null) {
      candleTimeSet.add(Number(time));
    }
  }
  const candleTimes = [...candleTimeSet].sort((left, right) => left - right);

  const projectedByTime = new Map<number, number>();
  let dailyIndex = -1;

  for (const candleTime of candleTimes) {
    while (dailyIndex + 1 < dailyPoints.length && dailyPoints[dailyIndex + 1].time <= candleTime) {
      dailyIndex += 1;
    }

    if (dailyIndex < 0) {
      continue;
    }

    const value = dailySma[dailyIndex];
    if (value === undefined || !Number.isFinite(value)) {
      continue;
    }

    projectedByTime.set(candleTime, value);
  }

  return [...projectedByTime.entries()].map(([time, value]) => ({
    time: time as UTCTimestamp,
    value,
  }));
}

function toLineData(candles: Candle[], values: (number | undefined)[]): LineData<UTCTimestamp>[] {
  const data: LineData<UTCTimestamp>[] = [];
  let previousTime = Number.NEGATIVE_INFINITY;

  for (let index = 0; index < candles.length; index += 1) {
    const value = values[index];
    if (value === undefined || !Number.isFinite(value)) {
      continue;
    }
    const time = toUnixTimestamp(candles[index].time);
    if (time === null) {
      continue;
    }
    const unixTime = Number(time);
    if (!Number.isFinite(unixTime) || unixTime <= previousTime) {
      continue;
    }

    data.push({
      time,
      value,
    });
    previousTime = unixTime;
  }

  return data;
}

function projectStepSeriesToCandles<T>(
  targetCandles: Candle[],
  sourceCandles: Candle[],
  sourceValues: Array<T | undefined>,
): Array<T | undefined> {
  const projected: Array<T | undefined> = new Array(targetCandles.length).fill(undefined);
  if (!targetCandles.length || !sourceCandles.length || !sourceValues.length) {
    return projected;
  }

  const sourcePoints: Array<{ time: number; value: T }> = [];
  const sourceLength = Math.min(sourceCandles.length, sourceValues.length);
  for (let index = 0; index < sourceLength; index += 1) {
    const value = sourceValues[index];
    if (value === undefined) {
      continue;
    }

    const time = toUnixTimestamp(sourceCandles[index].time);
    if (time === null) {
      continue;
    }

    sourcePoints.push({
      time: Number(time),
      value,
    });
  }

  if (!sourcePoints.length) {
    return projected;
  }

  sourcePoints.sort((left, right) => left.time - right.time);

  let sourceIndex = -1;
  let activeValue: T | undefined;
  for (let index = 0; index < targetCandles.length; index += 1) {
    const targetTime = toUnixTimestamp(targetCandles[index].time);
    if (targetTime === null) {
      continue;
    }

    const unixTime = Number(targetTime);
    while (sourceIndex + 1 < sourcePoints.length && sourcePoints[sourceIndex + 1].time <= unixTime) {
      sourceIndex += 1;
      activeValue = sourcePoints[sourceIndex].value;
    }

    projected[index] = activeValue;
  }

  return projected;
}

function toConstantLineData(
  candles: Candle[],
  value: number,
  startTimestampMs?: number,
): LineData<UTCTimestamp>[] {
  if (!Number.isFinite(value)) {
    return [];
  }

  const data: LineData<UTCTimestamp>[] = [];
  let previousTime = Number.NEGATIVE_INFINITY;

  for (let index = 0; index < candles.length; index += 1) {
    if (startTimestampMs !== undefined) {
      const candleTimeMs = Date.parse(candles[index].time);
      if (!Number.isFinite(candleTimeMs) || candleTimeMs < startTimestampMs) {
        continue;
      }
    }

    const time = toUnixTimestamp(candles[index].time);
    if (time === null) {
      continue;
    }
    const unixTime = Number(time);
    if (!Number.isFinite(unixTime) || unixTime <= previousTime) {
      continue;
    }
    data.push({ time, value });
    previousTime = unixTime;
  }

  return data;
}

const LINE_SERIES_ERROR_LOG_COOLDOWN_MS = 15_000;
const lineSeriesErrorLogTimestamps = new Map<string, number>();

function setLineSeriesDataSafely(
  series: ISeriesApi<'Line'>,
  data: LineData<UTCTimestamp>[],
  label: string,
): void {
  try {
    series.setData(data);
  } catch (error) {
    const now = Date.now();
    const lastLoggedAt = lineSeriesErrorLogTimestamps.get(label) ?? 0;
    if (now - lastLoggedAt >= LINE_SERIES_ERROR_LOG_COOLDOWN_MS) {
      console.error(`[chart] failed to set line data: ${label}`, error);
      lineSeriesErrorLogTimestamps.set(label, now);
    }
    try {
      series.setData([]);
    } catch {
      return;
    }
  }
}

function buildMaCrossMarkers(
  candles: Candle[],
  primary: Array<number | undefined>,
  secondary: Array<number | undefined>,
  bullishColor: string,
  bearishColor: string,
): SeriesMarker<UTCTimestamp>[] {
  const markers: SeriesMarker<UTCTimestamp>[] = [];

  for (let index = 1; index < candles.length; index += 1) {
    const previousPrimary = primary[index - 1];
    const previousSecondary = secondary[index - 1];
    const currentPrimary = primary[index];
    const currentSecondary = secondary[index];

    if (
      previousPrimary === undefined ||
      previousSecondary === undefined ||
      currentPrimary === undefined ||
      currentSecondary === undefined
    ) {
      continue;
    }

    const previousDiff = previousPrimary - previousSecondary;
    const currentDiff = currentPrimary - currentSecondary;

    if (previousDiff <= 0 && currentDiff > 0) {
      const time = toUnixTimestamp(candles[index].time);
      if (time === null) {
        continue;
      }
      markers.push({
        time,
        position: 'belowBar',
        shape: 'arrowUp',
        color: bullishColor,
      });
      continue;
    }

    if (previousDiff >= 0 && currentDiff < 0) {
      const time = toUnixTimestamp(candles[index].time);
      if (time === null) {
        continue;
      }
      markers.push({
        time,
        position: 'aboveBar',
        shape: 'arrowDown',
        color: bearishColor,
      });
    }
  }

  return markers;
}

const PIVOT_POINTS_LEVELS: PivotPointsLevel[] = ['p', 'r1', 'r2', 'r3', 'r4', 'r5', 's1', 's2', 's3', 's4', 's5'];

function pivotPointsLevelLabel(level: PivotPointsLevel): string {
  if (level === 'p') {
    return 'P';
  }
  return level.toUpperCase();
}

function buildPivotPointsDrawingLineInputs(
  computed: BasicStudyComputation,
  settings: BasicStudyOverlaySettings,
): BasicStudyDrawingLineInput[] {
  if (!computed.pivotLevels) {
    return [];
  }

  const showLabels = settings.inputs.pivotPointsShowLabels ?? true;
  const showPrices = settings.inputs.pivotPointsShowPrices ?? true;
  const labelsPosition = (settings.inputs.pivotPointsLabelsPosition ?? 'right') as PivotPointsLabelPosition;
  const visibility = settings.visibility.pivotLevels ?? DEFAULT_PIVOT_LEVELS_VISIBILITY;
  const colors = settings.style.pivotLevelsColor ?? DEFAULT_PIVOT_LEVELS_COLOR;
  const lines: BasicStudyDrawingLineInput[] = [];

  for (const level of PIVOT_POINTS_LEVELS) {
    const values = computed.pivotLevels[level];
    if (!values || values.every((value) => value === undefined)) {
      continue;
    }

    if ((visibility[level] ?? DEFAULT_PIVOT_LEVELS_VISIBILITY[level]) === false) {
      continue;
    }

    const label = showLabels ? pivotPointsLevelLabel(level) : '';
    const color = colors[level] ?? DEFAULT_PIVOT_LEVELS_COLOR[level];
    lines.push({
      values,
      color,
      width: settings.style.lineWidth,
      style: toChartLineStyle(settings.style.lineStyle),
      label,
      labelColor: color,
      labelMode: 'all',
      showPriceInLabel: showPrices,
      labelPosition: labelsPosition,
      breakOnValueChange: true,
    });
  }

  return lines;
}

function basicStudyDrawingLabel(
  key: BasicDrawingIndicatorKey,
  slot: 'primary' | 'secondary' | 'tertiary',
): string | undefined {
  if (key === 'autoFibRetracement') {
    if (slot === 'primary') {
      return 'Fib 38.2%';
    }
    if (slot === 'secondary') {
      return 'Fib 61.8%';
    }
    return 'Fib Base';
  }

  if (key === 'autoFibExtension') {
    if (slot === 'primary') {
      return 'Fib 127.2%';
    }
    if (slot === 'secondary') {
      return 'Fib 161.8%';
    }
    return 'Fib 200%';
  }

  if (key === 'autoPitchfork') {
    if (slot === 'primary') {
      return 'Median';
    }
    if (slot === 'secondary') {
      return 'Upper';
    }
    return 'Lower';
  }

  if (key === 'robBookerZivGhostPivots') {
    if (slot === 'primary') {
      return 'Ghost Pivot';
    }
    if (slot === 'secondary') {
      return 'Ghost High';
    }
    return 'Ghost Low';
  }

  if (key === 'zigZag' && slot === 'primary') {
    return 'ZigZag';
  }

  return undefined;
}

function buildBasicStudyDrawingLineInputs(
  key: BasicDrawingIndicatorKey,
  computed: BasicStudyComputation,
  settings: BasicStudyOverlaySettings,
): BasicStudyDrawingLineInput[] {
  if (key === 'pivotPointsStandard') {
    return buildPivotPointsDrawingLineInputs(computed, settings);
  }

  const lines: BasicStudyDrawingLineInput[] = [];

  if (settings.visibility.showPrimary) {
    lines.push({
      values: computed.primary,
      color: settings.style.primaryColor,
      width: settings.style.lineWidth,
      style: toChartLineStyle(settings.style.lineStyle),
      label: basicStudyDrawingLabel(key, 'primary'),
      labelColor: settings.style.primaryColor,
    });
  }

  if (
    key !== 'zigZag' &&
    settings.visibility.showSecondary &&
    computed.secondary.some((value) => value !== undefined)
  ) {
    lines.push({
      values: computed.secondary,
      color: settings.style.secondaryColor,
      width: settings.style.lineWidth,
      style: toChartLineStyle(settings.style.lineStyle),
      label: basicStudyDrawingLabel(key, 'secondary'),
      labelColor: settings.style.secondaryColor,
    });
  }

  if (
    key !== 'zigZag' &&
    settings.visibility.showTertiary &&
    computed.tertiary.some((value) => value !== undefined)
  ) {
    lines.push({
      values: computed.tertiary,
      color: settings.style.tertiaryColor,
      width: settings.style.lineWidth,
      style: toChartLineStyle(settings.style.lineStyle),
      label: basicStudyDrawingLabel(key, 'tertiary'),
      labelColor: settings.style.tertiaryColor,
    });
  }

  return lines;
}

interface IndexedDrawingPoint {
  index: number;
  time: UTCTimestamp;
  price: number;
}

function runToAnchoredSegments(
  run: IndexedDrawingPoint[],
  line: Pick<BasicStudyDrawingLineInput, 'color' | 'style' | 'width' | 'label' | 'labelColor'>,
): BasicStudyDrawingSegmentInput[] {
  if (run.length < 2) {
    return [];
  }

  const slopeEpsilon = 1e-6;
  const jumpEpsilon = 1e-5;
  const anchors: IndexedDrawingPoint[] = [run[0]];

  for (let index = 1; index < run.length - 1; index += 1) {
    const previous = run[index - 1];
    const current = run[index];
    const next = run[index + 1];

    const firstSpan = Math.max(1, current.index - previous.index);
    const secondSpan = Math.max(1, next.index - current.index);
    const firstSlope = (current.price - previous.price) / firstSpan;
    const secondSlope = (next.price - current.price) / secondSpan;
    const changedSlope = Math.abs(secondSlope - firstSlope) > slopeEpsilon;
    const jumped = firstSpan === 1 && Math.abs(current.price - previous.price) > jumpEpsilon;

    if (changedSlope || jumped) {
      anchors.push(current);
    }
  }

  anchors.push(run[run.length - 1]);

  const segments: BasicStudyDrawingSegmentInput[] = [];
  for (let index = 1; index < anchors.length; index += 1) {
    const start = anchors[index - 1];
    const end = anchors[index];
    if (start.time === end.time || start.price === end.price && start.index === end.index) {
      continue;
    }
    segments.push({
      points: [
        { time: start.time, price: start.price },
        { time: end.time, price: end.price },
      ],
      color: line.color,
      width: line.width,
      style: line.style,
    });
  }

  return segments;
}

function segmentsForLine(candles: Candle[], line: BasicStudyDrawingLineInput): BasicStudyDrawingSegmentInput[] {
  const indexedPoints: IndexedDrawingPoint[] = [];

  for (let index = 0; index < candles.length; index += 1) {
    const value = line.values[index];
    if (value === undefined || !Number.isFinite(value)) {
      continue;
    }

    const time = toUnixTimestamp(candles[index].time);
    if (time === null) {
      continue;
    }

    indexedPoints.push({
      index,
      time,
      price: value,
    });
  }

  if (indexedPoints.length < 2) {
    return [];
  }

  const segments: BasicStudyDrawingSegmentInput[] = [];
  let run: IndexedDrawingPoint[] = [indexedPoints[0]];

  for (let index = 1; index < indexedPoints.length; index += 1) {
    const point = indexedPoints[index];
    const previous = indexedPoints[index - 1];
    const valueChanged = line.breakOnValueChange === true && point.price !== previous.price;
    if (point.index !== previous.index + 1 || valueChanged) {
      segments.push(...runToAnchoredSegments(run, line));
      run = [point];
      continue;
    }

    run.push(point);
  }

  segments.push(...runToAnchoredSegments(run, line));

  const hasLabelText = (line.label ?? '').trim().length > 0;
  const shouldRenderPrice = line.showPriceInLabel === true;
  if ((hasLabelText || shouldRenderPrice) && segments.length > 0) {
    const formatLabel = (segment: BasicStudyDrawingSegmentInput): string => {
      const anchorPoint =
        line.labelPosition === 'left' ? segment.points[0] : segment.points[segment.points.length - 1];
      const baseLabel = (line.label ?? '').trim();
      if (!shouldRenderPrice || !anchorPoint || !Number.isFinite(anchorPoint.price)) {
        return baseLabel;
      }
      const priceText = anchorPoint.price.toFixed(2);
      return baseLabel.length > 0 ? `${baseLabel} ${priceText}` : priceText;
    };

    if (line.labelMode === 'all') {
      for (let index = 0; index < segments.length; index += 1) {
        const formattedLabel = formatLabel(segments[index]);
        if (formattedLabel.length === 0) {
          continue;
        }
        segments[index] = {
          ...segments[index],
          label: formattedLabel,
          labelColor: line.labelColor,
          labelPosition: line.labelPosition,
        };
      }
    } else {
      const lastIndex = segments.length - 1;
      const formattedLabel = formatLabel(segments[lastIndex]);
      if (formattedLabel.length > 0) {
        segments[lastIndex] = {
          ...segments[lastIndex],
          label: formattedLabel,
          labelColor: line.labelColor,
          labelPosition: line.labelPosition,
        };
      }
    }
  }

  return segments;
}

function buildBasicStudyDrawingSegments(
  key: BasicDrawingIndicatorKey,
  candles: Candle[],
  computed: BasicStudyComputation,
  settings: BasicStudyOverlaySettings,
): BasicStudyDrawingSegmentInput[] {
  if (key === 'chartPatterns') {
    return buildChartPatternDrawingSegments(candles, settings);
  }

  const lineInputs = buildBasicStudyDrawingLineInputs(key, computed, settings);
  return lineInputs.flatMap((line) => segmentsForLine(candles, line));
}

function toColorProMarker(
  signal: ColorProSignalPoint,
  candles: Candle[],
  colors: ColorProMarkerColors,
): SeriesMarker<UTCTimestamp> | null {
  const candle = candles[signal.index];
  const fallbackTime = candles[candles.length - 1]?.time ?? new Date().toISOString();
  const markerTime = toUnixTimestamp(candle ? candle.time : fallbackTime);
  if (markerTime === null) {
    return null;
  }

  if (signal.code === 'B') {
    return {
      time: markerTime,
      position: 'aboveBar',
      shape: 'arrowDown',
      color: colors.bearishPivot,
      text: 'B',
    };
  }

  if (signal.code === 'C') {
    return {
      time: markerTime,
      position: 'belowBar',
      shape: 'arrowUp',
      color: colors.confirmedReversal,
      text: 'C',
    };
  }

  const isBullish = signal.direction === 'bullish';
  const isRegular = signal.code === 'R';

  return {
    time: markerTime,
    position: isBullish ? 'belowBar' : 'aboveBar',
    shape: 'square',
    color: isRegular
      ? isBullish
        ? colors.bullishRegular
        : colors.bearishRegular
      : isBullish
      ? colors.bullishHidden
      : colors.bearishHidden,
    text: signal.code,
  };
}

function toCandleHelperMarker(
  signal: CandleHelperSignal,
  candles: Candle[],
  colors: CandleHelperMarkerColors,
  showLabels: boolean,
  markerSize: number,
): SeriesMarker<UTCTimestamp> | null {
  const candle = candles[signal.index];
  if (!candle) {
    return null;
  }
  const markerTime = toUnixTimestamp(candle.time);
  if (markerTime === null) {
    return null;
  }

  if (signal.type === 'bullishEngulfing') {
    return {
      time: markerTime,
      position: 'inBar',
      shape: 'circle',
      color: colors.bullishEngulfing,
      text: showLabels ? 'Bull Engulf' : '',
      size: markerSize,
    };
  }

  if (signal.type === 'bearishEngulfing') {
    return {
      time: markerTime,
      position: 'inBar',
      shape: 'square',
      color: colors.bearishEngulfing,
      text: showLabels ? 'Bear Engulf' : '',
      size: markerSize,
    };
  }

  return {
    time: markerTime,
    position: 'inBar',
    shape: 'circle',
    color: colors.doji,
    text: showLabels ? 'Doji' : '',
    size: markerSize,
  };
}

function classifyBotTradeEventType(eventTypeRaw: string): 'completed' | 'active' | 'unknown' {
  if (
    eventTypeRaw === 'entry' ||
    eventTypeRaw === 'open' ||
    eventTypeRaw === 'enter' ||
    eventTypeRaw === 'fill' ||
    eventTypeRaw === 'exit' ||
    eventTypeRaw === 'close' ||
    eventTypeRaw === 'take_profit' ||
    eventTypeRaw === 'stop_loss' ||
    eventTypeRaw === 'trailing_stop' ||
    eventTypeRaw === 'tp' ||
    eventTypeRaw === 'sl'
  ) {
    return 'completed';
  }

  if (
    eventTypeRaw === 'stop_update' ||
    eventTypeRaw === 'trail_update' ||
    eventTypeRaw === 'breakeven_update' ||
    eventTypeRaw === 'pending_entry'
  ) {
    return 'active';
  }

  return 'unknown';
}

function toBotTradeMarker(
  event: BotTradeEventPayload,
  colors: BotTradesMarkerColors,
  visibility: BotTradesMarkerVisibility,
): SeriesMarker<UTCTimestamp> | null {
  const occurredAtMs = Date.parse(event.occurred_at);
  if (!Number.isFinite(occurredAtMs)) {
    return null;
  }

  const side = (event.side ?? '').trim().toLowerCase();
  const isSellSide = side === 'sell' || side === 'short';
  const payload = event.payload;
  const eventTypeRaw =
    typeof payload.event_type === 'string' && payload.event_type.length > 0
      ? payload.event_type.toLowerCase()
      : '';
  const eventCategory = classifyBotTradeEventType(eventTypeRaw);
  const isEntryEvent =
    eventTypeRaw === 'entry' ||
    eventTypeRaw === 'open' ||
    eventTypeRaw === 'enter' ||
    eventTypeRaw === 'fill';
  const isExitEvent =
    eventTypeRaw === 'exit' ||
    eventTypeRaw === 'close' ||
    eventTypeRaw === 'take_profit' ||
    eventTypeRaw === 'stop_loss' ||
    eventTypeRaw === 'trailing_stop' ||
    eventTypeRaw === 'tp' ||
    eventTypeRaw === 'sl';
  if (eventCategory !== 'completed') {
    return null;
  }
  if (!visibility.showCompletedTrades) {
    return null;
  }

  const isPaperMode =
    payload.mode === 'paper' || payload.execution_mode === 'paper' || payload.trade_mode === 'paper';

  const color =
    isPaperMode
      ? colors.paper
      : isExitEvent || isSellSide
        ? colors.exit
        : colors.entry;

  let text = isSellSide ? 'S' : 'B';
  if (isEntryEvent) {
    text = isSellSide ? 'SE' : 'BE';
  } else if (isExitEvent) {
    text = isSellSide ? 'SX' : 'BX';
  }
  if (isPaperMode) {
    text = `${text}P`;
  }

  const markerTime = toUnixTimestamp(event.occurred_at);
  if (markerTime === null) {
    return null;
  }

  return {
    time: markerTime,
    position: isSellSide ? 'aboveBar' : 'belowBar',
    shape: isEntryEvent
      ? isSellSide
        ? 'arrowDown'
        : 'arrowUp'
      : isExitEvent
        ? 'circle'
        : 'square',
    color,
    text: visibility.showLabels ? text : '',
  };
}

export function CandlestickChart({
  candles,
  murreyMathLinesV6AltCandles,
  traderPro17AltCandles,
  cmVixFixAltCandles,
  breadthByTimestamp,
  botTradeEvents = [],
  botTradeStrategyKey,
  newsEvents = [],
  ideaEvents = [],
  economicEvents = [],
  eventsLoading = false,
  eventsErrorMessage,
  strategyMinuteCandles,
  strategyOverlayOverride,
  strategyOverlaySourceCandles,
  colorProV31StrategyOverlayOverride,
  colorProV31StrategyOverlaySourceCandles,
  openCode5mStrategyOverlayOverride,
  openCode5mStrategyOverlaySourceCandles,
  dailyCandles,
  weeklyCandles,
  monthlyCandles,
  provider,
  symbol,
  interval,
  providerLabel,
  statusIndicator,
  statusIndicatorDetails,
  overlays,
  overlaySettings,
  indicatorInstances = [],
  instanceSettings = {},
  overlayLegendItems = [],
  areOverlayLegendItemsVisible = true,
  showMarketStatus,
  showIndicatorTitles,
  mobileDrawingToolbarCollapsed,
  loading,
  errorMessage,
  staleMessage,
  staleTone = 'warning',
  isReplayMode = false,
  replayTotalBars = 0,
  replayIndex = null,
  replayControlsVisible = false,
  replaySelectionEnabled,
  onReplayStartSelect,
  onRequestMoreHistory,
  isLoadingMoreHistory,
  hasMoreHistory,
  onToggleOverlayLegendVisibility,
  onToggleOverlayLegendItemsVisibility,
  onShowMarketStatusChange,
  onShowIndicatorTitlesChange,
  onMobileDrawingToolbarCollapsedChange,
  onOpenOverlayLegendSettings,
  onRemoveOverlayLegendItem,
  onMoveOverlayLegendItem,
  onSetCandleHelperVisibility,
  onRefreshEvents,
  alertItems: controlledAlertItems,
  onAlertItemsChange,
  drawingItems: controlledDrawingItems,
  onDrawingItemsChange,
  activeDrawingTool = 'cursor',
  onActiveDrawingToolChange,
  isMobileOverlayActive = false,
  paperOrders: controlledPaperOrders,
  paperAccount,
  onCreatePaperOrder,
  onClearPaperOrders,
  openAlertsRequest,
  dataSources,
  defaultDataSources,
  onDataSourcesChange,
  onHardReloadCandles,
  onUpdateChartSettings,
  onVisibleCandleRangeChange,
}: Props) {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const candlesRef = useRef(candles);
  const statusLineRef = useRef<HTMLDivElement | null>(null);
  const statusLineHeaderRef = useRef<HTMLDivElement | null>(null);
  const statusLineValuesRef = useRef<HTMLDivElement | null>(null);
  const statusLineHeaderTextRef = useRef<HTMLSpanElement | null>(null);
  const statusLineValuesTextRef = useRef<HTMLSpanElement | null>(null);
  const statusLineSeparatorRef = useRef<HTMLSpanElement | null>(null);
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const containerRef = useCallback((node: HTMLDivElement | null) => {
    setContainer(node);
  }, []);
  const [mobileDrawingToolbarPanel, setMobileDrawingToolbarPanel] = useState<HTMLDivElement | null>(null);
  const mobileDrawingToolbarPanelRef = useCallback((node: HTMLDivElement | null) => {
    setMobileDrawingToolbarPanel(node);
  }, []);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
  const volumeSeriesRef = useRef<ISeriesApi<'Histogram'> | null>(null);
  const rsiSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const rsiZoneLinesRef = useRef<ReturnType<ISeriesApi<'Line'>['createPriceLine']>[]>([]);
  const macdLineSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const macdSignalSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const macdHistogramSeriesRef = useRef<ISeriesApi<'Histogram'> | null>(null);
  const cmVixFixHistogramSeriesRef = useRef<ISeriesApi<'Histogram'> | null>(null);
  const cmSqueezeMomentumSeriesRef = useRef<ISeriesApi<'Histogram'> | null>(null);
  const cmSqueezeOnSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const cmSqueezeOffSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const cmNoSqueezeSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const cmWvfUpperBandSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const cmWvfRangeHighSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const cmWvfRangeLowSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const movingAverageSeriesRef = useRef<Map<IndicatorInstanceId, ISeriesApi<'Line'>>>(new Map());
  const murreyMathLinesV6SeriesRef = useRef<Map<string, ISeriesApi<'Line'>>>(new Map());
  const murreyMathLinesV6ReversalBottomZonePrimitiveRef =
    useRef<BollingerBandsFillPrimitive | null>(null);
  const murreyMathLinesV6TradingZonePrimitiveRef = useRef<BollingerBandsFillPrimitive | null>(null);
  const murreyMathLinesV6ReversalTopZonePrimitiveRef = useRef<BollingerBandsFillPrimitive | null>(null);
  const bollingerUpperSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const bollingerBasisSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const bollingerLowerSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const bollingerFillPrimitiveRef = useRef<BollingerBandsFillPrimitive | null>(null);
  const ichimokuTenkanSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const ichimokuKijunSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const ichimokuSenkouASeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const ichimokuSenkouBSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const ichimokuChikouSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const ichimokuBullishFillPrimitiveRef = useRef<BollingerBandsFillPrimitive | null>(null);
  const ichimokuBearishFillPrimitiveRef = useRef<BollingerBandsFillPrimitive | null>(null);
  const traderProV3PriceLinesRef = useRef<ReturnType<ISeriesApi<'Candlestick'>['createPriceLine']>[]>(
    [],
  );
  const traderPro17DmaSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const traderPro17PrimitiveRef = useRef<TraderPro17ShapesPrimitive | null>(null);
  const colorProV3LineSeriesRef = useRef<Map<string, ISeriesApi<'Line'>>>(new Map());
  const colorProV31LineSeriesRef = useRef<Map<string, ISeriesApi<'Line'>>>(new Map());
  const basicStudySeriesRefs = useRef<Map<IndicatorInstanceId, BasicStudySeriesRefs>>(new Map());
  const basicStudyDrawingPrimitiveRefs =
    useRef<Map<IndicatorInstanceId, BasicStudyDrawingPrimitive>>(new Map());
  const colorProV3MarkersRef = useRef<ISeriesMarkersPluginApi<Time> | null>(null);
  const colorProV31MarkersRef = useRef<ISeriesMarkersPluginApi<Time> | null>(null);
  const maCrossMarkersRef = useRef<ISeriesMarkersPluginApi<Time> | null>(null);
  const botTradeMarkersRef = useRef<ISeriesMarkersPluginApi<Time> | null>(null);
  const candleHelperMarkersRef = useRef<ISeriesMarkersPluginApi<Time> | null>(null);
  const candleHelperMarkerKeyRef = useRef<string>('');
  const strategyMarkersRef = useRef<ISeriesMarkersPluginApi<Time> | null>(null);
  const strategyPriceLinesRef = useRef<ReturnType<ISeriesApi<'Candlestick'>['createPriceLine']>[]>([]);
  const openCode5mStrategyMarkersRef = useRef<ISeriesMarkersPluginApi<Time> | null>(null);
  const openCode5mStrategyPriceLinesRef = useRef<
    ReturnType<ISeriesApi<'Candlestick'>['createPriceLine']>[]
  >([]);
  const colorProV31StrategyMarkersRef = useRef<ISeriesMarkersPluginApi<Time> | null>(null);
  const colorProV31StrategyPriceLinesRef = useRef<
    ReturnType<ISeriesApi<'Candlestick'>['createPriceLine']>[]
  >([]);
  const hasInitialFitRef = useRef(false);
  const hasGrowthRefitRef = useRef(false);
  const previousCandleCountRef = useRef(0);
  const shouldAutoFollowRef = useRef(true);
  const manualTimeNavigationRef = useRef(false);
  const rightOffsetRef = useRef(0);
  const isReplayModeRef = useRef(isReplayMode);
  const previousReplayModeRef = useRef(isReplayMode);
  const replayViewportLockRef = useRef(false);
  const replayUnlockIntentRef = useRef(false);
  const lockedReplayPaddingRef = useRef(0);
  const lockedReplayPriceRangeRef = useRef<{ from: number; to: number } | null>(null);
  const lastAppliedRightOffsetRef = useRef<number | null>(null);
  const lastShiftVisibleRangeOnNewBarRef = useRef<boolean | null>(null);
  const hasManualPriceScaleBootstrapRef = useRef(false);
  const replayStartAnchorRef = useRef<ReplayStartAnchor | null>(null);
  const visibleLogicalRangeRef = useRef<{ from: number; to: number } | null>(null);
  const baseSeriesLastLogicalIndexRef = useRef(-1);
  const previousRenderedCandlesRef = useRef<Candle[]>([]);
  const lastRenderedFutureWhitespaceBarsRef = useRef(0);
  const preReplayVisibleRangeRef = useRef<{ from: number; to: number } | null>(null);
  const [isReplayViewportLocked, setIsReplayViewportLocked] = useState(false);
  const [isChartReady, setIsChartReady] = useState(false);
  const [chartViewportWidth, setChartViewportWidth] = useState(0);
  const [statusLineHeightPx, setStatusLineHeightPx] = useState(0);
  const [isStatusLineStacked, setIsStatusLineStacked] = useState(false);
  const [visibleRightGapBars, setVisibleRightGapBars] = useState(0);
  const [hoveredCandleIndex, setHoveredCandleIndex] = useState<number | null>(null);
  const [paneLegendTopByKey, setPaneLegendTopByKey] = useState<Partial<Record<IndicatorInstanceId, number>>>({});
  const [basicStudyVisibleRange, setBasicStudyVisibleRange] = useState<
    BasicStudyContext['visibleRange'] | undefined
  >(undefined);
  const [localChartDisplaySettings, setLocalChartDisplaySettings] =
    useState<ChartOverlaySettings>(DEFAULT_CHART_DISPLAY_SETTINGS);
  const [isChartSettingsOpen, setIsChartSettingsOpen] = useState(false);
  const [isStatusIndicatorOpen, setIsStatusIndicatorOpen] = useState(false);
  const [chartSettingsSubmenu, setChartSettingsSubmenu] = useState<'labels' | 'lines' | null>(null);
  const [isMoreSettingsModalOpen, setIsMoreSettingsModalOpen] = useState(false);
  const [marketEventsPanelRect, setMarketEventsPanelRect] = useState<FloatingPanelRect>({
    x: MARKET_EVENTS_PANEL_MARGIN_PX,
    y: MARKET_EVENTS_PANEL_MARGIN_PX,
    width: MARKET_EVENTS_PANEL_DEFAULT_WIDTH,
    height: MARKET_EVENTS_PANEL_DEFAULT_HEIGHT,
  });
  const [moreSettingsTab, setMoreSettingsTab] = useState<
    | 'symbol'
    | 'statusLine'
    | 'scalesAndLines'
    | 'canvas'
    | 'trading'
    | 'alerts'
    | 'events'
    | 'dataSources'
  >('symbol');
  const [modalDraftSettings, setModalDraftSettings] = useState<ChartOverlaySettings>(
    DEFAULT_CHART_DISPLAY_SETTINGS,
  );
  const [modalDraftDataSources, setModalDraftDataSources] = useState<DataSourceSettings>(dataSources);
  const [contextMenu, setContextMenu] = useState<ChartContextMenuState>(DEFAULT_CONTEXT_MENU_STATE);
  const [runtimeStatusOptions, setRuntimeStatusOptions] = useState<RuntimeStatusOptions>(
    DEFAULT_RUNTIME_STATUS_OPTIONS,
  );
  const [mobileDrawingToolbarSize, setMobileDrawingToolbarSize] = useState({ width: 0, height: 0 });
  const [mobileDrawingToolbarPosition, setMobileDrawingToolbarPosition] =
    useState<MobileDrawingToolbarPosition | null>(null);
  const [localAlertItems, setLocalAlertItems] = useState<ChartAlertItem[]>([]);
  const [localDrawingItems, setLocalDrawingItems] = useState<ChartDrawingItem[]>([]);
  const [localPaperOrders, setLocalPaperOrders] = useState<PaperTradingOrderItem[]>([]);
  const [chartTemplates, setChartTemplates] = useState<ChartTemplateItem[]>([]);
  const [isObjectTreeOpen, setIsObjectTreeOpen] = useState(false);
  const [isTableViewOpen, setIsTableViewOpen] = useState(false);
  const [isAlertsModalOpen, setIsAlertsModalOpen] = useState(false);

  const handleShowMarketStatusToggle = useCallback(() => {
    setRuntimeStatusOptions((previous) => {
      const next = !previous.showMarketStatus;
      onShowMarketStatusChange?.(next);
      return { ...previous, showMarketStatus: next };
    });
  }, [onShowMarketStatusChange]);

  const handleShowIndicatorTitlesToggle = useCallback(() => {
    setRuntimeStatusOptions((previous) => {
      const next = !previous.showIndicatorTitles;
      onShowIndicatorTitlesChange?.(next);
      return { ...previous, showIndicatorTitles: next };
    });
  }, [onShowIndicatorTitlesChange]);
  const [alertsModalTab, setAlertsModalTab] = useState<'current' | 'all'>('current');
  const [toastMessages, setToastMessages] = useState<ToastMessage[]>([]);
  const [selectedDrawingId, setSelectedDrawingId] = useState<string | null>(null);
  const [drawingDraft, setDrawingDraft] = useState<DrawingDraftState | null>(null);
  const lastHandledAlertsRequestIdRef = useRef<number | null>(null);
  const alertAudioContextRef = useRef<AudioContext | null>(null);
  const alertSoundCleanupRef = useRef<(() => void) | null>(null);
  const alertTriggerCandleTimeRef = useRef(new Map<string, number>());
  const hasInitializedAlertTriggersRef = useRef(false);
  const longPressTimerRef = useRef<number | null>(null);
  const clearLongPressPointerTrackingRef = useRef<(() => void) | null>(null);
  const longPressPointerTrackingSessionRef = useRef(0);
  const clearTouchAxisDragRef = useRef<(() => void) | null>(null);
  const clearMouseInteractionRef = useRef<(() => void) | null>(null);
  const suppressNextClickRef = useRef(false);
  const lastCrosshairPointRef = useRef<{ x: number; y: number } | null>(null);
  const drawingHandleDragRef = useRef<DrawingHandleDragState | null>(null);
  const drawingBodyDragRef = useRef<DrawingBodyDragState | null>(null);
  const alertPriceLinesRef = useRef<ReturnType<ISeriesApi<'Candlestick'>['createPriceLine']>[]>([]);
  const paperOrderPriceLinesRef = useRef<ReturnType<ISeriesApi<'Candlestick'>['createPriceLine']>[]>([]);
  const wasCandleHelperVisibleRef = useRef(false);
  const chartSettingsPanelRef = useRef<HTMLDivElement | null>(null);
  const statusIndicatorPanelRef = useRef<HTMLDivElement | null>(null);
  const contextMenuPanelRef = useRef<HTMLDivElement | null>(null);
  const marketEventsPanelPointerCleanupRef = useRef<(() => void) | null>(null);
  const mobileDrawingToolbarPointerCleanupRef = useRef<(() => void) | null>(null);
  const mobileDrawingToolbarLastHandleTapAtRef = useRef(0);
  const hasInitializedMarketEventsPanelRef = useRef(false);
  const isHistoryRequestInFlightRef = useRef(false);
  const activeMouseInteractionRef = useRef<MouseInteractionState | null>(null);
  const isCandleHelperVisible =
    overlays.candleHelper && overlaySettings.candleHelper.visibility.showMarkers;
  const allAlertItems = controlledAlertItems ?? localAlertItems;
  const allDrawingItems = controlledDrawingItems ?? localDrawingItems;
  const paperOrders = controlledPaperOrders ?? localPaperOrders;
  const alertItems = useMemo(
    () => allAlertItems.filter((item) => item.provider === provider && item.symbol === symbol),
    [allAlertItems, provider, symbol],
  );
  const drawingItems = useMemo(
    () => allDrawingItems.filter((item) => item.provider === provider && item.symbol === symbol),
    [allDrawingItems, provider, symbol],
  );

  useEffect(() => {
    alertTriggerCandleTimeRef.current.clear();
    hasInitializedAlertTriggersRef.current = false;
  }, [provider, symbol]);

  useEffect(() => {
    setSelectedDrawingId(null);
    setDrawingDraft(null);
    drawingHandleDragRef.current = null;
  }, [provider, symbol]);

  useEffect(() => {
    setMobileDrawingToolbarPosition(runtimeStatusOptions.mobileDrawingToolbarPosition);
  }, [runtimeStatusOptions.mobileDrawingToolbarPosition]);

  const setDrawingItemsState = useCallback(
    (next: ChartDrawingItem[] | ((current: ChartDrawingItem[]) => ChartDrawingItem[])) => {
      if (onDrawingItemsChange) {
        onDrawingItemsChange(next);
        return;
      }
      setLocalDrawingItems((current) => normalizeChartDrawingItems(typeof next === 'function' ? next(current) : next));
    },
    [onDrawingItemsChange],
  );

  useEffect(() => {
    candlesRef.current = candles;
  }, [candles]);

  const chartDisplaySettings = overlaySettings.chart ?? localChartDisplaySettings;
  const candleIndexByUnixTime = useMemo(() => {
    const mapping = new Map<number, number>();
    candles.forEach((candle, index) => {
      const time = toUnixTimestamp(candle.time);
      if (time !== null) {
        mapping.set(Number(time), index);
      }
    });
    return mapping;
  }, [candles]);

  const updateChartDisplaySettings = useCallback(
    (updater: (previous: ChartOverlaySettings) => ChartOverlaySettings) => {
      if (onUpdateChartSettings) {
        onUpdateChartSettings(updater);
        return;
      }
      setLocalChartDisplaySettings((previous) => updater(previous));
    },
    [onUpdateChartSettings],
  );

  const clearMarketEventsPanelPointerInteraction = useCallback(() => {
    const cleanup = marketEventsPanelPointerCleanupRef.current;
    if (cleanup) {
      cleanup();
    }
  }, []);

  const clearMobileDrawingToolbarPointerInteraction = useCallback(() => {
    const cleanup = mobileDrawingToolbarPointerCleanupRef.current;
    if (cleanup) {
      cleanup();
    }
  }, []);

  const clampMarketEventsPanelToWrapper = useCallback(
    (rect: FloatingPanelRect, anchorToTopRight: boolean = false): FloatingPanelRect => {
      const wrapper = wrapperRef.current;
      if (!wrapper) {
        return rect;
      }

      const boundsWidth = wrapper.clientWidth;
      const boundsHeight = wrapper.clientHeight;
      if (boundsWidth <= 0 || boundsHeight <= 0) {
        return rect;
      }

      const nextRect = clampFloatingPanelRect(rect, boundsWidth, boundsHeight);
      if (!anchorToTopRight) {
        return nextRect;
      }

      return {
        ...nextRect,
        x: Math.max(
          MARKET_EVENTS_PANEL_MARGIN_PX,
          boundsWidth - nextRect.width - MARKET_EVENTS_PANEL_MARGIN_PX,
        ),
        y: MARKET_EVENTS_PANEL_MARGIN_PX,
      };
    },
    [],
  );

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) {
      return undefined;
    }

    const syncMarketEventsPanel = () => {
      setMarketEventsPanelRect((previous) => {
        const next = clampMarketEventsPanelToWrapper(
          previous,
          !hasInitializedMarketEventsPanelRef.current,
        );
        hasInitializedMarketEventsPanelRef.current = true;
        return areFloatingPanelRectsEqual(previous, next) ? previous : next;
      });
    };

    syncMarketEventsPanel();

    const observer = new ResizeObserver(() => {
      syncMarketEventsPanel();
    });
    observer.observe(wrapper);

    return () => {
      observer.disconnect();
    };
  }, [clampMarketEventsPanelToWrapper]);

  useEffect(() => () => clearMarketEventsPanelPointerInteraction(), [clearMarketEventsPanelPointerInteraction]);

  useEffect(() => () => clearMobileDrawingToolbarPointerInteraction(), [clearMobileDrawingToolbarPointerInteraction]);

  useEffect(() => {
    const panel = mobileDrawingToolbarPanel;
    if (!panel) {
      setMobileDrawingToolbarSize((current) =>
        current.width === 0 && current.height === 0 ? current : { width: 0, height: 0 },
      );
      return undefined;
    }

    const syncPanelSize = () => {
      const next = {
        width: panel.clientWidth,
        height: panel.clientHeight,
      };
      setMobileDrawingToolbarSize((current) =>
        current.width === next.width && current.height === next.height ? current : next,
      );
    };

    syncPanelSize();

    const observer = new ResizeObserver(() => {
      syncPanelSize();
    });
    observer.observe(panel);

    return () => {
      observer.disconnect();
    };
  }, [mobileDrawingToolbarPanel]);

  const applyShiftVisibleRangeOnNewBar = useCallback(
    (enabled: boolean) => {
      const chart = chartRef.current;
      if (!chart || lastShiftVisibleRangeOnNewBarRef.current === enabled) {
        return;
      }

      chart.applyOptions({
        timeScale: {
          shiftVisibleRangeOnNewBar: enabled,
        },
      });
      lastShiftVisibleRangeOnNewBarRef.current = enabled;
    },
    [],
  );

  const setManualTimeNavigationEnabled = useCallback(
    (enabled: boolean, reason: string) => {
      const previous = manualTimeNavigationRef.current;
      manualTimeNavigationRef.current = enabled;
      if (enabled) {
        shouldAutoFollowRef.current = false;
      }

      applyShiftVisibleRangeOnNewBar(
        !isReplayModeRef.current && !replayViewportLockRef.current && !manualTimeNavigationRef.current,
      );
    },
    [applyShiftVisibleRangeOnNewBar],
  );

  const isMousePointerOnChartAxis = useCallback(
    (clientX: number, clientY: number) => {
      const wrapper = wrapperRef.current;
      const chart = chartRef.current;
      if (!wrapper || !container || !chart) {
        return false;
      }

      const wrapperRect = wrapper.getBoundingClientRect();
      const chartRect = container.getBoundingClientRect();
      const chartX = clientX - chartRect.left;
      const chartY = clientY - chartRect.top;
      const timeScale = chart.timeScale();
      const plotWidth = timeScale.width();
      if (plotWidth <= 0) {
        return false;
      }

      const activePriceScale = chart.priceScale(runtimeStatusOptions.priceScaleSide);
      const priceScaleWidth = activePriceScale.width();
      const plotLeft = runtimeStatusOptions.priceScaleSide === 'left' ? priceScaleWidth : 0;
      const plotRight = plotLeft + plotWidth;
      const timeAxisHeight = Math.max(timeScale.height(), TIME_AXIS_HIT_HEIGHT_PX);
      const isWithinTimeAxis = chartY >= chartRect.height - timeAxisHeight && chartY <= chartRect.height;
      const isWithinPlotArea = chartX >= plotLeft && chartX <= plotRight;
      if (isWithinTimeAxis && isWithinPlotArea) {
        return true;
      }

      return runtimeStatusOptions.priceScaleSide === 'left'
        ? chartX >= 0 && chartX <= plotLeft
        : chartX >= plotRight && chartX <= wrapperRect.width;
    },
    [container, runtimeStatusOptions.priceScaleSide],
  );

  const setAlertItemsState = useCallback(
    (next: ChartAlertItem[] | ((current: ChartAlertItem[]) => ChartAlertItem[])) => {
      if (onAlertItemsChange) {
        onAlertItemsChange(next);
        return;
      }
      setLocalAlertItems((current) => (typeof next === 'function' ? next(current) : next));
    },
    [onAlertItemsChange],
  );

  const setPaperOrdersState = useCallback(
    (next: PaperTradingOrderItem[] | ((current: PaperTradingOrderItem[]) => PaperTradingOrderItem[])) => {
      setLocalPaperOrders((current) => (typeof next === 'function' ? next(current) : next));
    },
    [],
  );

  const pushToast = useCallback((tone: ToastMessage['tone'], text: string) => {
    const id = createRuntimeId('toast');
    setToastMessages((previous) => [...previous, { id, tone, text }]);
    window.setTimeout(() => {
      setToastMessages((previous) => previous.filter((item) => item.id !== id));
    }, 2600);
  }, []);

  const setDrawingTool = useCallback(
    (tool: ChartDrawingTool) => {
      onActiveDrawingToolChange?.(tool);
      if (tool === 'cursor') {
        setDrawingDraft(null);
      }
    },
    [onActiveDrawingToolChange],
  );

  const resolveDrawingTimeFromLogical = useCallback(
    (logical: number): number | null => {
      if (!Number.isFinite(logical) || candles.length === 0) {
        return null;
      }

      const roundedLogical = Math.max(0, Math.round(logical));
      if (roundedLogical <= candles.length - 1) {
        const time = toUnixTimestamp(candles[roundedLogical].time);
        return time === null ? null : Number(time);
      }

      const lastCandleTime = toUnixTimestamp(candles[candles.length - 1].time);
      if (lastCandleTime === null) {
        return null;
      }

      const baseSeriesLastLogicalIndex = Math.max(candles.length - 1, baseSeriesLastLogicalIndexRef.current);
      const logicalOffset = roundedLogical - baseSeriesLastLogicalIndex;
      return addChartTimeSteps(Number(lastCandleTime), logicalOffset, interval);
    },
    [candles, interval],
  );

  const resolveDrawingInteractionPointFromClientPoint = useCallback(
    (clientX: number, clientY: number): { logical: number; price: number; time: number } | null => {
      const chart = chartRef.current;
      const series = seriesRef.current;
      const currentContainer = container;
      if (!chart || !series || !currentContainer || candles.length === 0) {
        return null;
      }

      const rect = currentContainer.getBoundingClientRect();
      const x = clampCoordinate(clientX - rect.left, rect.width);
      const y = clampCoordinate(clientY - rect.top, rect.height);
      const logical = chart.timeScale().coordinateToLogical(x);
      const price = series.coordinateToPrice(y);
      if (typeof logical !== 'number' || !Number.isFinite(logical) || typeof price !== 'number' || !Number.isFinite(price)) {
        return null;
      }

      const roundedLogical = Math.max(0, Math.round(logical));
      const time = resolveDrawingTimeFromLogical(roundedLogical);
      if (time === null) {
        return null;
      }

      return {
        logical: roundedLogical,
        price,
        time,
      };
    },
    [candles.length, container, resolveDrawingTimeFromLogical],
  );

  const resolveDrawingAnchorFromClientPoint = useCallback(
    (clientX: number, clientY: number): ChartDrawingAnchor | null => {
      const point = resolveDrawingInteractionPointFromClientPoint(clientX, clientY);
      if (!point) {
        return null;
      }

      return {
        time: point.time,
        price: point.price,
      };
    },
    [resolveDrawingInteractionPointFromClientPoint],
  );

  const resolveDrawingScreenPointFromClientPoint = useCallback(
    (clientX: number, clientY: number): ScreenPoint | null => {
      const currentContainer = container;
      if (!currentContainer) {
        return null;
      }

      const rect = currentContainer.getBoundingClientRect();
      return {
        x: clampCoordinate(clientX - rect.left, rect.width),
        y: clampCoordinate(clientY - rect.top, rect.height),
      };
    },
    [container],
  );

  const deleteSelectedDrawing = useCallback(() => {
    if (!selectedDrawingId) {
      return;
    }
    setDrawingItemsState((previous) => previous.filter((item) => item.id !== selectedDrawingId));
    setSelectedDrawingId(null);
    pushToast('success', 'Removed selected drawing.');
  }, [pushToast, selectedDrawingId, setDrawingItemsState]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (drawingDraft) {
          setDrawingDraft(null);
          setDrawingTool('cursor');
        }
        return;
      }

      if ((event.key === 'Delete' || event.key === 'Backspace') && selectedDrawingId) {
        event.preventDefault();
        deleteSelectedDrawing();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [deleteSelectedDrawing, drawingDraft, selectedDrawingId, setDrawingTool]);

  const commitDrawingFromAnchors = useCallback(
    (kind: ChartDrawingKind, anchors: ChartDrawingAnchor[], text?: string | null) => {
      setDrawingItemsState((previous) => [
        ...previous,
        createDrawingItem({
          provider,
          symbol,
          kind,
          anchors,
          text,
        }),
      ]);
      setDrawingDraft(null);
      setDrawingTool('cursor');
      pushToast('success', `Added ${getDrawingToolLabel(kind).toLowerCase()}.`);
    },
    [provider, pushToast, setDrawingItemsState, setDrawingTool, symbol],
  );

  const handleDrawingSurfacePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (activeDrawingTool === 'cursor') {
        return;
      }
      if (event.button !== 0) {
        return;
      }

      const kind = activeDrawingTool as ChartDrawingKind;
      const anchor = resolveDrawingAnchorFromClientPoint(event.clientX, event.clientY);
      const previewPoint = resolveDrawingScreenPointFromClientPoint(event.clientX, event.clientY);
      if (!anchor || !previewPoint) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      if (kind === 'text') {
        const nextText = window.prompt('Text label', 'Note');
        if (nextText === null) {
          setDrawingTool('cursor');
          return;
        }
        commitDrawingFromAnchors(kind, [anchor], nextText);
        return;
      }

      const requiredAnchors = getRequiredAnchorCount(kind);
      const nextAnchors = drawingDraft && drawingDraft.kind === kind ? [...drawingDraft.anchors, anchor] : [anchor];
      if (nextAnchors.length >= requiredAnchors) {
        commitDrawingFromAnchors(kind, nextAnchors.slice(0, requiredAnchors));
        return;
      }

      setDrawingDraft({
        kind,
        anchors: nextAnchors,
        previewAnchor: anchor,
        previewPoint,
      });
    },
    [activeDrawingTool, commitDrawingFromAnchors, drawingDraft, resolveDrawingAnchorFromClientPoint, resolveDrawingScreenPointFromClientPoint, setDrawingTool],
  );

  const handleDrawingSurfacePointerMove = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (!drawingDraft) {
        return;
      }
      const previewAnchor = resolveDrawingAnchorFromClientPoint(event.clientX, event.clientY);
      const previewPoint = resolveDrawingScreenPointFromClientPoint(event.clientX, event.clientY);
      if (!previewAnchor || !previewPoint) {
        return;
      }
      setDrawingDraft((previous) =>
        previous
          ? {
              ...previous,
              previewAnchor,
              previewPoint,
            }
          : previous,
      );
    },
    [drawingDraft, resolveDrawingAnchorFromClientPoint, resolveDrawingScreenPointFromClientPoint],
  );

  const handleDrawingHandlePointerDown = useCallback(
    (event: ReactPointerEvent<SVGCircleElement>, drawingId: string, anchorIndex: number) => {
      event.preventDefault();
      event.stopPropagation();
      suppressNextClickRef.current = true;
      setSelectedDrawingId(drawingId);
      drawingHandleDragRef.current = {
        drawingId,
        anchorIndex,
        pointerId: event.pointerId,
      };
    },
    [],
  );

  const handleDrawingBodyPointerDown = useCallback(
    (event: ReactPointerEvent<SVGElement>, drawing: ChartDrawingItem) => {
      event.preventDefault();
      event.stopPropagation();
      suppressNextClickRef.current = true;
      setSelectedDrawingId(drawing.id);
      setDrawingTool('cursor');

      if (drawing.locked) {
        return;
      }

      const interactionPoint = resolveDrawingInteractionPointFromClientPoint(event.clientX, event.clientY);
      if (!interactionPoint) {
        return;
      }

      drawingBodyDragRef.current = {
        drawingId: drawing.id,
        pointerId: event.pointerId,
        originLogical: interactionPoint.logical,
        originPrice: interactionPoint.price,
        originAnchors: drawing.anchors.map((anchor) => ({ ...anchor })),
      };
    },
    [resolveDrawingInteractionPointFromClientPoint, setDrawingTool],
  );

  useEffect(() => {
    const handlePointerMove = (event: PointerEvent) => {
      const handleDragState = drawingHandleDragRef.current;
      if (handleDragState && event.pointerId === handleDragState.pointerId) {
        const nextAnchor = resolveDrawingAnchorFromClientPoint(event.clientX, event.clientY);
        if (!nextAnchor) {
          return;
        }
        setDrawingItemsState((previous) =>
          previous.map((item) => {
            if (item.id !== handleDragState.drawingId || item.locked) {
              return item;
            }
            const nextAnchors = item.anchors.map((anchor, index) =>
              index === handleDragState.anchorIndex ? nextAnchor : anchor,
            );
            return updateDrawingAnchors(item, nextAnchors);
          }),
        );
        return;
      }

      const bodyDragState = drawingBodyDragRef.current;
      if (!bodyDragState || event.pointerId !== bodyDragState.pointerId) {
        return;
      }

      const interactionPoint = resolveDrawingInteractionPointFromClientPoint(event.clientX, event.clientY);
      if (!interactionPoint) {
        return;
      }

      const logicalDelta = interactionPoint.logical - bodyDragState.originLogical;
      const priceDelta = interactionPoint.price - bodyDragState.originPrice;
      setDrawingItemsState((previous) =>
        previous.map((item) => {
          if (item.id !== bodyDragState.drawingId || item.locked) {
            return item;
          }
          const nextAnchors = bodyDragState.originAnchors.map((anchor) => {
            const nextTime = addChartTimeSteps(anchor.time, logicalDelta, interval);
            if (nextTime === null) {
              return anchor;
            }
            return {
              time: nextTime,
              price: anchor.price + priceDelta,
            };
          });
          return updateDrawingAnchors(item, nextAnchors);
        }),
      );
    };

    const handlePointerEnd = (event: PointerEvent) => {
      const handleDragState = drawingHandleDragRef.current;
      if (handleDragState && event.pointerId === handleDragState.pointerId) {
        drawingHandleDragRef.current = null;
      }

      const bodyDragState = drawingBodyDragRef.current;
      if (bodyDragState && event.pointerId === bodyDragState.pointerId) {
        drawingBodyDragRef.current = null;
      }
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerEnd);
    window.addEventListener('pointercancel', handlePointerEnd);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerEnd);
      window.removeEventListener('pointercancel', handlePointerEnd);
    };
  }, [interval, resolveDrawingAnchorFromClientPoint, resolveDrawingInteractionPointFromClientPoint, setDrawingItemsState]);

  const stopAlertSound = useCallback(() => {
    const cleanup = alertSoundCleanupRef.current;
    if (!cleanup) {
      return;
    }

    alertSoundCleanupRef.current = null;
    cleanup();
  }, []);

  const playAlertSound = useCallback(
    async (soundType: AlertSoundType, volume: number, showUnsupportedToast = false) => {
      const AudioContextConstructor =
        window.AudioContext ??
        (window as Window & typeof globalThis & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;

      if (!AudioContextConstructor) {
        if (showUnsupportedToast) {
          pushToast('warning', 'Alert sounds are not supported in this browser.');
        }
        return;
      }

      stopAlertSound();

      let context = alertAudioContextRef.current;
      if (!context || context.state === 'closed') {
        context = new AudioContextConstructor();
        alertAudioContextRef.current = context;
      }

      if (context.state === 'suspended') {
        await context.resume();
      }

      const notes = buildAlertSoundNotes(soundType);
      const normalizedVolume = clampAlertSoundVolume(volume) / 100;
      if (normalizedVolume <= 0 || notes.length === 0) {
        return;
      }

      const masterGain = context.createGain();
      masterGain.gain.value = normalizedVolume;
      masterGain.connect(context.destination);

      const startedOscillators: OscillatorNode[] = [];
      const gainNodes: GainNode[] = [];
      const now = context.currentTime + 0.01;

      notes.forEach((note) => {
        const oscillator = context.createOscillator();
        const gainNode = context.createGain();
        const startTime = now + note.delay;
        const peakGain = Math.max(0.0001, note.gain);

        oscillator.type = note.type;
        oscillator.frequency.setValueAtTime(note.frequency, startTime);
        gainNode.gain.setValueAtTime(0.0001, startTime);
        gainNode.gain.exponentialRampToValueAtTime(peakGain, startTime + 0.02);
        gainNode.gain.exponentialRampToValueAtTime(0.0001, startTime + note.duration);

        oscillator.connect(gainNode);
        gainNode.connect(masterGain);
        oscillator.start(startTime);
        oscillator.stop(startTime + note.duration + 0.03);

        startedOscillators.push(oscillator);
        gainNodes.push(gainNode);
      });

      const totalDurationMs =
        (Math.max(...notes.map((note) => note.delay + note.duration), 0) + 0.08) * 1000;
      const cleanup = () => {
        startedOscillators.forEach((oscillator) => {
          try {
            oscillator.stop();
          } catch {
            // noop
          }
          oscillator.disconnect();
        });
        gainNodes.forEach((gainNode) => gainNode.disconnect());
        masterGain.disconnect();
      };

      alertSoundCleanupRef.current = cleanup;
      window.setTimeout(() => {
        if (alertSoundCleanupRef.current === cleanup) {
          alertSoundCleanupRef.current = null;
        }
        cleanup();
      }, totalDurationMs);
    },
    [pushToast, stopAlertSound],
  );

  const clearAlertPriceLines = useCallback(() => {
    const candleSeries = seriesRef.current;
    if (!candleSeries) {
      alertPriceLinesRef.current = [];
      return;
    }
    alertPriceLinesRef.current.forEach((line) => {
      candleSeries.removePriceLine(line);
    });
    alertPriceLinesRef.current = [];
  }, []);

  useEffect(() => {
    return () => {
      stopAlertSound();
      if (alertAudioContextRef.current && alertAudioContextRef.current.state !== 'closed') {
        void alertAudioContextRef.current.close();
      }
    };
  }, [stopAlertSound]);

  const clearPaperOrderPriceLines = useCallback(() => {
    const candleSeries = seriesRef.current;
    if (!candleSeries) {
      paperOrderPriceLinesRef.current = [];
      return;
    }
    paperOrderPriceLinesRef.current.forEach((line) => {
      candleSeries.removePriceLine(line);
    });
    paperOrderPriceLinesRef.current = [];
  }, []);

  useEffect(() => {
    if (controlledAlertItems !== undefined || controlledPaperOrders !== undefined || controlledDrawingItems !== undefined) {
      return;
    }
    if (typeof window === 'undefined') {
      return;
    }

    const snapshot = parseRuntimeStorageSnapshot(window.localStorage.getItem(createRuntimeStorageKey(provider, symbol)));
    const alertsSnapshot = parseAlertsStorageSnapshot(
      window.localStorage.getItem(createAlertsStorageKey()),
    );
    if (!snapshot) {
      setLocalAlertItems(alertsSnapshot?.alerts ?? []);
      setLocalDrawingItems([]);
      setLocalPaperOrders([]);
      setChartTemplates([]);
      setRuntimeStatusOptions(DEFAULT_RUNTIME_STATUS_OPTIONS);
      return;
    }

    setLocalAlertItems(alertsSnapshot?.alerts ?? snapshot.alerts);
    setLocalDrawingItems(snapshot.drawings.filter((item) => item.provider === provider && item.symbol === symbol));
    setLocalPaperOrders(snapshot.orders.filter((item) => item.provider === provider && item.symbol === symbol));
    setChartTemplates(snapshot.templates.filter((item) => item.provider === provider && item.symbol === symbol));
    setRuntimeStatusOptions(snapshot.runtimeStatus);
    if (showMarketStatus === undefined) {
      onShowMarketStatusChange?.(snapshot.runtimeStatus.showMarketStatus);
    }
    if (showIndicatorTitles === undefined) {
      onShowIndicatorTitlesChange?.(snapshot.runtimeStatus.showIndicatorTitles);
    }
    if (mobileDrawingToolbarCollapsed === undefined) {
      onMobileDrawingToolbarCollapsedChange?.(snapshot.runtimeStatus.mobileDrawingToolbarCollapsed);
    }
  }, [
    controlledAlertItems,
    controlledDrawingItems,
    controlledPaperOrders,
    mobileDrawingToolbarCollapsed,
    onMobileDrawingToolbarCollapsedChange,
    onShowIndicatorTitlesChange,
    onShowMarketStatusChange,
    provider,
    showIndicatorTitles,
    showMarketStatus,
    symbol,
  ]);

  useEffect(() => {
    if (controlledAlertItems !== undefined || controlledPaperOrders !== undefined || controlledDrawingItems !== undefined) {
      return;
    }
    if (typeof window === 'undefined') {
      return;
    }

    const payload: ChartRuntimeStorageSnapshot = {
      version: 2,
      alerts: [],
      drawings: allDrawingItems,
      orders: paperOrders,
      templates: chartTemplates,
      runtimeStatus: runtimeStatusOptions,
    };
    window.localStorage.setItem(createRuntimeStorageKey(provider, symbol), JSON.stringify(payload));
    window.localStorage.setItem(
      createAlertsStorageKey(),
      JSON.stringify({ version: 1, alerts: allAlertItems } satisfies ChartAlertsStorageSnapshot),
    );
  }, [allAlertItems, allDrawingItems, chartTemplates, controlledAlertItems, controlledDrawingItems, controlledPaperOrders, paperOrders, provider, runtimeStatusOptions, symbol]);

  useEffect(() => {
    if (
      showMarketStatus === undefined &&
      showIndicatorTitles === undefined &&
      mobileDrawingToolbarCollapsed === undefined
    ) {
      return;
    }

    setRuntimeStatusOptions((previous) => {
      const nextShowMarketStatus = showMarketStatus ?? previous.showMarketStatus;
      const nextShowIndicatorTitles = showIndicatorTitles ?? previous.showIndicatorTitles;
      const nextMobileDrawingToolbarCollapsed =
        mobileDrawingToolbarCollapsed ?? previous.mobileDrawingToolbarCollapsed;
      if (
        previous.showMarketStatus === nextShowMarketStatus &&
        previous.showIndicatorTitles === nextShowIndicatorTitles &&
        previous.mobileDrawingToolbarCollapsed === nextMobileDrawingToolbarCollapsed
      ) {
        return previous;
      }

      return {
        ...previous,
        showMarketStatus: nextShowMarketStatus,
        showIndicatorTitles: nextShowIndicatorTitles,
        mobileDrawingToolbarCollapsed: nextMobileDrawingToolbarCollapsed,
      };
    });
  }, [mobileDrawingToolbarCollapsed, showIndicatorTitles, showMarketStatus]);

  const selectedDrawing = useMemo(
    () => drawingItems.find((item) => item.id === selectedDrawingId) ?? null,
    [drawingItems, selectedDrawingId],
  );

  const drawingToolbarInsetLeftPx =
    chartViewportWidth >= MOBILE_DRAWING_TOOLBAR_BREAKPOINT_PX ? LEFT_DRAWING_TOOLBAR_WIDTH_PX + 16 : 0;
  const leftOverlayInsetPx = 12 + drawingToolbarInsetLeftPx;

  const overlayWidth = container?.clientWidth ?? chartViewportWidth;
  const overlayHeight = container?.clientHeight ?? 0;
  const isMobileDrawingToolbar =
    chartViewportWidth > 0 && chartViewportWidth < MOBILE_DRAWING_TOOLBAR_BREAKPOINT_PX;
  const isMobileDrawingToolbarCollapsed = runtimeStatusOptions.mobileDrawingToolbarCollapsed;
  const defaultMobileDrawingToolbarPosition = useMemo<MobileDrawingToolbarPosition>(
    () =>
      isMobileDrawingToolbar
        ? {
            x: MOBILE_DRAWING_TOOLBAR_MARGIN_PX,
            y: Math.max(
              MOBILE_DRAWING_TOOLBAR_MARGIN_PX,
              overlayHeight - mobileDrawingToolbarSize.height - MOBILE_DRAWING_TOOLBAR_MARGIN_PX,
            ),
          }
        : {
            x: MOBILE_DRAWING_TOOLBAR_MARGIN_PX,
            y: MOBILE_DRAWING_TOOLBAR_MARGIN_PX,
          },
    [isMobileDrawingToolbar, mobileDrawingToolbarSize.height, overlayHeight],
  );
  const resolvedMobileDrawingToolbarPosition = useMemo(
    () =>
      clampMobileDrawingToolbarPosition(
        mobileDrawingToolbarPosition ?? defaultMobileDrawingToolbarPosition,
        mobileDrawingToolbarSize.width,
        mobileDrawingToolbarSize.height,
        overlayWidth,
        overlayHeight,
      ),
    [
      defaultMobileDrawingToolbarPosition,
      mobileDrawingToolbarPosition,
      mobileDrawingToolbarSize.height,
      mobileDrawingToolbarSize.width,
      overlayHeight,
      overlayWidth,
    ],
  );

  useEffect(() => {
    if (
      overlayWidth <= 0 ||
      overlayHeight <= 0 ||
      mobileDrawingToolbarSize.width <= 0 ||
      mobileDrawingToolbarSize.height <= 0
    ) {
      return;
    }

    setMobileDrawingToolbarPosition((previous) => {
      if (!previous) {
        return previous;
      }

      const next = clampMobileDrawingToolbarPosition(
        previous,
        mobileDrawingToolbarSize.width,
        mobileDrawingToolbarSize.height,
        overlayWidth,
        overlayHeight,
      );

      return areMobileDrawingToolbarPositionsEqual(previous, next) ? previous : next;
    });
  }, [
    mobileDrawingToolbarSize.height,
    mobileDrawingToolbarSize.width,
    overlayHeight,
    overlayWidth,
  ]);

  useEffect(() => {
    if (
      !runtimeStatusOptions.mobileDrawingToolbarPosition ||
      overlayWidth <= 0 ||
      overlayHeight <= 0 ||
      mobileDrawingToolbarSize.width <= 0 ||
      mobileDrawingToolbarSize.height <= 0
    ) {
      return;
    }

    const next = clampMobileDrawingToolbarPosition(
      runtimeStatusOptions.mobileDrawingToolbarPosition,
      mobileDrawingToolbarSize.width,
      mobileDrawingToolbarSize.height,
      overlayWidth,
      overlayHeight,
    );

    if (areMobileDrawingToolbarPositionsEqual(runtimeStatusOptions.mobileDrawingToolbarPosition, next)) {
      return;
    }

    setRuntimeStatusOptions((previous) => ({
      ...previous,
      mobileDrawingToolbarPosition: next,
    }));
  }, [
    mobileDrawingToolbarSize.height,
    mobileDrawingToolbarSize.width,
    overlayHeight,
    overlayWidth,
    runtimeStatusOptions.mobileDrawingToolbarPosition,
  ]);

  const handleDrawingToolSelect = useCallback(
    (tool: ChartDrawingTool) => {
      setSelectedDrawingId(null);
      setDrawingDraft(null);
      setDrawingTool(tool);
    },
    [setDrawingTool],
  );

  const handleMobileDrawingToolbarDragStart = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>) => {
      if (event.button !== 0) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      if (longPressTimerRef.current !== null) {
        window.clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
      clearLongPressPointerTrackingRef.current?.();
      clearLongPressPointerTrackingRef.current = null;
      clearMobileDrawingToolbarPointerInteraction();
      suppressNextClickRef.current = true;

      const target = event.currentTarget;
      const dragState: MobileDrawingToolbarDragState = {
        pointerId: event.pointerId,
        originClientX: event.clientX,
        originClientY: event.clientY,
        originPosition: resolvedMobileDrawingToolbarPosition,
      };
      let movedBeyondTapThreshold = false;

      target.setPointerCapture(dragState.pointerId);
      setMobileDrawingToolbarPosition(dragState.originPosition);

      const resolveDraggedPosition = (nativeEvent: PointerEvent): MobileDrawingToolbarPosition =>
        clampMobileDrawingToolbarPosition(
          {
            x: dragState.originPosition.x + (nativeEvent.clientX - dragState.originClientX),
            y: dragState.originPosition.y + (nativeEvent.clientY - dragState.originClientY),
          },
          mobileDrawingToolbarSize.width,
          mobileDrawingToolbarSize.height,
          overlayWidth,
          overlayHeight,
        );

      const handlePointerMove = (nativeEvent: PointerEvent) => {
        if (nativeEvent.pointerId !== dragState.pointerId) {
          return;
        }

        nativeEvent.preventDefault();
        const distanceX = nativeEvent.clientX - dragState.originClientX;
        const distanceY = nativeEvent.clientY - dragState.originClientY;
        if (!movedBeyondTapThreshold) {
          movedBeyondTapThreshold = Math.hypot(distanceX, distanceY) > MOBILE_DRAWING_TOOLBAR_TAP_DRAG_THRESHOLD_PX;
        }
        setMobileDrawingToolbarPosition(resolveDraggedPosition(nativeEvent));
      };

      const handlePointerEnd = (nativeEvent: PointerEvent) => {
        if (nativeEvent.pointerId !== dragState.pointerId) {
          return;
        }

        const nextPosition = resolveDraggedPosition(nativeEvent);
        const wasTap = !movedBeyondTapThreshold;
        const now = typeof performance !== 'undefined' ? performance.now() : Date.now();

        if (wasTap) {
          const shouldToggleCollapse =
            now - mobileDrawingToolbarLastHandleTapAtRef.current <= MOBILE_DRAWING_TOOLBAR_HANDLE_DOUBLE_TAP_MS;
          mobileDrawingToolbarLastHandleTapAtRef.current = shouldToggleCollapse ? 0 : now;
          if (shouldToggleCollapse) {
            setMobileDrawingToolbarPosition(dragState.originPosition);
            setRuntimeStatusOptions((previous) => {
              const nextCollapsed = !previous.mobileDrawingToolbarCollapsed;
              onMobileDrawingToolbarCollapsedChange?.(nextCollapsed);
              return {
                ...previous,
                mobileDrawingToolbarCollapsed: nextCollapsed,
              };
            });
            clearPointerTracking();
            return;
          }
        } else {
          mobileDrawingToolbarLastHandleTapAtRef.current = 0;
        }

        setMobileDrawingToolbarPosition(nextPosition);
        setRuntimeStatusOptions((previous) => ({
          ...previous,
          mobileDrawingToolbarPosition: nextPosition,
        }));
        clearPointerTracking();
      };

      const clearPointerTracking = () => {
        window.removeEventListener('pointermove', handlePointerMove);
        window.removeEventListener('pointerup', handlePointerEnd);
        window.removeEventListener('pointercancel', handlePointerEnd);
        if (mobileDrawingToolbarPointerCleanupRef.current === clearPointerTracking) {
          mobileDrawingToolbarPointerCleanupRef.current = null;
        }
        if (target.hasPointerCapture(dragState.pointerId)) {
          target.releasePointerCapture(dragState.pointerId);
        }
      };

      mobileDrawingToolbarPointerCleanupRef.current = clearPointerTracking;
      window.addEventListener('pointermove', handlePointerMove, { passive: false });
      window.addEventListener('pointerup', handlePointerEnd);
      window.addEventListener('pointercancel', handlePointerEnd);
    },
    [
      clearMobileDrawingToolbarPointerInteraction,
      mobileDrawingToolbarSize.height,
      mobileDrawingToolbarSize.width,
      overlayHeight,
      overlayWidth,
      resolvedMobileDrawingToolbarPosition,
    ],
  );

  const projectDrawingPoint = useCallback(
    (anchor: ChartDrawingAnchor): ScreenPoint | null => {
      const chart = chartRef.current;
      const series = seriesRef.current;
      if (!chart || !series) {
        return null;
      }
      const x = chart.timeScale().timeToCoordinate(anchor.time as Time);
      const y = series.priceToCoordinate(anchor.price);
      if (x === null || y === null) {
        return null;
      }
      return { x, y };
    },
    [chartViewportWidth, visibleRightGapBars],
  );

  const drawingPreviewItem = useMemo(() => {
    if (!drawingDraft) {
      return null;
    }
    return createDrawingItem({
      provider,
      symbol,
      kind: drawingDraft.kind,
      anchors: drawingDraft.previewAnchor
        ? [...drawingDraft.anchors, drawingDraft.previewAnchor]
        : drawingDraft.anchors,
      text: drawingDraft.kind === 'text' ? 'Text' : null,
    });
  }, [drawingDraft, provider, symbol]);

  const toggleSelectedDrawingVisibility = useCallback(() => {
    if (!selectedDrawingId) {
      return;
    }
    setDrawingItemsState((previous) =>
      previous.map((item) =>
        item.id === selectedDrawingId
          ? {
              ...item,
              visible: !item.visible,
              updatedAt: new Date().toISOString(),
            }
          : item,
      ),
    );
  }, [selectedDrawingId, setDrawingItemsState]);

  const toggleSelectedDrawingLock = useCallback(() => {
    if (!selectedDrawingId) {
      return;
    }
    setDrawingItemsState((previous) =>
      previous.map((item) =>
        item.id === selectedDrawingId
          ? {
              ...item,
              locked: !item.locked,
              updatedAt: new Date().toISOString(),
            }
          : item,
      ),
    );
  }, [selectedDrawingId, setDrawingItemsState]);

  const traderPro17SourceCandles = useMemo(() => {
    const useAlternativeTimeframe = overlaySettings.traderPro17.inputs.useAlternativeTimeframe;
    const hasAlternativeCandles = Array.isArray(traderPro17AltCandles) && traderPro17AltCandles.length > 0;

    if (useAlternativeTimeframe && hasAlternativeCandles) {
      return traderPro17AltCandles;
    }

    return candles;
  }, [candles, traderPro17AltCandles, overlaySettings.traderPro17.inputs.useAlternativeTimeframe]);

  const traderPro17SourceInterval =
    overlaySettings.traderPro17.inputs.useAlternativeTimeframe &&
    Array.isArray(traderPro17AltCandles) &&
    traderPro17AltCandles.length > 0
      ? overlaySettings.traderPro17.inputs.alternativeTimeframe
      : interval;

  const cmVixFixSourceCandles = useMemo(() => {
    const useAlternativeTimeframe = overlaySettings.cmVixFixUcsSqueeze.inputs.useAlternativeTimeframe;
    const hasAlternativeCandles = Array.isArray(cmVixFixAltCandles) && cmVixFixAltCandles.length > 0;

    if (useAlternativeTimeframe && hasAlternativeCandles) {
      return cmVixFixAltCandles;
    }

    return candles;
  }, [candles, cmVixFixAltCandles, overlaySettings.cmVixFixUcsSqueeze.inputs.useAlternativeTimeframe]);

  const handleAutoFitChange = useCallback(
    (nextAutoFit: boolean) => {
      updateChartDisplaySettings((previous) => {
        if (previous.autoFit === nextAutoFit) {
          return previous;
        }

        return {
          ...previous,
          autoFit: nextAutoFit,
        };
      });

      if (nextAutoFit && isCandleHelperVisible && onSetCandleHelperVisibility) {
        onSetCandleHelperVisibility(false);
      }
    },
    [isCandleHelperVisible, onSetCandleHelperVisibility, updateChartDisplaySettings],
  );

  const traderPro17Overlay = useMemo(
    () =>
      overlays.traderPro17
        ? computeTraderPro17Overlay({
            candles: traderPro17SourceCandles,
            dailyCandles,
            symbol,
            interval: traderPro17SourceInterval,
            enable200Dma: overlaySettings.traderPro17.inputs.enable200Dma,
            enableAdvancedTrading: overlaySettings.traderPro17.inputs.enableAdvancedTrading,
            enableNoobProtection: overlaySettings.traderPro17.inputs.enableNoobProtection,
            rangeScale: overlaySettings.traderPro17.inputs.rangeScale,
          })
        : null,
    [
      traderPro17SourceCandles,
      traderPro17SourceInterval,
      dailyCandles,
      symbol,
      overlays.traderPro17,
      overlaySettings.traderPro17.inputs.enable200Dma,
      overlaySettings.traderPro17.inputs.enableAdvancedTrading,
      overlaySettings.traderPro17.inputs.enableNoobProtection,
      overlaySettings.traderPro17.inputs.rangeScale,
    ],
  );

  const traderProV3Overlay = useMemo(
    () =>
      overlays.traderProV3
        ? computeTraderProV3Overlay({
            candles,
            dailyCandles,
            symbol,
            interval,
            stopLossRatio: overlaySettings.traderProV3.inputs.stopLossRatio,
          })
        : null,
    [
      candles,
      dailyCandles,
      symbol,
      interval,
      overlays.traderProV3,
      overlaySettings.traderProV3.inputs.stopLossRatio,
    ],
  );

  const colorProV3Overlay = useMemo(
    () =>
      overlays.colorProTradingSuiteV3
        ? computeColorProTradingSuiteV3Overlay({
            candles,
            rsiLength: overlaySettings.colorProTradingSuiteV3.inputs.rsiLength,
            stochLength: overlaySettings.colorProTradingSuiteV3.inputs.stochLength,
            kSmoothing: overlaySettings.colorProTradingSuiteV3.inputs.kSmoothing,
            dSmoothing: overlaySettings.colorProTradingSuiteV3.inputs.dSmoothing,
            bollingerLength: overlaySettings.colorProTradingSuiteV3.inputs.bollingerLength,
            bollingerMultiplier: overlaySettings.colorProTradingSuiteV3.inputs.bollingerMultiplier,
            useBreakoutVolumeFilter:
              overlaySettings.colorProTradingSuiteV3.inputs.useBreakoutVolumeFilter,
            breakoutVolumeMultiplier:
              overlaySettings.colorProTradingSuiteV3.inputs.breakoutVolumeMultiplier,
          })
        : null,
    [
      candles,
      overlays.colorProTradingSuiteV3,
      overlaySettings.colorProTradingSuiteV3.inputs.rsiLength,
      overlaySettings.colorProTradingSuiteV3.inputs.stochLength,
      overlaySettings.colorProTradingSuiteV3.inputs.kSmoothing,
      overlaySettings.colorProTradingSuiteV3.inputs.dSmoothing,
      overlaySettings.colorProTradingSuiteV3.inputs.bollingerLength,
      overlaySettings.colorProTradingSuiteV3.inputs.bollingerMultiplier,
      overlaySettings.colorProTradingSuiteV3.inputs.useBreakoutVolumeFilter,
      overlaySettings.colorProTradingSuiteV3.inputs.breakoutVolumeMultiplier,
    ],
  );

  const colorProV31Overlay = useMemo(
    () =>
      overlays.colorProTradingSuiteV31
        ? computeColorProTradingSuiteV31Overlay({
            candles,
            rsiLength: overlaySettings.colorProTradingSuiteV31.inputs.rsiLength,
            stochLength: overlaySettings.colorProTradingSuiteV31.inputs.stochLength,
            kSmoothing: overlaySettings.colorProTradingSuiteV31.inputs.kSmoothing,
            dSmoothing: overlaySettings.colorProTradingSuiteV31.inputs.dSmoothing,
            bollingerLength: overlaySettings.colorProTradingSuiteV31.inputs.bollingerLength,
            bollingerMultiplier: overlaySettings.colorProTradingSuiteV31.inputs.bollingerMultiplier,
            useBreakoutVolumeFilter:
              overlaySettings.colorProTradingSuiteV31.inputs.useBreakoutVolumeFilter,
            breakoutVolumeMultiplier:
              overlaySettings.colorProTradingSuiteV31.inputs.breakoutVolumeMultiplier,
            allowUpperBandBridgeYellow:
              overlaySettings.colorProTradingSuiteV31.inputs.allowUpperBandBridgeYellow,
          })
        : null,
    [
      candles,
      overlays.colorProTradingSuiteV31,
      overlaySettings.colorProTradingSuiteV31.inputs.rsiLength,
      overlaySettings.colorProTradingSuiteV31.inputs.stochLength,
      overlaySettings.colorProTradingSuiteV31.inputs.kSmoothing,
      overlaySettings.colorProTradingSuiteV31.inputs.dSmoothing,
      overlaySettings.colorProTradingSuiteV31.inputs.bollingerLength,
      overlaySettings.colorProTradingSuiteV31.inputs.bollingerMultiplier,
      overlaySettings.colorProTradingSuiteV31.inputs.useBreakoutVolumeFilter,
      overlaySettings.colorProTradingSuiteV31.inputs.breakoutVolumeMultiplier,
      overlaySettings.colorProTradingSuiteV31.inputs.allowUpperBandBridgeYellow,
    ],
  );

  const bollingerOverlay = useMemo(
    () =>
      overlays.bollingerBands
        ? computeBollingerBandsOverlay({
            candles,
            length: overlaySettings.bollingerBands.inputs.length,
            multiplier: overlaySettings.bollingerBands.inputs.multiplier,
            source: overlaySettings.bollingerBands.inputs.source,
            basisType: overlaySettings.bollingerBands.inputs.basisType,
            offset: overlaySettings.bollingerBands.inputs.offset,
          })
        : null,
    [
      candles,
      overlays.bollingerBands,
      overlaySettings.bollingerBands.inputs.length,
      overlaySettings.bollingerBands.inputs.multiplier,
      overlaySettings.bollingerBands.inputs.source,
      overlaySettings.bollingerBands.inputs.basisType,
      overlaySettings.bollingerBands.inputs.offset,
    ],
  );

  const ichimokuOverlay = useMemo(
    () =>
      overlays.ichimokuCloud
        ? computeIchimokuCloudOverlay({
            candles,
            interval,
            tenkanPeriod: overlaySettings.ichimokuCloud.inputs.tenkanPeriod,
            kijunPeriod: overlaySettings.ichimokuCloud.inputs.kijunPeriod,
            senkouBPeriod: overlaySettings.ichimokuCloud.inputs.senkouBPeriod,
            displacement: overlaySettings.ichimokuCloud.inputs.displacement,
          })
        : null,
    [
      candles,
      interval,
      overlays.ichimokuCloud,
      overlaySettings.ichimokuCloud.inputs.tenkanPeriod,
      overlaySettings.ichimokuCloud.inputs.kijunPeriod,
      overlaySettings.ichimokuCloud.inputs.senkouBPeriod,
      overlaySettings.ichimokuCloud.inputs.displacement,
    ],
  );

  const cmVixFixOverlay = useMemo(
    () =>
      overlays.cmVixFixUcsSqueeze
        ? computeCmVixFixUcsSqueezeOverlay(
            cmVixFixSourceCandles,
            overlaySettings.cmVixFixUcsSqueeze.inputs,
          )
        : null,
    [
      cmVixFixSourceCandles,
      overlays.cmVixFixUcsSqueeze,
      overlaySettings.cmVixFixUcsSqueeze.inputs,
    ],
  );

  const projectedCmVixFixOverlay = useMemo(() => {
    if (!cmVixFixOverlay) {
      return null;
    }

    return {
      wvf: projectStepSeriesToCandles(candles, cmVixFixSourceCandles, cmVixFixOverlay.wvf),
      wvfSpike: projectStepSeriesToCandles(candles, cmVixFixSourceCandles, cmVixFixOverlay.wvfSpike),
      momentum: projectStepSeriesToCandles(candles, cmVixFixSourceCandles, cmVixFixOverlay.momentum),
      momentumState: projectStepSeriesToCandles(
        candles,
        cmVixFixSourceCandles,
        cmVixFixOverlay.momentumState,
      ),
      upperBand: projectStepSeriesToCandles(candles, cmVixFixSourceCandles, cmVixFixOverlay.upperBand),
      rangeHigh: projectStepSeriesToCandles(candles, cmVixFixSourceCandles, cmVixFixOverlay.rangeHigh),
      rangeLow: projectStepSeriesToCandles(candles, cmVixFixSourceCandles, cmVixFixOverlay.rangeLow),
      squeezeState: projectStepSeriesToCandles(
        candles,
        cmVixFixSourceCandles,
        cmVixFixOverlay.squeezeState,
      ),
    };
  }, [candles, cmVixFixOverlay, cmVixFixSourceCandles]);

  const murreyMathLinesV6SourceCandles = useMemo(() => {
    const useAlternativeTimeframe = overlaySettings.murreyMathLinesV6.inputs.useAlternativeTimeframe;
    const hasAlternativeCandles =
      Array.isArray(murreyMathLinesV6AltCandles) && murreyMathLinesV6AltCandles.length > 0;

    if (useAlternativeTimeframe && hasAlternativeCandles) {
      if (murreyMathLinesV6AltCandles.length <= 1) {
        return [] as Candle[];
      }
      return murreyMathLinesV6AltCandles.slice(0, -1);
    }

    return candles;
  }, [
    candles,
    murreyMathLinesV6AltCandles,
    overlaySettings.murreyMathLinesV6.inputs.useAlternativeTimeframe,
  ]);

  const murreyMathLinesV6Overlay = useMemo(
    () =>
      overlays.murreyMathLinesV6
        ? computeMurreyMathLinesV6Overlay({
            candles: murreyMathLinesV6SourceCandles,
            symbol,
            frameSize: overlaySettings.murreyMathLinesV6.inputs.frameSize,
            frameMultiplier: overlaySettings.murreyMathLinesV6.inputs.frameMultiplier,
            sourceMode: overlaySettings.murreyMathLinesV6.inputs.sourceMode,
            addExtendedLines: overlaySettings.murreyMathLinesV6.inputs.addExtendedLines,
            extraLinesCount: overlaySettings.murreyMathLinesV6.inputs.extraLinesCount,
          })
        : null,
    [
      murreyMathLinesV6SourceCandles,
      symbol,
      overlays.murreyMathLinesV6,
      overlaySettings.murreyMathLinesV6.inputs.frameSize,
      overlaySettings.murreyMathLinesV6.inputs.frameMultiplier,
      overlaySettings.murreyMathLinesV6.inputs.sourceMode,
      overlaySettings.murreyMathLinesV6.inputs.addExtendedLines,
      overlaySettings.murreyMathLinesV6.inputs.extraLinesCount,
    ],
  );

  const activeIndicatorInstances = useMemo(
    () => indicatorInstances.filter((instance) => instance.active),
    [indicatorInstances],
  );

  const activeBasicIndicatorInstances = useMemo(
    () => activeIndicatorInstances.filter((instance) => isBasicIndicatorKey(instance.key)),
    [activeIndicatorInstances],
  );

  const activeMovingAverageInstances = useMemo(
    () => activeIndicatorInstances.filter((instance) => instance.key === 'movingAverage'),
    [activeIndicatorInstances],
  );

  const indicatorInstancesById = useMemo(
    () => new Map(indicatorInstances.map((instance) => [instance.id, instance] as const)),
    [indicatorInstances],
  );

  const resolveBasicStudySettings = useCallback(
    (instance: IndicatorInstance): BasicStudyOverlaySettings => {
      const fallback = overlaySettings.basicStudies[instance.key as BasicIndicatorKey];
      const customSettings = instanceSettings[instance.id] as BasicStudyOverlaySettings | undefined;
      if (!customSettings || typeof customSettings !== 'object') {
        return fallback;
      }
      return customSettings;
    },
    [instanceSettings, overlaySettings.basicStudies],
  );

  const resolveMovingAverageSettings = useCallback(
    (instanceId: IndicatorInstanceId): OverlaySettings['movingAverage'] => {
      const customSettings = instanceSettings[instanceId] as OverlaySettings['movingAverage'] | undefined;
      if (!customSettings || typeof customSettings !== 'object') {
        return overlaySettings.movingAverage;
      }
      return customSettings;
    },
    [instanceSettings, overlaySettings.movingAverage],
  );

  const candleHelperSignals = useMemo(
    () => computeCandleHelperSignals(candles, overlaySettings.candleHelper.inputs),
    [candles, overlaySettings.candleHelper.inputs],
  );

  const coreIndicatorInputsByKey = useMemo<Partial<Record<IndicatorKey, Record<string, unknown>>>>(
    () => ({
      bollingerBands: overlaySettings.bollingerBands.inputs,
      botTrades: overlaySettings.botTrades.inputs,
      cmVixFixUcsSqueeze: overlaySettings.cmVixFixUcsSqueeze.inputs,
      candleHelper: overlaySettings.candleHelper.inputs,
      colorProTradingSuiteV3: overlaySettings.colorProTradingSuiteV3.inputs,
      colorProTradingSuiteV31: overlaySettings.colorProTradingSuiteV31.inputs,
      colorProTradingSuiteV31Strategy: overlaySettings.colorProTradingSuiteV31Strategy.inputs,
      ichimokuCloud: overlaySettings.ichimokuCloud.inputs,
      macd: overlaySettings.macd.inputs,
      murreyMathLinesV6: overlaySettings.murreyMathLinesV6.inputs,
      openCode5mStrategy: overlaySettings.openCode5mStrategy.inputs,
      rsi: overlaySettings.rsi.inputs,
      traderPro17: overlaySettings.traderPro17.inputs,
      traderPro17Strategy: overlaySettings.traderPro17Strategy.inputs,
      traderProV3: overlaySettings.traderProV3.inputs,
      volume: overlaySettings.volume.inputs,
    }),
    [overlaySettings],
  );

  const indicatorArgumentsByInstanceId = useMemo(() => {
    const argumentsById = new Map<IndicatorInstanceId, string>();

    for (const item of overlayLegendItems) {
      let inputsText = '';

      if (item.key === 'movingAverage') {
        inputsText = formatIndicatorInputsText(resolveMovingAverageSettings(item.instanceId).inputs);
      } else if (isBasicIndicatorKey(item.key)) {
        const instance = indicatorInstancesById.get(item.instanceId);
        if (instance) {
          inputsText = formatIndicatorInputsText(resolveBasicStudySettings(instance).inputs);
        }
      } else {
        const inputs = coreIndicatorInputsByKey[item.key];
        if (inputs) {
          inputsText = formatIndicatorInputsText(inputs);
        }
      }

      if (inputsText) {
        argumentsById.set(item.instanceId, inputsText);
      }
    }

    return argumentsById;
  }, [
    coreIndicatorInputsByKey,
    indicatorInstancesById,
    overlayLegendItems,
    resolveBasicStudySettings,
    resolveMovingAverageSettings,
  ]);

  const basicStudyDataByInstanceId = useMemo(() => {
    const context: BasicStudyContext = {
      visibleRange: basicStudyVisibleRange,
      breadthByTimestamp,
      chartInterval: interval,
      dailyCandles,
      weeklyCandles,
      monthlyCandles,
    };
    const data = new Map<IndicatorInstanceId, BasicStudyComputation>();
    for (const instance of activeBasicIndicatorInstances) {
      const key = instance.key as BasicIndicatorKey;
      const settings = resolveBasicStudySettings(instance);
      data.set(instance.id, computeBasicStudy(key, candles, settings, context));
    }
    return data;
  }, [
    activeBasicIndicatorInstances,
    basicStudyVisibleRange,
    breadthByTimestamp,
    candles,
    dailyCandles,
    interval,
    monthlyCandles,
    resolveBasicStudySettings,
    weeklyCandles,
  ]);

  const strategyMaxHistoryCandles = Number.isFinite(
    overlaySettings.traderPro17Strategy.inputs.maxHistoryCandles,
  )
    ? Math.max(500, Math.round(overlaySettings.traderPro17Strategy.inputs.maxHistoryCandles))
    : MAX_PERSISTED_CANDLES;

  const computedStrategyOverlay = useMemo(
    () => {
      if (strategyOverlayOverride !== undefined) {
        return null;
      }

      return (
        overlays.traderPro17Strategy &&
        interval === overlaySettings.traderPro17Strategy.inputs.timeframe
      )
        ? computeTraderPro17StrategyOverlay(candles, null, {
            timeframe: overlaySettings.traderPro17Strategy.inputs.timeframe,
            supportMode: overlaySettings.traderPro17Strategy.inputs.supportMode,
            rangeScale: overlaySettings.traderPro17Strategy.inputs.rangeScale,
            useOneMinuteStopRefinement:
              overlaySettings.traderPro17Strategy.inputs.useOneMinuteStopRefinement,
            requireMa200RisingForEntries:
              overlaySettings.traderPro17Strategy.inputs.requireMa200RisingForEntries,
            entryBodyPctMin: overlaySettings.traderPro17Strategy.inputs.entryBodyPctMin,
            entryClosePosMin: overlaySettings.traderPro17Strategy.inputs.entryClosePosMin,
            entryDistAboveMa200PctMin:
              overlaySettings.traderPro17Strategy.inputs.entryDistAboveMa200PctMin,
            makerFeePercent: overlaySettings.traderPro17Strategy.inputs.makerFeePercent,
            takerFeePercent: overlaySettings.traderPro17Strategy.inputs.takerFeePercent,
            stopLossPercent: overlaySettings.traderPro17Strategy.inputs.stopLossPercent,
            breakEvenTriggerR: overlaySettings.traderPro17Strategy.inputs.breakEvenTriggerR,
            breakEvenOffsetPercent:
              overlaySettings.traderPro17Strategy.inputs.breakEvenOffsetPercent,
            atrPeriod: overlaySettings.traderPro17Strategy.inputs.atrPeriod,
            atrMultiplier: overlaySettings.traderPro17Strategy.inputs.atrMultiplier,
            queueExpiryHours: overlaySettings.traderPro17Strategy.inputs.queueExpiryHours,
            trailingMode: overlaySettings.traderPro17Strategy.inputs.trailingMode,
            smartTrailArmR: overlaySettings.traderPro17Strategy.inputs.smartTrailArmR,
            smartTrailLockR: overlaySettings.traderPro17Strategy.inputs.smartTrailLockR,
            smartTrailMaxGivebackR:
              overlaySettings.traderPro17Strategy.inputs.smartTrailMaxGivebackR,
            smartTrailAtrMultiplier:
              overlaySettings.traderPro17Strategy.inputs.smartTrailAtrMultiplier,
            smartTrailTightenR: overlaySettings.traderPro17Strategy.inputs.smartTrailTightenR,
            smartTrailTightenAtrMultiplier:
              overlaySettings.traderPro17Strategy.inputs.smartTrailTightenAtrMultiplier,
          }, symbol, strategyMaxHistoryCandles, strategyMinuteCandles)
        : null;
    },
    [
      candles,
      overlays.traderPro17Strategy,
      interval,
      strategyMinuteCandles,
      strategyOverlayOverride,
      overlaySettings.traderPro17Strategy.inputs.timeframe,
      overlaySettings.traderPro17Strategy.inputs.supportMode,
      overlaySettings.traderPro17Strategy.inputs.rangeScale,
      overlaySettings.traderPro17Strategy.inputs.useOneMinuteStopRefinement,
      overlaySettings.traderPro17Strategy.inputs.requireMa200RisingForEntries,
      overlaySettings.traderPro17Strategy.inputs.entryBodyPctMin,
      overlaySettings.traderPro17Strategy.inputs.entryClosePosMin,
      overlaySettings.traderPro17Strategy.inputs.entryDistAboveMa200PctMin,
      overlaySettings.traderPro17Strategy.inputs.makerFeePercent,
      overlaySettings.traderPro17Strategy.inputs.takerFeePercent,
      overlaySettings.traderPro17Strategy.inputs.stopLossPercent,
      overlaySettings.traderPro17Strategy.inputs.breakEvenTriggerR,
      overlaySettings.traderPro17Strategy.inputs.breakEvenOffsetPercent,
      overlaySettings.traderPro17Strategy.inputs.atrPeriod,
      overlaySettings.traderPro17Strategy.inputs.atrMultiplier,
      overlaySettings.traderPro17Strategy.inputs.queueExpiryHours,
      overlaySettings.traderPro17Strategy.inputs.trailingMode,
      overlaySettings.traderPro17Strategy.inputs.smartTrailArmR,
      overlaySettings.traderPro17Strategy.inputs.smartTrailLockR,
      overlaySettings.traderPro17Strategy.inputs.smartTrailMaxGivebackR,
      overlaySettings.traderPro17Strategy.inputs.smartTrailAtrMultiplier,
      overlaySettings.traderPro17Strategy.inputs.smartTrailTightenR,
      overlaySettings.traderPro17Strategy.inputs.smartTrailTightenAtrMultiplier,
      strategyMaxHistoryCandles,
      symbol,
    ],
  );
  const strategyOverlay = strategyOverlayOverride ?? computedStrategyOverlay;
  const strategyOverlayCandles = strategyOverlaySourceCandles ?? candles;

  const openCode5mStrategyMaxHistoryCandles = Number.isFinite(
    overlaySettings.openCode5mStrategy.inputs.maxHistoryCandles,
  )
    ? Math.max(500, Math.round(overlaySettings.openCode5mStrategy.inputs.maxHistoryCandles))
    : MAX_PERSISTED_CANDLES;

  const computedOpenCode5mStrategyOverlay = useMemo(() => {
    if (openCode5mStrategyOverlayOverride !== undefined) {
      return null;
    }

    if (
      !overlays.openCode5mStrategy ||
      interval !== overlaySettings.openCode5mStrategy.inputs.timeframe
    ) {
      return null;
    }

    return computeOpenCode5mStrategyOverlay(
      candles,
      null,
      overlaySettings.openCode5mStrategy.inputs,
      openCode5mStrategyMaxHistoryCandles,
    );
  }, [
    candles,
    interval,
    openCode5mStrategyMaxHistoryCandles,
    openCode5mStrategyOverlayOverride,
    overlays.openCode5mStrategy,
    overlaySettings.openCode5mStrategy.inputs,
  ]);

  const openCode5mStrategyOverlay =
    openCode5mStrategyOverlayOverride ?? computedOpenCode5mStrategyOverlay;
  const openCode5mStrategyOverlayCandles = openCode5mStrategyOverlaySourceCandles ?? candles;

  const colorProV31StrategyMaxHistoryCandles = Number.isFinite(
    overlaySettings.colorProTradingSuiteV31Strategy.inputs.maxHistoryCandles,
  )
    ? Math.max(200, Math.round(overlaySettings.colorProTradingSuiteV31Strategy.inputs.maxHistoryCandles))
    : MAX_PERSISTED_CANDLES;

  const computedColorProV31StrategySourceCandles = useMemo(() => {
    if (
      !overlays.colorProTradingSuiteV31Strategy ||
      interval !== overlaySettings.colorProTradingSuiteV31Strategy.inputs.timeframe
    ) {
      return [] as Candle[];
    }

    const startIndex = Math.max(0, candles.length - colorProV31StrategyMaxHistoryCandles);
    return candles.slice(startIndex);
  }, [
    candles,
    colorProV31StrategyMaxHistoryCandles,
    interval,
    overlays.colorProTradingSuiteV31Strategy,
    overlaySettings.colorProTradingSuiteV31Strategy.inputs.timeframe,
  ]);

  const computedColorProV31OverlayForStrategy = useMemo(
    () =>
      computedColorProV31StrategySourceCandles.length > 0
        ? computeColorProTradingSuiteV31Overlay({
            candles: computedColorProV31StrategySourceCandles,
            rsiLength: overlaySettings.colorProTradingSuiteV31.inputs.rsiLength,
            stochLength: overlaySettings.colorProTradingSuiteV31.inputs.stochLength,
            kSmoothing: overlaySettings.colorProTradingSuiteV31.inputs.kSmoothing,
            dSmoothing: overlaySettings.colorProTradingSuiteV31.inputs.dSmoothing,
            bollingerLength: overlaySettings.colorProTradingSuiteV31.inputs.bollingerLength,
            bollingerMultiplier: overlaySettings.colorProTradingSuiteV31.inputs.bollingerMultiplier,
            useBreakoutVolumeFilter:
              overlaySettings.colorProTradingSuiteV31.inputs.useBreakoutVolumeFilter,
            breakoutVolumeMultiplier:
              overlaySettings.colorProTradingSuiteV31.inputs.breakoutVolumeMultiplier,
            allowUpperBandBridgeYellow:
              overlaySettings.colorProTradingSuiteV31.inputs.allowUpperBandBridgeYellow,
          })
        : null,
    [
      computedColorProV31StrategySourceCandles,
      overlaySettings.colorProTradingSuiteV31.inputs.rsiLength,
      overlaySettings.colorProTradingSuiteV31.inputs.stochLength,
      overlaySettings.colorProTradingSuiteV31.inputs.kSmoothing,
      overlaySettings.colorProTradingSuiteV31.inputs.dSmoothing,
      overlaySettings.colorProTradingSuiteV31.inputs.bollingerLength,
      overlaySettings.colorProTradingSuiteV31.inputs.bollingerMultiplier,
      overlaySettings.colorProTradingSuiteV31.inputs.useBreakoutVolumeFilter,
      overlaySettings.colorProTradingSuiteV31.inputs.breakoutVolumeMultiplier,
      overlaySettings.colorProTradingSuiteV31.inputs.allowUpperBandBridgeYellow,
    ],
  );

  const computedColorProV31StrategyOverlay = useMemo(() => {
    if (colorProV31StrategyOverlayOverride !== undefined) {
      return null;
    }

    if (computedColorProV31StrategySourceCandles.length === 0) {
      return null;
    }

    return computeColorProTradingSuiteV31StrategyOverlay(
      computedColorProV31StrategySourceCandles,
      computedColorProV31OverlayForStrategy,
      overlaySettings.colorProTradingSuiteV31Strategy.inputs,
    );
  }, [
    colorProV31StrategyOverlayOverride,
    computedColorProV31StrategySourceCandles,
    computedColorProV31OverlayForStrategy,
    overlaySettings.colorProTradingSuiteV31Strategy.inputs,
  ]);

  const colorProV31StrategyOverlay =
    colorProV31StrategyOverlayOverride ?? computedColorProV31StrategyOverlay;
  const colorProV31StrategyOverlayCandles =
    colorProV31StrategyOverlayOverride !== undefined
      ? colorProV31StrategyOverlaySourceCandles ?? candles
      : computedColorProV31StrategySourceCandles;

  const paneLegendItems = useMemo(
    () => overlayLegendItems.filter((item) => item.active && isPaneIndicator(item.key)),
    [overlayLegendItems],
  );

  const paneIndicators = useMemo<IndicatorKey[]>(
    () => paneLegendItems.map((item) => item.key),
    [paneLegendItems],
  );

  const paneIndexByIndicator = useMemo(() => {
    const mapping = new Map<IndicatorKey, number>();
    paneIndicators.forEach((key, index) => {
      if (!mapping.has(key)) {
        mapping.set(key, index + 1);
      }
    });
    return mapping;
  }, [paneIndicators]);

  const paneIndexByLegendInstance = useMemo(() => {
    const mapping = new Map<IndicatorInstanceId, number>();
    paneLegendItems.forEach((item, index) => {
      mapping.set(item.instanceId, index + 1);
    });
    return mapping;
  }, [paneLegendItems]);

  const paneIndicatorOrderIndexByKey = useMemo(() => {
    const mapping = new Map<IndicatorInstanceId, number>();
    paneLegendItems.forEach((item, index) => {
      mapping.set(item.instanceId, index);
    });
    return mapping;
  }, [paneLegendItems]);

  const clearTraderProV3PriceLines = useCallback(() => {
    const candleSeries = seriesRef.current;
    if (!candleSeries) {
      traderProV3PriceLinesRef.current = [];
      return;
    }

    traderProV3PriceLinesRef.current.forEach((line) => {
      candleSeries.removePriceLine(line);
    });
    traderProV3PriceLinesRef.current = [];
  }, []);

  const clearTraderPro17Primitive = useCallback(() => {
    const candleSeries = seriesRef.current;
    const primitive = traderPro17PrimitiveRef.current;
    if (candleSeries && primitive) {
      candleSeries.detachPrimitive(primitive);
    }
    traderPro17PrimitiveRef.current = null;
  }, []);

  const clearTraderPro17DmaSeries = useCallback(() => {
    const chart = chartRef.current;
    const dmaSeries = traderPro17DmaSeriesRef.current;
    if (chart && dmaSeries) {
      chart.removeSeries(dmaSeries);
    }

    traderPro17DmaSeriesRef.current = null;
  }, []);

  const clearColorProLineSeries = useCallback((key: 'colorProTradingSuiteV3' | 'colorProTradingSuiteV31') => {
    const chart = chartRef.current;
    const targetRef =
      key === 'colorProTradingSuiteV3' ? colorProV3LineSeriesRef : colorProV31LineSeriesRef;
    if (!chart) {
      targetRef.current.clear();
      return;
    }

    targetRef.current.forEach((series) => {
      chart.removeSeries(series);
    });
    targetRef.current.clear();
  }, []);

  const clearColorProMarkers = useCallback((key: 'colorProTradingSuiteV3' | 'colorProTradingSuiteV31') => {
    const targetRef = key === 'colorProTradingSuiteV3' ? colorProV3MarkersRef : colorProV31MarkersRef;
    if (targetRef.current) {
      targetRef.current.detach();
      targetRef.current = null;
    }
  }, []);

  const clearBotTradeMarkers = useCallback(() => {
    if (botTradeMarkersRef.current) {
      botTradeMarkersRef.current.detach();
      botTradeMarkersRef.current = null;
    }
  }, []);

  const clearMaCrossMarkers = useCallback(() => {
    if (maCrossMarkersRef.current) {
      maCrossMarkersRef.current.detach();
      maCrossMarkersRef.current = null;
    }
  }, []);

  const clearCandleHelperMarkers = useCallback(() => {
    if (candleHelperMarkersRef.current) {
      candleHelperMarkersRef.current.detach();
      candleHelperMarkersRef.current = null;
    }
    candleHelperMarkerKeyRef.current = '';
  }, []);

  const clearStrategyMarkers = useCallback(() => {
    if (strategyMarkersRef.current) {
      strategyMarkersRef.current.detach();
      strategyMarkersRef.current = null;
    }
  }, []);

  const clearStrategyPriceLines = useCallback(() => {
    const candleSeries = seriesRef.current;
    if (!candleSeries) {
      strategyPriceLinesRef.current = [];
      return;
    }

    strategyPriceLinesRef.current.forEach((line) => {
      candleSeries.removePriceLine(line);
    });
    strategyPriceLinesRef.current = [];
  }, []);

  const clearOpenCode5mStrategyMarkers = useCallback(() => {
    if (openCode5mStrategyMarkersRef.current) {
      openCode5mStrategyMarkersRef.current.detach();
      openCode5mStrategyMarkersRef.current = null;
    }
  }, []);

  const clearOpenCode5mStrategyPriceLines = useCallback(() => {
    const candleSeries = seriesRef.current;
    if (!candleSeries) {
      openCode5mStrategyPriceLinesRef.current = [];
      return;
    }

    openCode5mStrategyPriceLinesRef.current.forEach((line) => {
      candleSeries.removePriceLine(line);
    });
    openCode5mStrategyPriceLinesRef.current = [];
  }, []);

  const clearColorProV31StrategyMarkers = useCallback(() => {
    if (colorProV31StrategyMarkersRef.current) {
      colorProV31StrategyMarkersRef.current.detach();
      colorProV31StrategyMarkersRef.current = null;
    }
  }, []);

  const clearColorProV31StrategyPriceLines = useCallback(() => {
    const candleSeries = seriesRef.current;
    if (!candleSeries) {
      colorProV31StrategyPriceLinesRef.current = [];
      return;
    }

    colorProV31StrategyPriceLinesRef.current.forEach((line) => {
      candleSeries.removePriceLine(line);
    });
    colorProV31StrategyPriceLinesRef.current = [];
  }, []);

  const clearMurreyMathLinesV6Series = useCallback(() => {
    const chart = chartRef.current;
    if (chart) {
      murreyMathLinesV6SeriesRef.current.forEach((series) => {
        chart.removeSeries(series);
      });
    }
    murreyMathLinesV6SeriesRef.current.clear();
  }, []);

  const clearMurreyMathLinesV6Zones = useCallback(() => {
    const candleSeries = seriesRef.current;

    if (candleSeries && murreyMathLinesV6ReversalBottomZonePrimitiveRef.current) {
      candleSeries.detachPrimitive(murreyMathLinesV6ReversalBottomZonePrimitiveRef.current);
    }
    if (candleSeries && murreyMathLinesV6TradingZonePrimitiveRef.current) {
      candleSeries.detachPrimitive(murreyMathLinesV6TradingZonePrimitiveRef.current);
    }
    if (candleSeries && murreyMathLinesV6ReversalTopZonePrimitiveRef.current) {
      candleSeries.detachPrimitive(murreyMathLinesV6ReversalTopZonePrimitiveRef.current);
    }

    murreyMathLinesV6ReversalBottomZonePrimitiveRef.current = null;
    murreyMathLinesV6TradingZonePrimitiveRef.current = null;
    murreyMathLinesV6ReversalTopZonePrimitiveRef.current = null;
  }, []);

  const clearMurreyMathLinesV6Overlay = useCallback(() => {
    clearMurreyMathLinesV6Series();
    clearMurreyMathLinesV6Zones();
  }, [clearMurreyMathLinesV6Series, clearMurreyMathLinesV6Zones]);

  const clearRsiSeries = useCallback(() => {
    const chart = chartRef.current;
    const rsiSeries = rsiSeriesRef.current;

    if (!chart || !rsiSeries) {
      rsiZoneLinesRef.current = [];
      rsiSeriesRef.current = null;
      return;
    }

    rsiZoneLinesRef.current.forEach((line) => {
      rsiSeries.removePriceLine(line);
    });
    rsiZoneLinesRef.current = [];
    chart.removeSeries(rsiSeries);
    rsiSeriesRef.current = null;
  }, []);

  const clearMacdSeries = useCallback(() => {
    const chart = chartRef.current;

    if (!chart) {
      macdLineSeriesRef.current = null;
      macdSignalSeriesRef.current = null;
      macdHistogramSeriesRef.current = null;
      return;
    }

    if (macdLineSeriesRef.current) {
      chart.removeSeries(macdLineSeriesRef.current);
      macdLineSeriesRef.current = null;
    }

    if (macdSignalSeriesRef.current) {
      chart.removeSeries(macdSignalSeriesRef.current);
      macdSignalSeriesRef.current = null;
    }

    if (macdHistogramSeriesRef.current) {
      chart.removeSeries(macdHistogramSeriesRef.current);
      macdHistogramSeriesRef.current = null;
    }
  }, []);

  const clearCmVixFixUcsSqueezeSeries = useCallback(() => {
    const chart = chartRef.current;

    if (!chart) {
      cmVixFixHistogramSeriesRef.current = null;
      cmSqueezeMomentumSeriesRef.current = null;
      cmSqueezeOnSeriesRef.current = null;
      cmSqueezeOffSeriesRef.current = null;
      cmNoSqueezeSeriesRef.current = null;
      cmWvfUpperBandSeriesRef.current = null;
      cmWvfRangeHighSeriesRef.current = null;
      cmWvfRangeLowSeriesRef.current = null;
      return;
    }

    if (cmVixFixHistogramSeriesRef.current) {
      chart.removeSeries(cmVixFixHistogramSeriesRef.current);
      cmVixFixHistogramSeriesRef.current = null;
    }

    if (cmSqueezeMomentumSeriesRef.current) {
      chart.removeSeries(cmSqueezeMomentumSeriesRef.current);
      cmSqueezeMomentumSeriesRef.current = null;
    }

    if (cmSqueezeOnSeriesRef.current) {
      chart.removeSeries(cmSqueezeOnSeriesRef.current);
      cmSqueezeOnSeriesRef.current = null;
    }

    if (cmSqueezeOffSeriesRef.current) {
      chart.removeSeries(cmSqueezeOffSeriesRef.current);
      cmSqueezeOffSeriesRef.current = null;
    }

    if (cmNoSqueezeSeriesRef.current) {
      chart.removeSeries(cmNoSqueezeSeriesRef.current);
      cmNoSqueezeSeriesRef.current = null;
    }

    if (cmWvfUpperBandSeriesRef.current) {
      chart.removeSeries(cmWvfUpperBandSeriesRef.current);
      cmWvfUpperBandSeriesRef.current = null;
    }

    if (cmWvfRangeHighSeriesRef.current) {
      chart.removeSeries(cmWvfRangeHighSeriesRef.current);
      cmWvfRangeHighSeriesRef.current = null;
    }

    if (cmWvfRangeLowSeriesRef.current) {
      chart.removeSeries(cmWvfRangeLowSeriesRef.current);
      cmWvfRangeLowSeriesRef.current = null;
    }
  }, []);

  const clearMovingAverageSeries = useCallback(() => {
    const chart = chartRef.current;

    if (!chart) {
      movingAverageSeriesRef.current.clear();
      return;
    }

    for (const series of movingAverageSeriesRef.current.values()) {
      chart.removeSeries(series);
    }
    movingAverageSeriesRef.current.clear();
  }, []);

  const clearBollingerBandsSeries = useCallback(() => {
    const chart = chartRef.current;
    const candleSeries = seriesRef.current;

    if (chart && bollingerUpperSeriesRef.current) {
      chart.removeSeries(bollingerUpperSeriesRef.current);
    }
    if (chart && bollingerBasisSeriesRef.current) {
      chart.removeSeries(bollingerBasisSeriesRef.current);
    }
    if (chart && bollingerLowerSeriesRef.current) {
      chart.removeSeries(bollingerLowerSeriesRef.current);
    }

    if (candleSeries && bollingerFillPrimitiveRef.current) {
      candleSeries.detachPrimitive(bollingerFillPrimitiveRef.current);
    }

    bollingerUpperSeriesRef.current = null;
    bollingerBasisSeriesRef.current = null;
    bollingerLowerSeriesRef.current = null;
    bollingerFillPrimitiveRef.current = null;
  }, []);

  const clearIchimokuSeries = useCallback(() => {
    const chart = chartRef.current;
    const candleSeries = seriesRef.current;

    if (chart && ichimokuTenkanSeriesRef.current) {
      chart.removeSeries(ichimokuTenkanSeriesRef.current);
    }
    if (chart && ichimokuKijunSeriesRef.current) {
      chart.removeSeries(ichimokuKijunSeriesRef.current);
    }
    if (chart && ichimokuSenkouASeriesRef.current) {
      chart.removeSeries(ichimokuSenkouASeriesRef.current);
    }
    if (chart && ichimokuSenkouBSeriesRef.current) {
      chart.removeSeries(ichimokuSenkouBSeriesRef.current);
    }
    if (chart && ichimokuChikouSeriesRef.current) {
      chart.removeSeries(ichimokuChikouSeriesRef.current);
    }

    if (candleSeries && ichimokuBullishFillPrimitiveRef.current) {
      candleSeries.detachPrimitive(ichimokuBullishFillPrimitiveRef.current);
    }
    if (candleSeries && ichimokuBearishFillPrimitiveRef.current) {
      candleSeries.detachPrimitive(ichimokuBearishFillPrimitiveRef.current);
    }

    ichimokuTenkanSeriesRef.current = null;
    ichimokuKijunSeriesRef.current = null;
    ichimokuSenkouASeriesRef.current = null;
    ichimokuSenkouBSeriesRef.current = null;
    ichimokuChikouSeriesRef.current = null;
    ichimokuBullishFillPrimitiveRef.current = null;
    ichimokuBearishFillPrimitiveRef.current = null;
  }, []);

  const clearBasicStudySeriesForInstance = useCallback((instanceId: IndicatorInstanceId) => {
    const chart = chartRef.current;
    const refs = basicStudySeriesRefs.current.get(instanceId);

    if (!refs) {
      return;
    }

    if (chart) {
      if (refs.primary) {
        chart.removeSeries(refs.primary);
      }
      if (refs.secondary) {
        chart.removeSeries(refs.secondary);
      }
      if (refs.tertiary) {
        chart.removeSeries(refs.tertiary);
      }
      if (refs.histogram) {
        chart.removeSeries(refs.histogram);
      }
    }

    basicStudySeriesRefs.current.delete(instanceId);
  }, []);

  const clearBasicStudyDrawingPrimitiveForInstance = useCallback((instanceId: IndicatorInstanceId) => {
    const candleSeries = seriesRef.current;
    const primitive = basicStudyDrawingPrimitiveRefs.current.get(instanceId);
    if (!primitive) {
      return;
    }

    if (candleSeries) {
      candleSeries.detachPrimitive(primitive);
    }

    basicStudyDrawingPrimitiveRefs.current.delete(instanceId);
  }, []);

  const clearAllBasicStudySeries = useCallback(() => {
    for (const instanceId of basicStudySeriesRefs.current.keys()) {
      clearBasicStudySeriesForInstance(instanceId);
    }
    basicStudySeriesRefs.current.clear();
  }, [clearBasicStudySeriesForInstance]);

  const clearAllBasicStudyDrawingPrimitives = useCallback(() => {
    for (const instanceId of basicStudyDrawingPrimitiveRefs.current.keys()) {
      clearBasicStudyDrawingPrimitiveForInstance(instanceId);
    }
    basicStudyDrawingPrimitiveRefs.current.clear();
  }, [clearBasicStudyDrawingPrimitiveForInstance]);

  const pruneEmptyIndicatorPanes = useCallback(() => {
    const chart = chartRef.current;
    if (!chart) {
      return;
    }

    const panes = chart.panes();
    for (let index = panes.length - 1; index >= 1; index -= 1) {
      if (panes[index].getSeries().length === 0) {
        chart.removePane(index);
      }
    }
  }, []);

  const rebalancePaneStretchFactors = useCallback(() => {
    const chart = chartRef.current;
    if (!chart) {
      return;
    }

    const panes = chart.panes();
    if (panes.length === 0) {
      return;
    }

    if (panes.length === 1) {
      panes[0].setStretchFactor(1);
      return;
    }

    panes[0].setStretchFactor(0.68);
    const indicatorPaneSize = 0.32 / (panes.length - 1);
    for (let index = 1; index < panes.length; index += 1) {
      panes[index].setStretchFactor(indicatorPaneSize);
    }
  }, []);

  useEffect(() => {
    if (!container) {
      return () => undefined;
    }

    const chart = createChart(container, {
      layout: {
        textColor: '#e2e8f0',
        background: { type: ColorType.Solid, color: '#0f172a' },
        panes: {
          enableResize: true,
          separatorColor: '#1e293b',
          separatorHoverColor: 'rgba(148, 163, 184, 0.24)',
        },
      },
      rightPriceScale: {
        borderColor: '#1e293b',
      },
      timeScale: {
        borderColor: '#1e293b',
        timeVisible: INTRADAY_INTERVALS.includes(interval),
        secondsVisible: false,
        enableConflation: false,
        precomputeConflationOnInit: false,
        precomputeConflationPriority: 'background',
      },
      handleScroll: {
        mouseWheel: true,
        pressedMouseMove: true,
        horzTouchDrag: true,
        vertTouchDrag: false,
      },
      crosshair: {
        mode: CrosshairMode.Normal,
      },
      grid: {
        horzLines: { color: '#1e293b' },
        vertLines: { color: '#1e293b' },
      },
    });

    chartRef.current = chart;
    seriesRef.current = chart.addSeries(
      CandlestickSeries,
      {
        upColor: '#22c55e',
        borderUpColor: '#22c55e',
        wickUpColor: '#22c55e',
        downColor: '#ef4444',
        borderDownColor: '#ef4444',
        wickDownColor: '#ef4444',
      },
      0,
    );
    hasInitialFitRef.current = false;
    hasGrowthRefitRef.current = false;
    previousCandleCountRef.current = 0;
    shouldAutoFollowRef.current = true;
    rightOffsetRef.current = 0;
    setIsChartReady(true);

    const timeScale = chart.timeScale();
    let visibleRangeFrameId: number | null = null;
    let pendingVisibleRightGapBars = 0;
    let pendingBasicStudyRange: BasicStudyContext['visibleRange'] | undefined;
    let hasPendingVisibleRightGapBars = false;
    let hasPendingBasicStudyRange = false;

    const flushVisibleRangeState = () => {
      visibleRangeFrameId = null;

      if (hasPendingVisibleRightGapBars) {
        const nextVisibleRightGapBars = pendingVisibleRightGapBars;
        setVisibleRightGapBars((previous) =>
          previous === nextVisibleRightGapBars ? previous : nextVisibleRightGapBars,
        );
        hasPendingVisibleRightGapBars = false;
      }

      if (hasPendingBasicStudyRange) {
        const nextRange = pendingBasicStudyRange;
        setBasicStudyVisibleRange((previous) => {
          if (previous === nextRange) {
            return previous;
          }
          if (
            previous &&
            nextRange &&
            previous.fromIndex === nextRange.fromIndex &&
            previous.toIndex === nextRange.toIndex
          ) {
            return previous;
          }
          return nextRange;
        });
        hasPendingBasicStudyRange = false;
      }
    };

    const scheduleVisibleRangeStateFlush = () => {
      if (visibleRangeFrameId !== null) {
        return;
      }
      visibleRangeFrameId = window.requestAnimationFrame(flushVisibleRangeState);
    };

    const handleVisibleLogicalRangeChange = () => {
      const previousLogicalRange = cloneLogicalRange(visibleLogicalRangeRef.current);
      const logicalRange = timeScale.getVisibleLogicalRange();
      if (logicalRange) {
        visibleLogicalRangeRef.current = {
          from: logicalRange.from,
          to: logicalRange.to,
        };

        const baseSeriesLastLogicalIndex = baseSeriesLastLogicalIndexRef.current;
        const nextVisibleRightGapBars =
          baseSeriesLastLogicalIndex >= 0
            ? Math.min(
                MAX_FUTURE_WHITESPACE_BARS,
                Math.max(0, Math.ceil(logicalRange.to - baseSeriesLastLogicalIndex)),
              )
            : 0;
        pendingVisibleRightGapBars = nextVisibleRightGapBars;
        hasPendingVisibleRightGapBars = true;
        scheduleVisibleRangeStateFlush();
      } else {
        pendingVisibleRightGapBars = 0;
        hasPendingVisibleRightGapBars = true;
        scheduleVisibleRangeStateFlush();
      }

      if (didLogicalRangeChange(previousLogicalRange, logicalRange)) {
        if (activeMouseInteractionRef.current && !activeMouseInteractionRef.current.startedOnAxis) {
          if (!manualTimeNavigationRef.current) {
            setManualTimeNavigationEnabled(true, 'mouse-pan');
          }
        }
      }

      if (isReplayModeRef.current) {
        shouldAutoFollowRef.current = false;
      } else {
        const distanceFromRealtime = Math.abs(timeScale.scrollPosition() - rightOffsetRef.current);
        if (replayViewportLockRef.current) {
          if (replayUnlockIntentRef.current && distanceFromRealtime <= 0.5) {
            replayViewportLockRef.current = false;
            replayUnlockIntentRef.current = false;
            lockedReplayPaddingRef.current = 0;
            lockedReplayPriceRangeRef.current = null;
            preReplayVisibleRangeRef.current = null;
            replayStartAnchorRef.current = null;
            shouldAutoFollowRef.current = true;
            setIsReplayViewportLocked(false);
          } else {
            shouldAutoFollowRef.current = false;
          }
        } else {
          shouldAutoFollowRef.current =
            manualTimeNavigationRef.current ? false : distanceFromRealtime <= 0.5;
        }

      }

      if (!logicalRange) {
        onVisibleCandleRangeChange?.(null);
        return;
      }

      const nextRange = {
        fromIndex: Math.floor(logicalRange.from),
        toIndex: Math.ceil(logicalRange.to),
      };
      pendingBasicStudyRange = nextRange;
      hasPendingBasicStudyRange = true;
      scheduleVisibleRangeStateFlush();

      if (onVisibleCandleRangeChange) {
        const candleCount = candlesRef.current.length;
        const fromIndex = Math.max(0, Math.min(candleCount, Math.floor(logicalRange.from)));
        const toIndex = Math.max(fromIndex, Math.min(candleCount, Math.ceil(logicalRange.to)));
        onVisibleCandleRangeChange(
          toIndex > fromIndex
            ? {
                fromIndex,
                toIndex,
              }
            : null,
        );
      }
    };
    timeScale.subscribeVisibleLogicalRangeChange(handleVisibleLogicalRangeChange);
    handleVisibleLogicalRangeChange();

    const handleReplayUnlockIntent = () => {
      if (!isReplayModeRef.current && replayViewportLockRef.current) {
        replayUnlockIntentRef.current = true;
      }
    };

    const cleanupChartResize = bindChartContainerResize(container, chart);
    container.addEventListener('pointerdown', handleReplayUnlockIntent);
    container.addEventListener('wheel', handleReplayUnlockIntent, { passive: true });

    return () => {
      if (visibleRangeFrameId !== null) {
        window.cancelAnimationFrame(visibleRangeFrameId);
      }
      cleanupChartResize();
      container.removeEventListener('pointerdown', handleReplayUnlockIntent);
      container.removeEventListener('wheel', handleReplayUnlockIntent);
      timeScale.unsubscribeVisibleLogicalRangeChange(handleVisibleLogicalRangeChange);
      onVisibleCandleRangeChange?.(null);
      clearMovingAverageSeries();
      clearTraderPro17Primitive();
      clearTraderPro17DmaSeries();
      clearTraderProV3PriceLines();
      clearColorProLineSeries('colorProTradingSuiteV3');
      clearColorProLineSeries('colorProTradingSuiteV31');
      clearColorProMarkers('colorProTradingSuiteV3');
      clearColorProMarkers('colorProTradingSuiteV31');
      clearMaCrossMarkers();
      clearBotTradeMarkers();
      clearCandleHelperMarkers();
      clearOpenCode5mStrategyMarkers();
      clearOpenCode5mStrategyPriceLines();
      clearColorProV31StrategyMarkers();
      clearColorProV31StrategyPriceLines();
      clearMurreyMathLinesV6Overlay();
      clearRsiSeries();
      clearMacdSeries();
      clearCmVixFixUcsSqueezeSeries();
      clearBollingerBandsSeries();
      clearIchimokuSeries();
      clearAllBasicStudySeries();
      clearAllBasicStudyDrawingPrimitives();
      if (volumeSeriesRef.current) {
        chart.removeSeries(volumeSeriesRef.current);
        volumeSeriesRef.current = null;
      }
      chart.remove();
      chartRef.current = null;
      seriesRef.current = null;
      hasInitialFitRef.current = false;
      hasGrowthRefitRef.current = false;
      baseSeriesLastLogicalIndexRef.current = -1;
      previousRenderedCandlesRef.current = [];
      lastRenderedFutureWhitespaceBarsRef.current = 0;
      previousCandleCountRef.current = 0;
      shouldAutoFollowRef.current = true;
      manualTimeNavigationRef.current = false;
      rightOffsetRef.current = 0;
      replayViewportLockRef.current = false;
      replayUnlockIntentRef.current = false;
      lockedReplayPaddingRef.current = 0;
      lockedReplayPriceRangeRef.current = null;
      lastAppliedRightOffsetRef.current = null;
      lastShiftVisibleRangeOnNewBarRef.current = null;
      hasManualPriceScaleBootstrapRef.current = false;
      preReplayVisibleRangeRef.current = null;
      replayStartAnchorRef.current = null;
      setIsChartReady(false);
      setBasicStudyVisibleRange(undefined);
      setHoveredCandleIndex(null);
      setPaneLegendTopByKey({});
    };
  }, [
    container,
    clearTraderPro17Primitive,
    clearTraderPro17DmaSeries,
    clearTraderProV3PriceLines,
    clearColorProLineSeries,
    clearColorProMarkers,
    clearMaCrossMarkers,
    clearBotTradeMarkers,
    clearCandleHelperMarkers,
    clearOpenCode5mStrategyMarkers,
    clearOpenCode5mStrategyPriceLines,
    clearColorProV31StrategyMarkers,
    clearColorProV31StrategyPriceLines,
    clearMurreyMathLinesV6Overlay,
    clearMovingAverageSeries,
    clearRsiSeries,
    clearMacdSeries,
    clearCmVixFixUcsSqueezeSeries,
    clearBollingerBandsSeries,
    clearIchimokuSeries,
    clearAllBasicStudySeries,
    clearAllBasicStudyDrawingPrimitives,
    applyShiftVisibleRangeOnNewBar,
    isMousePointerOnChartAxis,
    setManualTimeNavigationEnabled,
  ]);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) {
      return;
    }

    chart.applyOptions({
      timeScale: {
        timeVisible: INTRADAY_INTERVALS.includes(interval),
        secondsVisible: false,
      },
    });

    setManualTimeNavigationEnabled(false, 'interval-or-replay-change');
    hasInitialFitRef.current = false;
    hasGrowthRefitRef.current = false;
    previousRenderedCandlesRef.current = [];
    lastRenderedFutureWhitespaceBarsRef.current = 0;
    previousCandleCountRef.current = 0;
    shouldAutoFollowRef.current = !isReplayMode;
    hasManualPriceScaleBootstrapRef.current = false;
  }, [interval, isReplayMode, setManualTimeNavigationEnabled]);

  useEffect(() => {
    isReplayModeRef.current = isReplayMode;

    const chart = chartRef.current;
    if (!chart) {
      previousReplayModeRef.current = isReplayMode;
      return;
    }

    const lockViewport = isReplayMode || isReplayViewportLocked;
    applyShiftVisibleRangeOnNewBar(!lockViewport && !manualTimeNavigationRef.current);

    if (isReplayMode) {
      const enteringReplay = !previousReplayModeRef.current;
      const fallbackReplayPadding =
        replayIndex !== null
          ? Math.max(0, replayTotalBars - 1 - replayIndex)
          : 0;
      const replayStartAnchor = replayStartAnchorRef.current;
      const hasReplayStartAnchor =
        replayIndex !== null &&
        replayStartAnchor !== null &&
        replayStartAnchor.selectedIndex === replayIndex;

      if (enteringReplay) {
        replayViewportLockRef.current = true;
        lockedReplayPaddingRef.current =
          hasReplayStartAnchor && replayStartAnchor
            ? replayStartAnchor.requiredReplayPadding
            : fallbackReplayPadding;
        lockedReplayPriceRangeRef.current = chart.priceScale('right').getVisibleRange();
        setIsReplayViewportLocked(true);
        preReplayVisibleRangeRef.current = chart.timeScale().getVisibleLogicalRange();

        if (
          hasReplayStartAnchor &&
          replayStartAnchor &&
          replayStartAnchor.visibleRange &&
          replayIndex !== null
        ) {
          const rangeWidth = replayStartAnchor.visibleRange.to - replayStartAnchor.visibleRange.from;
          if (Number.isFinite(rangeWidth) && rangeWidth > 0) {
            const delta = replayIndex - replayStartAnchor.clickedLogical;
            const anchoredRange = {
              from: replayStartAnchor.visibleRange.from + delta,
              to: replayStartAnchor.visibleRange.to + delta,
            };
            chart.timeScale().setVisibleLogicalRange(anchoredRange);
            visibleLogicalRangeRef.current = anchoredRange;
          }
        }

        replayStartAnchorRef.current = null;
      }

      shouldAutoFollowRef.current = false;
      replayUnlockIntentRef.current = false;
      const currentRange = chart.timeScale().getVisibleLogicalRange();
      if (currentRange) {
        visibleLogicalRangeRef.current = {
          from: currentRange.from,
          to: currentRange.to,
        };
      }
      previousReplayModeRef.current = true;
      return;
    }

    if (previousReplayModeRef.current) {
      shouldAutoFollowRef.current = false;
      const desiredRange = visibleLogicalRangeRef.current ?? preReplayVisibleRangeRef.current;
      const maxLogicalIndex = candles.length > 0 ? candles.length - 0.5 : null;
      if (desiredRange && maxLogicalIndex !== null) {
        const rangeWidth = Math.max(1, desiredRange.to - desiredRange.from);
        let restoredFrom = desiredRange.from;
        let restoredTo = desiredRange.to;
        if (restoredFrom > maxLogicalIndex) {
          restoredTo = maxLogicalIndex;
          restoredFrom = restoredTo - rangeWidth;
        }
        if (restoredTo < -0.5) {
          restoredFrom = -0.5;
          restoredTo = restoredFrom + rangeWidth;
        }
        chart.timeScale().setVisibleLogicalRange({
          from: restoredFrom,
          to: restoredTo,
        });
      } else if (preReplayVisibleRangeRef.current) {
        chart.timeScale().setVisibleLogicalRange(preReplayVisibleRangeRef.current);
      }
    }

    if (!isReplayViewportLocked) {
      replayStartAnchorRef.current = null;
    }

    previousReplayModeRef.current = false;
  }, [
    applyShiftVisibleRangeOnNewBar,
    candles.length,
    isReplayMode,
    isReplayViewportLocked,
    replayIndex,
    replayTotalBars,
  ]);

  useEffect(() => {
    const chart = chartRef.current;
    if (!isChartReady || !chart) {
      return;
    }

    const rightPriceScale = chart.priceScale('right');
    if (isReplayMode || isReplayViewportLocked) {
      rightPriceScale.applyOptions({ autoScale: false });
      if (lockedReplayPriceRangeRef.current) {
        rightPriceScale.setVisibleRange(lockedReplayPriceRangeRef.current);
      }
      return;
    }

    rightPriceScale.applyOptions({ autoScale: chartDisplaySettings.autoFit });
    if (chartDisplaySettings.autoFit) {
      hasManualPriceScaleBootstrapRef.current = false;
    }
    lockedReplayPriceRangeRef.current = null;
  }, [isChartReady, isReplayMode, isReplayViewportLocked, chartDisplaySettings.autoFit]);

  useEffect(() => {
    if (!isChartSettingsOpen) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      const panelElement = chartSettingsPanelRef.current;
      if (!panelElement) {
        return;
      }

      if (!panelElement.contains(event.target as Node)) {
        setIsChartSettingsOpen(false);
        setChartSettingsSubmenu(null);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsChartSettingsOpen(false);
        setChartSettingsSubmenu(null);
      }
    };

    window.addEventListener('pointerdown', handlePointerDown);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('pointerdown', handlePointerDown);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isChartSettingsOpen]);

  useEffect(() => {
    if (!isStatusIndicatorOpen) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      const panelElement = statusIndicatorPanelRef.current;
      if (!panelElement) {
        return;
      }

      if (!panelElement.contains(event.target as Node)) {
        setIsStatusIndicatorOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsStatusIndicatorOpen(false);
      }
    };

    window.addEventListener('pointerdown', handlePointerDown);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('pointerdown', handlePointerDown);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isStatusIndicatorOpen]);

  useEffect(() => {
    if (!contextMenu.open) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      const panelElement = contextMenuPanelRef.current;
      if (!panelElement) {
        return;
      }

      if (!panelElement.contains(event.target as Node)) {
        setContextMenu(DEFAULT_CONTEXT_MENU_STATE);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setContextMenu(DEFAULT_CONTEXT_MENU_STATE);
      }
    };

    window.addEventListener('pointerdown', handlePointerDown);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('pointerdown', handlePointerDown);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [contextMenu.open]);

  useEffect(() => {
    if (!openAlertsRequest) {
      return;
    }

    if (lastHandledAlertsRequestIdRef.current === openAlertsRequest.requestId) {
      return;
    }

    lastHandledAlertsRequestIdRef.current = openAlertsRequest.requestId;
    setAlertsModalTab(openAlertsRequest.tab);
    setIsAlertsModalOpen(true);
    setIsChartSettingsOpen(false);
    setChartSettingsSubmenu(null);
    setContextMenu(DEFAULT_CONTEXT_MENU_STATE);
  }, [openAlertsRequest]);

  useEffect(() => {
    if (!isMoreSettingsModalOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMoreSettingsModalOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMoreSettingsModalOpen]);

  useEffect(() => {
    if (!isMoreSettingsModalOpen) {
      return;
    }
    setMoreSettingsTab('symbol');
    setModalDraftSettings(JSON.parse(JSON.stringify(chartDisplaySettings)) as ChartOverlaySettings);
    setModalDraftDataSources(JSON.parse(JSON.stringify(dataSources)) as DataSourceSettings);
  }, [isMoreSettingsModalOpen]);

  useEffect(() => {
    if (isCandleHelperVisible && !wasCandleHelperVisibleRef.current && chartDisplaySettings.autoFit) {
      updateChartDisplaySettings((previous) => ({
        ...previous,
        autoFit: false,
      }));
    }

    wasCandleHelperVisibleRef.current = isCandleHelperVisible;
  }, [isCandleHelperVisible, chartDisplaySettings.autoFit, updateChartDisplaySettings]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!event.altKey) {
        return;
      }

      if (event.code === 'KeyI') {
        event.preventDefault();
        updateChartDisplaySettings((previous) => ({
          ...previous,
          invertScale: !previous.invertScale,
        }));
        return;
      }

      if (event.code === 'KeyP') {
        event.preventDefault();
        updateChartDisplaySettings((previous) => ({
          ...previous,
          scaleMode: 'percent',
        }));
        return;
      }

      if (event.code === 'KeyL') {
        event.preventDefault();
        updateChartDisplaySettings((previous) => ({
          ...previous,
          scaleMode: 'logarithmic',
        }));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [updateChartDisplaySettings]);

  useEffect(() => {
    const chart = chartRef.current;
    if (!isChartReady || !chart) {
      return;
    }

    const candleSeries = seriesRef.current;

    const priceScaleMode = toPriceScaleMode(chartDisplaySettings.scaleMode);
    const precisionConfig = resolvePricePrecisionConfig(chartDisplaySettings.symbol.precision);
    const scaleTopMargin = Math.max(0, Math.min(chartDisplaySettings.canvas.margins.top, 95)) / 100;
    const scaleBottomMargin =
      Math.max(0, Math.min(chartDisplaySettings.canvas.margins.bottom, 95)) / 100;
    const rightOffset = Math.max(0, chartDisplaySettings.canvas.margins.right);

    const layoutBackground =
      chartDisplaySettings.canvas.backgroundType === 'gradient'
        ? {
            type: ColorType.VerticalGradient,
            topColor: chartDisplaySettings.canvas.backgroundTopColor,
            bottomColor: chartDisplaySettings.canvas.backgroundBottomColor,
          }
        : {
            type: ColorType.Solid,
            color: chartDisplaySettings.canvas.backgroundColor,
          };

    chart.applyOptions({
      layout: {
        textColor: chartDisplaySettings.canvas.textColor,
        background: layoutBackground,
        panes: {
          enableResize: true,
          separatorColor: '#1e293b',
          separatorHoverColor: 'rgba(148, 163, 184, 0.24)',
        },
      },
      rightPriceScale: {
        autoScale: chartDisplaySettings.autoFit,
        invertScale: chartDisplaySettings.invertScale,
        mode: priceScaleMode,
        visible: runtimeStatusOptions.priceScaleSide === 'right',
        ticksVisible: chartDisplaySettings.labels.scaleTicks,
        borderColor: '#1e293b',
        scaleMargins: {
          top: scaleTopMargin,
          bottom: scaleBottomMargin,
        },
      },
      leftPriceScale: {
        visible: runtimeStatusOptions.priceScaleSide === 'left',
        mode: priceScaleMode,
        invertScale: chartDisplaySettings.invertScale,
        ticksVisible: chartDisplaySettings.labels.scaleTicks,
        borderColor: '#1e293b',
        scaleMargins: {
          top: scaleTopMargin,
          bottom: scaleBottomMargin,
        },
      },
      timeScale: {
        borderColor: '#1e293b',
        rightOffset,
      },
      handleScroll: {
        mouseWheel: true,
        pressedMouseMove: true,
        horzTouchDrag: true,
        vertTouchDrag: false,
      },
      handleScale: {
        mouseWheel: true,
        pinch: true,
        axisPressedMouseMove: {
          time: true,
          price: true,
        },
        axisDoubleClickReset: true,
      },
      crosshair: {
        vertLine: {
          visible: chartDisplaySettings.lines.crosshairLines,
          labelVisible: chartDisplaySettings.labels.crosshairLabels,
          color: chartDisplaySettings.canvas.crosshairColor,
        },
        horzLine: {
          visible: chartDisplaySettings.lines.crosshairLines,
          labelVisible: chartDisplaySettings.labels.crosshairLabels,
          color: chartDisplaySettings.canvas.crosshairColor,
        },
      },
      grid: {
        horzLines: {
          visible: chartDisplaySettings.lines.horizontalGrid,
          color: chartDisplaySettings.canvas.gridColor,
        },
        vertLines: {
          visible: chartDisplaySettings.lines.verticalGrid,
          color: chartDisplaySettings.canvas.gridColor,
        },
      },
    });

    if (candleSeries) {
      candleSeries.applyOptions({
        priceFormat: {
          type: 'price',
          precision: precisionConfig.fractionDigits,
          minMove: precisionConfig.minMove,
        },
        upColor: chartDisplaySettings.symbol.showBody
          ? chartDisplaySettings.symbol.upBodyColor
          : 'rgba(0, 0, 0, 0)',
        downColor: chartDisplaySettings.symbol.showBody
          ? chartDisplaySettings.symbol.downBodyColor
          : 'rgba(0, 0, 0, 0)',
        borderUpColor: chartDisplaySettings.symbol.showBorders
          ? chartDisplaySettings.symbol.upBorderColor
          : 'rgba(0, 0, 0, 0)',
        borderDownColor: chartDisplaySettings.symbol.showBorders
          ? chartDisplaySettings.symbol.downBorderColor
          : 'rgba(0, 0, 0, 0)',
        wickUpColor: chartDisplaySettings.symbol.showWick
          ? chartDisplaySettings.symbol.upWickColor
          : 'rgba(0, 0, 0, 0)',
        wickDownColor: chartDisplaySettings.symbol.showWick
          ? chartDisplaySettings.symbol.downWickColor
          : 'rgba(0, 0, 0, 0)',
      });
    }
  }, [chartDisplaySettings, isChartReady]);

  useEffect(() => {
    const chart = chartRef.current;

    if (!isChartReady || !chart || candles.length === 0) {
      setHoveredCandleIndex(null);
      return;
    }

    const maxIndex = candles.length - 1;

    const updateHoveredIndex = (nextIndex: number | null) => {
      setHoveredCandleIndex((previousIndex) => (previousIndex === nextIndex ? previousIndex : nextIndex));
    };

    const handleCrosshairMove = (param: MouseEventParams<Time>) => {
      if (!param.point || param.point.x < 0 || param.point.y < 0) {
        lastCrosshairPointRef.current = null;
        updateHoveredIndex(null);
        return;
      }

      lastCrosshairPointRef.current = { x: param.point.x, y: param.point.y };

      if (typeof param.logical === 'number' && Number.isFinite(param.logical)) {
        if (param.logical < -0.5 || param.logical > maxIndex + 0.5) {
          updateHoveredIndex(null);
          return;
        }

        const nextIndex = Math.max(0, Math.min(maxIndex, Math.round(param.logical)));
        updateHoveredIndex(nextIndex);
        return;
      }

      if (typeof param.time === 'number') {
        const unixTime = Number(param.time);
        const nextIndex = candleIndexByUnixTime.get(unixTime) ?? -1;
        updateHoveredIndex(nextIndex >= 0 ? nextIndex : null);
        return;
      }

      updateHoveredIndex(null);
    };

    chart.subscribeCrosshairMove(handleCrosshairMove);
    return () => {
      chart.unsubscribeCrosshairMove(handleCrosshairMove);
    };
  }, [candleIndexByUnixTime, candles, isChartReady]);

  const activeCandleIndex =
    hoveredCandleIndex !== null && hoveredCandleIndex >= 0 && hoveredCandleIndex < candles.length
      ? hoveredCandleIndex
      : candles.length - 1;
  const activeCandle = candles[activeCandleIndex];
  const previousClose = activeCandleIndex > 0 ? candles[activeCandleIndex - 1].close : activeCandle?.open;

  const statusLineContent = useMemo(() => {
    if (!activeCandle) {
      return null;
    }
    const precisionConfig = resolvePricePrecisionConfig(chartDisplaySettings.symbol.precision);
    const pricePrecision = precisionConfig.fractionDigits;
    const quantizedOpen = quantizePrice(activeCandle.open, precisionConfig.minMove);
    const quantizedHigh = quantizePrice(activeCandle.high, precisionConfig.minMove);
    const quantizedLow = quantizePrice(activeCandle.low, precisionConfig.minMove);
    const quantizedClose = quantizePrice(activeCandle.close, precisionConfig.minMove);

    const headerParts: string[] = [];
    if (chartDisplaySettings.statusLine.showSymbol) {
      headerParts.push(symbol);
    }
    if (chartDisplaySettings.statusLine.showInterval) {
      headerParts.push(interval);
    }
    if (chartDisplaySettings.statusLine.showExchange) {
      headerParts.push(providerLabel ?? 'BitMEX');
    }

    const valueParts: string[] = [];
    if (chartDisplaySettings.statusLine.showOHLC) {
      valueParts.push(
        `O ${formatNumber(quantizedOpen, pricePrecision)}`,
        `H ${formatNumber(quantizedHigh, pricePrecision)}`,
        `L ${formatNumber(quantizedLow, pricePrecision)}`,
        `C ${formatNumber(quantizedClose, pricePrecision)}`,
      );
    }

    let change = 0;
    let changePercent = 0;
    if (previousClose !== undefined && previousClose !== 0) {
      const quantizedPreviousClose = quantizePrice(previousClose, precisionConfig.minMove);
      change = quantizedClose - quantizedPreviousClose;
      changePercent = (change / previousClose) * 100;
    }

    if (chartDisplaySettings.statusLine.showChange) {
      valueParts.push(`${formatSignedNumber(change, pricePrecision)} (${formatSignedPercent(changePercent)})`);
    }

    if (chartDisplaySettings.statusLine.showVolume) {
      valueParts.push(`V ${formatNumber(activeCandle.volume, 0)}`);
    }

    if (chartDisplaySettings.statusLine.showLastDayChange && candles.length > 0) {
      const firstClose = candles[0].close;
      if (firstClose !== 0) {
        const quantizedFirstClose = quantizePrice(firstClose, precisionConfig.minMove);
        const dayChange = quantizedClose - quantizedFirstClose;
        const dayChangePercent = (dayChange / firstClose) * 100;
        valueParts.push(`D ${formatSignedNumber(dayChange, pricePrecision)} (${formatSignedPercent(dayChangePercent)})`);
      }
    }

    if (headerParts.length === 0 && valueParts.length === 0) {
      return null;
    }

    return {
      headerParts,
      headerText: headerParts.join(' | '),
      valueParts,
      valuesText: valueParts.join(' '),
    };
  }, [activeCandle, candles, chartDisplaySettings, interval, previousClose, providerLabel, symbol]);

  useEffect(() => {
    if (!isChartReady) {
      return;
    }

    clearAlertPriceLines();
    if (!chartDisplaySettings.alerts.showAlertLines) {
      return;
    }

    const candleSeries = seriesRef.current;
    if (!candleSeries) {
      return;
    }

    const precisionConfig = resolvePricePrecisionConfig(chartDisplaySettings.symbol.precision);
    alertItems
      .filter((item) => item.active && typeof item.price === 'number')
      .forEach((item) => {
        const line = candleSeries.createPriceLine({
          price: quantizePrice(item.price as number, precisionConfig.minMove),
          color: item.kind === 'indicator' ? '#38bdf8' : '#fbbf24',
          lineStyle: LineStyle.Dashed,
          lineWidth: 1,
          axisLabelVisible: true,
          title: item.kind === 'indicator' ? `Alert: ${item.indicatorLabel ?? 'Indicator'}` : 'Alert',
        });
        alertPriceLinesRef.current.push(line);
      });
  }, [
    alertItems,
    chartDisplaySettings.alerts.showAlertLines,
    chartDisplaySettings.symbol.precision,
    clearAlertPriceLines,
    isChartReady,
  ]);

  useEffect(() => {
    if (!isChartReady) {
      return;
    }

    clearPaperOrderPriceLines();
    if (!runtimeStatusOptions.showBuySellButtons && !chartDisplaySettings.trading.showPositionsAndOrders) {
      return;
    }

    const candleSeries = seriesRef.current;
    if (!candleSeries) {
      return;
    }

    const precisionConfig = resolvePricePrecisionConfig(chartDisplaySettings.symbol.precision);
    paperOrders
      .filter((item) => item.status === 'working')
      .forEach((item) => {
        const line = candleSeries.createPriceLine({
          price: quantizePrice(item.price, precisionConfig.minMove),
          color: item.side === 'buy' ? '#22c55e' : '#f97316',
          lineStyle: item.orderType === 'stop' ? LineStyle.Dotted : LineStyle.Solid,
          lineWidth: 1,
          axisLabelVisible: chartDisplaySettings.trading.showSeriesLabels,
          title: `${item.side.toUpperCase()} ${item.quantity} ${item.orderType}`,
        });
        paperOrderPriceLinesRef.current.push(line);
      });
  }, [
    chartDisplaySettings.symbol.precision,
    chartDisplaySettings.trading.showPositionsAndOrders,
    chartDisplaySettings.trading.showSeriesLabels,
    clearPaperOrderPriceLines,
    isChartReady,
    paperOrders,
    runtimeStatusOptions.showBuySellButtons,
  ]);

  const eventPreviewItems = useMemo(() => {
    const combined: Array<{ kind: 'news' | 'ideas' | 'economic'; item: MarketEventItem }> = [];
    if (chartDisplaySettings.events.showLatestNews || chartDisplaySettings.events.showNewsNotifications) {
      combined.push(...newsEvents.map((item) => ({ kind: 'news' as const, item })));
    }
    if (chartDisplaySettings.events.showIdeas) {
      combined.push(...ideaEvents.map((item) => ({ kind: 'ideas' as const, item })));
    }
    if (chartDisplaySettings.events.showEconomicEvents) {
      combined.push(...economicEvents.map((item) => ({ kind: 'economic' as const, item })));
    }

    combined.sort(
      (left, right) => Date.parse(right.item.publishedAt) - Date.parse(left.item.publishedAt),
    );
    return combined.slice(0, 50);
  }, [chartDisplaySettings.events, economicEvents, ideaEvents, newsEvents]);

  const hasEnabledChartEventPreview =
    chartDisplaySettings.events.showLatestNews ||
    chartDisplaySettings.events.showNewsNotifications ||
    chartDisplaySettings.events.showIdeas ||
    chartDisplaySettings.events.showEconomicEvents;

  const modalEventPreviewItems = useMemo(() => {
    const combined: Array<{ kind: 'news' | 'ideas' | 'economic'; item: MarketEventItem }> = [];
    if (modalDraftSettings.events.showLatestNews || modalDraftSettings.events.showNewsNotifications) {
      combined.push(...newsEvents.slice(0, 5).map((item) => ({ kind: 'news' as const, item })));
    }
    if (modalDraftSettings.events.showIdeas) {
      combined.push(...ideaEvents.slice(0, 5).map((item) => ({ kind: 'ideas' as const, item })));
    }
    if (modalDraftSettings.events.showEconomicEvents) {
      combined.push(...economicEvents.slice(0, 5).map((item) => ({ kind: 'economic' as const, item })));
    }
    combined.sort(
      (left, right) => Date.parse(right.item.publishedAt) - Date.parse(left.item.publishedAt),
    );
    return combined.slice(0, 8);
  }, [economicEvents, ideaEvents, modalDraftSettings.events, newsEvents]);

  const mainPaneLegendItems = useMemo(
    () => overlayLegendItems.filter((item) => !isPaneIndicator(item.key) || !item.active),
    [overlayLegendItems],
  );
  const hasMainPaneLegend = mainPaneLegendItems.length > 0;
  const hasAnyLegendItems = overlayLegendItems.length > 0;
  const legendItemKeySet = useMemo(
    () => new Set(overlayLegendItems.map((item) => item.key)),
    [overlayLegendItems],
  );
  const isCompactChart = chartViewportWidth > 0 && chartViewportWidth < MOBILE_LEGEND_BREAKPOINT_PX;
  const shouldOffsetLegendBelowStatus = isCompactChart || isStatusLineStacked;
  const showPaperSummary = Boolean(paperAccount) && chartDisplaySettings.statusLine.showPaperSummary;
  const statusActionsHeightPx = runtimeStatusOptions.showBuySellButtons
    ? STATUS_ACTIONS_HEIGHT_PX + (showPaperSummary ? 40 : 0)
    : 0;
  const statusActionsTopPx =
    statusLineContent && statusLineHeightPx > 0
      ? STATUS_LINE_TOP_PX + statusLineHeightPx + MOBILE_LEGEND_GAP_PX
      : DEFAULT_LEGEND_TOP_PX;
  const statusAreaBottomPx =
    runtimeStatusOptions.showBuySellButtons && statusLineContent
      ? statusActionsTopPx + statusActionsHeightPx + MOBILE_LEGEND_GAP_PX
      : statusActionsTopPx;
  const shouldOffsetLegendBelowStatusArea =
    (statusLineContent !== null && runtimeStatusOptions.showBuySellButtons) || shouldOffsetLegendBelowStatus;
  const legendTopPx =
    shouldOffsetLegendBelowStatusArea && statusLineContent && statusLineHeightPx > 0
      ? statusAreaBottomPx
      : DEFAULT_LEGEND_TOP_PX;
  const legendRowHeightPx = 24;
  const legendRowGapPx = 4;
  const legendToggleHeightPx = 26;
  const statusAnchorTopPx =
    statusLineContent && statusLineHeightPx > 0
      ? statusAreaBottomPx
      : DEFAULT_LEGEND_TOP_PX;
  useEffect(() => {
    if (!statusIndicator) {
      setIsStatusIndicatorOpen(false);
    }
  }, [statusIndicator]);
  const overlayLegendHeightPx =
    mainPaneLegendItems.length > 0
      ? mainPaneLegendItems.length * legendRowHeightPx + (mainPaneLegendItems.length - 1) * legendRowGapPx
      : 0;
  const overlayLegendToggleTopPx = areOverlayLegendItemsVisible
    ? hasMainPaneLegend
      ? legendTopPx + overlayLegendHeightPx + 6
      : legendTopPx
    : statusAnchorTopPx;
  const leftInfoBadgeBaseTopPx = hasAnyLegendItems
    ? overlayLegendToggleTopPx + legendToggleHeightPx
    : hasMainPaneLegend
      ? legendTopPx + overlayLegendHeightPx + 6
      : legendTopPx;
  const showTraderProV3Badge =
    overlays.traderProV3 &&
    traderProV3Overlay &&
    overlaySettings.traderProV3.visibility.showInfoBadge &&
    !legendItemKeySet.has('traderProV3');
  const showTraderPro17Badge =
    overlays.traderPro17 &&
    traderPro17Overlay &&
    overlaySettings.traderPro17.visibility.showInfoBadge &&
    !legendItemKeySet.has('traderPro17');
  const showColorProV3InfoBadge =
    overlays.colorProTradingSuiteV3 &&
    colorProV3Overlay &&
    overlaySettings.colorProTradingSuiteV3.visibility.showInfoBadge &&
    !legendItemKeySet.has('colorProTradingSuiteV3');
  const showColorProV31InfoBadge =
    overlays.colorProTradingSuiteV31 &&
    colorProV31Overlay &&
    overlaySettings.colorProTradingSuiteV31.visibility.showInfoBadge &&
    !legendItemKeySet.has('colorProTradingSuiteV31');
  const showStrategyInfoBadge =
    overlays.traderPro17Strategy &&
    strategyOverlay &&
    overlaySettings.traderPro17Strategy.visibility.showInfoBadge &&
    !legendItemKeySet.has('traderPro17Strategy');
  const showColorProV31StrategyInfoBadge =
    overlays.colorProTradingSuiteV31Strategy &&
    colorProV31StrategyOverlay &&
    overlaySettings.colorProTradingSuiteV31Strategy.visibility.showInfoBadge &&
    !legendItemKeySet.has('colorProTradingSuiteV31Strategy');
  const showOpenCode5mStrategyInfoBadge =
    overlays.openCode5mStrategy &&
    openCode5mStrategyOverlay &&
    overlaySettings.openCode5mStrategy.visibility.showInfoBadge &&
    !legendItemKeySet.has('openCode5mStrategy');
  const showMurreyMathLinesV6InfoBadge =
    overlays.murreyMathLinesV6 &&
    murreyMathLinesV6Overlay &&
    overlaySettings.murreyMathLinesV6.visibility.showInfo &&
    !legendItemKeySet.has('murreyMathLinesV6');
  const leftInfoBadgeSpacingPx = 32;
  const traderPro17InfoTopPx =
    leftInfoBadgeBaseTopPx + (showTraderProV3Badge ? leftInfoBadgeSpacingPx : 0);
  const strategyInfoTopPx =
    leftInfoBadgeBaseTopPx +
    (showTraderProV3Badge ? leftInfoBadgeSpacingPx : 0) +
    (showTraderPro17Badge ? leftInfoBadgeSpacingPx : 0);
  const murreyMathLinesV6InfoTopPx =
    leftInfoBadgeBaseTopPx +
    (showTraderProV3Badge ? leftInfoBadgeSpacingPx : 0) +
    (showTraderPro17Badge ? leftInfoBadgeSpacingPx : 0) +
    (showStrategyInfoBadge ? leftInfoBadgeSpacingPx : 0) +
    (showColorProV31StrategyInfoBadge ? leftInfoBadgeSpacingPx : 0) +
    (showOpenCode5mStrategyInfoBadge ? leftInfoBadgeSpacingPx : 0);
  const colorProV31StrategyInfoTopPx =
    leftInfoBadgeBaseTopPx +
    (showTraderProV3Badge ? leftInfoBadgeSpacingPx : 0) +
    (showTraderPro17Badge ? leftInfoBadgeSpacingPx : 0) +
    (showStrategyInfoBadge ? leftInfoBadgeSpacingPx : 0);
  const openCode5mStrategyInfoTopPx =
    leftInfoBadgeBaseTopPx +
    (showTraderProV3Badge ? leftInfoBadgeSpacingPx : 0) +
    (showTraderPro17Badge ? leftInfoBadgeSpacingPx : 0) +
    (showStrategyInfoBadge ? leftInfoBadgeSpacingPx : 0) +
    (showColorProV31StrategyInfoBadge ? leftInfoBadgeSpacingPx : 0);
  const leftInfoBadgeCount = [
    showTraderProV3Badge,
    showTraderPro17Badge,
    showStrategyInfoBadge,
    showColorProV31StrategyInfoBadge,
    showOpenCode5mStrategyInfoBadge,
    showMurreyMathLinesV6InfoBadge,
  ].filter(Boolean).length;
  const staleMessageTopPx = hasAnyLegendItems
    ? leftInfoBadgeBaseTopPx + leftInfoBadgeCount * leftInfoBadgeSpacingPx + 6
    : DEFAULT_LEGEND_TOP_PX + 32;
  const rightInfoBadgeBaseTopPx = 12;
  const rightInfoBadgeSpacingPx = 30;
  const colorProV31InfoTopPx =
    rightInfoBadgeBaseTopPx + (showColorProV3InfoBadge ? rightInfoBadgeSpacingPx : 0);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) {
      setChartViewportWidth(0);
      setStatusLineHeightPx(0);
      setIsStatusLineStacked(false);
      return;
    }

    const updateOverlayMetrics = () => {
      const nextWidth = wrapper.clientWidth;
      setChartViewportWidth((current) => (current === nextWidth ? current : nextWidth));

      const nextStatusLineHeight = statusLineRef.current
        ? Math.ceil(statusLineRef.current.getBoundingClientRect().height)
        : 0;
      setStatusLineHeightPx((current) =>
        current === nextStatusLineHeight ? current : nextStatusLineHeight,
      );

      const statusLineNode = statusLineRef.current;
      const headerTextNode = statusLineHeaderTextRef.current;
      const valuesTextNode = statusLineValuesTextRef.current;
      const indicatorNode = statusIndicatorPanelRef.current;
      const separatorNode = statusLineSeparatorRef.current;
      let nextIsStatusLineStacked = false;
      if (
        statusLineNode !== null &&
        headerTextNode !== null &&
        valuesTextNode !== null &&
        statusLineContent !== null &&
        statusLineContent.headerParts.length > 0 &&
        statusLineContent.valueParts.length > 0
      ) {
        const statusLineStyle = window.getComputedStyle(statusLineNode);
        const statusLineMaxWidth = Math.max(nextWidth - 24, 0);
        const availableWidth =
          statusLineMaxWidth -
          Number.parseFloat(statusLineStyle.paddingLeft) -
          Number.parseFloat(statusLineStyle.paddingRight);
        const inlineGapPx = 8;
        const headerWidth = headerTextNode.scrollWidth;
        const indicatorWidth = indicatorNode ? Math.ceil(indicatorNode.getBoundingClientRect().width) : 0;
        const valuesWidth = valuesTextNode.scrollWidth;
        const separatorWidth = separatorNode ? Math.ceil(separatorNode.getBoundingClientRect().width) : 0;
        const requiredWidth = statusIndicator
          ? headerWidth + inlineGapPx + indicatorWidth + inlineGapPx + valuesWidth
          : headerWidth + inlineGapPx + separatorWidth + inlineGapPx + valuesWidth;
        nextIsStatusLineStacked = requiredWidth > availableWidth;
      }
      setIsStatusLineStacked((current) =>
        current === nextIsStatusLineStacked ? current : nextIsStatusLineStacked,
      );
    };

    updateOverlayMetrics();

    const observer = new ResizeObserver(() => {
      updateOverlayMetrics();
    });

    observer.observe(wrapper);
    if (statusLineRef.current) {
      observer.observe(statusLineRef.current);
    }

    window.addEventListener('resize', updateOverlayMetrics);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateOverlayMetrics);
    };
  }, [statusLineContent, mainPaneLegendItems.length]);

  useEffect(() => {
    const chart = chartRef.current;
    if (!container || !isChartReady || !chart || paneLegendItems.length === 0) {
      setPaneLegendTopByKey({});
      return;
    }

    const wrapper = wrapperRef.current;
    if (!wrapper) {
      setPaneLegendTopByKey({});
      return;
    }

    const updatePaneLegendTop = () => {
      const wrapperRect = wrapper.getBoundingClientRect();
      const next: Partial<Record<IndicatorInstanceId, number>> = {};

      for (const item of paneLegendItems) {
        const paneIndex = paneIndexByLegendInstance.get(item.instanceId);
        if (paneIndex === undefined) {
          continue;
        }
        const pane = chart.panes()[paneIndex];
        if (!pane) {
          continue;
        }

        const paneElement = pane.getHTMLElement();
        if (!paneElement) {
          continue;
        }
        const paneRect = paneElement.getBoundingClientRect();
        next[item.instanceId] = Math.max(8, paneRect.top - wrapperRect.top + 8);
      }

      setPaneLegendTopByKey((current) => {
        const currentKeys = Object.keys(current);
        const nextKeys = Object.keys(next);
        if (currentKeys.length !== nextKeys.length) {
          return next;
        }

        for (const key of nextKeys) {
          if (current[key as IndicatorInstanceId] !== next[key as IndicatorInstanceId]) {
            return next;
          }
        }

        return current;
      });
    };

    updatePaneLegendTop();

    const observer = new ResizeObserver(() => {
      updatePaneLegendTop();
    });

    observer.observe(wrapper);
    chart.panes().forEach((pane) => {
      const paneElement = pane.getHTMLElement();
      if (paneElement) {
        observer.observe(paneElement);
      }
    });

    window.addEventListener('resize', updatePaneLegendTop);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updatePaneLegendTop);
    };
  }, [container, isChartReady, paneLegendItems, paneIndexByLegendInstance]);

  useEffect(() => {
    const chart = chartRef.current;

    if (!chart) {
      return;
    }

    const shouldShowVolume = overlays.volume && overlaySettings.volume.visibility.showHistogram;

    if (shouldShowVolume) {
      if (!volumeSeriesRef.current) {
        volumeSeriesRef.current = chart.addSeries(
          HistogramSeries,
          {
            priceScaleId: 'volume',
            priceFormat: { type: 'volume' },
            priceLineVisible: false,
            lastValueVisible: false,
          },
          0,
        );
        volumeSeriesRef.current
          .priceScale()
          .applyOptions({ scaleMargins: { top: 0.8, bottom: 0 } });
      }
    } else if (volumeSeriesRef.current) {
      chart.removeSeries(volumeSeriesRef.current);
      volumeSeriesRef.current = null;
    }
  }, [overlays.volume, overlaySettings.volume.visibility.showHistogram, interval]);

  useEffect(() => {
    const chart = chartRef.current;

    if (!isChartReady || !chart) {
      return;
    }

    const desiredPaneIndex = paneIndexByIndicator.get('rsi');

    if (!overlays.rsi || desiredPaneIndex === undefined) {
      clearRsiSeries();
      pruneEmptyIndicatorPanes();
      rebalancePaneStretchFactors();
      return;
    }

    const showRsiSeriesLabels =
      chartDisplaySettings.trading.showSeriesLabels && overlaySettings.rsi.visibility.showSeriesLabels;

    let rsiSeries = rsiSeriesRef.current;
    if (!rsiSeries) {
      rsiSeries = chart.addSeries(
        LineSeries,
        {
          color: overlaySettings.rsi.style.lineColor,
          lineWidth: overlaySettings.rsi.style.lineWidth,
          lineStyle: LineStyle.Solid,
          crosshairMarkerVisible: false,
          lastValueVisible: false,
          priceLineVisible: false,
          priceScaleId: 'right',
          title: showRsiSeriesLabels ? 'RSI' : '',
        },
        desiredPaneIndex,
      );
      rsiSeriesRef.current = rsiSeries;
    }

    rsiSeries.applyOptions({
      color: overlaySettings.rsi.style.lineColor,
      lineWidth: overlaySettings.rsi.style.lineWidth,
      visible: overlaySettings.rsi.visibility.showLine,
      title: showRsiSeriesLabels ? 'RSI' : '',
    });

    const rsiPriceScale = rsiSeries.priceScale();
    rsiPriceScale.applyOptions({
      autoScale: false,
      scaleMargins: {
        top: 0.12,
        bottom: 0.12,
      },
    });
    rsiPriceScale.setAutoScale(false);
    rsiPriceScale.setVisibleRange({ from: 0, to: 100 });

    rsiZoneLinesRef.current.forEach((line) => {
      rsiSeries?.removePriceLine(line);
    });
    rsiZoneLinesRef.current = [];

    if (overlaySettings.rsi.visibility.showZones) {
      rsiZoneLinesRef.current.push(
        rsiSeries.createPriceLine({
          price: 70,
          color: overlaySettings.rsi.style.overboughtColor,
          lineWidth: 1,
          lineStyle: LineStyle.Dashed,
          axisLabelVisible: false,
          title: '',
        }),
      );
      rsiZoneLinesRef.current.push(
        rsiSeries.createPriceLine({
          price: 30,
          color: overlaySettings.rsi.style.oversoldColor,
          lineWidth: 1,
          lineStyle: LineStyle.Dashed,
          axisLabelVisible: false,
          title: '',
        }),
      );
    }

    rebalancePaneStretchFactors();
  }, [
    isChartReady,
    overlays.rsi,
    paneIndexByIndicator,
    overlaySettings.rsi.style.lineColor,
    overlaySettings.rsi.style.lineWidth,
    overlaySettings.rsi.style.overboughtColor,
    overlaySettings.rsi.style.oversoldColor,
    overlaySettings.rsi.visibility.showLine,
    overlaySettings.rsi.visibility.showSeriesLabels,
    overlaySettings.rsi.visibility.showZones,
    chartDisplaySettings.trading.showSeriesLabels,
    clearRsiSeries,
    pruneEmptyIndicatorPanes,
    rebalancePaneStretchFactors,
  ]);

  useEffect(() => {
    const chart = chartRef.current;
    if (!isChartReady || !chart || paneLegendItems.length <= 1) {
      return;
    }

    const primarySeriesByInstance = new Map<IndicatorInstanceId, ISeriesApi<'Line'> | ISeriesApi<'Histogram'>>();

    for (const item of paneLegendItems) {
      if (item.key === 'rsi' && rsiSeriesRef.current) {
        primarySeriesByInstance.set(item.instanceId, rsiSeriesRef.current);
        continue;
      }

      if (item.key === 'macd' && macdLineSeriesRef.current) {
        primarySeriesByInstance.set(item.instanceId, macdLineSeriesRef.current);
        continue;
      }

      if (item.key === 'cmVixFixUcsSqueeze') {
        const cmSeries = cmSqueezeMomentumSeriesRef.current ?? cmVixFixHistogramSeriesRef.current;
        if (cmSeries) {
          primarySeriesByInstance.set(item.instanceId, cmSeries);
        }
        continue;
      }

      if (isBasicIndicatorKey(item.key)) {
        const refs = basicStudySeriesRefs.current.get(item.instanceId);
        if (!refs) {
          continue;
        }
        const primarySeries = refs.primary ?? refs.histogram ?? refs.secondary ?? refs.tertiary;
        if (primarySeries) {
          primarySeriesByInstance.set(item.instanceId, primarySeries);
        }
      }
    }

    for (let index = 0; index < paneLegendItems.length; index += 1) {
      const item = paneLegendItems[index];
      const series = primarySeriesByInstance.get(item.instanceId);
      if (!series) {
        continue;
      }

      const expectedPaneIndex = index + 1;
      const currentPaneIndex = series.getPane().paneIndex();
      if (currentPaneIndex !== expectedPaneIndex) {
        chart.swapPanes(currentPaneIndex, expectedPaneIndex);
      }
    }
  }, [isChartReady, paneLegendItems]);

  useEffect(() => {
    const chart = chartRef.current;
    if (!isChartReady || !chart) {
      return;
    }

    const desiredPaneIndex = paneIndexByIndicator.get('macd');
    if (!overlays.macd || desiredPaneIndex === undefined) {
      clearMacdSeries();
      pruneEmptyIndicatorPanes();
      rebalancePaneStretchFactors();
      return;
    }

    const showMacdSeriesLabels =
      chartDisplaySettings.trading.showSeriesLabels && overlaySettings.macd.visibility.showSeriesLabels;

    if (!macdLineSeriesRef.current) {
      macdLineSeriesRef.current = chart.addSeries(
        LineSeries,
        {
          color: overlaySettings.macd.style.macdColor,
          lineWidth: overlaySettings.macd.style.lineWidth,
          lineStyle: LineStyle.Solid,
          crosshairMarkerVisible: false,
          lastValueVisible: false,
          priceLineVisible: false,
          priceScaleId: 'right',
          title: showMacdSeriesLabels ? 'MACD' : '',
        },
        desiredPaneIndex,
      );
    }

    if (!macdSignalSeriesRef.current) {
      macdSignalSeriesRef.current = chart.addSeries(
        LineSeries,
        {
          color: overlaySettings.macd.style.signalColor,
          lineWidth: overlaySettings.macd.style.lineWidth,
          lineStyle: LineStyle.Solid,
          crosshairMarkerVisible: false,
          lastValueVisible: false,
          priceLineVisible: false,
          priceScaleId: 'right',
          title: showMacdSeriesLabels ? 'Signal' : '',
        },
        desiredPaneIndex,
      );
    }

    if (!macdHistogramSeriesRef.current) {
      macdHistogramSeriesRef.current = chart.addSeries(
        HistogramSeries,
        {
          priceScaleId: 'right',
          priceLineVisible: false,
          lastValueVisible: false,
          title: showMacdSeriesLabels ? 'Histogram' : '',
        },
        desiredPaneIndex,
      );
    }

    const macdLineSeries = macdLineSeriesRef.current;
    const macdSignalSeries = macdSignalSeriesRef.current;
    const macdHistogramSeries = macdHistogramSeriesRef.current;
    if (!macdLineSeries || !macdSignalSeries || !macdHistogramSeries) {
      return;
    }

    macdLineSeries.applyOptions({
      color: overlaySettings.macd.style.macdColor,
      lineWidth: overlaySettings.macd.style.lineWidth,
      visible: overlaySettings.macd.visibility.showMacdLine,
      title: showMacdSeriesLabels ? 'MACD' : '',
    });
    macdSignalSeries.applyOptions({
      color: overlaySettings.macd.style.signalColor,
      lineWidth: overlaySettings.macd.style.lineWidth,
      visible: overlaySettings.macd.visibility.showSignalLine,
      title: showMacdSeriesLabels ? 'Signal' : '',
    });
    macdHistogramSeries.applyOptions({
      visible: overlaySettings.macd.visibility.showHistogram,
      title: showMacdSeriesLabels ? 'Histogram' : '',
    });

    macdLineSeries.priceScale().applyOptions({
      scaleMargins: {
        top: 0.1,
        bottom: 0.12,
      },
    });

    rebalancePaneStretchFactors();
  }, [
    isChartReady,
    overlays.macd,
    paneIndexByIndicator,
    overlaySettings.macd.style.macdColor,
    overlaySettings.macd.style.signalColor,
    overlaySettings.macd.style.lineWidth,
    overlaySettings.macd.visibility.showMacdLine,
    overlaySettings.macd.visibility.showSignalLine,
    overlaySettings.macd.visibility.showHistogram,
    overlaySettings.macd.visibility.showSeriesLabels,
    chartDisplaySettings.trading.showSeriesLabels,
    clearMacdSeries,
    pruneEmptyIndicatorPanes,
    rebalancePaneStretchFactors,
  ]);

  useEffect(() => {
    const chart = chartRef.current;
    if (!isChartReady || !chart) {
      return;
    }

    const desiredPaneIndex = paneIndexByIndicator.get('cmVixFixUcsSqueeze');
    if (!overlays.cmVixFixUcsSqueeze || desiredPaneIndex === undefined) {
      clearCmVixFixUcsSqueezeSeries();
      pruneEmptyIndicatorPanes();
      rebalancePaneStretchFactors();
      return;
    }

    const showCmVixSeriesLabels =
      chartDisplaySettings.trading.showSeriesLabels &&
      overlaySettings.cmVixFixUcsSqueeze.visibility.showSeriesLabels;

    if (!cmVixFixHistogramSeriesRef.current) {
      cmVixFixHistogramSeriesRef.current = chart.addSeries(
        HistogramSeries,
        {
          priceScaleId: 'right',
          priceLineVisible: false,
          lastValueVisible: false,
          title: showCmVixSeriesLabels ? 'WVF' : '',
        },
        desiredPaneIndex,
      );
    }

    if (!cmSqueezeMomentumSeriesRef.current) {
      cmSqueezeMomentumSeriesRef.current = chart.addSeries(
        HistogramSeries,
        {
          priceScaleId: 'right',
          priceLineVisible: false,
          lastValueVisible: false,
          title: showCmVixSeriesLabels ? 'Momentum' : '',
        },
        desiredPaneIndex,
      );
    }

    if (!cmSqueezeOnSeriesRef.current) {
      cmSqueezeOnSeriesRef.current = chart.addSeries(
        LineSeries,
        {
          priceScaleId: 'right',
          priceLineVisible: false,
          lastValueVisible: false,
          crosshairMarkerVisible: false,
          title: showCmVixSeriesLabels ? 'Sqz On' : '',
        },
        desiredPaneIndex,
      );
    }

    if (!cmSqueezeOffSeriesRef.current) {
      cmSqueezeOffSeriesRef.current = chart.addSeries(
        LineSeries,
        {
          priceScaleId: 'right',
          priceLineVisible: false,
          lastValueVisible: false,
          crosshairMarkerVisible: false,
          title: showCmVixSeriesLabels ? 'Sqz Off' : '',
        },
        desiredPaneIndex,
      );
    }

    if (!cmNoSqueezeSeriesRef.current) {
      cmNoSqueezeSeriesRef.current = chart.addSeries(
        LineSeries,
        {
          priceScaleId: 'right',
          priceLineVisible: false,
          lastValueVisible: false,
          crosshairMarkerVisible: false,
          title: showCmVixSeriesLabels ? 'No Sqz' : '',
        },
        desiredPaneIndex,
      );
    }

    if (!cmWvfUpperBandSeriesRef.current) {
      cmWvfUpperBandSeriesRef.current = chart.addSeries(
        LineSeries,
        {
          priceScaleId: 'right',
          priceLineVisible: false,
          lastValueVisible: false,
          crosshairMarkerVisible: false,
          title: showCmVixSeriesLabels ? 'WVF Upper' : '',
        },
        desiredPaneIndex,
      );
    }

    if (!cmWvfRangeHighSeriesRef.current) {
      cmWvfRangeHighSeriesRef.current = chart.addSeries(
        LineSeries,
        {
          priceScaleId: 'right',
          priceLineVisible: false,
          lastValueVisible: false,
          crosshairMarkerVisible: false,
          title: showCmVixSeriesLabels ? 'WVF Range High' : '',
        },
        desiredPaneIndex,
      );
    }

    if (!cmWvfRangeLowSeriesRef.current) {
      cmWvfRangeLowSeriesRef.current = chart.addSeries(
        LineSeries,
        {
          priceScaleId: 'right',
          priceLineVisible: false,
          lastValueVisible: false,
          crosshairMarkerVisible: false,
          title: showCmVixSeriesLabels ? 'WVF Range Low' : '',
        },
        desiredPaneIndex,
      );
    }

    cmVixFixHistogramSeriesRef.current.applyOptions({
      visible: overlaySettings.cmVixFixUcsSqueeze.visibility.showWvfHistogram,
      color: overlaySettings.cmVixFixUcsSqueeze.style.wvfBaseColor,
      title: showCmVixSeriesLabels ? 'WVF' : '',
    });
    cmSqueezeMomentumSeriesRef.current.applyOptions({
      visible: overlaySettings.cmVixFixUcsSqueeze.visibility.showMomentumHistogram,
      color: overlaySettings.cmVixFixUcsSqueeze.style.momentumPositiveRisingColor,
      title: showCmVixSeriesLabels ? 'Momentum' : '',
    });

    cmSqueezeOnSeriesRef.current.applyOptions({
      color: overlaySettings.cmVixFixUcsSqueeze.style.squeezeOnColor,
      lineWidth: 2,
      visible: overlaySettings.cmVixFixUcsSqueeze.visibility.showSqueezeStateLine,
      title: showCmVixSeriesLabels ? 'Sqz On' : '',
    });
    cmSqueezeOffSeriesRef.current.applyOptions({
      color: overlaySettings.cmVixFixUcsSqueeze.style.squeezeOffColor,
      lineWidth: 2,
      visible: overlaySettings.cmVixFixUcsSqueeze.visibility.showSqueezeStateLine,
      title: showCmVixSeriesLabels ? 'Sqz Off' : '',
    });
    cmNoSqueezeSeriesRef.current.applyOptions({
      color: overlaySettings.cmVixFixUcsSqueeze.style.noSqueezeColor,
      lineWidth: 2,
      visible: overlaySettings.cmVixFixUcsSqueeze.visibility.showSqueezeStateLine,
      title: showCmVixSeriesLabels ? 'No Sqz' : '',
    });

    cmWvfUpperBandSeriesRef.current.applyOptions({
      color: CM_WVF_STD_LINE_COLOR,
      lineWidth: 2,
      lineStyle: LineStyle.Solid,
      visible: overlaySettings.cmVixFixUcsSqueeze.inputs.sd,
      title: showCmVixSeriesLabels ? 'WVF Upper' : '',
    });
    cmWvfRangeHighSeriesRef.current.applyOptions({
      color: CM_WVF_RANGE_LINE_COLOR,
      lineWidth: 2,
      lineStyle: LineStyle.Solid,
      visible: overlaySettings.cmVixFixUcsSqueeze.inputs.hp,
      title: showCmVixSeriesLabels ? 'WVF Range High' : '',
    });
    cmWvfRangeLowSeriesRef.current.applyOptions({
      color: CM_WVF_RANGE_LINE_COLOR,
      lineWidth: 2,
      lineStyle: LineStyle.Solid,
      visible: overlaySettings.cmVixFixUcsSqueeze.inputs.hp,
      title: showCmVixSeriesLabels ? 'WVF Range Low' : '',
    });

    cmVixFixHistogramSeriesRef.current.priceScale().applyOptions({
      scaleMargins: {
        top: 0.12,
        bottom: 0.12,
      },
    });

    rebalancePaneStretchFactors();
  }, [
    isChartReady,
    overlays.cmVixFixUcsSqueeze,
    paneIndexByIndicator,
    overlaySettings.cmVixFixUcsSqueeze.style.wvfSpikeColor,
    overlaySettings.cmVixFixUcsSqueeze.style.wvfBaseColor,
    overlaySettings.cmVixFixUcsSqueeze.style.momentumPositiveRisingColor,
    overlaySettings.cmVixFixUcsSqueeze.style.momentumPositiveFallingColor,
    overlaySettings.cmVixFixUcsSqueeze.style.momentumNegativeFallingColor,
    overlaySettings.cmVixFixUcsSqueeze.style.momentumNegativeRisingColor,
    overlaySettings.cmVixFixUcsSqueeze.style.squeezeOnColor,
    overlaySettings.cmVixFixUcsSqueeze.style.squeezeOffColor,
    overlaySettings.cmVixFixUcsSqueeze.style.noSqueezeColor,
    overlaySettings.cmVixFixUcsSqueeze.inputs.hp,
    overlaySettings.cmVixFixUcsSqueeze.inputs.sd,
    overlaySettings.cmVixFixUcsSqueeze.visibility.showWvfHistogram,
    overlaySettings.cmVixFixUcsSqueeze.visibility.showMomentumHistogram,
    overlaySettings.cmVixFixUcsSqueeze.visibility.showSqueezeStateLine,
    overlaySettings.cmVixFixUcsSqueeze.visibility.showSeriesLabels,
    chartDisplaySettings.trading.showSeriesLabels,
    clearCmVixFixUcsSqueezeSeries,
    pruneEmptyIndicatorPanes,
    rebalancePaneStretchFactors,
  ]);

  useEffect(() => {
    const chart = chartRef.current;

    if (!chart) {
      return;
    }

    const activeInstanceIds = new Set(activeMovingAverageInstances.map((instance) => instance.id));
    for (const [instanceId, series] of movingAverageSeriesRef.current.entries()) {
      if (activeInstanceIds.has(instanceId)) {
        continue;
      }
      chart.removeSeries(series);
      movingAverageSeriesRef.current.delete(instanceId);
    }

    for (const instance of activeMovingAverageInstances) {
      const settings = resolveMovingAverageSettings(instance.id);
      let series = movingAverageSeriesRef.current.get(instance.id);
      if (!series) {
        series = chart.addSeries(
          LineSeries,
          {
            color: settings.style.color,
            lineWidth: settings.style.lineWidth,
            lineStyle: toChartLineStyle(settings.style.lineStyle),
            visible: settings.visibility.showLine,
            priceScaleId: 'right',
            crosshairMarkerVisible: false,
            lastValueVisible: false,
          },
          0,
        );
        movingAverageSeriesRef.current.set(instance.id, series);
      }

      series.applyOptions({
        color: settings.style.color,
        lineWidth: settings.style.lineWidth,
        lineStyle: toChartLineStyle(settings.style.lineStyle),
        visible: settings.visibility.showLine,
      });
    }
  }, [
    activeMovingAverageInstances,
    resolveMovingAverageSettings,
  ]);

  useEffect(() => {
    const chart = chartRef.current;
    const candleSeries = seriesRef.current;

    if (!isChartReady || !chart || !candleSeries) {
      return;
    }

    if (!overlays.murreyMathLinesV6 || !murreyMathLinesV6Overlay || candles.length === 0) {
      clearMurreyMathLinesV6Overlay();
      return;
    }

    const murreySessionLockStartTimestampMs =
      overlaySettings.murreyMathLinesV6.inputs.lockToSessionStart && candles.length > 0
        ? resolveSessionStartMs(
            candles[candles.length - 1].time,
            overlaySettings.murreyMathLinesV6.inputs.tradingSession,
          ) ?? undefined
        : undefined;

    const style = overlaySettings.murreyMathLinesV6.style;
    const visibility = overlaySettings.murreyMathLinesV6.visibility;
    const lineSeriesMap = murreyMathLinesV6SeriesRef.current;
    const lineWidth = Math.max(1, Math.min(4, Math.round(style.lineWidth))) as 1 | 2 | 3 | 4;
    const lineStyle = toChartLineStyle(style.lineStyle);
    const showMurreySeriesLabels =
      chartDisplaySettings.trading.showSeriesLabels && visibility.showSeriesLabels;

    if (visibility.showLines) {
      const activeLevelKeys = new Set<string>();

      for (const level of murreyMathLinesV6Overlay.levels) {
        activeLevelKeys.add(level.key);

        let color = style.weakLineColor;
        if (level.kind === 'major') {
          color = style.majorLineColor;
        } else if (level.kind === 'range') {
          color = style.rangeLineColor;
        } else if (level.kind === 'pivot') {
          color = style.pivotLineColor;
        } else if (level.kind === 'extended') {
          color = style.extendedLineColor;
        }

        let series = lineSeriesMap.get(level.key);
        if (!series) {
          series = chart.addSeries(
            LineSeries,
            {
              color,
              lineWidth,
              lineStyle,
              priceScaleId: 'right',
              crosshairMarkerVisible: false,
              lastValueVisible: false,
              priceLineVisible: false,
              title: showMurreySeriesLabels ? `MML v6 ${level.label}` : '',
            },
            0,
          );
          lineSeriesMap.set(level.key, series);
        }

        series.applyOptions({
          color,
          lineWidth,
          lineStyle,
          visible: true,
          title: showMurreySeriesLabels ? `MML v6 ${level.label}` : '',
        });
        setLineSeriesDataSafely(
          series,
          toConstantLineData(candles, level.price, murreySessionLockStartTimestampMs),
          `murreyMathLinesV6.${level.key}`,
        );
      }

      for (const [key, series] of lineSeriesMap.entries()) {
        if (activeLevelKeys.has(key)) {
          continue;
        }
        chart.removeSeries(series);
        lineSeriesMap.delete(key);
      }
    } else {
      clearMurreyMathLinesV6Series();
    }

    const hasReversalBottomZone =
      Number.isFinite(murreyMathLinesV6Overlay.zeroEight) && Number.isFinite(murreyMathLinesV6Overlay.oneEight);
    const hasTradingZone =
      Number.isFinite(murreyMathLinesV6Overlay.threeEight) && Number.isFinite(murreyMathLinesV6Overlay.fiveEight);
    const hasReversalTopZone =
      Number.isFinite(murreyMathLinesV6Overlay.sevenEight) &&
      Number.isFinite(murreyMathLinesV6Overlay.eightEight);

    if (visibility.showZones && hasReversalBottomZone && hasTradingZone && hasReversalTopZone) {
      if (!murreyMathLinesV6ReversalBottomZonePrimitiveRef.current) {
        murreyMathLinesV6ReversalBottomZonePrimitiveRef.current = new BollingerBandsFillPrimitive();
        candleSeries.attachPrimitive(murreyMathLinesV6ReversalBottomZonePrimitiveRef.current);
      }
      if (!murreyMathLinesV6TradingZonePrimitiveRef.current) {
        murreyMathLinesV6TradingZonePrimitiveRef.current = new BollingerBandsFillPrimitive();
        candleSeries.attachPrimitive(murreyMathLinesV6TradingZonePrimitiveRef.current);
      }
      if (!murreyMathLinesV6ReversalTopZonePrimitiveRef.current) {
        murreyMathLinesV6ReversalTopZonePrimitiveRef.current = new BollingerBandsFillPrimitive();
        candleSeries.attachPrimitive(murreyMathLinesV6ReversalTopZonePrimitiveRef.current);
      }

      const fillOpacity = Math.max(0, Math.min(100, style.zoneOpacity)) / 100;
      const reversalFillColor = toRgba(style.majorLineColor, 1);
      const tradingFillColor = toRgba(style.rangeLineColor, 1);

      const reversalBottomUpper: Array<number | undefined> = [];
      const reversalBottomLower: Array<number | undefined> = [];
      const tradingZoneUpper: Array<number | undefined> = [];
      const tradingZoneLower: Array<number | undefined> = [];
      const reversalTopUpper: Array<number | undefined> = [];
      const reversalTopLower: Array<number | undefined> = [];

      for (const candle of candles) {
        const candleTimeMs = Date.parse(candle.time);
        const isPastSessionStart =
          murreySessionLockStartTimestampMs === undefined ||
          (Number.isFinite(candleTimeMs) && candleTimeMs >= murreySessionLockStartTimestampMs);

        reversalBottomUpper.push(isPastSessionStart ? murreyMathLinesV6Overlay.oneEight : undefined);
        reversalBottomLower.push(isPastSessionStart ? murreyMathLinesV6Overlay.zeroEight : undefined);
        tradingZoneUpper.push(isPastSessionStart ? murreyMathLinesV6Overlay.fiveEight : undefined);
        tradingZoneLower.push(isPastSessionStart ? murreyMathLinesV6Overlay.threeEight : undefined);
        reversalTopUpper.push(isPastSessionStart ? murreyMathLinesV6Overlay.eightEight : undefined);
        reversalTopLower.push(isPastSessionStart ? murreyMathLinesV6Overlay.sevenEight : undefined);
      }

      murreyMathLinesV6ReversalBottomZonePrimitiveRef.current.setData(
        candles,
        reversalBottomUpper,
        reversalBottomLower,
        reversalFillColor,
        fillOpacity,
      );
      murreyMathLinesV6TradingZonePrimitiveRef.current.setData(
        candles,
        tradingZoneUpper,
        tradingZoneLower,
        tradingFillColor,
        fillOpacity,
      );
      murreyMathLinesV6ReversalTopZonePrimitiveRef.current.setData(
        candles,
        reversalTopUpper,
        reversalTopLower,
        reversalFillColor,
        fillOpacity,
      );
    } else {
      clearMurreyMathLinesV6Zones();
    }
  }, [
    isChartReady,
    candles,
    overlays.murreyMathLinesV6,
    murreyMathLinesV6Overlay,
    overlaySettings.murreyMathLinesV6.style.majorLineColor,
    overlaySettings.murreyMathLinesV6.style.rangeLineColor,
    overlaySettings.murreyMathLinesV6.style.pivotLineColor,
    overlaySettings.murreyMathLinesV6.style.weakLineColor,
    overlaySettings.murreyMathLinesV6.style.extendedLineColor,
    overlaySettings.murreyMathLinesV6.style.lineWidth,
    overlaySettings.murreyMathLinesV6.style.lineStyle,
    overlaySettings.murreyMathLinesV6.style.zoneOpacity,
    overlaySettings.murreyMathLinesV6.visibility.showLines,
    overlaySettings.murreyMathLinesV6.visibility.showSeriesLabels,
    overlaySettings.murreyMathLinesV6.visibility.showZones,
    overlaySettings.murreyMathLinesV6.inputs.lockToSessionStart,
    overlaySettings.murreyMathLinesV6.inputs.tradingSession,
    chartDisplaySettings.trading.showSeriesLabels,
    clearMurreyMathLinesV6Overlay,
    clearMurreyMathLinesV6Series,
    clearMurreyMathLinesV6Zones,
  ]);

  useEffect(() => {
    const chart = chartRef.current;
    const candleSeries = seriesRef.current;
    if (!chart) {
      return;
    }

    const activeInstanceIds = new Set(activeBasicIndicatorInstances.map((instance) => instance.id));

    for (const instanceId of basicStudySeriesRefs.current.keys()) {
      if (activeInstanceIds.has(instanceId)) {
        continue;
      }
      clearBasicStudySeriesForInstance(instanceId);
    }

    for (const instanceId of basicStudyDrawingPrimitiveRefs.current.keys()) {
      if (activeInstanceIds.has(instanceId)) {
        continue;
      }
      clearBasicStudyDrawingPrimitiveForInstance(instanceId);
    }

    for (const instance of activeBasicIndicatorInstances) {
      const key = instance.key as BasicIndicatorKey;
      const definition = BASIC_INDICATOR_DEFINITIONS[key];
      const paneIndex = definition.pane ? paneIndexByLegendInstance.get(instance.id) : 0;
      const computed = basicStudyDataByInstanceId.get(instance.id);

      if (!computed || (definition.pane && paneIndex === undefined)) {
        clearBasicStudySeriesForInstance(instance.id);
        clearBasicStudyDrawingPrimitiveForInstance(instance.id);
        continue;
      }

      const settings = resolveBasicStudySettings(instance);
      const showBasicStudySeriesLabels =
        chartDisplaySettings.trading.showSeriesLabels && settings.visibility.showSeriesLabels;

      if (isBasicDrawingIndicatorKey(key)) {
        clearBasicStudySeriesForInstance(instance.id);
        if (!candleSeries) {
          clearBasicStudyDrawingPrimitiveForInstance(instance.id);
          continue;
        }

        const drawingSegments = buildBasicStudyDrawingSegments(key, candles, computed, settings);
        const existingPrimitive = basicStudyDrawingPrimitiveRefs.current.get(instance.id);
        if (existingPrimitive) {
          existingPrimitive.setSegments(drawingSegments);
        } else {
          const primitive = new BasicStudyDrawingPrimitive();
          candleSeries.attachPrimitive(primitive);
          primitive.setSegments(drawingSegments);
          basicStudyDrawingPrimitiveRefs.current.set(instance.id, primitive);
        }

        continue;
      }

      const targetPane = paneIndex ?? 0;
      const refs =
        basicStudySeriesRefs.current.get(instance.id) ?? {
          primary: null,
          secondary: null,
          tertiary: null,
          histogram: null,
        };

      const ensureLineSeries = (
        slot: 'primary' | 'secondary' | 'tertiary',
        title: string,
        color: string,
      ) => {
        if (!refs[slot]) {
          refs[slot] = chart.addSeries(
            LineSeries,
            {
              color,
              lineWidth: settings.style.lineWidth,
              lineStyle: toChartLineStyle(settings.style.lineStyle),
              priceScaleId: 'right',
              crosshairMarkerVisible: false,
              lastValueVisible: false,
              priceLineVisible: false,
              title: showBasicStudySeriesLabels ? title : '',
            },
            targetPane,
          );
        }

        refs[slot]?.applyOptions({
          color,
          lineWidth: settings.style.lineWidth,
          lineStyle: toChartLineStyle(settings.style.lineStyle),
          title: showBasicStudySeriesLabels ? title : '',
        });
      };

      const ensureHistogramSeries = () => {
        if (!refs.histogram) {
          refs.histogram = chart.addSeries(
            HistogramSeries,
            {
              priceScaleId: 'right',
              priceLineVisible: false,
              lastValueVisible: false,
              title: showBasicStudySeriesLabels ? definition.label : '',
            },
            targetPane,
          );
        }

        refs.histogram?.applyOptions({
          title: showBasicStudySeriesLabels ? definition.label : '',
        });
      };

      if (!definition.series.histogram && refs.histogram) {
        chart.removeSeries(refs.histogram);
        refs.histogram = null;
      }

      if (!definition.series.secondary && refs.secondary) {
        chart.removeSeries(refs.secondary);
        refs.secondary = null;
      }

      if (!definition.series.tertiary && refs.tertiary) {
        chart.removeSeries(refs.tertiary);
        refs.tertiary = null;
      }

      ensureLineSeries('primary', definition.label, settings.style.primaryColor);
      if (definition.series.secondary) {
        ensureLineSeries('secondary', `${definition.label} 2`, settings.style.secondaryColor);
      }
      if (definition.series.tertiary) {
        ensureLineSeries('tertiary', `${definition.label} 3`, settings.style.tertiaryColor);
      }
      if (definition.series.histogram) {
        ensureHistogramSeries();
      }

      if (refs.primary) {
        refs.primary.applyOptions({ visible: settings.visibility.showPrimary });
        setLineSeriesDataSafely(
          refs.primary,
          settings.visibility.showPrimary ? toLineData(candles, computed.primary) : [],
          `${instance.id}.primary`,
        );
      }

      if (refs.secondary) {
        refs.secondary.applyOptions({ visible: settings.visibility.showSecondary });
        setLineSeriesDataSafely(
          refs.secondary,
          settings.visibility.showSecondary ? toLineData(candles, computed.secondary) : [],
          `${instance.id}.secondary`,
        );
      }

      if (refs.tertiary) {
        refs.tertiary.applyOptions({ visible: settings.visibility.showTertiary });
        setLineSeriesDataSafely(
          refs.tertiary,
          settings.visibility.showTertiary ? toLineData(candles, computed.tertiary) : [],
          `${instance.id}.tertiary`,
        );
      }

      if (refs.histogram) {
        refs.histogram.applyOptions({ visible: settings.visibility.showHistogram });
        refs.histogram.setData(
          settings.visibility.showHistogram
            ? computed.histogram.flatMap((value, index) => {
                if (value === undefined || !Number.isFinite(value)) {
                  return [];
                }
                const time = toUnixTimestamp(candles[index].time);
                if (time === null) {
                  return [];
                }
                return [
                  {
                    time,
                    value,
                    color: value >= 0 ? settings.style.positiveColor : settings.style.negativeColor,
                  },
                ];
              })
            : [],
        );
      }

      basicStudySeriesRefs.current.set(instance.id, refs);
    }

    if (isChartReady) {
      pruneEmptyIndicatorPanes();
      rebalancePaneStretchFactors();
    }
  }, [
    activeBasicIndicatorInstances,
    basicStudyDataByInstanceId,
    candles,
    paneIndexByLegendInstance,
    resolveBasicStudySettings,
    clearBasicStudySeriesForInstance,
    clearBasicStudyDrawingPrimitiveForInstance,
    isChartReady,
    pruneEmptyIndicatorPanes,
    rebalancePaneStretchFactors,
    chartDisplaySettings.trading.showSeriesLabels,
  ]);

  useEffect(() => {
    const chart = chartRef.current;

    if (!isChartReady || !chart) {
      return;
    }

    if (!overlays.bollingerBands || !bollingerOverlay) {
      clearBollingerBandsSeries();
      return;
    }

    if (!bollingerUpperSeriesRef.current) {
      bollingerUpperSeriesRef.current = chart.addSeries(
        LineSeries,
        {
          color: overlaySettings.bollingerBands.style.upperColor,
          lineWidth: overlaySettings.bollingerBands.style.upperLineWidth,
          lineStyle: toChartLineStyle(overlaySettings.bollingerBands.style.upperLineStyle),
          priceScaleId: 'right',
          crosshairMarkerVisible: false,
          lastValueVisible: false,
          priceLineVisible: false,
        },
        0,
      );
    }

    if (!bollingerBasisSeriesRef.current) {
      bollingerBasisSeriesRef.current = chart.addSeries(
        LineSeries,
        {
          color: overlaySettings.bollingerBands.style.basisColor,
          lineWidth: overlaySettings.bollingerBands.style.basisLineWidth,
          lineStyle: toChartLineStyle(overlaySettings.bollingerBands.style.basisLineStyle),
          priceScaleId: 'right',
          crosshairMarkerVisible: false,
          lastValueVisible: false,
          priceLineVisible: false,
        },
        0,
      );
    }

    if (!bollingerLowerSeriesRef.current) {
      bollingerLowerSeriesRef.current = chart.addSeries(
        LineSeries,
        {
          color: overlaySettings.bollingerBands.style.lowerColor,
          lineWidth: overlaySettings.bollingerBands.style.lowerLineWidth,
          lineStyle: toChartLineStyle(overlaySettings.bollingerBands.style.lowerLineStyle),
          priceScaleId: 'right',
          crosshairMarkerVisible: false,
          lastValueVisible: false,
          priceLineVisible: false,
        },
        0,
      );
    }

    bollingerUpperSeriesRef.current.applyOptions({
      color: overlaySettings.bollingerBands.style.upperColor,
      lineWidth: overlaySettings.bollingerBands.style.upperLineWidth,
      lineStyle: toChartLineStyle(overlaySettings.bollingerBands.style.upperLineStyle),
      visible: overlaySettings.bollingerBands.visibility.showUpper,
    });
    bollingerBasisSeriesRef.current.applyOptions({
      color: overlaySettings.bollingerBands.style.basisColor,
      lineWidth: overlaySettings.bollingerBands.style.basisLineWidth,
      lineStyle: toChartLineStyle(overlaySettings.bollingerBands.style.basisLineStyle),
      visible: overlaySettings.bollingerBands.visibility.showBasis,
    });
    bollingerLowerSeriesRef.current.applyOptions({
      color: overlaySettings.bollingerBands.style.lowerColor,
      lineWidth: overlaySettings.bollingerBands.style.lowerLineWidth,
      lineStyle: toChartLineStyle(overlaySettings.bollingerBands.style.lowerLineStyle),
      visible: overlaySettings.bollingerBands.visibility.showLower,
    });
  }, [
    isChartReady,
    overlays.bollingerBands,
    bollingerOverlay,
    overlaySettings.bollingerBands.style.upperColor,
    overlaySettings.bollingerBands.style.basisColor,
    overlaySettings.bollingerBands.style.lowerColor,
    overlaySettings.bollingerBands.style.upperLineWidth,
    overlaySettings.bollingerBands.style.basisLineWidth,
    overlaySettings.bollingerBands.style.lowerLineWidth,
    overlaySettings.bollingerBands.style.upperLineStyle,
    overlaySettings.bollingerBands.style.basisLineStyle,
    overlaySettings.bollingerBands.style.lowerLineStyle,
    overlaySettings.bollingerBands.visibility.showUpper,
    overlaySettings.bollingerBands.visibility.showBasis,
    overlaySettings.bollingerBands.visibility.showLower,
    clearBollingerBandsSeries,
  ]);

  useEffect(() => {
    const candleSeries = seriesRef.current;

    if (!isChartReady || !candleSeries) {
      return;
    }

    if (!overlays.bollingerBands || !bollingerOverlay) {
      clearBollingerBandsSeries();
      return;
    }

    if (
      !bollingerUpperSeriesRef.current ||
      !bollingerBasisSeriesRef.current ||
      !bollingerLowerSeriesRef.current
    ) {
      return;
    }

    setLineSeriesDataSafely(
      bollingerUpperSeriesRef.current,
      toLineData(candles, bollingerOverlay.upper),
      'bollinger.upper',
    );
    setLineSeriesDataSafely(
      bollingerBasisSeriesRef.current,
      toLineData(candles, bollingerOverlay.basis),
      'bollinger.basis',
    );
    setLineSeriesDataSafely(
      bollingerLowerSeriesRef.current,
      toLineData(candles, bollingerOverlay.lower),
      'bollinger.lower',
    );

    if (overlaySettings.bollingerBands.visibility.showFill) {
      if (!bollingerFillPrimitiveRef.current) {
        bollingerFillPrimitiveRef.current = new BollingerBandsFillPrimitive();
        candleSeries.attachPrimitive(bollingerFillPrimitiveRef.current);
      }
      bollingerFillPrimitiveRef.current.setData(
        candles,
        bollingerOverlay.upper,
        bollingerOverlay.lower,
        toRgba(overlaySettings.bollingerBands.style.fillColor, 1),
        overlaySettings.bollingerBands.style.fillOpacity / 100,
      );
    } else if (bollingerFillPrimitiveRef.current) {
      candleSeries.detachPrimitive(bollingerFillPrimitiveRef.current);
      bollingerFillPrimitiveRef.current = null;
    }
  }, [
    isChartReady,
    candles,
    overlays.bollingerBands,
    bollingerOverlay,
    overlaySettings.bollingerBands.style.fillColor,
    overlaySettings.bollingerBands.style.fillOpacity,
    overlaySettings.bollingerBands.visibility.showFill,
    clearBollingerBandsSeries,
  ]);

  useEffect(() => {
    const chart = chartRef.current;
    const candleSeries = seriesRef.current;

    if (!isChartReady || !chart || !candleSeries) {
      return;
    }

    if (!overlays.ichimokuCloud || !ichimokuOverlay) {
      clearIchimokuSeries();
      return;
    }

    if (!ichimokuTenkanSeriesRef.current) {
      ichimokuTenkanSeriesRef.current = chart.addSeries(
        LineSeries,
        {
          color: overlaySettings.ichimokuCloud.style.tenkanColor,
          lineWidth: overlaySettings.ichimokuCloud.style.tenkanLineWidth,
          lineStyle: toChartLineStyle(overlaySettings.ichimokuCloud.style.tenkanLineStyle),
          priceScaleId: 'right',
          crosshairMarkerVisible: false,
          lastValueVisible: false,
          priceLineVisible: false,
        },
        0,
      );
    }

    if (!ichimokuKijunSeriesRef.current) {
      ichimokuKijunSeriesRef.current = chart.addSeries(
        LineSeries,
        {
          color: overlaySettings.ichimokuCloud.style.kijunColor,
          lineWidth: overlaySettings.ichimokuCloud.style.kijunLineWidth,
          lineStyle: toChartLineStyle(overlaySettings.ichimokuCloud.style.kijunLineStyle),
          priceScaleId: 'right',
          crosshairMarkerVisible: false,
          lastValueVisible: false,
          priceLineVisible: false,
        },
        0,
      );
    }

    if (!ichimokuSenkouASeriesRef.current) {
      ichimokuSenkouASeriesRef.current = chart.addSeries(
        LineSeries,
        {
          color: overlaySettings.ichimokuCloud.style.senkouAColor,
          lineWidth: overlaySettings.ichimokuCloud.style.senkouALineWidth,
          lineStyle: toChartLineStyle(overlaySettings.ichimokuCloud.style.senkouALineStyle),
          priceScaleId: 'right',
          crosshairMarkerVisible: false,
          lastValueVisible: false,
          priceLineVisible: false,
        },
        0,
      );
    }

    if (!ichimokuSenkouBSeriesRef.current) {
      ichimokuSenkouBSeriesRef.current = chart.addSeries(
        LineSeries,
        {
          color: overlaySettings.ichimokuCloud.style.senkouBColor,
          lineWidth: overlaySettings.ichimokuCloud.style.senkouBLineWidth,
          lineStyle: toChartLineStyle(overlaySettings.ichimokuCloud.style.senkouBLineStyle),
          priceScaleId: 'right',
          crosshairMarkerVisible: false,
          lastValueVisible: false,
          priceLineVisible: false,
        },
        0,
      );
    }

    if (!ichimokuChikouSeriesRef.current) {
      ichimokuChikouSeriesRef.current = chart.addSeries(
        LineSeries,
        {
          color: overlaySettings.ichimokuCloud.style.chikouColor,
          lineWidth: overlaySettings.ichimokuCloud.style.chikouLineWidth,
          lineStyle: toChartLineStyle(overlaySettings.ichimokuCloud.style.chikouLineStyle),
          priceScaleId: 'right',
          crosshairMarkerVisible: false,
          lastValueVisible: false,
          priceLineVisible: false,
        },
        0,
      );
    }

    ichimokuTenkanSeriesRef.current.applyOptions({
      color: overlaySettings.ichimokuCloud.style.tenkanColor,
      lineWidth: overlaySettings.ichimokuCloud.style.tenkanLineWidth,
      lineStyle: toChartLineStyle(overlaySettings.ichimokuCloud.style.tenkanLineStyle),
      visible: overlaySettings.ichimokuCloud.visibility.showTenkan,
    });
    ichimokuKijunSeriesRef.current.applyOptions({
      color: overlaySettings.ichimokuCloud.style.kijunColor,
      lineWidth: overlaySettings.ichimokuCloud.style.kijunLineWidth,
      lineStyle: toChartLineStyle(overlaySettings.ichimokuCloud.style.kijunLineStyle),
      visible: overlaySettings.ichimokuCloud.visibility.showKijun,
    });
    ichimokuSenkouASeriesRef.current.applyOptions({
      color: overlaySettings.ichimokuCloud.style.senkouAColor,
      lineWidth: overlaySettings.ichimokuCloud.style.senkouALineWidth,
      lineStyle: toChartLineStyle(overlaySettings.ichimokuCloud.style.senkouALineStyle),
      visible: overlaySettings.ichimokuCloud.visibility.showSenkouA,
    });
    ichimokuSenkouBSeriesRef.current.applyOptions({
      color: overlaySettings.ichimokuCloud.style.senkouBColor,
      lineWidth: overlaySettings.ichimokuCloud.style.senkouBLineWidth,
      lineStyle: toChartLineStyle(overlaySettings.ichimokuCloud.style.senkouBLineStyle),
      visible: overlaySettings.ichimokuCloud.visibility.showSenkouB,
    });
    ichimokuChikouSeriesRef.current.applyOptions({
      color: overlaySettings.ichimokuCloud.style.chikouColor,
      lineWidth: overlaySettings.ichimokuCloud.style.chikouLineWidth,
      lineStyle: toChartLineStyle(overlaySettings.ichimokuCloud.style.chikouLineStyle),
      visible: overlaySettings.ichimokuCloud.visibility.showChikou,
    });

    const projectedCandles = ichimokuOverlay.projectedCandles;
    setLineSeriesDataSafely(
      ichimokuTenkanSeriesRef.current,
      toLineData(projectedCandles, ichimokuOverlay.tenkan),
      'ichimoku.tenkan',
    );
    setLineSeriesDataSafely(
      ichimokuKijunSeriesRef.current,
      toLineData(projectedCandles, ichimokuOverlay.kijun),
      'ichimoku.kijun',
    );
    setLineSeriesDataSafely(
      ichimokuSenkouASeriesRef.current,
      toLineData(projectedCandles, ichimokuOverlay.senkouA),
      'ichimoku.senkouA',
    );
    setLineSeriesDataSafely(
      ichimokuSenkouBSeriesRef.current,
      toLineData(projectedCandles, ichimokuOverlay.senkouB),
      'ichimoku.senkouB',
    );
    setLineSeriesDataSafely(
      ichimokuChikouSeriesRef.current,
      toLineData(projectedCandles, ichimokuOverlay.chikou),
      'ichimoku.chikou',
    );

    if (overlaySettings.ichimokuCloud.visibility.showCloud) {
      if (!ichimokuBullishFillPrimitiveRef.current) {
        ichimokuBullishFillPrimitiveRef.current = new BollingerBandsFillPrimitive();
        candleSeries.attachPrimitive(ichimokuBullishFillPrimitiveRef.current);
      }
      if (!ichimokuBearishFillPrimitiveRef.current) {
        ichimokuBearishFillPrimitiveRef.current = new BollingerBandsFillPrimitive();
        candleSeries.attachPrimitive(ichimokuBearishFillPrimitiveRef.current);
      }

      ichimokuBullishFillPrimitiveRef.current.setData(
        projectedCandles,
        ichimokuOverlay.bullishCloudUpper,
        ichimokuOverlay.bullishCloudLower,
        toRgba(overlaySettings.ichimokuCloud.style.bullishFillColor, 1),
        overlaySettings.ichimokuCloud.style.fillOpacity / 100,
      );
      ichimokuBearishFillPrimitiveRef.current.setData(
        projectedCandles,
        ichimokuOverlay.bearishCloudUpper,
        ichimokuOverlay.bearishCloudLower,
        toRgba(overlaySettings.ichimokuCloud.style.bearishFillColor, 1),
        overlaySettings.ichimokuCloud.style.fillOpacity / 100,
      );
    } else {
      if (ichimokuBullishFillPrimitiveRef.current) {
        candleSeries.detachPrimitive(ichimokuBullishFillPrimitiveRef.current);
        ichimokuBullishFillPrimitiveRef.current = null;
      }
      if (ichimokuBearishFillPrimitiveRef.current) {
        candleSeries.detachPrimitive(ichimokuBearishFillPrimitiveRef.current);
        ichimokuBearishFillPrimitiveRef.current = null;
      }
    }
  }, [
    isChartReady,
    overlays.ichimokuCloud,
    ichimokuOverlay,
    overlaySettings.ichimokuCloud.style.tenkanColor,
    overlaySettings.ichimokuCloud.style.kijunColor,
    overlaySettings.ichimokuCloud.style.senkouAColor,
    overlaySettings.ichimokuCloud.style.senkouBColor,
    overlaySettings.ichimokuCloud.style.chikouColor,
    overlaySettings.ichimokuCloud.style.bullishFillColor,
    overlaySettings.ichimokuCloud.style.bearishFillColor,
    overlaySettings.ichimokuCloud.style.tenkanLineWidth,
    overlaySettings.ichimokuCloud.style.kijunLineWidth,
    overlaySettings.ichimokuCloud.style.senkouALineWidth,
    overlaySettings.ichimokuCloud.style.senkouBLineWidth,
    overlaySettings.ichimokuCloud.style.chikouLineWidth,
    overlaySettings.ichimokuCloud.style.tenkanLineStyle,
    overlaySettings.ichimokuCloud.style.kijunLineStyle,
    overlaySettings.ichimokuCloud.style.senkouALineStyle,
    overlaySettings.ichimokuCloud.style.senkouBLineStyle,
    overlaySettings.ichimokuCloud.style.chikouLineStyle,
    overlaySettings.ichimokuCloud.style.fillOpacity,
    overlaySettings.ichimokuCloud.visibility.showTenkan,
    overlaySettings.ichimokuCloud.visibility.showKijun,
    overlaySettings.ichimokuCloud.visibility.showSenkouA,
    overlaySettings.ichimokuCloud.visibility.showSenkouB,
    overlaySettings.ichimokuCloud.visibility.showChikou,
    overlaySettings.ichimokuCloud.visibility.showCloud,
    clearIchimokuSeries,
  ]);

  useEffect(() => {
    const chart = chartRef.current;

    if (!isChartReady || !chart) {
      return;
    }

    if (!overlays.traderPro17 || !overlaySettings.traderPro17.inputs.enable200Dma) {
      clearTraderPro17DmaSeries();
      return;
    }

    let dmaSeries = traderPro17DmaSeriesRef.current;
    if (!dmaSeries) {
      dmaSeries = chart.addSeries(
        LineSeries,
        {
          color: '#7dd3fc',
          lineWidth: 3,
          lineStyle: LineStyle.Solid,
          priceScaleId: 'right',
          crosshairMarkerVisible: false,
          lastValueVisible: false,
          priceLineVisible: false,
        },
        0,
      );
      traderPro17DmaSeriesRef.current = dmaSeries;
    }

    dmaSeries.applyOptions({
      color: '#7dd3fc',
      lineWidth: 3,
      lineStyle: LineStyle.Solid,
    });

    const dmaData = computeProjectedDailyMovingAverage(candles, dailyCandles, 200);
    setLineSeriesDataSafely(dmaSeries, dmaData, 'traderPro17.200dma');
  }, [
    isChartReady,
    overlays.traderPro17,
    overlaySettings.traderPro17.inputs.enable200Dma,
    candles,
    dailyCandles,
    clearTraderPro17DmaSeries,
  ]);

  useEffect(() => {
    if (!isChartReady || !seriesRef.current || !chartRef.current) {
      return;
    }

    const candleSeries = seriesRef.current;

    const clearVisuals = () => {
      clearTraderPro17Primitive();
    };

    if (!overlays.traderPro17 || !traderPro17Overlay || !candles.length) {
      clearVisuals();
      return;
    }

    if (!traderPro17Overlay.allowRender) {
      clearTraderPro17Primitive();
      return;
    }

    let primitive = traderPro17PrimitiveRef.current;
    if (!primitive) {
      primitive = new TraderPro17ShapesPrimitive();
      candleSeries.attachPrimitive(primitive);
      traderPro17PrimitiveRef.current = primitive;
    }

    const shapeItems = buildTraderPro17ShapeItems(traderPro17Overlay, {
      visibility: {
        showLevelGuides: overlaySettings.traderPro17.visibility.showLevelGuides,
        showPullbackHints: overlaySettings.traderPro17.visibility.showPullbackHints,
        showAlignmentHints: overlaySettings.traderPro17.visibility.showAlignmentHints,
        showAlgoTargets: overlaySettings.traderPro17.visibility.showAlgoTargets,
        horizontalOffset: overlaySettings.traderPro17.visibility.horizontalOffset,
      },
      style: {
        resistanceColor: overlaySettings.traderPro17.style.resistanceColor,
        neutralColor: overlaySettings.traderPro17.style.neutralColor,
        supportColor: overlaySettings.traderPro17.style.supportColor,
        extensionColor: overlaySettings.traderPro17.style.extensionColor,
        pullbackTextColor: overlaySettings.traderPro17.style.pullbackTextColor,
        algoTargetColor: overlaySettings.traderPro17.style.algoTargetColor,
      },
    });
    primitive.setData(
      shapeItems,
      Math.max(0, candles.length - 1),
      overlaySettings.traderPro17.visibility.horizontalOffset,
    );
  }, [
    isChartReady,
    overlays.traderPro17,
    traderPro17Overlay,
    clearTraderPro17Primitive,
    candles,
    overlaySettings.traderPro17.visibility.showLevelGuides,
    overlaySettings.traderPro17.visibility.showPullbackHints,
    overlaySettings.traderPro17.visibility.showAlignmentHints,
    overlaySettings.traderPro17.visibility.showAlgoTargets,
    overlaySettings.traderPro17.visibility.horizontalOffset,
    overlaySettings.traderPro17.style.resistanceColor,
    overlaySettings.traderPro17.style.neutralColor,
    overlaySettings.traderPro17.style.supportColor,
    overlaySettings.traderPro17.style.extensionColor,
    overlaySettings.traderPro17.style.pullbackTextColor,
    overlaySettings.traderPro17.style.algoTargetColor,
  ]);

  const traderPro17RightOffset =
    overlays.traderPro17 && traderPro17Overlay?.allowRender
      ? Math.max(0, 42 + overlaySettings.traderPro17.visibility.horizontalOffset)
      : 0;
  const ichimokuRightOffset =
    overlays.ichimokuCloud && ichimokuOverlay
      ? Math.max(0, ichimokuOverlay.projectedCandles.length - candles.length + 2)
      : 0;
  const chartRightOffset = Math.max(traderPro17RightOffset, ichimokuRightOffset);
  const currentReplayPadding =
    replayIndex !== null
      ? Math.max(0, replayTotalBars - 1 - replayIndex)
      : 0;
  const replayStartAnchor = replayStartAnchorRef.current;
  const replayStartAnchorPadding =
    isReplayMode &&
    replayIndex !== null &&
    replayStartAnchor !== null &&
    replayStartAnchor.selectedIndex === replayIndex
      ? replayStartAnchor.requiredReplayPadding
      : null;
  const effectiveReplayPadding = isReplayMode
    ? (replayStartAnchorPadding ??
      (replayViewportLockRef.current
        ? lockedReplayPaddingRef.current
        : currentReplayPadding))
    : isReplayViewportLocked
      ? lockedReplayPaddingRef.current
      : 0;
  const rawEffectiveRightOffset = chartRightOffset + effectiveReplayPadding;
  const maxNonReplayRightOffset = Math.max(0, candles.length - MIN_NON_REPLAY_VISIBLE_BARS);
  const effectiveRightOffset =
    isReplayMode || isReplayViewportLocked
      ? rawEffectiveRightOffset
      : Math.min(rawEffectiveRightOffset, maxNonReplayRightOffset);

  useEffect(() => {
    if (!isChartReady || !chartRef.current) {
      return;
    }

    rightOffsetRef.current = effectiveRightOffset;
    if (lastAppliedRightOffsetRef.current !== effectiveRightOffset) {
      chartRef.current.timeScale().applyOptions({ rightOffset: effectiveRightOffset });
      lastAppliedRightOffsetRef.current = effectiveRightOffset;
    }
  }, [isChartReady, effectiveRightOffset, isReplayMode]);

  useEffect(() => {
    if (!isChartReady || !seriesRef.current) {
      return;
    }

    clearTraderProV3PriceLines();

    if (!overlays.traderProV3 || !traderProV3Overlay) {
      return;
    }

    const candleSeries = seriesRef.current;
    const showTraderProV3SeriesLabels =
      chartDisplaySettings.trading.showSeriesLabels && overlaySettings.traderProV3.visibility.showSeriesLabels;
    const lineSpecs: Array<{
      price: number | undefined;
      title: string;
      group: 'trade' | 'murrey';
      color: string;
      lineWidth?: 1 | 2 | 3 | 4;
      lineStyle?: LineStyle;
    }> = [
      {
        price: traderProV3Overlay.entry,
        title: 'Trader Pro v3 Entry',
        group: 'trade',
        color: overlaySettings.traderProV3.style.entryColor,
        lineWidth: overlaySettings.traderProV3.style.tradeLineWidth,
      },
      {
        price: traderProV3Overlay.tp1,
        title: 'Trader Pro v3 TP1',
        group: 'trade',
        color: overlaySettings.traderProV3.style.tp1Color,
        lineWidth: overlaySettings.traderProV3.style.tradeLineWidth,
      },
      {
        price: traderProV3Overlay.tp2,
        title: 'Trader Pro v3 TP2',
        group: 'trade',
        color: overlaySettings.traderProV3.style.tp2Color,
        lineWidth: overlaySettings.traderProV3.style.tradeLineWidth,
      },
      {
        price: traderProV3Overlay.stop,
        title: 'Trader Pro v3 Stop',
        group: 'trade',
        color: overlaySettings.traderProV3.style.stopColor,
        lineWidth: overlaySettings.traderProV3.style.tradeLineWidth,
        lineStyle: LineStyle.Dashed,
      },
      {
        price: traderProV3Overlay.resistance,
        title: 'Trader Pro v3 8/8',
        group: 'murrey',
        color: overlaySettings.traderProV3.style.resistanceColor,
        lineWidth: overlaySettings.traderProV3.style.murreyLineWidth,
        lineStyle: toChartLineStyle(overlaySettings.traderProV3.style.murreyLineStyle),
      },
      {
        price: traderProV3Overlay.midpoint,
        title: 'Trader Pro v3 4/8',
        group: 'murrey',
        color: overlaySettings.traderProV3.style.midpointColor,
        lineWidth: overlaySettings.traderProV3.style.murreyLineWidth,
        lineStyle: toChartLineStyle(overlaySettings.traderProV3.style.murreyLineStyle),
      },
      {
        price: traderProV3Overlay.support,
        title: 'Trader Pro v3 0/8',
        group: 'murrey',
        color: overlaySettings.traderProV3.style.supportColor,
        lineWidth: overlaySettings.traderProV3.style.murreyLineWidth,
        lineStyle: toChartLineStyle(overlaySettings.traderProV3.style.murreyLineStyle),
      },
    ];

    lineSpecs.forEach((spec) => {
      if (spec.group === 'trade' && !overlaySettings.traderProV3.visibility.showTradeLevels) {
        return;
      }
      if (spec.group === 'murrey' && !overlaySettings.traderProV3.visibility.showMurreyLevels) {
        return;
      }

      if (spec.price === undefined || !Number.isFinite(spec.price)) {
        return;
      }

      const line = candleSeries.createPriceLine({
        price: spec.price,
        color: spec.color,
        lineWidth: spec.lineWidth ?? 1,
        lineStyle: spec.lineStyle ?? LineStyle.Solid,
        title: showTraderProV3SeriesLabels ? spec.title : '',
        axisLabelVisible: true,
      });
      traderProV3PriceLinesRef.current.push(line);
    });
  }, [
    isChartReady,
    overlays.traderProV3,
    traderProV3Overlay,
    clearTraderProV3PriceLines,
    overlaySettings.traderProV3.style.entryColor,
    overlaySettings.traderProV3.style.tp1Color,
    overlaySettings.traderProV3.style.tp2Color,
    overlaySettings.traderProV3.style.stopColor,
    overlaySettings.traderProV3.style.resistanceColor,
    overlaySettings.traderProV3.style.midpointColor,
    overlaySettings.traderProV3.style.supportColor,
    overlaySettings.traderProV3.style.tradeLineWidth,
    overlaySettings.traderProV3.style.murreyLineWidth,
    overlaySettings.traderProV3.style.murreyLineStyle,
    overlaySettings.traderProV3.visibility.showTradeLevels,
    overlaySettings.traderProV3.visibility.showMurreyLevels,
    overlaySettings.traderProV3.visibility.showSeriesLabels,
    chartDisplaySettings.trading.showSeriesLabels,
    interval,
  ]);

  useEffect(() => {
    const chart = chartRef.current;
    const candleSeries = seriesRef.current;

    if (!isChartReady || !chart || !candleSeries) {
      return;
    }

    const renderColorProOverlay = ({
      key,
      enabled,
      overlay,
      settings,
      lineSeriesRef,
      markersRef,
    }: {
      key: 'colorProTradingSuiteV3' | 'colorProTradingSuiteV31';
      enabled: boolean;
      overlay: ColorProOverlayResult | null;
      settings: OverlaySettings['colorProTradingSuiteV3'] | OverlaySettings['colorProTradingSuiteV31'];
      lineSeriesRef: MutableRefObject<Map<string, ISeriesApi<'Line'>>>;
      markersRef: MutableRefObject<ISeriesMarkersPluginApi<Time> | null>;
    }) => {
      if (!enabled || !overlay || candles.length === 0) {
        clearColorProMarkers(key);
        clearColorProLineSeries(key);
        return;
      }

      const colorSettings = settings.style;
      const visibility = settings.visibility;

      const lineColorByKey: Record<ColorProLineSpec['key'], string> = {
        bollingerUpper: colorSettings.bollingerColor,
        bollingerBasis: colorSettings.basisColor,
        bollingerLower: colorSettings.bollingerColor,
        dailyResistance: colorSettings.dailyResistanceColor,
        dailySupport: colorSettings.dailySupportColor,
        weeklyResistance: colorSettings.weeklyResistanceColor,
        weeklySupport: colorSettings.weeklySupportColor,
        pivotPoints: colorSettings.pivotColor,
      };

      const visibleByGroup: Record<ColorProLineSpec['group'], boolean> = {
        bollinger: visibility.showBollinger,
        daily: visibility.showDailyLevels,
        weekly: visibility.showWeeklyLevels,
        pivot: false,
      };

      const lineSeries = lineSeriesRef.current;

      COLORPRO_LINE_SPECS.forEach((spec) => {
        const isVisible = visibleByGroup[spec.group];
        if (!isVisible) {
          const existing = lineSeries.get(spec.key);
          if (existing) {
            chart.removeSeries(existing);
            lineSeries.delete(spec.key);
          }
          return;
        }

        const lineColor = lineColorByKey[spec.key];

        if (!lineSeries.has(spec.key)) {
          const series = chart.addSeries(
            LineSeries,
            {
              color: lineColor,
              lineWidth: spec.lineWidth,
              lineStyle: spec.lineStyle,
              priceScaleId: 'right',
              crosshairMarkerVisible: false,
              lastValueVisible: false,
              priceLineVisible: false,
            },
            0,
          );
          lineSeries.set(spec.key, series);
        }

        const series = lineSeries.get(spec.key);
        if (!series) {
          return;
        }

        series.applyOptions({ color: lineColor });

        const values = overlay[spec.key];
        setLineSeriesDataSafely(series, toLineData(candles, values), `${key}.${spec.key}`);
      });

      if (!visibility.showSignals) {
        clearColorProMarkers(key);
        return;
      }

      const markerColors: ColorProMarkerColors = {
        bearishPivot: colorSettings.bearishPivotSignalColor,
        confirmedReversal: colorSettings.confirmedReversalSignalColor,
        bullishRegular: colorSettings.bullishRegularSignalColor,
        bearishRegular: colorSettings.bearishRegularSignalColor,
        bullishHidden: colorSettings.bullishHiddenSignalColor,
        bearishHidden: colorSettings.bearishHiddenSignalColor,
      };

      const markers = overlay.signals.flatMap((signal) => {
        const marker = toColorProMarker(signal, candles, markerColors);
        return marker ? [marker] : [];
      });

      if (!markersRef.current) {
        markersRef.current = createSeriesMarkers(candleSeries, markers);
        return;
      }

      markersRef.current.setMarkers(markers);
    };

    renderColorProOverlay({
      key: 'colorProTradingSuiteV3',
      enabled: overlays.colorProTradingSuiteV3,
      overlay: colorProV3Overlay,
      settings: overlaySettings.colorProTradingSuiteV3,
      lineSeriesRef: colorProV3LineSeriesRef,
      markersRef: colorProV3MarkersRef,
    });

    renderColorProOverlay({
      key: 'colorProTradingSuiteV31',
      enabled: overlays.colorProTradingSuiteV31,
      overlay: colorProV31Overlay,
      settings: overlaySettings.colorProTradingSuiteV31,
      lineSeriesRef: colorProV31LineSeriesRef,
      markersRef: colorProV31MarkersRef,
    });
  }, [
    isChartReady,
    candles,
    overlays.colorProTradingSuiteV3,
    overlays.colorProTradingSuiteV31,
    colorProV3Overlay,
    colorProV31Overlay,
    overlaySettings.colorProTradingSuiteV3,
    overlaySettings.colorProTradingSuiteV31,
    clearColorProMarkers,
    clearColorProLineSeries,
  ]);

  useEffect(() => {
    const candleSeries = seriesRef.current;

    if (!isChartReady || !candleSeries) {
      return;
    }

    const maCrossInstance = activeBasicIndicatorInstances.find((instance) => instance.key === 'maCross');
    if (!maCrossInstance || candles.length === 0) {
      clearMaCrossMarkers();
      return;
    }

    const maCross = basicStudyDataByInstanceId.get(maCrossInstance.id);
    if (!maCross) {
      clearMaCrossMarkers();
      return;
    }
    const maCrossSettings = resolveBasicStudySettings(maCrossInstance);

    const markers = buildMaCrossMarkers(
      candles,
      maCross.primary,
      maCross.secondary,
      maCrossSettings.style.secondaryColor,
      maCrossSettings.style.primaryColor,
    );

    if (!maCrossMarkersRef.current) {
      maCrossMarkersRef.current = createSeriesMarkers(candleSeries, markers);
      return;
    }

    maCrossMarkersRef.current.setMarkers(markers);
  }, [
    activeBasicIndicatorInstances,
    basicStudyDataByInstanceId,
    isChartReady,
    candles,
    resolveBasicStudySettings,
    clearMaCrossMarkers,
  ]);

  useEffect(() => {
    const candleSeries = seriesRef.current;

    if (!isChartReady || !candleSeries) {
      return;
    }

    if (
      !overlays.botTrades ||
      !chartDisplaySettings.trading.showPositionsAndOrders ||
      botTradeEvents.length === 0
    ) {
      clearBotTradeMarkers();
      return;
    }

    const strategyFilteredEvents =
      typeof botTradeStrategyKey === 'string' && botTradeStrategyKey.length > 0
        ? botTradeEvents.filter((event) => {
            if (event.strategy === botTradeStrategyKey) {
              return true;
            }
            return event.payload.strategy === botTradeStrategyKey;
          })
        : botTradeEvents;

    if (strategyFilteredEvents.length === 0) {
      clearBotTradeMarkers();
      return;
    }

    const markerColors: BotTradesMarkerColors = {
      entry: overlaySettings.botTrades.style.entryColor,
      exit: overlaySettings.botTrades.style.exitColor,
      paper: overlaySettings.botTrades.style.paperColor,
    };
    const markerVisibility: BotTradesMarkerVisibility = {
      showCompletedTrades:
        overlaySettings.botTrades.visibility.showCompletedTrades &&
        chartDisplaySettings.trading.showExecutionMarks,
      showLabels:
        overlaySettings.botTrades.visibility.showLabels &&
        chartDisplaySettings.trading.showExecutionLabels,
    };

    const markers = [...strategyFilteredEvents]
      .sort((left, right) => Date.parse(left.occurred_at) - Date.parse(right.occurred_at))
      .flatMap((event) => {
        const marker = toBotTradeMarker(event, markerColors, markerVisibility);
        return marker ? [marker] : [];
      });

    if (!botTradeMarkersRef.current) {
      botTradeMarkersRef.current = createSeriesMarkers(candleSeries, markers);
      return;
    }

    botTradeMarkersRef.current.setMarkers(markers);
  }, [
    isChartReady,
    overlays.botTrades,
    botTradeEvents,
    botTradeStrategyKey,
    chartDisplaySettings.trading,
    clearBotTradeMarkers,
    overlaySettings.botTrades.style,
    overlaySettings.botTrades.visibility,
  ]);

  useEffect(() => {
    const candleSeries = seriesRef.current;

    if (!isChartReady || !candleSeries) {
      return;
    }

    if (
      !overlays.candleHelper ||
      !overlaySettings.candleHelper.visibility.showMarkers ||
      chartDisplaySettings.autoFit
    ) {
      clearCandleHelperMarkers();
      return;
    }

    const markerColors: CandleHelperMarkerColors = {
      bullishEngulfing: overlaySettings.candleHelper.style.bullishEngulfingColor,
      bearishEngulfing: overlaySettings.candleHelper.style.bearishEngulfingColor,
      doji: overlaySettings.candleHelper.style.dojiColor,
    };
    const showMarkerLabels =
      overlaySettings.candleHelper.visibility.showLabels && !chartDisplaySettings.autoFit;

    const hoveredSignal =
      hoveredCandleIndex === null
        ? null
        : candleHelperSignals.find((signal) => signal.index === hoveredCandleIndex) ?? null;
    const markerKey = hoveredSignal
      ? `${hoveredSignal.type}:${hoveredSignal.index}:${overlaySettings.candleHelper.style.markerSize}:${showMarkerLabels ? 'labels' : 'no-labels'}:${markerColors.bullishEngulfing}:${markerColors.bearishEngulfing}:${markerColors.doji}`
      : 'none';
    if (candleHelperMarkerKeyRef.current === markerKey) {
      return;
    }
    candleHelperMarkerKeyRef.current = markerKey;

    const markers: SeriesMarker<UTCTimestamp>[] = hoveredSignal
      ? (() => {
          const marker = toCandleHelperMarker(
            hoveredSignal,
            candles,
            markerColors,
            showMarkerLabels,
            overlaySettings.candleHelper.style.markerSize,
          );
          return marker ? [marker] : [];
        })()
      : [];

    if (!candleHelperMarkersRef.current) {
      candleHelperMarkersRef.current = createSeriesMarkers(candleSeries, markers);
      return;
    }

    candleHelperMarkersRef.current.setMarkers(markers);
  }, [
    isChartReady,
    candles,
    candleHelperSignals,
    hoveredCandleIndex,
    overlays.candleHelper,
    chartDisplaySettings.autoFit,
    overlaySettings.candleHelper.style,
    overlaySettings.candleHelper.visibility,
    clearCandleHelperMarkers,
  ]);

  useEffect(() => {
    const chart = chartRef.current;
    const candleSeries = seriesRef.current;

    if (!isChartReady || !chart || !candleSeries) {
      return;
    }

    clearStrategyMarkers();
    clearStrategyPriceLines();

    if (!overlays.traderPro17Strategy || !strategyOverlay || !candles.length) {
      return;
    }

    const styleSettings = overlaySettings.traderPro17Strategy.style;
    const visibility = overlaySettings.traderPro17Strategy.visibility;
    const showTraderPro17StrategyLabels =
      chartDisplaySettings.trading.showSeriesLabels && visibility.showSeriesLabels;

    if (visibility.showMarkers && strategyOverlay.markers.length > 0) {
      const markers: SeriesMarker<UTCTimestamp>[] = strategyOverlay.markers.flatMap((marker) => {
        const candle = strategyOverlayCandles[marker.index];
        if (!candle) {
          return [];
        }
        const markerTime = toUnixTimestamp(candle.time);
        if (markerTime === null) {
          return [];
        }
        const isBuy = marker.type === 'buy';
        const isTrail = marker.reason === 'trailingStop';

        return [{
          time: markerTime,
          position: isBuy ? 'belowBar' : 'aboveBar',
          shape: isBuy ? 'arrowUp' : 'arrowDown',
          color: isBuy
            ? styleSettings.buyMarkerColor
            : isTrail
              ? styleSettings.takeProfitColor
              : styleSettings.stopLossColor,
          text: isBuy ? 'B' : isTrail ? 'TR' : 'SL',
        }];
      });

      if (!strategyMarkersRef.current) {
        strategyMarkersRef.current = createSeriesMarkers(candleSeries, markers);
      } else {
        strategyMarkersRef.current.setMarkers(markers);
      }
    }

    if (visibility.showActiveLevels && visibility.useDottedLines && strategyOverlay.activeTrade) {
      const active = strategyOverlay.activeTrade;

      if (active.state === 'inTrade') {
        const slLine = candleSeries.createPriceLine({
          price: active.stopLossPrice,
          color:
            active.entryPrice !== null && active.stopLossPrice > active.entryPrice
              ? styleSettings.takeProfitColor
              : styleSettings.stopLossColor,
          lineWidth: 2,
          lineStyle: LineStyle.Dashed,
          title:
            showTraderPro17StrategyLabels
              ? active.entryPrice !== null && active.stopLossPrice > active.entryPrice
                ? 'TRAIL'
                : 'SL'
              : '',
          axisLabelVisible: true,
        });
        strategyPriceLinesRef.current.push(slLine);
      } else if (active.state === 'pendingBuy') {
        const limitLine = candleSeries.createPriceLine({
          price: active.limitPrice,
          color: styleSettings.buyMarkerColor,
          lineWidth: 1,
          lineStyle: LineStyle.Dotted,
          title: showTraderPro17StrategyLabels ? 'Limit' : '',
          axisLabelVisible: true,
        });
        strategyPriceLinesRef.current.push(limitLine);
      }
    }
  }, [
    isChartReady,
    overlays.traderPro17Strategy,
    strategyOverlay,
    candles,
    strategyOverlayCandles,
    clearStrategyMarkers,
    clearStrategyPriceLines,
    overlaySettings.traderPro17Strategy.style,
    overlaySettings.traderPro17Strategy.visibility,
    chartDisplaySettings.trading.showSeriesLabels,
  ]);

  useEffect(() => {
    const candleSeries = seriesRef.current;

    if (!isChartReady || !candleSeries) {
      return;
    }

    clearOpenCode5mStrategyMarkers();
    clearOpenCode5mStrategyPriceLines();

    if (
      !overlays.openCode5mStrategy ||
      !openCode5mStrategyOverlay ||
      openCode5mStrategyOverlayCandles.length === 0
    ) {
      return;
    }

    const styleSettings = overlaySettings.openCode5mStrategy.style;
    const visibility = overlaySettings.openCode5mStrategy.visibility;
    const showOpenCode5mStrategyLabels =
      chartDisplaySettings.trading.showSeriesLabels && visibility.showSeriesLabels;

    if (visibility.showMarkers && openCode5mStrategyOverlay.markers.length > 0) {
      const markers: SeriesMarker<UTCTimestamp>[] = openCode5mStrategyOverlay.markers.flatMap((marker) => {
        const candle = openCode5mStrategyOverlayCandles[marker.index];
        if (!candle) {
          return [];
        }

        const markerTime = toUnixTimestamp(candle.time);
        if (markerTime === null) {
          return [];
        }

        const isEntryLong = marker.action === 'entryLong';
        const isEntryShort = marker.action === 'entryShort';
        const isExitLong = marker.action === 'exitLong';
        const isExitShort = marker.action === 'exitShort';
        const isEntry = isEntryLong || isEntryShort;
        const markerColor = isEntryLong
          ? styleSettings.longEntryColor
          : isEntryShort
            ? styleSettings.shortEntryColor
            : marker.reason === 'stopLoss'
              ? styleSettings.stopLossColor
              : styleSettings.takeProfitColor;
        const markerText = isEntryLong
          ? 'L'
          : isEntryShort
            ? 'S'
            : marker.reason === 'takeProfit'
              ? 'TP'
              : marker.reason === 'stopLoss'
                ? 'SL'
                : marker.reason === 'timeExit'
                  ? 'TX'
                  : 'FX';

        return [{
          time: markerTime,
          position: isEntryLong || isExitShort ? 'belowBar' : 'aboveBar',
          shape: isEntry ? (isEntryLong ? 'arrowUp' : 'arrowDown') : 'circle',
          color: markerColor,
          text: markerText,
        }];
      });

      if (!openCode5mStrategyMarkersRef.current) {
        openCode5mStrategyMarkersRef.current = createSeriesMarkers(candleSeries, markers);
      } else {
        openCode5mStrategyMarkersRef.current.setMarkers(markers);
      }
    }

    if (
      visibility.showActiveLevels &&
      visibility.useDottedLines &&
      openCode5mStrategyOverlay.activeTrade
    ) {
      const active = openCode5mStrategyOverlay.activeTrade;
      const isLong = active.side === 'long';

      const entryLine = candleSeries.createPriceLine({
        price: active.entryPrice,
        color: isLong ? styleSettings.longEntryColor : styleSettings.shortEntryColor,
        lineWidth: 1,
        lineStyle: LineStyle.Dotted,
        title: showOpenCode5mStrategyLabels ? (isLong ? 'LONG' : 'SHORT') : '',
        axisLabelVisible: true,
      });
      openCode5mStrategyPriceLinesRef.current.push(entryLine);

      const stopLine = candleSeries.createPriceLine({
        price: active.stopPrice,
        color: styleSettings.stopLossColor,
        lineWidth: 2,
        lineStyle: LineStyle.Dashed,
        title: showOpenCode5mStrategyLabels ? 'SL' : '',
        axisLabelVisible: true,
      });
      openCode5mStrategyPriceLinesRef.current.push(stopLine);

      const takeProfitLine = candleSeries.createPriceLine({
        price: active.takeProfitPrice,
        color: styleSettings.takeProfitColor,
        lineWidth: 2,
        lineStyle: LineStyle.Dashed,
        title: showOpenCode5mStrategyLabels ? 'TP' : '',
        axisLabelVisible: true,
      });
      openCode5mStrategyPriceLinesRef.current.push(takeProfitLine);
    }
  }, [
    clearOpenCode5mStrategyMarkers,
    clearOpenCode5mStrategyPriceLines,
    isChartReady,
    openCode5mStrategyOverlay,
    openCode5mStrategyOverlayCandles,
    overlaySettings.openCode5mStrategy.style,
    overlaySettings.openCode5mStrategy.visibility,
    chartDisplaySettings.trading.showSeriesLabels,
    overlays.openCode5mStrategy,
  ]);

  useEffect(() => {
    const candleSeries = seriesRef.current;

    if (!isChartReady || !candleSeries) {
      return;
    }

    clearColorProV31StrategyMarkers();
    clearColorProV31StrategyPriceLines();

    if (
      !overlays.colorProTradingSuiteV31Strategy ||
      !colorProV31StrategyOverlay ||
      colorProV31StrategyOverlayCandles.length === 0
    ) {
      return;
    }

    const styleSettings = overlaySettings.colorProTradingSuiteV31Strategy.style;
    const visibility = overlaySettings.colorProTradingSuiteV31Strategy.visibility;
    const showColorProV31StrategyLabels =
      chartDisplaySettings.trading.showSeriesLabels && visibility.showSeriesLabels;

    if (visibility.showMarkers && colorProV31StrategyOverlay.markers.length > 0) {
      const markers: SeriesMarker<UTCTimestamp>[] = colorProV31StrategyOverlay.markers.flatMap((marker) => {
        const candle = colorProV31StrategyOverlayCandles[marker.index];
        if (!candle) {
          return [];
        }

        const markerTime = toUnixTimestamp(candle.time);
        if (markerTime === null) {
          return [];
        }

        const isBuy = marker.type === 'buy';
        const isTrailingStop = marker.reason === 'trailingStop';
        const markerColor = isBuy
          ? styleSettings.buyMarkerColor
          : isTrailingStop
            ? styleSettings.takeProfitColor
            : styleSettings.stopLossColor;
        const markerText = isBuy
          ? 'B'
          : isTrailingStop
            ? 'TR'
            : 'SL';

        return [{
          time: markerTime,
          position: isBuy ? 'belowBar' : 'aboveBar',
          shape: isBuy ? 'arrowUp' : 'arrowDown',
          color: markerColor,
          text: markerText,
        }];
      });

      if (!colorProV31StrategyMarkersRef.current) {
        colorProV31StrategyMarkersRef.current = createSeriesMarkers(candleSeries, markers);
      } else {
        colorProV31StrategyMarkersRef.current.setMarkers(markers);
      }
    }

    if (visibility.showActiveLevels && visibility.useDottedLines && colorProV31StrategyOverlay.activeTrade) {
      const active = colorProV31StrategyOverlay.activeTrade;
      const stopLine = candleSeries.createPriceLine({
        price: active.stopPrice,
        color: styleSettings.stopLossColor,
        lineWidth: 2,
        lineStyle: LineStyle.Dashed,
        title: showColorProV31StrategyLabels ? 'STEP SL' : '',
        axisLabelVisible: true,
      });
      colorProV31StrategyPriceLinesRef.current.push(stopLine);
    }
  }, [
    isChartReady,
    overlays.colorProTradingSuiteV31Strategy,
    colorProV31StrategyOverlay,
    colorProV31StrategyOverlayCandles,
    clearColorProV31StrategyMarkers,
    clearColorProV31StrategyPriceLines,
    overlaySettings.colorProTradingSuiteV31Strategy.style,
    overlaySettings.colorProTradingSuiteV31Strategy.visibility,
    chartDisplaySettings.trading.showSeriesLabels,
  ]);

  useEffect(() => {
    if (!isChartReady || !candles.length || !seriesRef.current || !chartRef.current) {
      return;
    }

    const expectedStepSeconds = INTERVAL_STEP_SECONDS[interval];
    const shouldFillSyntheticGaps = interval !== '1M';
    const buildCandlePoint = (candle: Candle, index: number): CandlestickData<Time> | null => {
      const time = toUnixTimestamp(candle.time);
      if (time === null) {
        return null;
      }

      const point: CandlestickData<Time> = {
        time,
        open: candle.open,
        high: candle.high,
        low: candle.low,
        close: candle.close,
      };

      let candleColor: string | undefined;
      const useV31CandleColors =
        overlays.colorProTradingSuiteV31 &&
        colorProV31Overlay &&
        overlaySettings.colorProTradingSuiteV31.visibility.showCandleColors;
      if (
        overlays.colorProTradingSuiteV3 &&
        colorProV3Overlay &&
        overlaySettings.colorProTradingSuiteV3.visibility.showCandleColors &&
        !useV31CandleColors
      ) {
        candleColor = colorProV3Overlay.candleColors[index];
      }
      if (useV31CandleColors) {
        const v31Color = colorProV31Overlay.candleColors[index];
        if (v31Color) {
          candleColor = v31Color;
        }
      }

      if (candleColor) {
        const useFullV31CandleColoring =
          useV31CandleColors &&
          overlaySettings.colorProTradingSuiteV31.visibility.matchEntireCandleToColor;

        return {
          ...point,
          color: candleColor,
          ...(useFullV31CandleColoring
            ? {
                borderColor: candleColor,
                wickColor: candleColor,
              }
            : {}),
        };
      }

      return point;
    };

    const buildVolumePoint = (candle: Candle): VolumePoint | null => {
      const time = toUnixTimestamp(candle.time);
      if (time === null) {
        return null;
      }

      return {
        time,
        value: candle.volume,
        color:
          candle.close >= candle.open
            ? toRgba(overlaySettings.volume.style.upColor, overlaySettings.volume.style.opacity)
            : toRgba(overlaySettings.volume.style.downColor, overlaySettings.volume.style.opacity),
      };
    };

    const previousRenderedCandles = previousRenderedCandlesRef.current;
    let previousTimeSeconds: number | null = null;
    if (candles.length > 0) {
      const lastCandleTime = toUnixTimestamp(candles[candles.length - 1].time);
      previousTimeSeconds = lastCandleTime === null ? null : Number(lastCandleTime);
    }

    const visibleRange = visibleLogicalRangeRef.current;
    const visibleRangeRightGapBars =
      visibleRange && baseSeriesLastLogicalIndexRef.current >= 0
        ? Math.min(
            MAX_FUTURE_WHITESPACE_BARS,
            Math.max(0, Math.ceil(visibleRange.to - baseSeriesLastLogicalIndexRef.current)),
          )
        : 0;
    const futureWhitespaceBars = Math.min(
      MAX_FUTURE_WHITESPACE_BARS,
      Math.max(
        0,
        Math.ceil(effectiveRightOffset),
        visibleRightGapBars,
        visibleRangeRightGapBars,
      ),
    );

    const candlesChanged = previousRenderedCandles !== candles;
    const previousFutureWhitespaceBars = lastRenderedFutureWhitespaceBarsRef.current;
    const canReplaceTailCandle =
      candlesChanged &&
      previousFutureWhitespaceBars === 0 &&
      futureWhitespaceBars === 0 &&
      previousRenderedCandles.length === candles.length &&
      candles.length > 0 &&
      candlesSharePrefix(previousRenderedCandles, candles, Math.max(0, candles.length - 1));
    const canAppendTailCandle =
      candlesChanged &&
      previousFutureWhitespaceBars === 0 &&
      futureWhitespaceBars === 0 &&
      previousRenderedCandles.length + 1 === candles.length &&
      previousRenderedCandles.length > 0 &&
      candlesSharePrefix(previousRenderedCandles, candles, previousRenderedCandles.length);
    const lastPreviousCandle = previousRenderedCandles[previousRenderedCandles.length - 1];
    const lastPreviousTime = lastPreviousCandle ? toUnixTimestamp(lastPreviousCandle.time) : null;
    const lastCurrentTime = candles.length > 0 ? toUnixTimestamp(candles[candles.length - 1].time) : null;
    const safeIncrementalAppend =
      canAppendTailCandle &&
      lastPreviousTime !== null &&
      lastCurrentTime !== null &&
      isSingleChartStep(Number(lastPreviousTime), Number(lastCurrentTime), interval);

    let formattedLength = baseSeriesLastLogicalIndexRef.current + 1 + futureWhitespaceBars;
    let performedIncrementalBaseUpdate = false;
    if (canReplaceTailCandle) {
      const updatedPoint = buildCandlePoint(candles[candles.length - 1], candles.length - 1);
      if (updatedPoint) {
        seriesRef.current.update(updatedPoint);
        formattedLength = baseSeriesLastLogicalIndexRef.current + 1;
        performedIncrementalBaseUpdate = true;
      }
    }

    if (safeIncrementalAppend) {
      const appendedPoint = buildCandlePoint(candles[candles.length - 1], candles.length - 1);
      if (appendedPoint) {
        seriesRef.current.update(appendedPoint);
        baseSeriesLastLogicalIndexRef.current += 1;
        formattedLength = baseSeriesLastLogicalIndexRef.current + 1;
        performedIncrementalBaseUpdate = true;
      }
    }

    if (!performedIncrementalBaseUpdate) {
      const formatted: Array<CandlestickData<Time> | WhitespaceData<Time>> = [];
      previousTimeSeconds = null;

      candles.forEach((candle, index) => {
        const point = buildCandlePoint(candle, index);
        if (!point) {
          return;
        }

        const unixTime = Number(point.time);
        if (
          shouldFillSyntheticGaps &&
          previousTimeSeconds !== null &&
          Number.isFinite(unixTime) &&
          unixTime > previousTimeSeconds + expectedStepSeconds
        ) {
          for (
            let missingTime = previousTimeSeconds + expectedStepSeconds;
            missingTime < unixTime;
            missingTime += expectedStepSeconds
          ) {
            formatted.push({
              time: missingTime as UTCTimestamp,
            });
          }
        }

        formatted.push(point);
        previousTimeSeconds = unixTime;
      });

      const baseSeriesLastLogicalIndex = formatted.length - 1;
      baseSeriesLastLogicalIndexRef.current = baseSeriesLastLogicalIndex;
      if (
        futureWhitespaceBars > 0 &&
        previousTimeSeconds !== null &&
        Number.isFinite(expectedStepSeconds) &&
        expectedStepSeconds > 0 &&
        interval !== '1M'
      ) {
        for (let index = 1; index <= futureWhitespaceBars; index += 1) {
          formatted.push({
            time: (previousTimeSeconds + expectedStepSeconds * index) as UTCTimestamp,
          });
        }
      }

      seriesRef.current.setData(formatted);
      formattedLength = formatted.length;
    }

    lastRenderedFutureWhitespaceBarsRef.current = futureWhitespaceBars;
    previousRenderedCandlesRef.current = candles;
    const previousCandleCount = previousCandleCountRef.current;
    const currentCandleCount = candles.length;
    previousCandleCountRef.current = currentCandleCount;
    const shouldRefitAfterInitialGrowth =
      hasInitialFitRef.current &&
      !hasGrowthRefitRef.current &&
      !manualTimeNavigationRef.current &&
      !isReplayMode &&
      previousCandleCount > 0 &&
      previousCandleCount <= INITIAL_FIT_GROWTH_REFIT_MAX_PREVIOUS_BARS &&
      currentCandleCount >= MIN_NON_REPLAY_VISIBLE_BARS;

    if (
      !isReplayMode &&
      !isReplayViewportLocked &&
      !chartDisplaySettings.autoFit &&
      !hasManualPriceScaleBootstrapRef.current
    ) {
      let minPrice = Number.POSITIVE_INFINITY;
      let maxPrice = Number.NEGATIVE_INFINITY;
      for (const candle of candles) {
        if (candle.low < minPrice) {
          minPrice = candle.low;
        }
        if (candle.high > maxPrice) {
          maxPrice = candle.high;
        }
      }

      if (Number.isFinite(minPrice) && Number.isFinite(maxPrice)) {
        const rightPriceScale = chartRef.current.priceScale('right');
        const visiblePriceRange = rightPriceScale.getVisibleRange();
        const hasValidVisibleRange =
          visiblePriceRange !== null &&
          Number.isFinite(visiblePriceRange.from) &&
          Number.isFinite(visiblePriceRange.to) &&
          visiblePriceRange.to > visiblePriceRange.from;
        const isOutsideData = hasValidVisibleRange
          ? visiblePriceRange.from > maxPrice || visiblePriceRange.to < minPrice
          : true;

        if (!hasValidVisibleRange || isOutsideData) {
          const rawSpan = maxPrice - minPrice;
          const referencePrice = Math.max(Math.abs(maxPrice), Math.abs(minPrice), 1);
          const span = rawSpan > 0 ? rawSpan : referencePrice * 0.01;
          const padding = span * 0.1;
          rightPriceScale.setVisibleRange({
            from: minPrice - padding,
            to: maxPrice + padding,
          });
        }

        hasManualPriceScaleBootstrapRef.current = true;
      }
    }

    const logicalRange = chartRef.current.timeScale().getVisibleLogicalRange();
    const maxLogicalIndex = formattedLength - 0.5;
    if (
      !isReplayMode &&
      !replayViewportLockRef.current &&
      candlesChanged &&
      logicalRange &&
      (logicalRange.from > maxLogicalIndex || logicalRange.to < -0.5)
    ) {
      chartRef.current.timeScale().resetTimeScale();
    }
    if (!hasInitialFitRef.current) {
      chartRef.current.timeScale().resetTimeScale();
      hasInitialFitRef.current = true;
      shouldAutoFollowRef.current = !isReplayMode;
    } else if (shouldRefitAfterInitialGrowth) {
      chartRef.current.timeScale().resetTimeScale();
      hasGrowthRefitRef.current = true;
      shouldAutoFollowRef.current = true;
    } else if (candlesChanged && shouldAutoFollowRef.current && !isReplayMode) {
      chartRef.current.timeScale().scrollToRealTime();
    }
    chartRef.current
      .priceScale('right')
      .applyOptions({
        scaleMargins: {
          top: 0.05,
          bottom:
            overlays.volume && overlaySettings.volume.visibility.showHistogram ? 0.2 : 0.05,
        },
      });

    if (overlays.volume && overlaySettings.volume.visibility.showHistogram && volumeSeriesRef.current) {
      if (performedIncrementalBaseUpdate) {
        const nextVolumePoint = buildVolumePoint(candles[candles.length - 1]);
        if (nextVolumePoint) {
          volumeSeriesRef.current.update(nextVolumePoint);
        }
      } else {
        const volumeData = candles.flatMap((candle) => {
          const point = buildVolumePoint(candle);
          return point ? [point] : [];
        });
        volumeSeriesRef.current.setData(volumeData);
      }
    } else if (volumeSeriesRef.current) {
      volumeSeriesRef.current.setData([]);
    }

    if (overlays.rsi && rsiSeriesRef.current) {
      const rsiData = computeRsiSeries(candles, overlaySettings.rsi.inputs.length);
      setLineSeriesDataSafely(rsiSeriesRef.current, rsiData, 'rsi');
      rsiSeriesRef.current.priceScale().setVisibleRange({ from: 0, to: 100 });
    } else if (rsiSeriesRef.current) {
      setLineSeriesDataSafely(rsiSeriesRef.current, [], 'rsi');
    }

    if (overlays.macd) {
      const macdData = computeMacdSeries(
        candles,
        overlaySettings.macd.inputs.fastLength,
        overlaySettings.macd.inputs.slowLength,
        overlaySettings.macd.inputs.signalLength,
      );

      if (macdLineSeriesRef.current) {
        setLineSeriesDataSafely(macdLineSeriesRef.current, macdData.macdLine, 'macd.line');
      }
      if (macdSignalSeriesRef.current) {
        setLineSeriesDataSafely(macdSignalSeriesRef.current, macdData.signalLine, 'macd.signal');
      }
      if (macdHistogramSeriesRef.current) {
        macdHistogramSeriesRef.current.setData(
          macdData.histogram.map((point) => ({
            ...point,
            color:
              point.value >= 0
                ? overlaySettings.macd.style.histogramPositiveColor
                : overlaySettings.macd.style.histogramNegativeColor,
          })),
        );
      }
    } else {
      if (macdLineSeriesRef.current) {
        setLineSeriesDataSafely(macdLineSeriesRef.current, [], 'macd.line');
      }
      if (macdSignalSeriesRef.current) {
        setLineSeriesDataSafely(macdSignalSeriesRef.current, [], 'macd.signal');
      }
      macdHistogramSeriesRef.current?.setData([]);
    }

    if (overlays.cmVixFixUcsSqueeze && projectedCmVixFixOverlay) {
      if (cmVixFixHistogramSeriesRef.current) {
        cmVixFixHistogramSeriesRef.current.setData(
          candles.flatMap((candle, index) => {
            const value = projectedCmVixFixOverlay.wvf[index];
            if (value === undefined || !Number.isFinite(value)) {
              return [];
            }

            const time = toUnixTimestamp(candle.time);
            if (time === null) {
              return [];
            }

            return [{
              time,
              value,
              color: projectedCmVixFixOverlay.wvfSpike[index]
                ? overlaySettings.cmVixFixUcsSqueeze.style.wvfSpikeColor
                : overlaySettings.cmVixFixUcsSqueeze.style.wvfBaseColor,
            }];
          }),
        );
      }

      if (cmSqueezeMomentumSeriesRef.current) {
        const momentumData: Array<{
          time: UTCTimestamp;
          value: number;
          color: string;
        }> = [];

        for (let index = 0; index < candles.length; index += 1) {
          const value = projectedCmVixFixOverlay.momentum[index];
          const state = projectedCmVixFixOverlay.momentumState[index];
          if (value === undefined || state === undefined) {
            continue;
          }

          let color = overlaySettings.cmVixFixUcsSqueeze.style.momentumNegativeRisingColor;
          if (state === 'positiveRising') {
            color = overlaySettings.cmVixFixUcsSqueeze.style.momentumPositiveRisingColor;
          } else if (state === 'positiveFalling') {
            color = overlaySettings.cmVixFixUcsSqueeze.style.momentumPositiveFallingColor;
          } else if (state === 'negativeFalling') {
            color = overlaySettings.cmVixFixUcsSqueeze.style.momentumNegativeFallingColor;
          }

          const time = toUnixTimestamp(candles[index].time);
          if (time === null) {
            continue;
          }

          momentumData.push({
            time,
            value,
            color,
          });
        }

        cmSqueezeMomentumSeriesRef.current.setData(momentumData);
      }

      const onValues = projectedCmVixFixOverlay.squeezeState.map((state) => (state === 'on' ? 0 : undefined));
      const offValues = projectedCmVixFixOverlay.squeezeState.map((state) => (state === 'off' ? 0 : undefined));
      const neutralValues = projectedCmVixFixOverlay.squeezeState.map((state) =>
        state === 'neutral' ? 0 : undefined,
      );

      if (cmWvfUpperBandSeriesRef.current) {
        setLineSeriesDataSafely(
          cmWvfUpperBandSeriesRef.current,
          toLineData(candles, projectedCmVixFixOverlay.upperBand),
          'cmvix.upperBand',
        );
      }
      if (cmWvfRangeHighSeriesRef.current) {
        setLineSeriesDataSafely(
          cmWvfRangeHighSeriesRef.current,
          toLineData(candles, projectedCmVixFixOverlay.rangeHigh),
          'cmvix.rangeHigh',
        );
      }
      if (cmWvfRangeLowSeriesRef.current) {
        setLineSeriesDataSafely(
          cmWvfRangeLowSeriesRef.current,
          toLineData(candles, projectedCmVixFixOverlay.rangeLow),
          'cmvix.rangeLow',
        );
      }
      if (cmSqueezeOnSeriesRef.current) {
        setLineSeriesDataSafely(
          cmSqueezeOnSeriesRef.current,
          toLineData(candles, onValues),
          'cmvix.squeezeOn',
        );
      }
      if (cmSqueezeOffSeriesRef.current) {
        setLineSeriesDataSafely(
          cmSqueezeOffSeriesRef.current,
          toLineData(candles, offValues),
          'cmvix.squeezeOff',
        );
      }
      if (cmNoSqueezeSeriesRef.current) {
        setLineSeriesDataSafely(
          cmNoSqueezeSeriesRef.current,
          toLineData(candles, neutralValues),
          'cmvix.noSqueeze',
        );
      }
    } else {
      cmVixFixHistogramSeriesRef.current?.setData([]);
      cmSqueezeMomentumSeriesRef.current?.setData([]);
      if (cmWvfUpperBandSeriesRef.current) {
        setLineSeriesDataSafely(cmWvfUpperBandSeriesRef.current, [], 'cmvix.upperBand');
      }
      if (cmWvfRangeHighSeriesRef.current) {
        setLineSeriesDataSafely(cmWvfRangeHighSeriesRef.current, [], 'cmvix.rangeHigh');
      }
      if (cmWvfRangeLowSeriesRef.current) {
        setLineSeriesDataSafely(cmWvfRangeLowSeriesRef.current, [], 'cmvix.rangeLow');
      }
      if (cmSqueezeOnSeriesRef.current) {
        setLineSeriesDataSafely(cmSqueezeOnSeriesRef.current, [], 'cmvix.squeezeOn');
      }
      if (cmSqueezeOffSeriesRef.current) {
        setLineSeriesDataSafely(cmSqueezeOffSeriesRef.current, [], 'cmvix.squeezeOff');
      }
      if (cmNoSqueezeSeriesRef.current) {
        setLineSeriesDataSafely(cmNoSqueezeSeriesRef.current, [], 'cmvix.noSqueeze');
      }
    }

    const activeMovingAverageInstanceIds = new Set(
      activeMovingAverageInstances.map((instance) => instance.id),
    );
    for (const [instanceId, series] of movingAverageSeriesRef.current.entries()) {
      if (!activeMovingAverageInstanceIds.has(instanceId)) {
        setLineSeriesDataSafely(series, [], `movingAverage.${instanceId}`);
        continue;
      }

      const settings = resolveMovingAverageSettings(instanceId);
      if (!settings.visibility.showLine) {
        setLineSeriesDataSafely(series, [], `movingAverage.${instanceId}`);
        continue;
      }

      const lineData = computeMovingAverageSeries(
        candles,
        settings.inputs.period,
        settings.inputs.maType,
        settings.inputs.source,
        settings.inputs.offsetPercent,
      );
      setLineSeriesDataSafely(series, lineData, `movingAverage.${instanceId}`);
    }
  }, [
    activeMovingAverageInstances,
    candles,
    overlays.volume,
    overlays.rsi,
    overlays.macd,
    overlays.cmVixFixUcsSqueeze,
    overlays.colorProTradingSuiteV3,
    overlays.colorProTradingSuiteV31,
    colorProV3Overlay,
    colorProV31Overlay,
    cmVixFixOverlay,
    projectedCmVixFixOverlay,
    isChartReady,
    isReplayMode,
    isReplayViewportLocked,
    chartDisplaySettings.autoFit,
    effectiveRightOffset,
    visibleRightGapBars,
    overlaySettings.volume.visibility.showHistogram,
    overlaySettings.volume.style.upColor,
    overlaySettings.volume.style.downColor,
    overlaySettings.volume.style.opacity,
    overlaySettings.rsi.inputs.length,
    overlaySettings.macd.inputs.fastLength,
    overlaySettings.macd.inputs.slowLength,
    overlaySettings.macd.inputs.signalLength,
    overlaySettings.macd.style.histogramPositiveColor,
    overlaySettings.macd.style.histogramNegativeColor,
    overlaySettings.cmVixFixUcsSqueeze.style.wvfSpikeColor,
    overlaySettings.cmVixFixUcsSqueeze.style.wvfBaseColor,
    overlaySettings.cmVixFixUcsSqueeze.style.momentumPositiveRisingColor,
    overlaySettings.cmVixFixUcsSqueeze.style.momentumPositiveFallingColor,
    overlaySettings.cmVixFixUcsSqueeze.style.momentumNegativeFallingColor,
    overlaySettings.cmVixFixUcsSqueeze.style.momentumNegativeRisingColor,
    overlaySettings.colorProTradingSuiteV3.visibility.showCandleColors,
    overlaySettings.colorProTradingSuiteV31.visibility.showCandleColors,
    overlaySettings.colorProTradingSuiteV31.visibility.matchEntireCandleToColor,
    resolveMovingAverageSettings,
  ]);

  useEffect(() => {
    const chart = chartRef.current;

    if (!isChartReady || !chart || !replaySelectionEnabled || !onReplayStartSelect || !candles.length) {
      return;
    }

    const maxIndex = candles.length - 1;

    const handleChartClick = (param: MouseEventParams<Time>) => {
      let selectedIndex: number | null = null;
      let clickedLogical: number | null = null;

      if (typeof param.logical === 'number' && Number.isFinite(param.logical)) {
        if (param.logical < -0.5 || param.logical > maxIndex + 0.5) {
          return;
        }
        clickedLogical = param.logical;
        selectedIndex = Math.max(0, Math.min(maxIndex, Math.round(param.logical)));
      } else if (typeof param.time === 'number') {
        const unixTime = Number(param.time);
        selectedIndex = candleIndexByUnixTime.get(unixTime) ?? -1;
      }

      if (selectedIndex === null || selectedIndex < 0) {
        return;
      }

      const timeScale = chart.timeScale();
      const visibleRange = timeScale.getVisibleLogicalRange();
      const anchorLogical = clickedLogical ?? selectedIndex;
      let requiredReplayPadding = 0;
      if (visibleRange) {
        const rangeWidth = visibleRange.to - visibleRange.from;
        if (Number.isFinite(rangeWidth) && rangeWidth > 0) {
          const ratio = (anchorLogical - visibleRange.from) / rangeWidth;
          const boundedRatio = Math.max(0, Math.min(1, ratio));
          const targetFrom = selectedIndex - boundedRatio * rangeWidth;
          const targetTo = targetFrom + rangeWidth;
          const requiredEffectiveRightOffset = Math.max(0, targetTo - selectedIndex);
          requiredReplayPadding = Math.max(0, requiredEffectiveRightOffset - chartRightOffset);
        }
      }

      replayStartAnchorRef.current = {
        selectedIndex,
        clickedLogical: anchorLogical,
        visibleRange: visibleRange
          ? {
              from: visibleRange.from,
              to: visibleRange.to,
            }
          : null,
        requiredReplayPadding,
      };

      onReplayStartSelect(selectedIndex);
    };

    chart.subscribeClick(handleChartClick);
    return () => {
      chart.unsubscribeClick(handleChartClick);
    };
  }, [candleIndexByUnixTime, isChartReady, replaySelectionEnabled, onReplayStartSelect, candles, chartRightOffset]);

  useEffect(() => {
    const chart = chartRef.current;

    if (!isChartReady || !chart || !onRequestMoreHistory || !hasMoreHistory || isLoadingMoreHistory) {
      return;
    }

    if (!candles.length) {
      return;
    }

    const timeScale = chart.timeScale();
    const triggerThreshold = 25;

    const requestIfNearLeftEdge = () => {
      if (shouldAutoFollowRef.current) {
        return;
      }

      const logicalRange = timeScale.getVisibleLogicalRange();
      if (!logicalRange) {
        return;
      }

      if (logicalRange.from > triggerThreshold) {
        return;
      }

      if (isHistoryRequestInFlightRef.current) {
        return;
      }

      isHistoryRequestInFlightRef.current = true;
      Promise.resolve(onRequestMoreHistory()).finally(() => {
        isHistoryRequestInFlightRef.current = false;
      });
    };

    timeScale.subscribeVisibleLogicalRangeChange(requestIfNearLeftEdge);
    requestIfNearLeftEdge();

    return () => {
      timeScale.unsubscribeVisibleLogicalRangeChange(requestIfNearLeftEdge);
    };
  }, [
    isChartReady,
    onRequestMoreHistory,
    hasMoreHistory,
    isLoadingMoreHistory,
    candles.length,
  ]);

  const renderLegendRow = (item: OverlayLegendItem) => {
    const paneOrderIndex = paneIndicatorOrderIndexByKey.get(item.instanceId);
    const isPaneEntry = paneOrderIndex !== undefined;
    const canMoveUp = isPaneEntry && paneOrderIndex > 0;
    const canMoveDown = isPaneEntry && paneOrderIndex < paneIndicators.length - 1;
    const paneIndex = isPaneEntry ? paneOrderIndex + 1 : 0;
    const indicatorArguments = indicatorArgumentsByInstanceId.get(item.instanceId) ?? '';

    return (
      <div
        key={item.instanceId}
        className="pointer-events-auto group flex max-w-[min(420px,calc(100vw-2rem))] items-center gap-1.5 rounded-md border border-transparent bg-transparent px-2 py-1 transition-colors hover:border-slate-700/80 hover:bg-slate-900/85 focus-within:border-slate-700/80 focus-within:bg-slate-900/85"
        onContextMenu={(event) => handleOpenLegendContextMenu(event, item, paneIndex)}
        onPointerDown={(event) =>
          startAnchoredLongPress(event, {
            kind: 'legendItem',
            paneIndex,
            price: activeCandle?.close ?? null,
            time: resolveHoveredUnixTime(),
            indicatorInstanceId: item.instanceId,
            indicatorLabel: item.settingsLabel,
          })
        }
        onPointerUp={clearLongPressTimer}
        onPointerLeave={clearLongPressTimer}
        onPointerCancel={clearLongPressTimer}
      >
        <span
          className={`truncate text-xs font-medium leading-none transition-colors ${
            item.active ? 'text-slate-200 group-hover:text-slate-100' : 'text-slate-600 group-hover:text-slate-400'
          }`}
        >
          {item.settingsLabel}
        </span>
        {chartDisplaySettings.statusLine.showIndicatorArguments && indicatorArguments ? (
          <span
            className={`truncate text-[10px] leading-none transition-colors ${
              item.active ? 'text-slate-400 group-hover:text-slate-300' : 'text-slate-600 group-hover:text-slate-500'
            }`}
            title={indicatorArguments}
          >
            {indicatorArguments}
          </span>
        ) : null}
        <div className="pointer-events-none flex max-w-0 items-center gap-1 overflow-hidden text-[9px] leading-none opacity-0 transition-all duration-150 group-hover:pointer-events-auto group-hover:max-w-[16rem] group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:max-w-[16rem] group-focus-within:opacity-100">
          {isPaneEntry ? (
            <>
              <button
                type="button"
                onClick={() => onMoveOverlayLegendItem?.(item.instanceId, 'up')}
                data-chart-context-ignore="true"
                disabled={!canMoveUp}
                className="inline-flex h-4 w-4 items-center justify-center rounded border border-slate-600 bg-slate-800 text-[10px] text-slate-200 transition-colors hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
                aria-label={`Move ${item.label} pane up`}
                title="Move pane up"
              >
                ^
              </button>
              <button
                type="button"
                onClick={() => onMoveOverlayLegendItem?.(item.instanceId, 'down')}
                data-chart-context-ignore="true"
                disabled={!canMoveDown}
                className="inline-flex h-4 w-4 items-center justify-center rounded border border-slate-600 bg-slate-800 text-[10px] text-slate-200 transition-colors hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
                aria-label={`Move ${item.label} pane down`}
                title="Move pane down"
              >
                v
              </button>
            </>
          ) : null}
          <button
            type="button"
            onClick={() => onToggleOverlayLegendVisibility?.(item.instanceId)}
            data-chart-context-ignore="true"
            className={`inline-flex h-4 w-4 items-center justify-center rounded border transition-colors ${
              item.active
                ? 'border-emerald-500/70 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20'
                : 'border-slate-600 bg-slate-800 text-slate-300 hover:bg-slate-700'
            }`}
            aria-label={`${item.active ? 'Hide' : 'Show'} ${item.label} overlay`}
            title={item.active ? 'Hide overlay' : 'Show overlay'}
          >
            {item.active ? <LegendEyeIcon /> : <LegendEyeOffIcon />}
          </button>
          <button
            type="button"
            onClick={() => onOpenOverlayLegendSettings?.(item.instanceId)}
            data-chart-context-ignore="true"
            className="inline-flex h-4 w-4 items-center justify-center rounded border border-slate-600 bg-slate-800 text-slate-200 transition-colors hover:bg-slate-700"
            aria-label={`Open ${item.label} settings`}
            title="Open settings"
          >
            <LegendSettingsIcon />
          </button>
          <button
            type="button"
            onClick={() => onRemoveOverlayLegendItem?.(item.instanceId)}
            data-chart-context-ignore="true"
            className="inline-flex h-4 w-4 items-center justify-center rounded border border-rose-500/60 bg-rose-500/10 text-rose-200 transition-colors hover:bg-rose-500/20"
            aria-label={`Remove ${item.label} overlay`}
            title="Remove from legend"
          >
            X
          </button>
        </div>
      </div>
    );
  };

  const renderMenuCheck = (enabled: boolean) => (
    <span className="inline-flex h-4 w-4 items-center justify-center text-emerald-300">
      {enabled ? <ChartMenuCheckIcon /> : null}
    </span>
  );

  const menuRowClassName =
    'flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-xs text-slate-100 transition-colors hover:bg-slate-800/90';

  const statusBackgroundOpacity = Math.max(
    0,
    Math.min(100, chartDisplaySettings.statusLine.backgroundOpacity),
  );

  const formatEventTime = useCallback(
    (isoTimestamp: string): string => {
      const date = new Date(isoTimestamp);
      if (Number.isNaN(date.getTime())) {
        return '';
      }
      const timezone = resolveSymbolTimezone(chartDisplaySettings.symbol.timezone);
      return new Intl.DateTimeFormat('en-GB', {
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: timezone,
      }).format(date);
    },
    [chartDisplaySettings.symbol.timezone],
  );

  const sortedCurrentSymbolAlerts = useMemo(
    () => [...alertItems].sort((left, right) => right.createdAt.localeCompare(left.createdAt)),
    [alertItems],
  );

  const sortedAllAlerts = useMemo(
    () => [...allAlertItems].sort((left, right) => right.createdAt.localeCompare(left.createdAt)),
    [allAlertItems],
  );

  const updateAlertItem = useCallback((alertId: string, updater: (alert: ChartAlertItem) => ChartAlertItem) => {
    setAlertItemsState((previous) => previous.map((item) => (item.id === alertId ? updater(item) : item)));
  }, [setAlertItemsState]);

  const removeAlertItem = useCallback((alertId: string) => {
    setAlertItemsState((previous) => previous.filter((item) => item.id !== alertId));
  }, [setAlertItemsState]);

  const closeContextMenu = useCallback(() => {
    setContextMenu(DEFAULT_CONTEXT_MENU_STATE);
  }, []);

  const removeTransientChartObjects = useCallback(() => {
    setAlertItemsState((previous) => previous.filter((item) => item.provider !== provider || item.symbol !== symbol));
    setDrawingItemsState((previous) => previous.filter((item) => item.provider !== provider || item.symbol !== symbol));
    setSelectedDrawingId(null);
    setDrawingDraft(null);
    if (onClearPaperOrders) {
      void onClearPaperOrders(provider, symbol);
    } else {
      setPaperOrdersState([]);
    }
    setRuntimeStatusOptions((previous) => ({
      ...previous,
      verticalCursorLockTime: null,
    }));
    pushToast('success', 'Removed chart drawings and local paper-trading objects.');
  }, [onClearPaperOrders, provider, pushToast, setAlertItemsState, setDrawingItemsState, setPaperOrdersState, symbol]);

  const removeAllIndicators = useCallback(() => {
    overlayLegendItems.forEach((item) => {
      onRemoveOverlayLegendItem?.(item.instanceId);
    });
    pushToast('success', 'Removed all indicators from the chart.');
  }, [onRemoveOverlayLegendItem, overlayLegendItems, pushToast]);

  const openMoreSettings = useCallback(
    (
      nextTab: 'symbol' | 'statusLine' | 'scalesAndLines' | 'canvas' | 'trading' | 'alerts' | 'events' = 'symbol',
    ) => {
      setModalDraftSettings(chartDisplaySettings);
      setMoreSettingsTab(nextTab);
      setIsMoreSettingsModalOpen(true);
      closeContextMenu();
    },
    [chartDisplaySettings, closeContextMenu],
  );

  const resolveContextPriceLabel = useCallback(
    (price: number | null) => {
      if (price === null) {
        return '0';
      }
      const precisionConfig = resolvePricePrecisionConfig(chartDisplaySettings.symbol.precision);
      return formatNumber(quantizePrice(price, precisionConfig.minMove), precisionConfig.fractionDigits);
    },
    [chartDisplaySettings.symbol.precision],
  );

  useEffect(() => {
    const activeAlertIds = new Set(alertItems.filter((item) => item.active).map((item) => item.id));
    Array.from(alertTriggerCandleTimeRef.current.keys()).forEach((alertId) => {
      if (!activeAlertIds.has(alertId)) {
        alertTriggerCandleTimeRef.current.delete(alertId);
      }
    });

    const currentCandle = candles.length > 0 ? candles[candles.length - 1] : null;
    const previousCandle = candles.length > 1 ? candles[candles.length - 2] : null;
    if (!currentCandle) {
      return;
    }

    const currentCandleTime = toUnixTimestamp(currentCandle.time);
    if (currentCandleTime === null) {
      return;
    }

    if (!hasInitializedAlertTriggersRef.current) {
      alertItems.forEach((item) => {
        if (item.active && typeof item.price === 'number' && isPriceWithinCandle(currentCandle, item.price)) {
          alertTriggerCandleTimeRef.current.set(item.id, Number(currentCandleTime));
        }
      });
      hasInitializedAlertTriggersRef.current = true;
      return;
    }

    const triggeredAlerts = alertItems.filter((item) => {
      if (!item.active || typeof item.price !== 'number') {
        return false;
      }

      if (!didPriceAlertTrigger(previousCandle, currentCandle, item.price)) {
        return false;
      }

      if (alertTriggerCandleTimeRef.current.get(item.id) === Number(currentCandleTime)) {
        return false;
      }

      alertTriggerCandleTimeRef.current.set(item.id, Number(currentCandleTime));
      return true;
    });

    if (triggeredAlerts.length === 0) {
      return;
    }

    void playAlertSound(
      chartDisplaySettings.alerts.alertSoundType,
      chartDisplaySettings.alerts.alertSoundVolume,
    );

    triggeredAlerts.forEach((item) => {
      const targetLabel = item.indicatorLabel ?? item.symbol;
      pushToast('warning', `Alert hit ${resolveContextPriceLabel(item.price)} on ${targetLabel}.`);
    });
  }, [
    alertItems,
    candles,
    chartDisplaySettings.alerts.alertSoundType,
    chartDisplaySettings.alerts.alertSoundVolume,
    playAlertSound,
    pushToast,
    resolveContextPriceLabel,
  ]);

  const openContextMenuAt = useCallback(
    (partial: Partial<ChartContextMenuState>) => {
      setIsChartSettingsOpen(false);
      setChartSettingsSubmenu(null);
      setIsStatusIndicatorOpen(false);
      setContextMenu({
        ...DEFAULT_CONTEXT_MENU_STATE,
        ...partial,
        open: true,
      });
    },
    [],
  );

  const resolveHoveredUnixTime = useCallback((): number | null => {
    const candle = activeCandle;
    if (!candle) {
      return null;
    }
    const timestamp = toUnixTimestamp(candle.time);
    return timestamp === null ? null : Number(timestamp);
  }, [activeCandle]);

  const resolvePrimarySeriesForPane = useCallback(
    (paneIndex: number): TouchAxisSeries | null => {
      if (paneIndex === 0) {
        return seriesRef.current;
      }

      const paneItem = paneLegendItems[paneIndex - 1] ?? null;
      if (!paneItem) {
        return null;
      }

      if (paneItem.key === 'rsi') {
        return rsiSeriesRef.current;
      }

      if (paneItem.key === 'macd') {
        return macdLineSeriesRef.current;
      }

      if (paneItem.key === 'cmVixFixUcsSqueeze') {
        return cmSqueezeMomentumSeriesRef.current ?? cmVixFixHistogramSeriesRef.current;
      }

      if (isBasicIndicatorKey(paneItem.key)) {
        const refs = basicStudySeriesRefs.current.get(paneItem.instanceId);
        return refs?.primary ?? refs?.histogram ?? refs?.secondary ?? refs?.tertiary ?? null;
      }

      return null;
    },
    [paneLegendItems],
  );

  const resolveTouchAxisTarget = useCallback(
    (clientX: number, clientY: number): TouchAxisTarget | null => {
      const wrapper = wrapperRef.current;
      const chart = chartRef.current;
      if (!wrapper || !container || !chart) {
        return null;
      }

      const wrapperRect = wrapper.getBoundingClientRect();
      const chartRect = container.getBoundingClientRect();
      const chartX = clientX - chartRect.left;
      const chartY = clientY - chartRect.top;
      const timeScale = chart.timeScale();
      const plotWidth = timeScale.width();
      if (plotWidth <= 0) {
        return null;
      }

      const activePriceScale = chart.priceScale(runtimeStatusOptions.priceScaleSide);
      const priceScaleWidth = activePriceScale.width();
      const plotLeft = runtimeStatusOptions.priceScaleSide === 'left' ? priceScaleWidth : 0;
      const plotRight = plotLeft + plotWidth;
      const paneHit = resolveTouchPaneHit(chart, chartRect, clientY);
      const isWithinPriceAxis =
        runtimeStatusOptions.priceScaleSide === 'left'
          ? chartX >= 0 && chartX <= plotLeft
          : chartX >= plotRight && chartX <= wrapperRect.width;

      if (paneHit && isWithinPriceAxis) {
        return {
          ...paneHit,
          axisKind: 'priceAxis',
          chartX,
          chartY,
          plotLeft,
          plotWidth,
          plotX: clampCoordinate(chartX - plotLeft, plotWidth),
          targetSeries: resolvePrimarySeriesForPane(paneHit.paneIndex),
        };
      }

      const timeAxisHeight = Math.max(timeScale.height(), TIME_AXIS_HIT_HEIGHT_PX);
      const isWithinTimeAxis = chartY >= chartRect.height - timeAxisHeight && chartY <= chartRect.height;
      const isWithinPlotArea = chartX >= plotLeft && chartX <= plotRight;

      if (isWithinTimeAxis && isWithinPlotArea) {
        return {
          paneIndex: 0,
          paneTop: 0,
          paneHeight: 0,
          paneLocalY: 0,
          axisKind: 'timeAxis',
          chartX,
          chartY,
          plotLeft,
          plotWidth,
          plotX: clampCoordinate(chartX - plotLeft, plotWidth),
          targetSeries: null,
        };
      }

      return null;
    },
    [container, resolvePrimarySeriesForPane, runtimeStatusOptions.priceScaleSide],
  );

  const resolvePointerContext = useCallback(
    (clientX: number, clientY: number): Partial<ChartContextMenuState> | null => {
      const wrapper = wrapperRef.current;
      const chart = chartRef.current;
      if (!wrapper || !container || !chart) {
        return null;
      }

      const wrapperRect = wrapper.getBoundingClientRect();
      const chartRect = container.getBoundingClientRect();
      const x = clientX - wrapperRect.left;
      const y = clientY - wrapperRect.top;
      const chartX = clientX - chartRect.left;
      const chartY = clientY - chartRect.top;
      const axisTarget = resolveTouchAxisTarget(clientX, clientY);
      const axisKind = axisTarget?.axisKind ?? null;
      const isTimeAxis = axisKind === 'timeAxis';
      const isPriceAxis = axisKind === 'priceAxis';

      let paneIndex = axisTarget?.paneIndex ?? 0;
      let paneLocalY = axisTarget?.paneLocalY ?? chartY;
      let paneHeight = axisTarget?.paneHeight ?? chartRect.height;
      if (!isTimeAxis && !isPriceAxis) {
        const paneHit = resolveTouchPaneHit(chart, chartRect, clientY);
        if (paneHit) {
          paneIndex = paneHit.paneIndex;
          paneLocalY = paneHit.paneLocalY;
          paneHeight = paneHit.paneHeight;
        }
      }

      const rawTime = chart.timeScale().coordinateToTime(chartX);
      const time = typeof rawTime === 'number' ? Number(rawTime) : resolveHoveredUnixTime();
      const priceSeries = axisTarget?.targetSeries ?? resolvePrimarySeriesForPane(paneIndex);
      const price =
        priceSeries && paneHeight > 0
          ? getPriceAxisAnchorValue(
              priceSeries.priceScale().getVisibleRange(),
              paneLocalY,
              paneHeight,
              chartDisplaySettings.invertScale,
            )
          : activeCandle?.close ?? null;
      const indicatorItem = paneIndex > 0 ? paneLegendItems[paneIndex - 1] ?? null : null;

      return {
        x,
        y,
        paneIndex,
        kind: isTimeAxis ? 'timeAxis' : isPriceAxis ? 'priceAxis' : paneIndex > 0 ? 'indicatorPane' : 'mainPane',
        price,
        time,
        indicatorInstanceId: indicatorItem?.instanceId ?? null,
        indicatorLabel: indicatorItem?.settingsLabel ?? null,
      };
    },
    [
      activeCandle?.close,
      chartDisplaySettings.invertScale,
      container,
      paneLegendItems,
      resolveHoveredUnixTime,
      resolvePrimarySeriesForPane,
      resolveTouchAxisTarget,
    ],
  );

  const clearLongPressPointerTracking = useCallback(() => {
    clearLongPressPointerTrackingRef.current?.();
    clearLongPressPointerTrackingRef.current = null;
  }, []);

  const clearTouchAxisDrag = useCallback(() => {
    clearTouchAxisDragRef.current?.();
    clearTouchAxisDragRef.current = null;
  }, []);

  const clearMouseInteraction = useCallback(() => {
    clearMouseInteractionRef.current?.();
    clearMouseInteractionRef.current = null;
    activeMouseInteractionRef.current = null;
  }, []);

  const clearLongPressTimer = useCallback(() => {
    if (longPressTimerRef.current !== null) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    clearLongPressPointerTracking();
  }, [clearLongPressPointerTracking]);

  const handleMarketEventsPanelDragStart = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (event.button !== 0) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      clearLongPressTimer();
      clearMarketEventsPanelPointerInteraction();
      suppressNextClickRef.current = true;

      const target = event.currentTarget;
      const pointerId = event.pointerId;
      const originClientX = event.clientX;
      const originClientY = event.clientY;
      const originRect = marketEventsPanelRect;

      target.setPointerCapture(pointerId);

      const handlePointerMove = (nativeEvent: PointerEvent) => {
        if (nativeEvent.pointerId !== pointerId) {
          return;
        }

        nativeEvent.preventDefault();
        setMarketEventsPanelRect(
          clampMarketEventsPanelToWrapper({
            ...originRect,
            x: originRect.x + (nativeEvent.clientX - originClientX),
            y: originRect.y + (nativeEvent.clientY - originClientY),
          }),
        );
      };

      const handlePointerEnd = (nativeEvent: PointerEvent) => {
        if (nativeEvent.pointerId !== pointerId) {
          return;
        }
        clearPointerTracking();
      };

      const clearPointerTracking = () => {
        window.removeEventListener('pointermove', handlePointerMove);
        window.removeEventListener('pointerup', handlePointerEnd);
        window.removeEventListener('pointercancel', handlePointerEnd);
        if (marketEventsPanelPointerCleanupRef.current === clearPointerTracking) {
          marketEventsPanelPointerCleanupRef.current = null;
        }
        if (target.hasPointerCapture(pointerId)) {
          target.releasePointerCapture(pointerId);
        }
      };

      marketEventsPanelPointerCleanupRef.current = clearPointerTracking;
      window.addEventListener('pointermove', handlePointerMove, { passive: false });
      window.addEventListener('pointerup', handlePointerEnd);
      window.addEventListener('pointercancel', handlePointerEnd);
    },
    [
      clampMarketEventsPanelToWrapper,
      clearLongPressTimer,
      clearMarketEventsPanelPointerInteraction,
      marketEventsPanelRect,
    ],
  );

  const handleMarketEventsPanelResizeStart = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>) => {
      if (event.button !== 0) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      clearLongPressTimer();
      clearMarketEventsPanelPointerInteraction();
      suppressNextClickRef.current = true;

      const target = event.currentTarget;
      const pointerId = event.pointerId;
      const originClientX = event.clientX;
      const originClientY = event.clientY;
      const originRect = marketEventsPanelRect;

      target.setPointerCapture(pointerId);

      const handlePointerMove = (nativeEvent: PointerEvent) => {
        if (nativeEvent.pointerId !== pointerId) {
          return;
        }

        nativeEvent.preventDefault();
        setMarketEventsPanelRect(
          clampMarketEventsPanelToWrapper({
            ...originRect,
            width: originRect.width + (nativeEvent.clientX - originClientX),
            height: originRect.height + (nativeEvent.clientY - originClientY),
          }),
        );
      };

      const handlePointerEnd = (nativeEvent: PointerEvent) => {
        if (nativeEvent.pointerId !== pointerId) {
          return;
        }
        clearPointerTracking();
      };

      const clearPointerTracking = () => {
        window.removeEventListener('pointermove', handlePointerMove);
        window.removeEventListener('pointerup', handlePointerEnd);
        window.removeEventListener('pointercancel', handlePointerEnd);
        if (marketEventsPanelPointerCleanupRef.current === clearPointerTracking) {
          marketEventsPanelPointerCleanupRef.current = null;
        }
        if (target.hasPointerCapture(pointerId)) {
          target.releasePointerCapture(pointerId);
        }
      };

      marketEventsPanelPointerCleanupRef.current = clearPointerTracking;
      window.addEventListener('pointermove', handlePointerMove, { passive: false });
      window.addEventListener('pointerup', handlePointerEnd);
      window.addEventListener('pointercancel', handlePointerEnd);
    },
    [
      clampMarketEventsPanelToWrapper,
      clearLongPressTimer,
      clearMarketEventsPanelPointerInteraction,
      marketEventsPanelRect,
    ],
  );

  const finishLongPressTimer = useCallback(() => {
    longPressTimerRef.current = null;
    clearLongPressPointerTracking();
  }, [clearLongPressPointerTracking]);

  useEffect(() => {
    return () => {
      clearLongPressTimer();
      clearTouchAxisDrag();
      clearMouseInteraction();
    };
  }, [clearLongPressTimer, clearMouseInteraction, clearTouchAxisDrag]);

  const startTouchAxisDrag = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>): boolean => {
      if (event.pointerType === 'mouse' || event.button !== 0) {
        return false;
      }

      if ((event.target as HTMLElement | null)?.closest('[data-chart-context-ignore="true"]')) {
        return false;
      }

      const wrapper = wrapperRef.current;
      const chart = chartRef.current;
      if (!wrapper || !container || !chart) {
        return false;
      }

      const chartRect = container.getBoundingClientRect();
      const axisTarget = resolveTouchAxisTarget(event.clientX, event.clientY);
      const axisKind = axisTarget?.axisKind ?? null;
      if (
        axisKind === null ||
        !shouldUseCustomAxisTouchDrag(event.pointerType, axisKind, chartDisplaySettings.scalePriceChartOnly)
      ) {
        return false;
      }

      const initialLogicalRange = axisKind === 'timeAxis' ? chart.timeScale().getVisibleLogicalRange() : null;
      const initialPriceRange =
        axisKind === 'priceAxis' ? axisTarget?.targetSeries?.priceScale().getVisibleRange() ?? null : null;
      const anchorLogical =
        axisKind === 'timeAxis' && axisTarget
          ? getTimeAxisAnchorLogicalValue(initialLogicalRange, axisTarget.plotX, axisTarget.plotWidth)
          : null;
      const anchorPrice =
        axisKind === 'priceAxis' && axisTarget
          ? getPriceAxisAnchorValue(
              initialPriceRange,
              axisTarget.paneLocalY,
              axisTarget.paneHeight,
              chartDisplaySettings.invertScale,
            )
          : null;

      if (
        !axisTarget ||
        (axisKind === 'priceAxis' && !axisTarget.targetSeries) ||
        (axisKind === 'timeAxis' && (!isFiniteRange(initialLogicalRange) || anchorLogical === null)) ||
        (axisKind === 'priceAxis' && (!isFiniteRange(initialPriceRange) || anchorPrice === null))
      ) {
        return false;
      }

      clearTouchAxisDrag();

      if (!isReplayModeRef.current && replayViewportLockRef.current) {
        replayUnlockIntentRef.current = true;
      }

      const pointerTarget = event.currentTarget;
      const dragState: TouchAxisDragState = {
        pointerId: event.pointerId,
        axisKind,
        paneIndex: axisTarget.paneIndex,
        originClientX: event.clientX,
        originClientY: event.clientY,
        plotLeft: axisTarget.plotLeft,
        paneTop: axisTarget.paneTop,
        targetSeries: axisTarget.targetSeries,
        anchorX: axisTarget.plotX,
        anchorY: axisTarget.paneLocalY,
        chartWidth: axisTarget.plotWidth,
        chartHeight: axisTarget.paneHeight,
        initialLogicalRange,
        initialPriceRange,
        anchorLogical,
        anchorPrice,
        dragging: false,
      };

      let hasPointerCapture = false;

      const clearDrag = () => {
        window.removeEventListener('pointermove', handlePointerMove);
        window.removeEventListener('pointerup', handlePointerEnd);
        window.removeEventListener('pointercancel', handlePointerEnd);
        if (hasPointerCapture && pointerTarget.hasPointerCapture(event.pointerId)) {
          pointerTarget.releasePointerCapture(event.pointerId);
        }
        if (clearTouchAxisDragRef.current === clearDrag) {
          clearTouchAxisDragRef.current = null;
        }
      };

      const handlePointerEnd = (nativeEvent: PointerEvent) => {
        if (nativeEvent.pointerId !== dragState.pointerId) {
          return;
        }

        clearLongPressTimer();
        clearDrag();
      };

      const handlePointerMove = (nativeEvent: PointerEvent) => {
        if (nativeEvent.pointerId !== dragState.pointerId) {
          return;
        }

        if (!dragState.dragging) {
          if (
            shouldStartTouchAxisDrag(
              dragState.axisKind,
              dragState.originClientX,
              dragState.originClientY,
              nativeEvent.clientX,
              nativeEvent.clientY,
            )
          ) {
            dragState.dragging = true;
            clearLongPressTimer();
            try {
              pointerTarget.setPointerCapture(event.pointerId);
              hasPointerCapture = true;
            } catch {
              // Ignore pointer capture failures from synthetic or unsupported targets.
            }
            if (dragState.axisKind === 'priceAxis') {
              dragState.targetSeries?.priceScale().setAutoScale(false);
              if (dragState.paneIndex === 0) {
                hasManualPriceScaleBootstrapRef.current = true;
                handleAutoFitChange(false);
              }
            }
          } else if (
            hasLongPressMovedBeyondThreshold(
              dragState.originClientX,
              dragState.originClientY,
              nativeEvent.clientX,
              nativeEvent.clientY,
            )
          ) {
            clearLongPressTimer();
            clearDrag();
            return;
          } else {
            return;
          }
        }

        nativeEvent.preventDefault();

        if (dragState.axisKind === 'priceAxis') {
          const targetPriceScale = dragState.targetSeries?.priceScale();
          if (!targetPriceScale) {
            return;
          }

          const nextRange = getPriceAxisRangeForTouchDrag(
            dragState.initialPriceRange,
            dragState.anchorPrice,
            dragState.anchorY,
            nativeEvent.clientY - chartRect.top - dragState.paneTop,
            dragState.chartHeight,
            chartDisplaySettings.invertScale,
          );
          if (!nextRange) {
            return;
          }
          targetPriceScale.setAutoScale(false);
          targetPriceScale.setVisibleRange(nextRange);
          if (dragState.paneIndex === 0) {
            lockedReplayPriceRangeRef.current = nextRange;
          }
          return;
        }

        const nextRange = getTimeAxisLogicalRangeForTouchDrag(
          dragState.initialLogicalRange,
          dragState.anchorLogical,
          dragState.anchorX,
          nativeEvent.clientX - chartRect.left - dragState.plotLeft,
          dragState.chartWidth,
        );
        if (!nextRange) {
          return;
        }

        shouldAutoFollowRef.current = false;
        visibleLogicalRangeRef.current = nextRange;
        chart.timeScale().setVisibleLogicalRange(nextRange);
      };

      clearTouchAxisDragRef.current = clearDrag;
      window.addEventListener('pointermove', handlePointerMove, { passive: false });
      window.addEventListener('pointerup', handlePointerEnd);
      window.addEventListener('pointercancel', handlePointerEnd);
      return true;
    },
    [
      chartDisplaySettings.invertScale,
      chartDisplaySettings.scalePriceChartOnly,
      clearLongPressTimer,
      clearTouchAxisDrag,
      container,
      handleAutoFitChange,
      resolveTouchAxisTarget,
    ],
  );

  const handleWrapperPointerDownCapture = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if ((event.target as HTMLElement | null)?.closest('[data-chart-context-ignore="true"]')) {
        return;
      }

      if (event.pointerType === 'mouse' && event.button === 0) {
        clearMouseInteraction();

        const startedOnAxis = isMousePointerOnChartAxis(event.clientX, event.clientY);
        activeMouseInteractionRef.current = {
          pointerId: event.pointerId,
          startedOnAxis,
        };

        const handlePointerEnd = (nativeEvent: PointerEvent) => {
          if (nativeEvent.pointerId !== event.pointerId) {
            return;
          }

          window.removeEventListener('pointerup', handlePointerEnd);
          window.removeEventListener('pointercancel', handlePointerEnd);
          if (clearMouseInteractionRef.current === clearPointerTracking) {
            clearMouseInteractionRef.current = null;
          }
          activeMouseInteractionRef.current = null;
        };

        const clearPointerTracking = () => {
          window.removeEventListener('pointerup', handlePointerEnd);
          window.removeEventListener('pointercancel', handlePointerEnd);
          if (clearMouseInteractionRef.current === clearPointerTracking) {
            clearMouseInteractionRef.current = null;
          }
          activeMouseInteractionRef.current = null;
        };

        clearMouseInteractionRef.current = clearPointerTracking;
        window.addEventListener('pointerup', handlePointerEnd);
        window.addEventListener('pointercancel', handlePointerEnd);
      }

      startTouchAxisDrag(event);
    },
    [clearMouseInteraction, isMousePointerOnChartAxis, startTouchAxisDrag],
  );

  const handleWrapperContextMenu = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      if ((event.target as HTMLElement | null)?.closest('[data-chart-context-ignore="true"]')) {
        return;
      }
      event.preventDefault();
      const nextState = resolvePointerContext(event.clientX, event.clientY);
      if (!nextState) {
        return;
      }
      openContextMenuAt(nextState);
    },
    [openContextMenuAt, resolvePointerContext],
  );

  const beginLongPressPointerTracking = useCallback(
    (pointerId: number, originClientX: number, originClientY: number) => {
      const trackingSessionId = longPressPointerTrackingSessionRef.current + 1;
      longPressPointerTrackingSessionRef.current = trackingSessionId;
      const handlePointerMove = (event: PointerEvent) => {
        if (event.pointerId !== pointerId) {
          return;
        }
        if (
          hasLongPressMovedBeyondThreshold(
            originClientX,
            originClientY,
            event.clientX,
            event.clientY,
          )
        ) {
          clearLongPressTimer();
        }
      };
      const clearPointerTracking = () => {
        window.removeEventListener('pointermove', handlePointerMove);
        if (longPressPointerTrackingSessionRef.current === trackingSessionId) {
          clearLongPressPointerTrackingRef.current = null;
        }
      };
      window.addEventListener('pointermove', handlePointerMove, { passive: true });
      clearLongPressPointerTrackingRef.current = clearPointerTracking;
    },
    [clearLongPressTimer],
  );

  const startWrapperLongPress = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (
        event.defaultPrevented ||
        event.pointerType === 'mouse' ||
        event.button !== 0 ||
        (event.target as HTMLElement | null)?.closest('[data-chart-context-ignore="true"]')
      ) {
        return;
      }
      clearLongPressTimer();
      beginLongPressPointerTracking(event.pointerId, event.clientX, event.clientY);
      const { clientX, clientY } = event;
      longPressTimerRef.current = window.setTimeout(() => {
        finishLongPressTimer();
        const nextState = resolvePointerContext(clientX, clientY);
        if (!nextState) {
          return;
        }
        suppressNextClickRef.current = true;
        openContextMenuAt(nextState);
      }, LONG_PRESS_DURATION_MS);
    },
    [
      beginLongPressPointerTracking,
      clearLongPressTimer,
      finishLongPressTimer,
      openContextMenuAt,
      resolvePointerContext,
    ],
  );

  const startAnchoredLongPress = useCallback(
    (event: ReactPointerEvent<HTMLElement>, partial: Partial<ChartContextMenuState>) => {
      if (event.pointerType === 'mouse' || event.button !== 0) {
        return;
      }
      clearLongPressTimer();
      const wrapper = wrapperRef.current;
      if (!wrapper) {
        return;
      }
      beginLongPressPointerTracking(event.pointerId, event.clientX, event.clientY);
      const rect = wrapper.getBoundingClientRect();
      const nextState: Partial<ChartContextMenuState> = {
        ...partial,
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      };
      longPressTimerRef.current = window.setTimeout(() => {
        finishLongPressTimer();
        suppressNextClickRef.current = true;
        openContextMenuAt(nextState);
      }, LONG_PRESS_DURATION_MS);
    },
    [
      beginLongPressPointerTracking,
      clearLongPressTimer,
      finishLongPressTimer,
      openContextMenuAt,
    ],
  );

  const handleCopyPrice = useCallback(async () => {
    const price = contextMenu.price ?? activeCandle?.close ?? null;
    if (price === null) {
      return;
    }
    await navigator.clipboard.writeText(resolveContextPriceLabel(price));
    pushToast('success', `Copied price ${resolveContextPriceLabel(price)}.`);
    closeContextMenu();
  }, [activeCandle?.close, closeContextMenu, contextMenu.price, pushToast, resolveContextPriceLabel]);

  const handlePaste = useCallback(async () => {
    const text = await navigator.clipboard.readText();
    const numericValue = Number.parseFloat(text.replace(/,/g, ''));
    if (!Number.isFinite(numericValue)) {
      pushToast('warning', 'Clipboard does not contain a valid price.');
      closeContextMenu();
      return;
    }
    const nextAlert: ChartAlertItem = {
      id: createRuntimeId('alert'),
      provider,
      symbol,
      kind: 'price',
      price: numericValue,
      time: contextMenu.time,
      indicatorLabel: null,
      createdAt: new Date().toISOString(),
      active: true,
    };
    setAlertItemsState((previous) => [nextAlert, ...previous]);
    pushToast('success', `Pasted price ${resolveContextPriceLabel(numericValue)} as a new alert.`);
    closeContextMenu();
  }, [closeContextMenu, contextMenu.time, provider, pushToast, resolveContextPriceLabel, symbol]);

  const handleAddAlert = useCallback(() => {
    const nextAlert: ChartAlertItem = {
      id: createRuntimeId('alert'),
      provider,
      symbol,
      kind: contextMenu.kind === 'indicatorPane' || contextMenu.kind === 'legendItem' ? 'indicator' : 'price',
      price: contextMenu.price,
      time: contextMenu.time,
      indicatorLabel: contextMenu.indicatorLabel,
      createdAt: new Date().toISOString(),
      active: true,
    };
    setAlertItemsState((previous) => [nextAlert, ...previous]);
    pushToast('success', `Added alert on ${contextMenu.indicatorLabel ?? symbol}.`);
    closeContextMenu();
  }, [closeContextMenu, contextMenu.indicatorLabel, contextMenu.kind, contextMenu.price, contextMenu.time, provider, pushToast, symbol]);

  const handleAddPaperOrder = useCallback(
    (side: 'buy' | 'sell', orderType: 'limit' | 'stop' | 'market') => {
      const fallbackPrice = activeCandle?.close ?? 0;
      const nextOrder: PaperTradingOrderItem = {
        id: createRuntimeId('order'),
        provider,
        symbol,
        side,
        orderType,
        price: contextMenu.price ?? fallbackPrice,
        quantity: 1,
        createdAt: new Date().toISOString(),
        status: 'working',
      };
      if (onCreatePaperOrder) {
        void onCreatePaperOrder({
          provider,
          symbol,
          side,
          orderType,
          price: contextMenu.price ?? fallbackPrice,
          quantity: 1,
        });
      } else {
        setPaperOrdersState((previous) => [nextOrder, ...previous]);
      }
      pushToast('success', `${side.toUpperCase()} ${orderType} order added to paper trading.`);
      closeContextMenu();
    },
    [activeCandle?.close, closeContextMenu, contextMenu.price, onCreatePaperOrder, provider, pushToast, setPaperOrdersState, symbol],
  );

  const handleSaveTemplate = useCallback(() => {
    const templateCount = chartTemplates.length + 1;
    const nextTemplate: ChartTemplateItem = {
      id: createRuntimeId('template'),
      provider,
      symbol,
      name: `Template ${templateCount}`,
      createdAt: new Date().toISOString(),
      chartSettings: chartDisplaySettings,
      statusOptions: runtimeStatusOptions,
    };
    setChartTemplates((previous) => [nextTemplate, ...previous].slice(0, 8));
    pushToast('success', `Saved ${nextTemplate.name}.`);
    closeContextMenu();
  }, [chartDisplaySettings, chartTemplates.length, closeContextMenu, provider, pushToast, runtimeStatusOptions, symbol]);

  const handleApplyTemplate = useCallback(
    (template: ChartTemplateItem) => {
      updateChartDisplaySettings(() => template.chartSettings);
      setRuntimeStatusOptions(template.statusOptions);
      pushToast('success', `Applied ${template.name}.`);
      closeContextMenu();
    },
    [closeContextMenu, pushToast, updateChartDisplaySettings],
  );

  const handleDeleteTemplate = useCallback(
    (templateId: string) => {
      setChartTemplates((previous) => previous.filter((item) => item.id !== templateId));
      pushToast('info', 'Removed chart template.');
      closeContextMenu();
    },
    [closeContextMenu, pushToast],
  );

  const handleResetChartView = useCallback(() => {
    const chart = chartRef.current;
    if (!chart) {
      return;
    }
    setManualTimeNavigationEnabled(false, 'reset-chart-view');
    shouldAutoFollowRef.current = true;
    chart.timeScale().resetTimeScale();
    chart.timeScale().scrollToRealTime();
    handleAutoFitChange(true);
    pushToast('success', 'Reset chart view.');
    closeContextMenu();
  }, [closeContextMenu, handleAutoFitChange, pushToast, setManualTimeNavigationEnabled]);

  const handleResetTimeScale = useCallback(() => {
    const chart = chartRef.current;
    if (!chart) {
      return;
    }
    chart.timeScale().resetTimeScale();
    pushToast('success', 'Reset time scale.');
    closeContextMenu();
  }, [closeContextMenu, pushToast]);

  const handleOpenLegendContextMenu = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>, item: OverlayLegendItem, paneIndex: number) => {
      event.preventDefault();
      const wrapper = wrapperRef.current;
      if (!wrapper) {
        return;
      }
      const rect = wrapper.getBoundingClientRect();
      openContextMenuAt({
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
        kind: 'legendItem',
        paneIndex,
        price: activeCandle?.close ?? null,
        time: resolveHoveredUnixTime(),
        indicatorInstanceId: item.instanceId,
        indicatorLabel: item.settingsLabel,
      });
    },
    [activeCandle?.close, openContextMenuAt, resolveHoveredUnixTime],
  );

  const handleOpenStatusContextMenu = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      event.preventDefault();
      const wrapper = wrapperRef.current;
      if (!wrapper) {
        return;
      }
      const rect = wrapper.getBoundingClientRect();
      openContextMenuAt({
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
        kind: 'statusLine',
        paneIndex: 0,
        price: activeCandle?.close ?? null,
        time: resolveHoveredUnixTime(),
      });
    },
    [activeCandle?.close, openContextMenuAt, resolveHoveredUnixTime],
  );

  const sessionBreakTimes = useMemo(
    () => buildSessionBreakTimes(candles, interval),
    [candles, interval],
  );

  const sessionBreakCoordinates = useMemo(() => {
    const chart = chartRef.current;
    if (!chart || !chartDisplaySettings.events.showSessionBreaks) {
      return [] as number[];
    }
    const coordinates: number[] = [];
    sessionBreakTimes.forEach((time) => {
      const coordinate = chart.timeScale().timeToCoordinate(time as Time);
      if (typeof coordinate === 'number') {
        coordinates.push(coordinate);
      }
    });
    return coordinates;
  }, [chartDisplaySettings.events.showSessionBreaks, sessionBreakTimes, visibleRightGapBars]);

  const lockedCursorCoordinate = useMemo(() => {
    const chart = chartRef.current;
    const lockedTime = runtimeStatusOptions.verticalCursorLockTime;
    if (!chart || lockedTime === null) {
      return null;
    }
    const coordinate = chart.timeScale().timeToCoordinate(lockedTime as Time);
    return typeof coordinate === 'number' ? coordinate : null;
  }, [runtimeStatusOptions.verticalCursorLockTime, visibleRightGapBars]);

  const removeDrawingsCount =
    alertItems.length + drawingItems.length + paperOrders.length + (runtimeStatusOptions.verticalCursorLockTime === null ? 0 : 1);
  const removeIndicatorsCount = overlayLegendItems.length;
  const workingPaperOrders = useMemo(
    () => paperOrders.filter((item) => item.status === 'working'),
    [paperOrders],
  );
  const filledPaperOrders = useMemo(
    () => paperOrders.filter((item) => item.status === 'filled'),
    [paperOrders],
  );

  const renderContextMenuButton = useCallback(
    (
      label: string,
      onClick: () => void,
      options?: {
        shortcut?: string;
        checked?: boolean;
        disabled?: boolean;
        danger?: boolean;
      },
    ) => (
      <button
        type="button"
        disabled={options?.disabled}
        className={`${
          options?.disabled
            ? 'flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-xs text-slate-500'
            : `${menuRowClassName} ${options?.danger ? 'text-rose-200 hover:bg-rose-500/15' : ''}`
        }`}
        onClick={onClick}
      >
        <span className="flex items-center gap-2">
          {typeof options?.checked === 'boolean' ? renderMenuCheck(options.checked) : null}
          <span>{label}</span>
        </span>
        {options?.shortcut ? <span className="text-[10px] text-slate-400">{options.shortcut}</span> : null}
      </button>
    ),
    [menuRowClassName],
  );

  const renderAlertsList = useCallback(
    (items: ChartAlertItem[], emptyLabel: string) => {
      if (items.length === 0) {
        return <div className="rounded border border-slate-800 bg-slate-950/50 px-3 py-4 text-sm text-slate-500">{emptyLabel}</div>;
      }

      return (
        <div className="space-y-2">
          {items.map((item) => (
            <div key={item.id} className="rounded-lg border border-slate-800 bg-slate-950/60 px-3 py-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-medium text-slate-100">{item.indicatorLabel ?? item.symbol}</div>
                  <div className="mt-1 text-xs text-slate-400">
                    {item.price !== null ? `Price ${resolveContextPriceLabel(item.price)}` : 'Indicator alert'}
                    {item.time !== null ? ` • ${new Date(item.time * 1000).toLocaleString()}` : ''}
                  </div>
                  <div className="mt-1 text-[11px] uppercase tracking-[0.14em] text-slate-500">
                    {item.symbol} • {item.active ? 'Active' : 'Paused'}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      updateAlertItem(item.id, (current) => ({
                        ...current,
                        active: !current.active,
                      }))
                    }
                    className="rounded border border-slate-700 bg-slate-900 px-2 py-1 text-[11px] text-slate-200 transition-colors hover:bg-slate-800"
                  >
                    {item.active ? 'Pause' : 'Resume'}
                  </button>
                  <button
                    type="button"
                    onClick={() => removeAlertItem(item.id)}
                    className="rounded border border-rose-500/50 bg-rose-500/10 px-2 py-1 text-[11px] text-rose-200 transition-colors hover:bg-rose-500/20"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    },
    [removeAlertItem, resolveContextPriceLabel, updateAlertItem],
  );

  const renderContextMenuContent = useCallback(() => {
    const contextPriceLabel = resolveContextPriceLabel(contextMenu.price ?? activeCandle?.close ?? 0);

    if (contextMenu.kind === 'priceAxis') {
      return (
        <>
          {renderContextMenuButton('Auto (fits data to screen)', () => handleAutoFitChange(!chartDisplaySettings.autoFit), {
            checked: chartDisplaySettings.autoFit,
          })}
          {renderContextMenuButton(
            'Lock price to bar ratio',
            () => setRuntimeStatusOptions((previous) => ({ ...previous, lockPriceToBarRatio: !previous.lockPriceToBarRatio })),
            { checked: runtimeStatusOptions.lockPriceToBarRatio },
          )}
          {renderContextMenuButton(
            'Scale price chart only',
            () =>
              updateChartDisplaySettings((previous) => ({
                ...previous,
                scalePriceChartOnly: !previous.scalePriceChartOnly,
              })),
            { checked: chartDisplaySettings.scalePriceChartOnly },
          )}
          {renderContextMenuButton(
            'Invert scale',
            () => updateChartDisplaySettings((previous) => ({ ...previous, invertScale: !previous.invertScale })),
            { checked: chartDisplaySettings.invertScale, shortcut: 'Alt + I' },
          )}
          <div className="my-2 border-t border-slate-800/90" />
          {renderContextMenuButton('Regular', () => updateChartDisplaySettings((previous) => ({ ...previous, scaleMode: 'regular' })), {
            checked: chartDisplaySettings.scaleMode === 'regular',
          })}
          {renderContextMenuButton('Percent', () => updateChartDisplaySettings((previous) => ({ ...previous, scaleMode: 'percent' })), {
            checked: chartDisplaySettings.scaleMode === 'percent',
            shortcut: 'Alt + P',
          })}
          {renderContextMenuButton(
            'Indexed to 100',
            () => updateChartDisplaySettings((previous) => ({ ...previous, scaleMode: 'indexedTo100' })),
            { checked: chartDisplaySettings.scaleMode === 'indexedTo100' },
          )}
          {renderContextMenuButton(
            'Logarithmic',
            () => updateChartDisplaySettings((previous) => ({ ...previous, scaleMode: 'logarithmic' })),
            { checked: chartDisplaySettings.scaleMode === 'logarithmic', shortcut: 'Alt + L' },
          )}
          <div className="my-2 border-t border-slate-800/90" />
          {renderContextMenuButton(
            runtimeStatusOptions.priceScaleSide === 'right' ? 'Move scale to left' : 'Move scale to right',
            () =>
              setRuntimeStatusOptions((previous) => ({
                ...previous,
                priceScaleSide: previous.priceScaleSide === 'right' ? 'left' : 'right',
              })),
          )}
          {renderContextMenuButton('Labels', () =>
            setContextMenu((previous) => ({
              ...previous,
              submenu: previous.submenu === 'labels' ? null : 'labels',
            })),
          )}
          {contextMenu.submenu === 'labels' ? (
            <div className="space-y-1 rounded bg-slate-950/50 p-2">
              {renderContextMenuButton(
                'Crosshair labels',
                () => updateChartDisplaySettings((previous) => ({
                  ...previous,
                  labels: {
                    ...previous.labels,
                    crosshairLabels: !previous.labels.crosshairLabels,
                  },
                })),
                { checked: chartDisplaySettings.labels.crosshairLabels },
              )}
              {renderContextMenuButton(
                'Price scale ticks',
                () => updateChartDisplaySettings((previous) => ({
                  ...previous,
                  labels: {
                    ...previous.labels,
                    scaleTicks: !previous.labels.scaleTicks,
                  },
                })),
                { checked: chartDisplaySettings.labels.scaleTicks },
              )}
            </div>
          ) : null}
          {renderContextMenuButton('Lines', () =>
            setContextMenu((previous) => ({
              ...previous,
              submenu: previous.submenu === 'lines' ? null : 'lines',
            })),
          )}
          {contextMenu.submenu === 'lines' ? (
            <div className="space-y-1 rounded bg-slate-950/50 p-2">
              {renderContextMenuButton(
                'Horizontal grid',
                () => updateChartDisplaySettings((previous) => ({
                  ...previous,
                  lines: {
                    ...previous.lines,
                    horizontalGrid: !previous.lines.horizontalGrid,
                  },
                })),
                { checked: chartDisplaySettings.lines.horizontalGrid },
              )}
              {renderContextMenuButton(
                'Vertical grid',
                () => updateChartDisplaySettings((previous) => ({
                  ...previous,
                  lines: {
                    ...previous.lines,
                    verticalGrid: !previous.lines.verticalGrid,
                  },
                })),
                { checked: chartDisplaySettings.lines.verticalGrid },
              )}
              {renderContextMenuButton(
                'Crosshair lines',
                () => updateChartDisplaySettings((previous) => ({
                  ...previous,
                  lines: {
                    ...previous.lines,
                    crosshairLines: !previous.lines.crosshairLines,
                  },
                })),
                { checked: chartDisplaySettings.lines.crosshairLines },
              )}
            </div>
          ) : null}
          <div className="my-2 border-t border-slate-800/90" />
          {renderContextMenuButton('More settings...', () => openMoreSettings('scalesAndLines'))}
        </>
      );
    }

    if (contextMenu.kind === 'timeAxis') {
      return (
        <>
          {renderContextMenuButton('Reset time scale', handleResetTimeScale, { shortcut: 'Ctrl + Alt + Q' })}
          {renderContextMenuButton('Time zone', () =>
            setContextMenu((previous) => ({
              ...previous,
              submenu: previous.submenu === 'timeZone' ? null : 'timeZone',
            })),
          )}
          {contextMenu.submenu === 'timeZone' ? (
            <div className="max-h-64 space-y-1 overflow-y-auto rounded bg-slate-950/50 p-2">
              {SYMBOL_TIMEZONE_OPTIONS.map((option) =>
                renderContextMenuButton(
                  option.label,
                  () => updateChartDisplaySettings((previous) => ({
                    ...previous,
                    symbol: {
                      ...previous.symbol,
                      timezone: option.value,
                    },
                  })),
                  { checked: chartDisplaySettings.symbol.timezone === option.value },
                ),
              )}
            </div>
          ) : null}
          {renderContextMenuButton(
            'Session breaks',
            () => updateChartDisplaySettings((previous) => ({
              ...previous,
              events: {
                ...previous.events,
                showSessionBreaks: !previous.events.showSessionBreaks,
              },
            })),
            { checked: chartDisplaySettings.events.showSessionBreaks },
          )}
          <div className="my-2 border-t border-slate-800/90" />
          {renderContextMenuButton('More settings...', () => openMoreSettings('events'))}
        </>
      );
    }

    if (contextMenu.kind === 'statusLine' || contextMenu.kind === 'legendItem') {
      return (
        <>
          {renderContextMenuButton('Symbol title', () => updateChartDisplaySettings((previous) => ({
            ...previous,
            statusLine: {
              ...previous.statusLine,
              showSymbol: !previous.statusLine.showSymbol,
            },
          })), { checked: chartDisplaySettings.statusLine.showSymbol })}
          {renderContextMenuButton(
            'Open market status',
            handleShowMarketStatusToggle,
            { checked: runtimeStatusOptions.showMarketStatus },
          )}
          {renderContextMenuButton('Chart values', () => updateChartDisplaySettings((previous) => ({
            ...previous,
            statusLine: {
              ...previous.statusLine,
              showOHLC: !previous.statusLine.showOHLC,
            },
          })), { checked: chartDisplaySettings.statusLine.showOHLC })}
          {renderContextMenuButton('Bar change values', () => updateChartDisplaySettings((previous) => ({
            ...previous,
            statusLine: {
              ...previous.statusLine,
              showChange: !previous.statusLine.showChange,
            },
          })), { checked: chartDisplaySettings.statusLine.showChange })}
          {renderContextMenuButton('Volume', () => updateChartDisplaySettings((previous) => ({
            ...previous,
            statusLine: {
              ...previous.statusLine,
              showVolume: !previous.statusLine.showVolume,
            },
          })), { checked: chartDisplaySettings.statusLine.showVolume })}
          {renderContextMenuButton('Last day change values', () => updateChartDisplaySettings((previous) => ({
            ...previous,
            statusLine: {
              ...previous.statusLine,
              showLastDayChange: !previous.statusLine.showLastDayChange,
            },
          })), { checked: chartDisplaySettings.statusLine.showLastDayChange })}
          <div className="my-2 border-t border-slate-800/90" />
          {renderContextMenuButton(
            'Show Buy/Sell buttons',
            () => setRuntimeStatusOptions((previous) => ({ ...previous, showBuySellButtons: !previous.showBuySellButtons })),
            { checked: runtimeStatusOptions.showBuySellButtons },
          )}
          {renderContextMenuButton(
            'Indicator titles',
            handleShowIndicatorTitlesToggle,
            { checked: runtimeStatusOptions.showIndicatorTitles },
          )}
          {renderContextMenuButton(
            'Indicator arguments',
            () => updateChartDisplaySettings((previous) => ({
              ...previous,
              statusLine: {
                ...previous.statusLine,
                showIndicatorArguments: !previous.statusLine.showIndicatorArguments,
              },
            })),
            { checked: chartDisplaySettings.statusLine.showIndicatorArguments },
          )}
          <div className="my-2 border-t border-slate-800/90" />
          {renderContextMenuButton('Settings...', () => openMoreSettings('statusLine'))}
        </>
      );
    }

    return (
      <>
        {renderContextMenuButton('Reset chart view', handleResetChartView, { shortcut: 'Alt + R' })}
        <div className="my-2 border-t border-slate-800/90" />
        {renderContextMenuButton(`Copy price ${contextPriceLabel}`, () => {
          void handleCopyPrice();
        })}
        {renderContextMenuButton('Paste', () => {
          void handlePaste();
        }, { shortcut: 'Ctrl + V' })}
        <div className="my-2 border-t border-slate-800/90" />
        {renderContextMenuButton(
          `Add alert on ${contextMenu.indicatorLabel ?? symbol}${contextMenu.price !== null ? ` at ${contextPriceLabel}` : ''}`,
          handleAddAlert,
          { shortcut: 'Alt + A' },
        )}
        {contextMenu.kind === 'mainPane'
          ? renderContextMenuButton(`Sell 1 ${symbol} @ ${contextPriceLabel} limit`, () => handleAddPaperOrder('sell', 'limit'))
          : null}
        {contextMenu.kind === 'mainPane'
          ? renderContextMenuButton(`Buy 1 ${symbol} @ ${contextPriceLabel} stop`, () => handleAddPaperOrder('buy', 'stop'))
          : null}
        {contextMenu.kind === 'mainPane'
          ? renderContextMenuButton(`Add order on ${symbol} @ ${contextPriceLabel}`, () => handleAddPaperOrder('buy', 'market'))
          : null}
        <div className="my-2 border-t border-slate-800/90" />
        {renderContextMenuButton(
          'Lock vertical cursor line by time',
          () => {
            setRuntimeStatusOptions((previous) => ({
              ...previous,
              verticalCursorLockTime:
                previous.verticalCursorLockTime === contextMenu.time ? null : (contextMenu.time ?? resolveHoveredUnixTime()),
            }));
            pushToast('info', 'Updated locked vertical cursor line.');
            closeContextMenu();
          },
          {
            checked:
              runtimeStatusOptions.verticalCursorLockTime !== null &&
              runtimeStatusOptions.verticalCursorLockTime === (contextMenu.time ?? resolveHoveredUnixTime()),
          },
        )}
        {renderContextMenuButton('Table view', () => {
          setIsTableViewOpen(true);
          closeContextMenu();
        })}
        {renderContextMenuButton('Object tree', () => {
          setIsObjectTreeOpen(true);
          closeContextMenu();
        })}
        {renderContextMenuButton('Chart template', () =>
          setContextMenu((previous) => ({
            ...previous,
            submenu: previous.submenu === 'chartTemplate' ? null : 'chartTemplate',
          })),
        )}
        {contextMenu.submenu === 'chartTemplate' ? (
          <div className="space-y-1 rounded bg-slate-950/50 p-2">
            {renderContextMenuButton('Save new template', handleSaveTemplate)}
            {chartTemplates.length === 0 ? (
              <div className="px-2 py-1 text-[11px] text-slate-500">No saved templates yet.</div>
            ) : null}
            {chartTemplates.map((template) => (
              <div key={template.id} className="rounded border border-slate-800/80 bg-slate-900/70 p-1">
                {renderContextMenuButton(template.name, () => handleApplyTemplate(template))}
                {renderContextMenuButton('Delete', () => handleDeleteTemplate(template.id), { danger: true })}
              </div>
            ))}
          </div>
        ) : null}
        <div className="my-2 border-t border-slate-800/90" />
        {renderContextMenuButton(`Remove ${removeDrawingsCount} drawings`, () => {
          removeTransientChartObjects();
          closeContextMenu();
        }, { danger: removeDrawingsCount > 0 })}
        {renderContextMenuButton(`Remove ${removeIndicatorsCount} indicators`, () => {
          removeAllIndicators();
          closeContextMenu();
        }, { danger: removeIndicatorsCount > 0 })}
        <div className="my-2 border-t border-slate-800/90" />
        {renderContextMenuButton('Settings...', () => openMoreSettings(contextMenu.kind === 'indicatorPane' ? 'alerts' : 'symbol'))}
      </>
    );
  }, [
    activeCandle?.close,
    chartDisplaySettings,
    chartTemplates,
    closeContextMenu,
    contextMenu,
    handleAddAlert,
    handleAddPaperOrder,
    handleAutoFitChange,
    handleCopyPrice,
    handleDeleteTemplate,
    handlePaste,
    handleResetChartView,
    handleResetTimeScale,
    handleSaveTemplate,
    openMoreSettings,
    pushToast,
    removeAllIndicators,
    removeDrawingsCount,
    removeIndicatorsCount,
    removeTransientChartObjects,
    renderContextMenuButton,
    resolveContextPriceLabel,
    resolveHoveredUnixTime,
    runtimeStatusOptions,
    symbol,
    updateChartDisplaySettings,
  ]);

  const renderDrawingElement = useCallback(
    (drawing: ChartDrawingItem, options?: { draft?: boolean }) => {
      if (!drawing.visible && !options?.draft) {
        return null;
      }

      const dashArray = toSvgDashArray(drawing.style.lineStyle);
      const projectedAnchors = drawing.anchors.map(projectDrawingPoint);
      const anchors = projectedAnchors.filter((point): point is ScreenPoint => point !== null);
      if (anchors.length === 0) {
        return null;
      }

      const isSelected = drawing.id === selectedDrawingId && !options?.draft;
      const showHandles = isSelected && !drawing.locked;
      const strokeColor = options?.draft ? '#fbbf24' : drawing.style.strokeColor;
      const fillColor = drawing.style.fillColor ?? 'transparent';
      const pointerProps = options?.draft
        ? {}
        : {
            onPointerDown: (event: ReactPointerEvent<SVGElement>) => {
              handleDrawingBodyPointerDown(event, drawing);
            },
          };

      const renderHandles = () =>
        showHandles
          ? anchors.map((point, index) => (
              <circle
                key={`${drawing.id}-handle-${index}`}
                cx={point.x}
                cy={point.y}
                r={5}
                fill="#f8fafc"
                stroke="#0f172a"
                strokeWidth={1.5}
                style={{ pointerEvents: 'auto', cursor: 'grab' }}
                onPointerDown={(event) => handleDrawingHandlePointerDown(event, drawing.id, index)}
              />
            ))
          : null;

      if (drawing.kind === 'horizontalLine' || drawing.kind === 'horizontalRay') {
        const point = projectedAnchors[0];
        if (!point) {
          return null;
        }
        const x1 = drawing.kind === 'horizontalLine' ? 0 : point.x;
        const x2 = overlayWidth;
        return (
          <g key={drawing.id}>
            <line x1={x1} y1={point.y} x2={x2} y2={point.y} stroke={strokeColor} strokeWidth={drawing.style.strokeWidth} strokeDasharray={dashArray} />
             <line x1={x1} y1={point.y} x2={x2} y2={point.y} stroke="transparent" strokeWidth={18} style={{ pointerEvents: options?.draft ? 'none' : 'stroke', cursor: drawing.locked ? 'pointer' : 'grab' }} {...pointerProps} />
            {renderHandles()}
          </g>
        );
      }

      if (drawing.kind === 'text') {
        const point = projectedAnchors[0];
        if (!point) {
          return null;
        }
        return (
          <g key={drawing.id} {...pointerProps}>
            <text
              x={point.x}
              y={point.y}
              fill={drawing.style.textColor}
              fontSize={drawing.style.textSize}
              fontWeight={600}
               style={{ pointerEvents: options?.draft ? 'none' : 'auto', cursor: drawing.locked ? 'pointer' : 'grab', userSelect: 'none' }}
            >
              {drawing.text ?? 'Text'}
            </text>
            {renderHandles()}
          </g>
        );
      }

      const first = projectedAnchors[0];
      const second = projectedAnchors[1];
      if (!first || !second) {
        return null;
      }
      const deltaX = second.x - first.x;
      const deltaY = second.y - first.y;
      const slope = Math.abs(deltaX) < 0.001 ? null : deltaY / deltaX;

      if (drawing.kind === 'trendLine' || drawing.kind === 'ray' || drawing.kind === 'extendedLine') {
        let start = first;
        let end = second;

        if (slope !== null && drawing.kind !== 'trendLine') {
          if (drawing.kind === 'ray') {
            const targetX = second.x >= first.x ? overlayWidth : 0;
            end = { x: targetX, y: first.y + slope * (targetX - first.x) };
          } else {
            start = { x: 0, y: first.y + slope * (0 - first.x) };
            end = { x: overlayWidth, y: first.y + slope * (overlayWidth - first.x) };
          }
        }

        return (
          <g key={drawing.id}>
            <line x1={start.x} y1={start.y} x2={end.x} y2={end.y} stroke={strokeColor} strokeWidth={drawing.style.strokeWidth} strokeDasharray={dashArray} />
             <line x1={start.x} y1={start.y} x2={end.x} y2={end.y} stroke="transparent" strokeWidth={18} style={{ pointerEvents: options?.draft ? 'none' : 'stroke', cursor: drawing.locked ? 'pointer' : 'grab' }} {...pointerProps} />
            {renderHandles()}
          </g>
        );
      }

      if (drawing.kind === 'rectangle') {
        const left = Math.min(first.x, second.x);
        const top = Math.min(first.y, second.y);
        const width = Math.abs(second.x - first.x);
        const height = Math.abs(second.y - first.y);
        return (
          <g key={drawing.id}>
            <rect x={left} y={top} width={width} height={height} fill={fillColor} stroke={strokeColor} strokeWidth={drawing.style.strokeWidth} strokeDasharray={dashArray} />
             <rect x={left} y={top} width={width} height={height} fill="transparent" stroke="transparent" strokeWidth={12} style={{ pointerEvents: options?.draft ? 'none' : 'all', cursor: drawing.locked ? 'pointer' : 'grab' }} {...pointerProps} />
            {renderHandles()}
          </g>
        );
      }

      if (drawing.kind === 'parallelChannel') {
        const third = projectedAnchors[2] ?? (options?.draft ? drawingDraft?.previewPoint : null);
        if (!third) {
          return null;
        }
        const fourth = { x: third.x + (second.x - first.x), y: third.y + (second.y - first.y) };
        const points = `${first.x},${first.y} ${second.x},${second.y} ${fourth.x},${fourth.y} ${third.x},${third.y}`;
        return (
          <g key={drawing.id}>
            <polygon points={points} fill={fillColor} stroke={strokeColor} strokeWidth={drawing.style.strokeWidth} strokeDasharray={dashArray} />
            <line x1={first.x} y1={first.y} x2={second.x} y2={second.y} stroke={strokeColor} strokeWidth={drawing.style.strokeWidth} strokeDasharray={dashArray} />
            <line x1={third.x} y1={third.y} x2={fourth.x} y2={fourth.y} stroke={strokeColor} strokeWidth={drawing.style.strokeWidth} strokeDasharray={dashArray} />
             <polygon points={points} fill="transparent" stroke="transparent" strokeWidth={12} style={{ pointerEvents: options?.draft ? 'none' : 'all', cursor: drawing.locked ? 'pointer' : 'grab' }} {...pointerProps} />
            {renderHandles()}
          </g>
        );
      }

      if (drawing.kind === 'fibRetracement') {
        const levels = buildFibPriceLevels(drawing.anchors[0].price, drawing.anchors[1].price)
          .map((level) => ({
            ...level,
            y: projectDrawingPoint({ time: drawing.anchors[0].time, price: level.price })?.y ?? null,
          }))
          .filter((level): level is { ratio: number; price: number; y: number } => level.y !== null);
        const left = Math.min(first.x, second.x);
        const right = Math.max(first.x, second.x);
        return (
          <g key={drawing.id} {...pointerProps}>
            <rect x={left} y={Math.min(first.y, second.y)} width={Math.max(1, right - left)} height={Math.abs(second.y - first.y)} fill={fillColor} opacity={0.45} style={{ pointerEvents: 'none' }} />
            {levels.map((level) => (
              <g key={`${drawing.id}-${level.ratio}`}>
                <line x1={left} y1={level.y} x2={right} y2={level.y} stroke={strokeColor} strokeWidth={drawing.style.strokeWidth} strokeDasharray={dashArray} />
                <text x={right + 6} y={level.y - 2} fill={drawing.style.textColor} fontSize={10} style={{ pointerEvents: 'none', userSelect: 'none' }}>
                  {`${(level.ratio * 100).toFixed(1)}%`}
                </text>
              </g>
            ))}
             <rect x={left} y={Math.min(first.y, second.y)} width={Math.max(1, right - left)} height={Math.abs(second.y - first.y)} fill="transparent" style={{ pointerEvents: options?.draft ? 'none' : 'all', cursor: drawing.locked ? 'pointer' : 'grab' }} />
            {renderHandles()}
          </g>
        );
      }

      return null;
    },
    [drawingDraft?.previewPoint, handleDrawingBodyPointerDown, handleDrawingHandlePointerDown, overlayWidth, projectDrawingPoint, selectedDrawingId],
  );

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-slate-300">
        Loading candles...
      </div>
    );
  }

  if (errorMessage && candles.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 text-center text-sm text-rose-300">
        <span>Failed to load data.</span>
        <span className="text-xs text-rose-200">{errorMessage}</span>
      </div>
    );
  }

  if (!candles.length) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-slate-400">
        No data available for the selected interval.
      </div>
    );
  }

  return (
    <div
      ref={wrapperRef}
      className="relative h-full w-full"
      style={{ touchAction: 'none', overscrollBehavior: 'contain' }}
      onContextMenu={handleWrapperContextMenu}
      onPointerDownCapture={handleWrapperPointerDownCapture}
      onPointerDown={startWrapperLongPress}
      onPointerUp={clearLongPressTimer}
      onPointerLeave={clearLongPressTimer}
      onPointerCancel={clearLongPressTimer}
      onClick={() => {
        if (suppressNextClickRef.current) {
          suppressNextClickRef.current = false;
          return;
        }
        if (activeDrawingTool === 'cursor') {
          setSelectedDrawingId(null);
        }
      }}
    >
      <div ref={containerRef} className="h-full w-full" style={{ touchAction: 'none', overscrollBehavior: 'contain' }} />
      <div
        ref={mobileDrawingToolbarPanelRef}
        data-chart-context-ignore="true"
        className={`absolute border border-slate-700/80 bg-slate-950/96 p-1.5 shadow-[0_14px_30px_rgba(2,8,24,0.55)] backdrop-blur ${
          isMobileDrawingToolbar ? 'overflow-x-auto rounded-2xl' : 'overflow-visible rounded-[18px]'
        } ${isMobileOverlayActive ? 'z-30' : 'z-[65]'}`}
        style={{
          left: `${resolvedMobileDrawingToolbarPosition.x}px`,
          top: `${resolvedMobileDrawingToolbarPosition.y}px`,
          maxWidth: isMobileDrawingToolbar ? `calc(100% - ${MOBILE_DRAWING_TOOLBAR_MARGIN_PX * 2}px)` : undefined,
        }}
      >
        <ChartDrawingToolbar
          activeTool={activeDrawingTool}
          onSelectTool={handleDrawingToolSelect}
          orientation={isMobileDrawingToolbar ? 'horizontal' : 'vertical'}
          showDragHandle
          collapsed={isMobileDrawingToolbarCollapsed}
          onDragHandlePointerDown={handleMobileDrawingToolbarDragStart}
          className="border-0 bg-transparent p-0 shadow-none"
        />
      </div>
      {overlayWidth > 0 && overlayHeight > 0 ? (
        <svg className="pointer-events-none absolute inset-0 z-[58] h-full w-full" viewBox={`0 0 ${overlayWidth} ${overlayHeight}`} preserveAspectRatio="none">
          {drawingItems.map((drawing) => renderDrawingElement(drawing))}
          {drawingPreviewItem ? renderDrawingElement(drawingPreviewItem, { draft: true }) : null}
        </svg>
      ) : null}
      {activeDrawingTool !== 'cursor' || drawingDraft ? (
        <div
          data-chart-context-ignore="true"
          className="absolute inset-0 z-[57]"
          style={{ cursor: 'crosshair' }}
          onPointerDown={handleDrawingSurfacePointerDown}
          onPointerMove={handleDrawingSurfacePointerMove}
        />
      ) : null}
      {selectedDrawing ? (
        <div
          data-chart-context-ignore="true"
          className="absolute z-[66] flex items-center gap-1 rounded-2xl border border-slate-700/80 bg-slate-950/96 p-1.5 shadow-[0_12px_24px_rgba(2,8,24,0.48)] backdrop-blur"
          style={{ left: `${leftOverlayInsetPx}px`, bottom: '12px' }}
        >
          <span className="px-2 text-[11px] font-medium text-slate-200">{getDrawingToolLabel(selectedDrawing.kind)}</span>
          <button
            type="button"
            onClick={toggleSelectedDrawingVisibility}
            className="rounded-lg border border-transparent px-2 py-1 text-[11px] text-slate-200 transition-colors hover:border-slate-700 hover:bg-slate-900"
          >
            {selectedDrawing.visible ? 'Hide' : 'Show'}
          </button>
          <button
            type="button"
            onClick={toggleSelectedDrawingLock}
            className="rounded-lg border border-transparent px-2 py-1 text-[11px] text-slate-200 transition-colors hover:border-slate-700 hover:bg-slate-900"
          >
            {selectedDrawing.locked ? 'Unlock' : 'Lock'}
          </button>
          <button
            type="button"
            onClick={deleteSelectedDrawing}
            className="rounded-lg border border-rose-500/40 bg-rose-500/10 px-2 py-1 text-[11px] text-rose-100 transition-colors hover:bg-rose-500/20"
          >
            Delete
          </button>
        </div>
      ) : null}
      {statusLineContent ? (
        <div
          ref={statusLineRef}
          onContextMenu={handleOpenStatusContextMenu}
          onPointerDown={(event) =>
            startAnchoredLongPress(event, {
              kind: 'statusLine',
              paneIndex: 0,
              price: activeCandle?.close ?? null,
              time: resolveHoveredUnixTime(),
            })
          }
          onPointerUp={clearLongPressTimer}
          onPointerLeave={clearLongPressTimer}
          onPointerCancel={clearLongPressTimer}
          className={`absolute top-3 z-40 flex max-w-[calc(100%-1.5rem)] rounded-md border border-slate-700/40 px-2 py-1 text-[15px] font-medium leading-tight text-slate-100 ${
            isStatusLineStacked ? 'flex-col items-start gap-y-1' : 'items-center gap-x-2'
          }`}
          style={{
            left: `${leftOverlayInsetPx}px`,
            backgroundColor: `rgba(2, 6, 23, ${0.08 + statusBackgroundOpacity / 120})`,
          }}
        >
          <div
            ref={statusLineHeaderRef}
            className={`flex min-w-0 max-w-full items-center ${isStatusLineStacked ? 'w-full gap-x-2' : 'shrink gap-x-2'}`}
          >
            {statusLineContent.headerParts.length > 0 ? (
              <span className="block min-w-0 truncate">
                <span ref={statusLineHeaderTextRef} className="inline-block whitespace-nowrap">
                  {statusLineContent.headerText}
                </span>
              </span>
            ) : null}
            {runtimeStatusOptions.showMarketStatus && statusIndicator && statusLineContent.headerParts.length > 0 ? (
              <span ref={statusIndicatorPanelRef} className="relative inline-flex items-center">
                <button
                  type="button"
                  data-chart-context-ignore="true"
                  onClick={() => setIsStatusIndicatorOpen((previous) => !previous)}
                  className={`inline-flex h-2.5 w-2.5 shrink-0 rounded-full ring-1 ring-white/15 transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-slate-200/70 focus:ring-offset-2 focus:ring-offset-slate-950 ${
                    statusIndicator.state === 'live'
                      ? 'bg-emerald-400 shadow-[0_0_12px_rgba(52,211,153,0.75)]'
                      : statusIndicator.state === 'marketClosed'
                        ? 'bg-amber-400 shadow-[0_0_12px_rgba(251,191,36,0.7)]'
                        : statusIndicator.state === 'reconnecting'
                          ? 'status-indicator-reconnecting bg-emerald-400 shadow-[0_0_12px_rgba(52,211,153,0.75)]'
                          : 'bg-rose-400 shadow-[0_0_12px_rgba(251,113,133,0.72)]'
                  }`}
                  aria-label={statusIndicator.label}
                  aria-expanded={isStatusIndicatorOpen}
                  aria-haspopup="dialog"
                  title={statusIndicator.label}
                />
                {isStatusIndicatorOpen && statusIndicatorDetails ? (
                  <div className="absolute left-1/2 top-[calc(100%+0.6rem)] z-50 w-64 -translate-x-1/2 rounded-lg border border-slate-700/80 bg-slate-950/95 p-3 text-left shadow-2xl backdrop-blur">
                    <div className="mb-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                      {statusIndicatorDetails.title}
                    </div>
                    <div className="text-xs leading-5 text-slate-100">{statusIndicatorDetails.description}</div>
                  </div>
                ) : null}
              </span>
            ) : null}
          </div>
          {!statusIndicator &&
          statusLineContent.headerParts.length > 0 &&
          statusLineContent.valueParts.length > 0 &&
          !isStatusLineStacked ? (
            <span ref={statusLineSeparatorRef} className="text-slate-400">
              |
            </span>
          ) : null}
          {statusLineContent.valueParts.length > 0 ? (
            <div
              ref={statusLineValuesRef}
              className={`min-w-0 max-w-full ${isStatusLineStacked ? 'w-full' : 'flex-1'}`}
            >
              <span className="block min-w-0 truncate">
                <span ref={statusLineValuesTextRef} className="inline-block whitespace-nowrap">
                  {statusLineContent.valuesText}
                </span>
              </span>
            </div>
          ) : null}
          {statusLineContent.valueParts.length === 0 ? <div ref={statusLineValuesRef} className="hidden" /> : null}
        </div>
      ) : null}
      {staleMessage ? (
        <div
          className={`pointer-events-none absolute z-20 max-w-[min(28rem,calc(100%-1.5rem))] rounded-md border px-3 py-2 text-xs font-medium ${
            staleTone === 'offline'
              ? 'border-rose-400/45 bg-rose-500/12 text-rose-100'
              : 'border-amber-400/45 bg-amber-500/12 text-amber-100'
          }`}
          style={{ left: `${leftOverlayInsetPx}px`, top: `${staleMessageTopPx}px` }}
        >
          {staleMessage}
        </div>
      ) : null}
      {hasEnabledChartEventPreview && (eventPreviewItems.length > 0 || eventsLoading || eventsErrorMessage) ? (
        <div
          data-chart-context-ignore="true"
          className="absolute z-[60] flex flex-col overflow-hidden rounded-md border border-slate-700/70 bg-slate-950/90 text-xs text-slate-200 shadow-2xl backdrop-blur-sm"
          style={{
            left: `${marketEventsPanelRect.x + drawingToolbarInsetLeftPx}px`,
            top: `${marketEventsPanelRect.y}px`,
            width: `${marketEventsPanelRect.width}px`,
            height: `${marketEventsPanelRect.height}px`,
          }}
        >
          <div
            data-chart-context-ignore="true"
            onPointerDown={handleMarketEventsPanelDragStart}
            className="flex cursor-move items-center justify-between gap-2 border-b border-slate-700/70 px-3 py-2"
          >
            <span className="font-medium text-slate-100 select-none">Market events</span>
            <button
              type="button"
              data-chart-context-ignore="true"
              onPointerDown={(event) => event.stopPropagation()}
              onClick={() => {
                void onRefreshEvents?.();
              }}
              className="rounded border border-slate-600 px-1.5 py-0.5 text-[10px] text-slate-300 transition-colors hover:bg-slate-800"
            >
              Refresh
            </button>
          </div>
          {eventsErrorMessage ? (
            <div className="mx-2 mt-2 rounded border border-rose-500/40 bg-rose-500/10 px-2 py-1 text-[11px] text-rose-200">
              {eventsErrorMessage}
            </div>
          ) : null}
          {eventsLoading && eventPreviewItems.length === 0 ? (
            <div className="px-3 py-2 text-[11px] text-slate-400">Loading latest items...</div>
          ) : null}
          <div className="min-h-0 flex-1 space-y-1 overflow-y-auto px-2 py-2">
            {eventPreviewItems.map(({ kind, item }) => (
              <a
                key={`${kind}-${item.id}`}
                href={item.url}
                target="_blank"
                rel="noreferrer"
                className="block rounded border border-slate-800/70 bg-slate-900/75 px-2 py-1 transition-colors hover:bg-slate-800"
              >
                <div className="mb-0.5 flex items-center justify-between gap-2">
                  <span className="text-[10px] uppercase tracking-wide text-slate-400">{kind}</span>
                  <span className="text-[10px] text-slate-500">{formatEventTime(item.publishedAt)}</span>
                </div>
                <div className="line-clamp-2 text-[11px] text-slate-100">{item.title}</div>
              </a>
            ))}
          </div>
          <button
            type="button"
            aria-label="Resize market events panel"
            data-chart-context-ignore="true"
            onPointerDown={handleMarketEventsPanelResizeStart}
            className="absolute bottom-1 right-1 h-4 w-4 cursor-se-resize rounded-sm text-slate-500 transition-colors hover:bg-slate-800 hover:text-slate-300"
          >
            <span className="pointer-events-none absolute bottom-0.5 right-0.5 leading-none">//</span>
          </button>
        </div>
      ) : null}
      {hasMainPaneLegend && areOverlayLegendItemsVisible && runtimeStatusOptions.showIndicatorTitles ? (
        <div
          className="pointer-events-none absolute z-20 flex flex-col items-start gap-1"
          style={{ left: `${leftOverlayInsetPx}px`, top: `${legendTopPx}px` }}
        >
          {mainPaneLegendItems.map((item) => renderLegendRow(item))}
        </div>
      ) : null}
      {areOverlayLegendItemsVisible && runtimeStatusOptions.showIndicatorTitles
        ? paneLegendItems.map((item) => {
            const top = paneLegendTopByKey[item.instanceId];
            if (top === undefined) {
              return null;
            }

            return (
              <div
                key={`pane-legend-${item.instanceId}`}
                className="pointer-events-none absolute z-20"
                style={{ left: `${leftOverlayInsetPx}px`, top: `${top}px` }}
              >
                {renderLegendRow(item)}
              </div>
            );
          })
        : null}
      {hasAnyLegendItems ? (
          <div
            className="pointer-events-none absolute z-20 pl-2"
            style={{ left: `${leftOverlayInsetPx}px`, top: `${overlayLegendToggleTopPx}px` }}
          >
          <button
            type="button"
            onClick={() => onToggleOverlayLegendItemsVisibility?.()}
            className="pointer-events-auto inline-flex h-5 min-w-5 items-center justify-center rounded border border-slate-600 bg-slate-900/90 px-1.5 text-[10px] text-slate-200 transition-colors hover:bg-slate-800"
            aria-label={areOverlayLegendItemsVisible ? 'Hide indicator labels' : 'Show indicator labels'}
            title={areOverlayLegendItemsVisible ? 'Hide indicator labels' : 'Show indicator labels'}
          >
            {areOverlayLegendItemsVisible ? '^' : 'v'}
          </button>
        </div>
      ) : null}
      {showTraderProV3Badge ? (
        <div
          className="pointer-events-none absolute z-10 rounded-md border border-slate-700/80 bg-slate-900/85 px-2 py-1 text-xs text-slate-200"
          style={{ left: `${leftOverlayInsetPx}px`, top: `${leftInfoBadgeBaseTopPx}px` }}
        >
          {traderProV3Overlay.info}
        </div>
      ) : null}
      {showTraderPro17Badge ? (
        <div
          className="pointer-events-none absolute z-10 rounded-md border border-slate-700/80 bg-slate-900/85 px-2 py-1 text-xs text-amber-100"
          style={{ left: `${leftOverlayInsetPx}px`, top: `${traderPro17InfoTopPx}px` }}
        >
          {traderPro17Overlay.info}
        </div>
      ) : null}
      {showColorProV3InfoBadge ? (
        <div
          className="pointer-events-none absolute right-3 rounded-md border border-slate-700/80 bg-slate-900/85 px-2 py-1 text-xs text-orange-100"
          style={{ top: `${rightInfoBadgeBaseTopPx}px` }}
        >
          {colorProV3Overlay?.info}
        </div>
      ) : null}
      {showColorProV31InfoBadge ? (
        <div
          className="pointer-events-none absolute right-3 rounded-md border border-slate-700/80 bg-slate-900/85 px-2 py-1 text-xs text-orange-100"
          style={{ top: `${colorProV31InfoTopPx}px` }}
        >
          {colorProV31Overlay?.info}
        </div>
      ) : null}
      {showStrategyInfoBadge && strategyOverlay ? (
        <div
          className="pointer-events-none absolute z-10 rounded-md border border-slate-700/80 bg-slate-900/85 px-2 py-1 text-xs text-indigo-100"
          style={{ left: `${leftOverlayInsetPx}px`, top: `${strategyInfoTopPx}px` }}
        >
          <span className="font-medium">Strategy:</span>{' '}
          {strategyOverlay.stats.totalTrades} trades |{' '}
          {strategyOverlay.stats.winRate.toFixed(1)}% win |{' '}
          {formatSignedPercent(strategyOverlay.stats.totalPnlPercent)}
          {strategyOverlay.activeTrade ? (
            <span className="ml-2 text-indigo-200">
              ({strategyOverlay.activeTrade.state === 'pendingBuy' ? 'Pending' : 'Active'})
            </span>
          ) : null}
        </div>
      ) : null}
      {showColorProV31StrategyInfoBadge && colorProV31StrategyOverlay ? (
        <div
          className="pointer-events-none absolute z-10 rounded-md border border-slate-700/80 bg-slate-900/85 px-2 py-1 text-xs text-emerald-100"
          style={{ left: `${leftOverlayInsetPx}px`, top: `${colorProV31StrategyInfoTopPx}px` }}
        >
          <span className="font-medium">ColorPro v3.1 Strategy:</span>{' '}
          {colorProV31StrategyOverlay.stats.totalTrades} trades |{' '}
          {colorProV31StrategyOverlay.stats.winRate.toFixed(1)}% win |{' '}
          {formatSignedPercent(colorProV31StrategyOverlay.stats.totalPnlPercent)}
          {colorProV31StrategyOverlay.activeTrade ? (
            <span className="ml-2 text-emerald-200">(Active)</span>
          ) : null}
        </div>
      ) : null}
      {showOpenCode5mStrategyInfoBadge && openCode5mStrategyOverlay ? (
        <div
          className="pointer-events-none absolute z-10 rounded-md border border-slate-700/80 bg-slate-900/85 px-2 py-1 text-xs text-cyan-100"
          style={{ left: `${leftOverlayInsetPx}px`, top: `${openCode5mStrategyInfoTopPx}px` }}
        >
          <span className="font-medium">OpenCode 5m:</span>{' '}
          {openCode5mStrategyOverlay.stats.totalTrades} trades |{' '}
          {openCode5mStrategyOverlay.stats.winRate.toFixed(1)}% win |{' '}
          {formatSignedPercent(openCode5mStrategyOverlay.stats.totalPnlPercent)}
          {openCode5mStrategyOverlay.activeTrade ? (
            <span className="ml-2 text-cyan-200">({openCode5mStrategyOverlay.activeTrade.side.toUpperCase()})</span>
          ) : null}
        </div>
      ) : null}
      {showMurreyMathLinesV6InfoBadge && murreyMathLinesV6Overlay ? (
        <div
          className="pointer-events-none absolute z-10 rounded-md border border-slate-700/80 bg-slate-900/85 px-2 py-1 text-xs text-cyan-100"
          style={{ left: `${leftOverlayInsetPx}px`, top: `${murreyMathLinesV6InfoTopPx}px` }}
        >
          {murreyMathLinesV6Overlay.info}
        </div>
      ) : null}
      {chartDisplaySettings.events.showSessionBreaks
        ? sessionBreakCoordinates.map((coordinate) => (
            <div
              key={`session-break-${coordinate}`}
              className="pointer-events-none absolute top-0 bottom-6 z-10 w-px bg-slate-500/35"
              style={{ left: `${coordinate}px` }}
            />
          ))
        : null}
      {lockedCursorCoordinate !== null ? (
        <div
          className="pointer-events-none absolute top-0 bottom-6 z-20 w-px bg-amber-300/80"
          style={{ left: `${lockedCursorCoordinate}px` }}
        />
      ) : null}
      {runtimeStatusOptions.showBuySellButtons ? (
        <div className="pointer-events-none absolute z-20 flex flex-col gap-2" style={{ left: `${leftOverlayInsetPx}px`, top: `${statusActionsTopPx}px` }}>
          <div className="flex gap-2">
            <button
              type="button"
              data-chart-context-ignore="true"
              onClick={() => handleAddPaperOrder('buy', 'market')}
              className="pointer-events-auto rounded-md border border-emerald-500/70 bg-emerald-500/12 px-3 py-1.5 text-xs font-semibold text-emerald-100 transition-colors hover:bg-emerald-500/20"
            >
              Buy 1 {symbol}
            </button>
            <button
              type="button"
              data-chart-context-ignore="true"
              onClick={() => handleAddPaperOrder('sell', 'market')}
              className="pointer-events-auto rounded-md border border-orange-400/70 bg-orange-500/12 px-3 py-1.5 text-xs font-semibold text-orange-100 transition-colors hover:bg-orange-500/20"
            >
              Sell 1 {symbol}
            </button>
          </div>
          {showPaperSummary && paperAccount ? (
            <div className="pointer-events-none inline-flex flex-wrap items-center gap-3 rounded-md border border-slate-700/80 bg-slate-950/90 px-3 py-2 text-[11px] text-slate-200 shadow-lg">
              <span>Cash {formatNumber(paperAccount.cashBalance, 2)}</span>
              <span>Equity {formatNumber(paperAccount.equity, 2)}</span>
              <span>Realized {formatSignedNumber(paperAccount.realizedPnl, 2)}</span>
              <span>Working {workingPaperOrders.length}</span>
            </div>
          ) : null}
        </div>
      ) : null}
      {contextMenu.open ? (
        <div
          ref={contextMenuPanelRef}
          className="absolute z-[70] w-[290px] overflow-hidden rounded-lg border border-slate-700/90 bg-slate-900/97 p-2 shadow-2xl backdrop-blur"
          style={{
            left: `${Math.max(8, Math.min(contextMenu.x, Math.max(8, chartViewportWidth - 306)))}px`,
            top: `${Math.max(8, contextMenu.y)}px`,
          }}
        >
          {renderContextMenuContent()}
        </div>
      ) : null}
      <div
        ref={chartSettingsPanelRef}
        className="absolute right-[0.1875rem] bottom-[0.1875rem] z-30"
      >
        {isChartSettingsOpen ? (
          <div className="absolute right-0 bottom-full mb-2 w-64 overflow-hidden rounded-lg border border-slate-700/90 bg-slate-900/95 p-2 shadow-2xl backdrop-blur">
            <button
              type="button"
              className={menuRowClassName}
              onClick={() => handleAutoFitChange(!chartDisplaySettings.autoFit)}
            >
              <span className="flex items-center gap-2">
                {renderMenuCheck(chartDisplaySettings.autoFit)}
                <span>Auto (fits data to screen)</span>
              </span>
            </button>
            <button
              type="button"
              disabled
              className="flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-xs text-slate-500"
            >
              <span>Lock price to bar ratio</span>
              <span className="text-[10px] uppercase tracking-wide text-slate-600">Unavailable</span>
            </button>
            <button
              type="button"
              className={menuRowClassName}
              onClick={() =>
                updateChartDisplaySettings((previous) => ({
                  ...previous,
                  scalePriceChartOnly: !previous.scalePriceChartOnly,
                }))
              }
            >
              <span className="flex items-center gap-2">
                {renderMenuCheck(chartDisplaySettings.scalePriceChartOnly)}
                <span>Scale price chart only</span>
              </span>
            </button>
            <button
              type="button"
              className={menuRowClassName}
              onClick={() =>
                updateChartDisplaySettings((previous) => ({
                  ...previous,
                  invertScale: !previous.invertScale,
                }))
              }
            >
              <span className="flex items-center gap-2">
                {renderMenuCheck(chartDisplaySettings.invertScale)}
                <span>Invert scale</span>
              </span>
              <span className="text-[10px] text-slate-400">Alt + I</span>
            </button>

            <div className="my-2 border-t border-slate-800/90" />

            <button
              type="button"
              className={menuRowClassName}
              onClick={() =>
                updateChartDisplaySettings((previous) => ({
                  ...previous,
                  scaleMode: 'regular',
                }))
              }
            >
              <span className="flex items-center gap-2">{renderMenuCheck(chartDisplaySettings.scaleMode === 'regular')}</span>
              <span className="mr-auto">Regular</span>
            </button>
            <button
              type="button"
              className={menuRowClassName}
              onClick={() =>
                updateChartDisplaySettings((previous) => ({
                  ...previous,
                  scaleMode: 'percent',
                }))
              }
            >
              <span className="flex items-center gap-2">{renderMenuCheck(chartDisplaySettings.scaleMode === 'percent')}</span>
              <span className="mr-auto">Percent</span>
              <span className="text-[10px] text-slate-400">Alt + P</span>
            </button>
            <button
              type="button"
              className={menuRowClassName}
              onClick={() =>
                updateChartDisplaySettings((previous) => ({
                  ...previous,
                  scaleMode: 'indexedTo100',
                }))
              }
            >
              <span className="flex items-center gap-2">
                {renderMenuCheck(chartDisplaySettings.scaleMode === 'indexedTo100')}
              </span>
              <span className="mr-auto">Indexed to 100</span>
            </button>
            <button
              type="button"
              className={menuRowClassName}
              onClick={() =>
                updateChartDisplaySettings((previous) => ({
                  ...previous,
                  scaleMode: 'logarithmic',
                }))
              }
            >
              <span className="flex items-center gap-2">
                {renderMenuCheck(chartDisplaySettings.scaleMode === 'logarithmic')}
              </span>
              <span className="mr-auto">Logarithmic</span>
              <span className="text-[10px] text-slate-400">Alt + L</span>
            </button>

            <div className="my-2 border-t border-slate-800/90" />

            <button
              type="button"
              disabled
              className="flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-xs text-slate-500"
            >
              <span>Move scale to left</span>
              <span className="text-[10px] uppercase tracking-wide text-slate-600">Unavailable</span>
            </button>
            <button
              type="button"
              className={menuRowClassName}
              onClick={() =>
                setChartSettingsSubmenu((previous) => (previous === 'labels' ? null : 'labels'))
              }
            >
              <span>Labels</span>
              <span className="text-slate-400">&gt;</span>
            </button>
            {chartSettingsSubmenu === 'labels' ? (
              <div className="space-y-1 rounded bg-slate-950/50 p-2">
                <button
                  type="button"
                  className={menuRowClassName}
                  onClick={() =>
                    updateChartDisplaySettings((previous) => ({
                      ...previous,
                      labels: {
                        ...previous.labels,
                        crosshairLabels: !previous.labels.crosshairLabels,
                      },
                    }))
                  }
                >
                  <span className="flex items-center gap-2">
                    {renderMenuCheck(chartDisplaySettings.labels.crosshairLabels)}
                    <span>Crosshair labels</span>
                  </span>
                </button>
                <button
                  type="button"
                  className={menuRowClassName}
                  onClick={() =>
                    updateChartDisplaySettings((previous) => ({
                      ...previous,
                      labels: {
                        ...previous.labels,
                        scaleTicks: !previous.labels.scaleTicks,
                      },
                    }))
                  }
                >
                  <span className="flex items-center gap-2">
                    {renderMenuCheck(chartDisplaySettings.labels.scaleTicks)}
                    <span>Price scale ticks</span>
                  </span>
                </button>
              </div>
            ) : null}
            <button
              type="button"
              className={menuRowClassName}
              onClick={() =>
                setChartSettingsSubmenu((previous) => (previous === 'lines' ? null : 'lines'))
              }
            >
              <span>Lines</span>
              <span className="text-slate-400">&gt;</span>
            </button>
            {chartSettingsSubmenu === 'lines' ? (
              <div className="space-y-1 rounded bg-slate-950/50 p-2">
                <button
                  type="button"
                  className={menuRowClassName}
                  onClick={() =>
                    updateChartDisplaySettings((previous) => ({
                      ...previous,
                      lines: {
                        ...previous.lines,
                        horizontalGrid: !previous.lines.horizontalGrid,
                      },
                    }))
                  }
                >
                  <span className="flex items-center gap-2">
                    {renderMenuCheck(chartDisplaySettings.lines.horizontalGrid)}
                    <span>Horizontal grid</span>
                  </span>
                </button>
                <button
                  type="button"
                  className={menuRowClassName}
                  onClick={() =>
                    updateChartDisplaySettings((previous) => ({
                      ...previous,
                      lines: {
                        ...previous.lines,
                        verticalGrid: !previous.lines.verticalGrid,
                      },
                    }))
                  }
                >
                  <span className="flex items-center gap-2">
                    {renderMenuCheck(chartDisplaySettings.lines.verticalGrid)}
                    <span>Vertical grid</span>
                  </span>
                </button>
                <button
                  type="button"
                  className={menuRowClassName}
                  onClick={() =>
                    updateChartDisplaySettings((previous) => ({
                      ...previous,
                      lines: {
                        ...previous.lines,
                        crosshairLines: !previous.lines.crosshairLines,
                      },
                    }))
                  }
                >
                  <span className="flex items-center gap-2">
                    {renderMenuCheck(chartDisplaySettings.lines.crosshairLines)}
                    <span>Crosshair lines</span>
                  </span>
                </button>
              </div>
            ) : null}
            <div className="my-2 border-t border-slate-800/90" />

            <button
              type="button"
              className="flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-xs text-slate-300 transition-colors hover:bg-slate-800/90"
              onClick={() => {
                setChartSettingsSubmenu(null);
                setIsChartSettingsOpen(false);
                setIsMoreSettingsModalOpen(true);
              }}
            >
              <span>More settings...</span>
            </button>
          </div>
        ) : null}

        <button
          type="button"
          data-chart-context-ignore="true"
          onClick={() => {
            setIsChartSettingsOpen((previous) => !previous);
            setChartSettingsSubmenu(null);
          }}
          className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-slate-700 bg-slate-900/90 text-slate-300 shadow-lg transition-colors hover:bg-slate-800 hover:text-slate-100"
          aria-label="Open chart settings"
          title="Chart settings"
        >
          <ChartSettingsIcon />
        </button>
      </div>
      {isMoreSettingsModalOpen ? (
        <ChartSettingsModalFrame open={isMoreSettingsModalOpen} onClose={() => setIsMoreSettingsModalOpen(false)}>
            <div className="flex items-center justify-between border-b border-slate-700 px-4 py-3">
              <div>
                <h2 className="text-lg font-semibold text-slate-100">Settings</h2>
                <p className="text-xs text-slate-400">TradingView-style chart controls.</p>
              </div>
              <button
                type="button"
                onClick={() => setIsMoreSettingsModalOpen(false)}
                className="rounded p-1 text-slate-400 transition-colors hover:bg-slate-800 hover:text-slate-100"
                aria-label="Close chart settings"
              >
                X
              </button>
            </div>

            <div className="grid min-h-0 flex-1 grid-cols-1 overflow-hidden md:min-h-[520px] md:grid-cols-[220px_minmax(0,1fr)]">
              <aside className="border-b border-slate-800 p-3 md:border-b-0 md:border-r">
                {[
                  ['symbol', 'Symbol'],
                  ['statusLine', 'Status line'],
                  ['scalesAndLines', 'Scales and lines'],
                  ['canvas', 'Canvas'],
                  ['trading', 'Trading'],
                  ['alerts', 'Alerts'],
                  ['events', 'Events'],
                  ['dataSources', 'Data Sources'],
                ].map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    className={`mb-1 flex w-full items-center rounded px-3 py-2 text-left text-sm transition-colors ${
                      moreSettingsTab === value
                        ? 'bg-slate-800 text-slate-100'
                        : 'text-slate-300 hover:bg-slate-800/70'
                    }`}
                    onClick={() =>
                      setMoreSettingsTab(
                        value as
                          | 'symbol'
                          | 'statusLine'
                          | 'scalesAndLines'
                          | 'canvas'
                          | 'trading'
                          | 'alerts'
                          | 'events'
                          | 'dataSources',
                       )
                     }
                   >
                    {label}
                  </button>
                ))}
              </aside>

              <div className="min-h-0 overflow-y-auto p-4 text-sm text-slate-200 md:max-h-[60vh]">
                {moreSettingsTab === 'symbol' ? (
                  <div className="space-y-5">
                    <h3 className="text-xs uppercase tracking-wide text-slate-400">Candles</h3>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={modalDraftSettings.symbol.colorBarsBasedOnPreviousClose}
                        onChange={(event) =>
                          setModalDraftSettings((previous) => ({
                            ...previous,
                            symbol: {
                              ...previous.symbol,
                              colorBarsBasedOnPreviousClose: event.target.checked,
                            },
                          }))
                        }
                      />
                      <span>Color bars based on previous close</span>
                    </label>
                    <div className="grid gap-2 sm:grid-cols-2">
                      <label className="flex items-center justify-between gap-2 rounded border border-slate-700 p-2">
                        <span>Body</span>
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={modalDraftSettings.symbol.showBody}
                            onChange={(event) =>
                              setModalDraftSettings((previous) => ({
                                ...previous,
                                symbol: {
                                  ...previous.symbol,
                                  showBody: event.target.checked,
                                },
                              }))
                            }
                          />
                          <input
                            type="color"
                            value={modalDraftSettings.symbol.upBodyColor}
                            onChange={(event) =>
                              setModalDraftSettings((previous) => ({
                                ...previous,
                                symbol: {
                                  ...previous.symbol,
                                  upBodyColor: event.target.value,
                                },
                              }))
                            }
                          />
                          <input
                            type="color"
                            value={modalDraftSettings.symbol.downBodyColor}
                            onChange={(event) =>
                              setModalDraftSettings((previous) => ({
                                ...previous,
                                symbol: {
                                  ...previous.symbol,
                                  downBodyColor: event.target.value,
                                },
                              }))
                            }
                          />
                        </div>
                      </label>
                      <label className="flex items-center justify-between gap-2 rounded border border-slate-700 p-2">
                        <span>Borders</span>
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={modalDraftSettings.symbol.showBorders}
                            onChange={(event) =>
                              setModalDraftSettings((previous) => ({
                                ...previous,
                                symbol: {
                                  ...previous.symbol,
                                  showBorders: event.target.checked,
                                },
                              }))
                            }
                          />
                          <input
                            type="color"
                            value={modalDraftSettings.symbol.upBorderColor}
                            onChange={(event) =>
                              setModalDraftSettings((previous) => ({
                                ...previous,
                                symbol: {
                                  ...previous.symbol,
                                  upBorderColor: event.target.value,
                                },
                              }))
                            }
                          />
                          <input
                            type="color"
                            value={modalDraftSettings.symbol.downBorderColor}
                            onChange={(event) =>
                              setModalDraftSettings((previous) => ({
                                ...previous,
                                symbol: {
                                  ...previous.symbol,
                                  downBorderColor: event.target.value,
                                },
                              }))
                            }
                          />
                        </div>
                      </label>
                      <label className="flex items-center justify-between gap-2 rounded border border-slate-700 p-2 sm:col-span-2">
                        <span>Wick</span>
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={modalDraftSettings.symbol.showWick}
                            onChange={(event) =>
                              setModalDraftSettings((previous) => ({
                                ...previous,
                                symbol: {
                                  ...previous.symbol,
                                  showWick: event.target.checked,
                                },
                              }))
                            }
                          />
                          <input
                            type="color"
                            value={modalDraftSettings.symbol.upWickColor}
                            onChange={(event) =>
                              setModalDraftSettings((previous) => ({
                                ...previous,
                                symbol: {
                                  ...previous.symbol,
                                  upWickColor: event.target.value,
                                },
                              }))
                            }
                          />
                          <input
                            type="color"
                            value={modalDraftSettings.symbol.downWickColor}
                            onChange={(event) =>
                              setModalDraftSettings((previous) => ({
                                ...previous,
                                symbol: {
                                  ...previous.symbol,
                                  downWickColor: event.target.value,
                                },
                              }))
                            }
                          />
                        </div>
                      </label>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <label className="space-y-1">
                        <span className="text-xs uppercase tracking-wide text-slate-400">Precision</span>
                        <select
                          value={modalDraftSettings.symbol.precision}
                          onChange={(event) =>
                            setModalDraftSettings((previous) => ({
                              ...previous,
                              symbol: {
                                ...previous.symbol,
                                precision: event.target.value as ChartSymbolPrecisionOption,
                              },
                            }))
                          }
                          className="w-full rounded border border-slate-700 bg-slate-800 px-2 py-1"
                        >
                          {SYMBOL_PRECISION_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="space-y-1">
                        <span className="text-xs uppercase tracking-wide text-slate-400">Timezone</span>
                        <select
                          value={modalDraftSettings.symbol.timezone}
                          onChange={(event) =>
                            setModalDraftSettings((previous) => ({
                              ...previous,
                              symbol: {
                                ...previous.symbol,
                                timezone: event.target.value,
                              },
                            }))
                          }
                          className="w-full rounded border border-slate-700 bg-slate-800 px-2 py-1"
                        >
                          {SYMBOL_TIMEZONE_OPTIONS.map((option) => (
                            <option key={`${option.value}-${option.label}`} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                  </div>
                ) : null}

                {moreSettingsTab === 'statusLine' ? (
                  <div className="space-y-3">
                    {[
                      ['showSymbol', 'Symbol'],
                      ['showInterval', 'Interval'],
                      ['showExchange', 'Exchange'],
                      ['showOHLC', 'Chart values'],
                      ['showChange', 'Bar change values'],
                      ['showVolume', 'Volume'],
                      ['showLastDayChange', 'Last day change values'],
                      ['showPaperSummary', 'Paper account summary'],
                      ['showIndicatorArguments', 'Indicator arguments'],
                    ].map(([field, label]) => (
                      <label key={field} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={
                            modalDraftSettings.statusLine[
                              field as
                                | 'showSymbol'
                                | 'showInterval'
                                | 'showExchange'
                                | 'showOHLC'
                                | 'showChange'
                                | 'showVolume'
                                | 'showLastDayChange'
                                | 'showPaperSummary'
                                | 'showIndicatorArguments'
                            ]
                          }
                          onChange={(event) =>
                            setModalDraftSettings((previous) => ({
                              ...previous,
                              statusLine: {
                                ...previous.statusLine,
                                [field]: event.target.checked,
                              },
                            }))
                          }
                        />
                        <span>{label}</span>
                      </label>
                    ))}
                    <label className="block space-y-1">
                      <span className="text-xs uppercase tracking-wide text-slate-400">
                        Background opacity ({modalDraftSettings.statusLine.backgroundOpacity}%)
                      </span>
                      <input
                        type="range"
                        min={0}
                        max={100}
                        value={modalDraftSettings.statusLine.backgroundOpacity}
                        onChange={(event) =>
                          setModalDraftSettings((previous) => ({
                            ...previous,
                            statusLine: {
                              ...previous.statusLine,
                              backgroundOpacity: Number(event.target.value),
                            },
                          }))
                        }
                        className="w-full"
                      />
                    </label>
                  </div>
                ) : null}

                {moreSettingsTab === 'scalesAndLines' ? (
                  <div className="space-y-3">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={modalDraftSettings.autoFit}
                        onChange={(event) =>
                          setModalDraftSettings((previous) => ({
                            ...previous,
                            autoFit: event.target.checked,
                          }))
                        }
                      />
                      <span>Auto fit price scale</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={modalDraftSettings.scalePriceChartOnly}
                        onChange={(event) =>
                          setModalDraftSettings((previous) => ({
                            ...previous,
                            scalePriceChartOnly: event.target.checked,
                          }))
                        }
                      />
                      <span>Scale price chart only</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={modalDraftSettings.invertScale}
                        onChange={(event) =>
                          setModalDraftSettings((previous) => ({
                            ...previous,
                            invertScale: event.target.checked,
                          }))
                        }
                      />
                      <span>Invert scale</span>
                    </label>
                    <label className="space-y-1">
                      <span className="text-xs uppercase tracking-wide text-slate-400">Scale mode</span>
                      <select
                        value={modalDraftSettings.scaleMode}
                        onChange={(event) =>
                          setModalDraftSettings((previous) => ({
                            ...previous,
                            scaleMode: event.target.value as ChartScaleModeOption,
                          }))
                        }
                        className="w-full rounded border border-slate-700 bg-slate-800 px-2 py-1"
                      >
                        <option value="regular">Regular</option>
                        <option value="percent">Percent</option>
                        <option value="indexedTo100">Indexed to 100</option>
                        <option value="logarithmic">Logarithmic</option>
                      </select>
                    </label>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={modalDraftSettings.labels.crosshairLabels}
                          onChange={(event) =>
                            setModalDraftSettings((previous) => ({
                              ...previous,
                              labels: {
                                ...previous.labels,
                                crosshairLabels: event.target.checked,
                              },
                            }))
                          }
                        />
                        <span>Crosshair labels</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={modalDraftSettings.labels.scaleTicks}
                          onChange={(event) =>
                            setModalDraftSettings((previous) => ({
                              ...previous,
                              labels: {
                                ...previous.labels,
                                scaleTicks: event.target.checked,
                              },
                            }))
                          }
                        />
                        <span>Price scale ticks</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={modalDraftSettings.lines.horizontalGrid}
                          onChange={(event) =>
                            setModalDraftSettings((previous) => ({
                              ...previous,
                              lines: {
                                ...previous.lines,
                                horizontalGrid: event.target.checked,
                              },
                            }))
                          }
                        />
                        <span>Horizontal grid</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={modalDraftSettings.lines.verticalGrid}
                          onChange={(event) =>
                            setModalDraftSettings((previous) => ({
                              ...previous,
                              lines: {
                                ...previous.lines,
                                verticalGrid: event.target.checked,
                              },
                            }))
                          }
                        />
                        <span>Vertical grid</span>
                      </label>
                      <label className="flex items-center gap-2 sm:col-span-2">
                        <input
                          type="checkbox"
                          checked={modalDraftSettings.lines.crosshairLines}
                          onChange={(event) =>
                            setModalDraftSettings((previous) => ({
                              ...previous,
                              lines: {
                                ...previous.lines,
                                crosshairLines: event.target.checked,
                              },
                            }))
                          }
                        />
                        <span>Crosshair lines</span>
                      </label>
                    </div>
                  </div>
                ) : null}

                {moreSettingsTab === 'canvas' ? (
                  <div className="space-y-3">
                    <label className="space-y-1">
                      <span className="text-xs uppercase tracking-wide text-slate-400">Background</span>
                      <select
                        value={modalDraftSettings.canvas.backgroundType}
                        onChange={(event) =>
                          setModalDraftSettings((previous) => ({
                            ...previous,
                            canvas: {
                              ...previous.canvas,
                              backgroundType: event.target.value as 'solid' | 'gradient',
                            },
                          }))
                        }
                        className="w-full rounded border border-slate-700 bg-slate-800 px-2 py-1"
                      >
                        <option value="solid">Solid</option>
                        <option value="gradient">Gradient</option>
                      </select>
                    </label>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <label className="space-y-1">
                        <span className="text-xs text-slate-400">Solid color</span>
                        <input
                          type="color"
                          value={modalDraftSettings.canvas.backgroundColor}
                          onChange={(event) =>
                            setModalDraftSettings((previous) => ({
                              ...previous,
                              canvas: {
                                ...previous.canvas,
                                backgroundColor: event.target.value,
                              },
                            }))
                          }
                        />
                      </label>
                      <label className="space-y-1">
                        <span className="text-xs text-slate-400">Grid color</span>
                        <input
                          type="color"
                          value={modalDraftSettings.canvas.gridColor}
                          onChange={(event) =>
                            setModalDraftSettings((previous) => ({
                              ...previous,
                              canvas: {
                                ...previous.canvas,
                                gridColor: event.target.value,
                              },
                            }))
                          }
                        />
                      </label>
                      <label className="space-y-1">
                        <span className="text-xs text-slate-400">Gradient top</span>
                        <input
                          type="color"
                          value={modalDraftSettings.canvas.backgroundTopColor}
                          onChange={(event) =>
                            setModalDraftSettings((previous) => ({
                              ...previous,
                              canvas: {
                                ...previous.canvas,
                                backgroundTopColor: event.target.value,
                              },
                            }))
                          }
                        />
                      </label>
                      <label className="space-y-1">
                        <span className="text-xs text-slate-400">Gradient bottom</span>
                        <input
                          type="color"
                          value={modalDraftSettings.canvas.backgroundBottomColor}
                          onChange={(event) =>
                            setModalDraftSettings((previous) => ({
                              ...previous,
                              canvas: {
                                ...previous.canvas,
                                backgroundBottomColor: event.target.value,
                              },
                            }))
                          }
                        />
                      </label>
                      <label className="space-y-1">
                        <span className="text-xs text-slate-400">Text color</span>
                        <input
                          type="color"
                          value={modalDraftSettings.canvas.textColor}
                          onChange={(event) =>
                            setModalDraftSettings((previous) => ({
                              ...previous,
                              canvas: {
                                ...previous.canvas,
                                textColor: event.target.value,
                              },
                            }))
                          }
                        />
                      </label>
                      <label className="space-y-1">
                        <span className="text-xs text-slate-400">Crosshair color</span>
                        <input
                          type="color"
                          value={modalDraftSettings.canvas.crosshairColor}
                          onChange={(event) =>
                            setModalDraftSettings((previous) => ({
                              ...previous,
                              canvas: {
                                ...previous.canvas,
                                crosshairColor: event.target.value,
                              },
                            }))
                          }
                        />
                      </label>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-3">
                      <label className="space-y-1">
                        <span className="text-xs text-slate-400">Top margin %</span>
                        <input
                          type="number"
                          min={0}
                          max={95}
                          value={modalDraftSettings.canvas.margins.top}
                          onChange={(event) =>
                            setModalDraftSettings((previous) => ({
                              ...previous,
                              canvas: {
                                ...previous.canvas,
                                margins: {
                                  ...previous.canvas.margins,
                                  top: Number(event.target.value),
                                },
                              },
                            }))
                          }
                          className="w-full rounded border border-slate-700 bg-slate-800 px-2 py-1"
                        />
                      </label>
                      <label className="space-y-1">
                        <span className="text-xs text-slate-400">Bottom margin %</span>
                        <input
                          type="number"
                          min={0}
                          max={95}
                          value={modalDraftSettings.canvas.margins.bottom}
                          onChange={(event) =>
                            setModalDraftSettings((previous) => ({
                              ...previous,
                              canvas: {
                                ...previous.canvas,
                                margins: {
                                  ...previous.canvas.margins,
                                  bottom: Number(event.target.value),
                                },
                              },
                            }))
                          }
                          className="w-full rounded border border-slate-700 bg-slate-800 px-2 py-1"
                        />
                      </label>
                      <label className="space-y-1">
                        <span className="text-xs text-slate-400">Right bars</span>
                        <input
                          type="number"
                          min={0}
                          max={50}
                          value={modalDraftSettings.canvas.margins.right}
                          onChange={(event) =>
                            setModalDraftSettings((previous) => ({
                              ...previous,
                              canvas: {
                                ...previous.canvas,
                                margins: {
                                  ...previous.canvas.margins,
                                  right: Number(event.target.value),
                                },
                              },
                            }))
                          }
                          className="w-full rounded border border-slate-700 bg-slate-800 px-2 py-1"
                        />
                      </label>
                    </div>
                  </div>
                ) : null}

                {moreSettingsTab === 'trading' ? (
                  <div className="space-y-3">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={modalDraftSettings.trading.showPositionsAndOrders}
                        onChange={(event) =>
                          setModalDraftSettings((previous) => ({
                            ...previous,
                            trading: {
                              ...previous.trading,
                              showPositionsAndOrders: event.target.checked,
                            },
                          }))
                        }
                      />
                      <span>Positions and orders</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={modalDraftSettings.trading.showExecutionMarks}
                        onChange={(event) =>
                          setModalDraftSettings((previous) => ({
                            ...previous,
                            trading: {
                              ...previous.trading,
                              showExecutionMarks: event.target.checked,
                            },
                          }))
                        }
                      />
                      <span>Execution marks</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={modalDraftSettings.trading.showExecutionLabels}
                        onChange={(event) =>
                          setModalDraftSettings((previous) => ({
                            ...previous,
                            trading: {
                              ...previous.trading,
                              showExecutionLabels: event.target.checked,
                            },
                          }))
                        }
                      />
                      <span>Execution labels</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={modalDraftSettings.trading.showSeriesLabels}
                        onChange={(event) =>
                          setModalDraftSettings((previous) => ({
                            ...previous,
                            trading: {
                              ...previous.trading,
                              showSeriesLabels: event.target.checked,
                            },
                          }))
                        }
                      />
                      <span>Indicator labels</span>
                    </label>
                  </div>
                ) : null}

                {moreSettingsTab === 'alerts' ? (
                  <div className="space-y-3">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={modalDraftSettings.alerts.showAlertLines}
                        onChange={(event) =>
                          setModalDraftSettings((previous) => ({
                            ...previous,
                            alerts: {
                              ...previous.alerts,
                              showAlertLines: event.target.checked,
                            },
                          }))
                        }
                      />
                      <span>Alert lines</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={modalDraftSettings.alerts.onlyActiveAlerts}
                        onChange={(event) =>
                          setModalDraftSettings((previous) => ({
                            ...previous,
                            alerts: {
                              ...previous.alerts,
                              onlyActiveAlerts: event.target.checked,
                            },
                          }))
                        }
                      />
                      <span>Only active alerts</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={modalDraftSettings.alerts.autoHideToasts}
                        onChange={(event) =>
                          setModalDraftSettings((previous) => ({
                            ...previous,
                            alerts: {
                              ...previous.alerts,
                              autoHideToasts: event.target.checked,
                            },
                          }))
                        }
                      />
                      <span>Automatically hide toasts</span>
                    </label>
                    <div className="rounded border border-slate-800 bg-slate-950/40 p-3">
                      <div className="mb-3 flex items-start justify-between gap-3">
                        <div>
                          <div className="text-sm font-medium text-slate-100">Alert sound</div>
                          <p className="mt-1 text-xs text-slate-400">Choose the sound and preview the current volume.</p>
                        </div>
                        <button
                          type="button"
                          onClick={() =>
                            void playAlertSound(
                              modalDraftSettings.alerts.alertSoundType,
                              modalDraftSettings.alerts.alertSoundVolume,
                              true,
                            )
                          }
                          className="rounded-md border border-slate-600 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 transition-colors hover:bg-slate-700"
                        >
                          Test sound
                        </button>
                      </div>
                      <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
                        <label className="space-y-1">
                          <span className="text-xs text-slate-400">Sound type</span>
                          <select
                            value={modalDraftSettings.alerts.alertSoundType}
                            onChange={(event) =>
                              setModalDraftSettings((previous) => ({
                                ...previous,
                                alerts: {
                                  ...previous.alerts,
                                  alertSoundType: event.target.value as AlertSoundType,
                                },
                              }))
                            }
                            className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100"
                          >
                            {ALERT_SOUND_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </label>
                        <div className="text-xs text-slate-500">
                          {ALERT_SOUND_OPTIONS.find((option) => option.value === modalDraftSettings.alerts.alertSoundType)
                            ?.description ?? ''}
                        </div>
                      </div>
                      <label className="mt-3 block space-y-1">
                        <span className="flex items-center justify-between text-xs text-slate-400">
                          <span>Volume</span>
                          <span>{clampAlertSoundVolume(modalDraftSettings.alerts.alertSoundVolume)}%</span>
                        </span>
                        <input
                          type="range"
                          min={0}
                          max={100}
                          step={1}
                          value={clampAlertSoundVolume(modalDraftSettings.alerts.alertSoundVolume)}
                          onChange={(event) =>
                            setModalDraftSettings((previous) => ({
                              ...previous,
                              alerts: {
                                ...previous.alerts,
                                alertSoundVolume: clampAlertSoundVolume(Number(event.target.value)),
                              },
                            }))
                          }
                          className="w-full accent-amber-400"
                        />
                      </label>
                    </div>
                  </div>
                ) : null}

                {moreSettingsTab === 'events' ? (
                  <div className="space-y-3">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={modalDraftSettings.events.showIdeas}
                        onChange={(event) =>
                          setModalDraftSettings((previous) => ({
                            ...previous,
                            events: {
                              ...previous.events,
                              showIdeas: event.target.checked,
                            },
                          }))
                        }
                      />
                      <span>Ideas</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={modalDraftSettings.events.showSessionBreaks}
                        onChange={(event) =>
                          setModalDraftSettings((previous) => ({
                            ...previous,
                            events: {
                              ...previous.events,
                              showSessionBreaks: event.target.checked,
                            },
                          }))
                        }
                      />
                      <span>Session breaks</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={modalDraftSettings.events.showEconomicEvents}
                        onChange={(event) =>
                          setModalDraftSettings((previous) => ({
                            ...previous,
                            events: {
                              ...previous.events,
                              showEconomicEvents: event.target.checked,
                            },
                          }))
                        }
                      />
                      <span>Economic events</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={modalDraftSettings.events.showLatestNews}
                        onChange={(event) =>
                          setModalDraftSettings((previous) => ({
                            ...previous,
                            events: {
                              ...previous.events,
                              showLatestNews: event.target.checked,
                            },
                          }))
                        }
                      />
                      <span>Latest news</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={modalDraftSettings.events.showNewsNotifications}
                        onChange={(event) =>
                          setModalDraftSettings((previous) => ({
                            ...previous,
                            events: {
                              ...previous.events,
                              showNewsNotifications: event.target.checked,
                            },
                          }))
                        }
                      />
                      <span>News notification</span>
                    </label>

                    <div className="rounded border border-slate-700 bg-slate-950/40 p-2 text-xs">
                      <div className="mb-2 flex items-center justify-between">
                        <span className="font-medium text-slate-200">Live feed preview</span>
                        <button
                          type="button"
                          onClick={() => {
                            void onRefreshEvents?.();
                          }}
                          className="rounded border border-slate-600 px-2 py-1 text-[10px] text-slate-300 transition-colors hover:bg-slate-800"
                        >
                          Refresh
                        </button>
                      </div>
                      {eventsErrorMessage ? (
                        <div className="mb-2 rounded border border-rose-500/40 bg-rose-500/10 px-2 py-1 text-rose-200">
                          {eventsErrorMessage}
                        </div>
                      ) : null}
                      {eventsLoading && modalEventPreviewItems.length === 0 ? (
                        <div className="text-slate-400">Loading feed items...</div>
                      ) : null}
                      <div className="max-h-52 space-y-1 overflow-y-auto">
                        {modalEventPreviewItems.map(({ kind, item }) => (
                          <a
                            key={`modal-${kind}-${item.id}`}
                            href={item.url}
                            target="_blank"
                            rel="noreferrer"
                            className="block rounded border border-slate-800 bg-slate-900 px-2 py-1 hover:bg-slate-800"
                          >
                            <div className="mb-0.5 flex items-center justify-between text-[10px] text-slate-400">
                              <span className="uppercase">{kind}</span>
                              <span>{formatEventTime(item.publishedAt)}</span>
                            </div>
                            <div className="line-clamp-2 text-[11px] text-slate-100">{item.title}</div>
                          </a>
                        ))}
                        {!eventsLoading && modalEventPreviewItems.length === 0 ? (
                          <div className="text-slate-500">Enable one or more event streams to preview items.</div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ) : null}

                {moreSettingsTab === 'dataSources' ? (
                  <div className="space-y-5">
                    <div>
                      <h3 className="text-xs uppercase tracking-wide text-slate-400">Data Sources</h3>
                      <p className="mt-1 text-xs text-slate-500">Debug toggles for each price data source.</p>
                    </div>

                    <div className="-mx-4">
                      <DataSourceSettingsPanel
                        settings={modalDraftDataSources}
                        onChange={setModalDraftDataSources}
                        showFooterActions={false}
                      />
                    </div>

                    <div className="flex flex-wrap items-start gap-3 rounded border border-slate-800 bg-slate-950/40 p-3">
                      <button
                        type="button"
                        onClick={() => setModalDraftDataSources(defaultDataSources)}
                        className="rounded-md border border-slate-600 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 transition-colors hover:bg-slate-700"
                      >
                        Reset defaults
                      </button>
                      <div>
                        <button
                          type="button"
                          onClick={() => {
                            onDataSourcesChange(modalDraftDataSources);
                            onHardReloadCandles();
                          }}
                          className="rounded-md border border-amber-500/50 bg-amber-500/10 px-3 py-1.5 text-sm text-amber-200 transition-colors hover:bg-amber-500/20"
                        >
                          Hard Reload Candles
                        </button>
                        <p className="mt-1 text-[11px] text-slate-400">
                          Clears browser candle cache for this symbol and forces one fresh fetch without backend cache.
                        </p>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>

            <div className="flex items-center justify-between border-t border-slate-700 px-4 py-3">
              {moreSettingsTab === 'dataSources' ? <div /> : (
                <button
                  type="button"
                  onClick={() => setModalDraftSettings(DEFAULT_CHART_DISPLAY_SETTINGS)}
                  className="rounded-md border border-slate-600 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 transition-colors hover:bg-slate-700"
                >
                  Defaults
                </button>
              )}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setIsMoreSettingsModalOpen(false)}
                  className="rounded-md border border-slate-600 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 transition-colors hover:bg-slate-700"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    onDataSourcesChange(modalDraftDataSources);
                    updateChartDisplaySettings(() => modalDraftSettings);
                    if (
                      modalDraftSettings.autoFit &&
                      isCandleHelperVisible &&
                      onSetCandleHelperVisibility
                    ) {
                      onSetCandleHelperVisibility(false);
                    }
                    setIsMoreSettingsModalOpen(false);
                  }}
                  className="rounded-md bg-slate-100 px-4 py-1.5 text-sm font-medium text-slate-900 transition-colors hover:bg-white"
                >
                  Ok
                </button>
              </div>
            </div>
        </ChartSettingsModalFrame>
      ) : null}
      {isAlertsModalOpen ? (
        <ChartSettingsModalFrame open={isAlertsModalOpen} onClose={() => setIsAlertsModalOpen(false)}>
          <div className="flex items-center justify-between border-b border-slate-700 px-4 py-3">
            <div>
              <h2 className="text-lg font-semibold text-slate-100">Alerts</h2>
              <p className="text-xs text-slate-400">Review and manage active chart alerts.</p>
            </div>
            <button
              type="button"
              onClick={() => setIsAlertsModalOpen(false)}
              className="rounded p-1 text-slate-400 transition-colors hover:bg-slate-800 hover:text-slate-100"
              aria-label="Close alerts"
            >
              X
            </button>
          </div>
          <div className="flex items-center gap-2 border-b border-slate-800 px-4 py-3">
            {[
              ['current', `Current (${sortedCurrentSymbolAlerts.length})`],
              ['all', `All (${sortedAllAlerts.length})`],
            ].map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => setAlertsModalTab(value as 'current' | 'all')}
                className={`rounded-md px-3 py-1.5 text-sm transition-colors ${
                  alertsModalTab === value
                    ? 'bg-slate-100 text-slate-900'
                    : 'bg-slate-900 text-slate-300 hover:bg-slate-800'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="border-b border-slate-800 px-4 py-4">
            <div className="rounded-lg border border-slate-800 bg-slate-950/50 p-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-medium text-slate-100">Alert sound</div>
                  <p className="mt-1 text-xs text-slate-400">Preview the alert sound and adjust it before a price alert fires.</p>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    void playAlertSound(
                      chartDisplaySettings.alerts.alertSoundType,
                      chartDisplaySettings.alerts.alertSoundVolume,
                      true,
                    )
                  }
                  className="rounded-md border border-slate-600 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 transition-colors hover:bg-slate-700"
                >
                  Test sound
                </button>
              </div>
              <div className="mt-3 grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] md:items-end">
                <label className="space-y-1">
                  <span className="text-xs text-slate-400">Sound type</span>
                  <select
                    value={chartDisplaySettings.alerts.alertSoundType}
                    onChange={(event) =>
                      updateChartDisplaySettings((previous) => ({
                        ...previous,
                        alerts: {
                          ...previous.alerts,
                          alertSoundType: event.target.value as AlertSoundType,
                        },
                      }))
                    }
                    className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100"
                  >
                    {ALERT_SOUND_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="text-xs text-slate-500 md:pb-2">
                  {ALERT_SOUND_OPTIONS.find((option) => option.value === chartDisplaySettings.alerts.alertSoundType)
                    ?.description ?? ''}
                </div>
              </div>
              <label className="mt-3 block space-y-1">
                <span className="flex items-center justify-between text-xs text-slate-400">
                  <span>Volume</span>
                  <span>{clampAlertSoundVolume(chartDisplaySettings.alerts.alertSoundVolume)}%</span>
                </span>
                <input
                  type="range"
                  min={0}
                  max={100}
                  step={1}
                  value={clampAlertSoundVolume(chartDisplaySettings.alerts.alertSoundVolume)}
                  onChange={(event) =>
                    updateChartDisplaySettings((previous) => ({
                      ...previous,
                      alerts: {
                        ...previous.alerts,
                        alertSoundVolume: clampAlertSoundVolume(Number(event.target.value)),
                      },
                    }))
                  }
                  className="w-full accent-amber-400"
                />
              </label>
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-4">
            {alertsModalTab === 'current'
              ? renderAlertsList(sortedCurrentSymbolAlerts, `No alerts saved for ${symbol}.`)
              : renderAlertsList(sortedAllAlerts, 'No alerts saved yet.')}
          </div>
        </ChartSettingsModalFrame>
      ) : null}
      {isObjectTreeOpen ? (
        <ChartSettingsModalFrame open={isObjectTreeOpen} onClose={() => setIsObjectTreeOpen(false)}>
          <div className="flex items-center justify-between border-b border-slate-700 px-4 py-3">
            <div>
              <h2 className="text-lg font-semibold text-slate-100">Object tree</h2>
              <p className="text-xs text-slate-400">Indicators, alerts, orders, and cursor locks for {symbol}.</p>
            </div>
            <button
              type="button"
              onClick={() => setIsObjectTreeOpen(false)}
              className="rounded p-1 text-slate-400 transition-colors hover:bg-slate-800 hover:text-slate-100"
            >
              X
            </button>
          </div>
          <div className="grid min-h-0 flex-1 gap-4 overflow-y-auto p-4 text-sm text-slate-200 md:grid-cols-2">
            <div>
              <div className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Indicators</div>
              <div className="space-y-2">
                {overlayLegendItems.length === 0 ? <div className="text-slate-500">No indicators loaded.</div> : null}
                {overlayLegendItems.map((item) => (
                  <div key={item.instanceId} className="rounded border border-slate-800 bg-slate-950/60 px-3 py-2">
                    <div className="font-medium text-slate-100">{item.settingsLabel}</div>
                    <div className="text-xs text-slate-400">{item.active ? 'Visible' : 'Hidden'}</div>
                  </div>
                ))}
              </div>
              <div className="mb-2 mt-4 text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Drawings</div>
              <div className="space-y-2">
                {drawingItems.length === 0 ? <div className="text-slate-500">No drawings saved.</div> : null}
                {drawingItems.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => {
                      setSelectedDrawingId(item.id);
                      setIsObjectTreeOpen(false);
                    }}
                    className={`w-full rounded border px-3 py-2 text-left transition-colors ${
                      item.id === selectedDrawingId
                        ? 'border-sky-400/70 bg-sky-500/12 text-sky-100'
                        : 'border-slate-800 bg-slate-950/60 text-slate-200 hover:bg-slate-900/80'
                    }`}
                  >
                    <div className="font-medium text-inherit">{getDrawingToolLabel(item.kind)}</div>
                    <div className="text-xs text-slate-400">
                      {item.visible ? 'Visible' : 'Hidden'} {item.locked ? '• Locked' : ''}
                    </div>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Alerts</div>
              <div className="space-y-2">
                {alertItems.length === 0 ? <div className="text-slate-500">No alerts saved.</div> : null}
                {alertItems.map((item) => (
                  <div key={item.id} className="rounded border border-slate-800 bg-slate-950/60 px-3 py-2">
                    <div className="font-medium text-slate-100">{item.indicatorLabel ?? symbol}</div>
                    <div className="text-xs text-slate-400">
                      {item.price !== null ? `Price ${resolveContextPriceLabel(item.price)}` : 'Indicator alert'}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mb-2 mt-4 text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Paper orders</div>
              {paperAccount ? (
                <div className="mb-3 rounded border border-slate-800 bg-slate-950/60 px-3 py-2">
                  <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Paper account</div>
                  <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-slate-300">
                    <div>Cash</div>
                    <div className="text-right">{formatNumber(paperAccount.cashBalance, 2)} {paperAccount.currency}</div>
                    <div>Equity</div>
                    <div className="text-right">{formatNumber(paperAccount.equity, 2)} {paperAccount.currency}</div>
                    <div>Realized P&amp;L</div>
                    <div className="text-right">{formatSignedNumber(paperAccount.realizedPnl, 2)}</div>
                    <div>Starting balance</div>
                    <div className="text-right">{formatNumber(paperAccount.startingBalance, 2)} {paperAccount.currency}</div>
                  </div>
                </div>
              ) : null}
              <div className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Working orders</div>
              <div className="space-y-2">
                {workingPaperOrders.length === 0 ? <div className="text-slate-500">No working paper orders.</div> : null}
                {workingPaperOrders.map((order) => (
                  <div key={order.id} className="rounded border border-slate-800 bg-slate-950/60 px-3 py-2">
                    <div className="font-medium text-slate-100">
                      {order.side.toUpperCase()} {order.quantity} {symbol}
                    </div>
                    <div className="text-xs text-slate-400">
                      {order.orderType} @ {resolveContextPriceLabel(order.price)} - {order.status}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mb-2 mt-4 text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Fills</div>
              <div className="space-y-2">
                {filledPaperOrders.length === 0 ? <div className="text-slate-500">No fills yet.</div> : null}
                {filledPaperOrders.map((order) => (
                  <div key={`fill-${order.id}`} className="rounded border border-slate-800 bg-slate-950/60 px-3 py-2">
                    <div className="font-medium text-slate-100">
                      {order.side.toUpperCase()} {order.quantity} {symbol}
                    </div>
                    <div className="text-xs text-slate-400">
                      Filled @ {resolveContextPriceLabel(order.filledPrice ?? order.price)}
                      {order.filledAt ? ` • ${new Date(order.filledAt).toLocaleString()}` : ''}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </ChartSettingsModalFrame>
      ) : null}
      {isTableViewOpen ? (
        <ChartSettingsModalFrame open={isTableViewOpen} onClose={() => setIsTableViewOpen(false)}>
          <div className="flex items-center justify-between border-b border-slate-700 px-4 py-3">
            <div>
              <h2 className="text-lg font-semibold text-slate-100">Table view</h2>
              <p className="text-xs text-slate-400">Active candle snapshot and current chart objects.</p>
            </div>
            <button
              type="button"
              onClick={() => setIsTableViewOpen(false)}
              className="rounded p-1 text-slate-400 transition-colors hover:bg-slate-800 hover:text-slate-100"
            >
              X
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-4">
            <table className="min-w-full border-collapse text-left text-sm text-slate-200">
              <thead>
                <tr className="border-b border-slate-800 text-xs uppercase tracking-[0.14em] text-slate-400">
                  <th className="px-3 py-2">Field</th>
                  <th className="px-3 py-2">Value</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-slate-900/80"><td className="px-3 py-2">Symbol</td><td className="px-3 py-2">{symbol}</td></tr>
                <tr className="border-b border-slate-900/80"><td className="px-3 py-2">Interval</td><td className="px-3 py-2">{interval}</td></tr>
                <tr className="border-b border-slate-900/80"><td className="px-3 py-2">Time</td><td className="px-3 py-2">{activeCandle?.time ?? '-'}</td></tr>
                <tr className="border-b border-slate-900/80"><td className="px-3 py-2">Open</td><td className="px-3 py-2">{activeCandle ? resolveContextPriceLabel(activeCandle.open) : '-'}</td></tr>
                <tr className="border-b border-slate-900/80"><td className="px-3 py-2">High</td><td className="px-3 py-2">{activeCandle ? resolveContextPriceLabel(activeCandle.high) : '-'}</td></tr>
                <tr className="border-b border-slate-900/80"><td className="px-3 py-2">Low</td><td className="px-3 py-2">{activeCandle ? resolveContextPriceLabel(activeCandle.low) : '-'}</td></tr>
                <tr className="border-b border-slate-900/80"><td className="px-3 py-2">Close</td><td className="px-3 py-2">{activeCandle ? resolveContextPriceLabel(activeCandle.close) : '-'}</td></tr>
                <tr className="border-b border-slate-900/80"><td className="px-3 py-2">Volume</td><td className="px-3 py-2">{activeCandle ? formatNumber(activeCandle.volume, 0) : '-'}</td></tr>
                <tr className="border-b border-slate-900/80"><td className="px-3 py-2">Alerts</td><td className="px-3 py-2">{alertItems.length}</td></tr>
                <tr><td className="px-3 py-2">Working paper orders</td><td className="px-3 py-2">{paperOrders.filter((item) => item.status === 'working').length}</td></tr>
              </tbody>
            </table>
          </div>
        </ChartSettingsModalFrame>
      ) : null}
      {toastMessages.length > 0 ? (
        <div className="pointer-events-none absolute inset-x-0 bottom-12 z-[80] flex flex-col items-center gap-2 px-3">
          {toastMessages.map((toast) => (
            <div
              key={toast.id}
              className={`rounded-md border px-3 py-1.5 text-xs shadow-lg ${
                toast.tone === 'success'
                  ? 'border-emerald-400/50 bg-emerald-500/12 text-emerald-100'
                  : toast.tone === 'warning'
                    ? 'border-amber-400/50 bg-amber-500/12 text-amber-100'
                    : 'border-slate-500/50 bg-slate-900/95 text-slate-100'
              }`}
            >
              {toast.text}
            </div>
          ))}
        </div>
      ) : null}
      {isLoadingMoreHistory ? (
        <div className="pointer-events-none absolute inset-x-0 top-3 flex justify-center px-3">
          <div className="rounded-md border border-indigo-400/70 bg-slate-900/90 px-3 py-1 text-[11px] font-medium text-indigo-200">
            Loading older candles...
          </div>
        </div>
      ) : null}
    </div>
  );
}
